import { buildPhysicalContinuityBlock, trimAfterTimeskip } from './physicalState.js';
import { useBottomAnchor, autoGrowTextarea } from './useBottomAnchor.js';
import { loadEncyclopediaRows } from './encyclopediaLive.js';
import { enterShouldSend, enterSendsEnabled, setEnterSends } from './enterSends.js';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useContext, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { supabase, safeGetSession, safeRefreshSession } from "./supabaseClient.js";
import { createMemoryGate } from './memoryGate.js';
import { findUnknownNames, guardDirective, logGuardEvent } from './nameGuard.js';
import { ThemeContext } from "./ThemeContext.jsx";
import { buildSpeciesLoreSection } from "./speciesLore.js";
import { fetchWithRetry } from "./aiRetry.js";
import { AI_ABORT_MS, AI_SAFETY_MS, AI_TRUNCATED_NOTE, aiProxyErrorMessage } from "./aiTimeouts.js";
// readableOn picks legible ink for a given background — used where a bubble is
// painted in a CHARACTER's colour, which no theme token can know in advance.
import { fetchSteppedWindow } from './historyWindow.js';
import { readableOn } from "./theme.js";
import { prefersReduced } from "./motion.js";
import { CHARACTERS, PLAYERS, SCENE_TYPES } from "./characters.js";
import { ToastContainer } from "./ThemedUI.jsx";
import { isUserOnline } from "./presence.js";
import FeatureCompass from "./FeatureCompass.jsx";
// ─── EAGER IMPORTS (always needed or used on main tabs) ────────────────────
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getPushSubscription, registerServiceWorker, listenForSubscriptionChanges, sendPushNotification, ensurePushSubscription, startPushHealLoop } from './pushUtils.js';
import { loadNotifPrefs, saveNotifPrefs, setSnooze, isNotifAllowed, getSnoozeStatus, DEFAULT_PREFS, CHANNEL_PUSH_MODES, CHARACTER_DM_MODES, characterMayDM, nextCharacterDmPrefs } from './notifPrefs.js';
import { saveDraft, loadDraft, clearDraft, cleanupOldDrafts, setDraftUser } from './draftPersistence.js';
import { useSyncedDraft } from './useSyncedDraft.js';
import { getCharacterDiscordContext, getSceneUniverseContext, getCharacterRecentMessages, formatRecentMessagesForPrompt, getPriorFeedback, formatPriorFeedbackForPrompt, perceptionTag, PERCEPTION_RULES, recordFeedback } from './discordContext.js';
import { getPlayerPreferenceProfiles, formatPreferencesForPrompt, maybeRefreshPreferences, getCounterpartVoiceSection } from './playerPreferences.js';
import { buildDirectorBlock, appendDirector, isNearDuplicate, matchingPrior, buildRegenDiagnosis } from './director.js';
import { detectDialect as detectAiDialect } from './dialectClient.js';
import { getThreadModel } from './threadModel.js';
import ModelSwitch from './ModelSwitch.jsx';
import ThreadLorePanel from './ThreadLorePanel.jsx';
import { formatThreadLore } from './threadLore.js';
import { readLastView, saveLastView, wasDiscarded } from './lastView.js';
import { scopeFullHistory, FULL_STORY_MSGS } from './sceneTags.js';
import { MovieNightHomeCard } from './MovieNightCard.jsx';
import { MOVIE_NIGHT_CHANNEL_ID } from './movieNight.js';
import { mergeStoryHistory } from './storyHistory.js';
import { matchCharactersInText, firstNameOf, detectProtagonist } from './rpCast.js';
import { measureCastPresence, buildCastPresenceDirective } from './scenePresence.js';
import { buildMatureContentRules } from './matureContent.js';
import { assemblePrompt, castBalance, formatManifest, LAYER } from './promptManifest.js';
import RelationshipsPanel from './RelationshipsPanel.jsx';
import { getOutreachPrefs, saveOutreachPrefs } from './characterOutreach.js';
import { getCharacterFacts, formatFactsForPrompt, extractAndStoreFacts } from './characterFacts.js';
import { getRollingSummary, formatSummaryForPrompt, refreshRollingSummary, shouldRefreshSummary, getTrueMessageCount } from './rollingSummary.js';
import { getUnconsumedWhispers, formatWhispersForPrompt, formatWhispersForUserPrefix, markWhispersConsumed } from './narratorWhispers.js';
import SirenWhisperBox from './SirenWhisperBox.jsx';
import StoryLinksManager from './StoryLinksManager.jsx';
import { getCharacterAvatar } from './avatarMap.js';
import { getCharacterArt } from './characterArtMap.js';
import { getGlossaryPromptSection } from './rpGlossary.js';
import { getSpeciesContextForPair } from './speciesTraits.js';
import { getUniverseChannelContext } from './channelContext.js';
import { canCallAI, recordAICall } from './aiCooldown.js';
import { recordDailyLogin, awardXP } from './gamification.js';
import { buildIdentityAnchor, buildIdentityAnchorParts, loadCharacterOverrides, mergeCharWithOverrides, getRelatedCharacters, publicSummaryFor, scrubIdentityReveals, buildBoundaries, voiceContractFor, housePhraseList, characterPhraseList } from './characterIdentity.js';
import { IDENTITY_GATES, detectIdentityReveals, buildGateBlocks, gateFlagKey } from './identityGates.js';
import { extractStoryEvents, getCharacterStoryArc, formatStoryArcForPrompt, backfillStoryMemory } from './storyMemory.js';
import { getCharacterDynamics, formatDynamicsForPrompt } from './characterDynamics.js';
import { resolveRelationship } from './relationshipDynamics.js';
import { extractFacts, getCharacterMemory, formatMemoryForPrompt } from './conversationMemory.js';
import { buildLinkedMemorySection, embedSceneText, buildSceneQueryText } from './conversationLinks.js';
import { buildArchiveLoreSection } from './archiveLore.js';
import { withEpochCache, epochKey } from './retrievalEpoch.js';
import ConversationLinkPanel from './ConversationLinkPanel.jsx';
import { buildRPTimeline } from './storyTimeline.js';
import { buildBirthdayContext } from './userBirthdays.js';
import { matchesCharacterSearch } from './charSearch.js';
import { AppSplash, SectionLoader } from './SplashScreen.jsx';
import { ProfileFrame } from './ProfileFrames.jsx';
import { useFriendships, FriendButton, acceptFriendRequest, rejectFriendRequest, UserProfileModal } from './FriendSystem.jsx';
import { buildStoryPrefsPrompt } from './StoryPreferencesPanel.jsx';
import IncomingCallOverlay, { markPhoneRingShown } from './IncomingCallOverlay.jsx';
import { initNativePush, isNativePushActive } from './nativePush.js';
import { installBadgeSync, scheduleBadgeSync } from './appBadge.js';
import { formatUsage, usageLineStyle } from './usageBadge.js';
// ─── LEGAL + COMPLIANCE IMPORTS ──────────────────────────────────────────────
import CookieConsent from './legal/CookieConsent.jsx';
import SiteFooter from './legal/SiteFooter.jsx';
import AgeGate from './legal/AgeGate.jsx';
import CrisisResourcePanel from './legal/CrisisResourcePanel.jsx';
import DraftAssistant from './DraftAssistant.jsx';
import ContentConsentModal, { hasConsented } from './ContentConsentModal.jsx';
import { PinPanel, PinPopover } from './PinnedMessages.jsx';
import AnnouncementModal from './AnnouncementModal.jsx';
import { copyMessageText, isDoubleTap } from './messageActions.js';
import { initCrashCatcher, logClientError } from './crashCatcher.js';
import { isChunkLoadError } from './staleClient.js';
import { charIdToPhoneKey } from './phoneKeys.js';
import { loadIdeaFromStorage, cacheIdea, cleanIdea, buildIdeaPrompt } from './dailyRpIdea.js';
import { renderWithSpoilers } from './spoilers.jsx';
// Shared style objects live in ./sharedStyles.js (2026-09-14): the /design
// page renders the same vocabulary, and defining them here after 12k lines
// of use tripped no-use-before-define at every site.
import { btnPrimary, btnSmall, genTab, inputStyle, topBar, backBtn, modalOverlay, modalContent } from './sharedStyles.js';
import { isOOCMessage } from './oocDetect.js';

// ─── LAZY IMPORTS (loaded on demand when user navigates to them) ───────────
const StoryBrowser = lazy(() => import("./StoryBrowser.jsx"));
const AdminPanel = lazy(() => import("./AdminPanel.jsx"));
const RouteDirector = lazy(() => import("./RouteDirector.jsx"));
const CharacterEditor = lazy(() => import("./CharacterEditor.jsx"));
const CharacterBrowser = lazy(() => import("./CharacterBrowser.jsx"));
const UnifiedSearch = lazy(() => import("./UnifiedSearch.jsx"));
const ProfileSettings = lazy(() => import("./ProfileSettings.jsx"));
const DialMyFate = lazy(() => import('./DialMyFate.jsx'));
const CharacterCrosstalk = lazy(() => import('./CharacterCrosstalk.jsx'));
const CharacterArtGallery = lazy(() => import('./CharacterArtGallery.jsx'));
const GachaCollection = lazy(() => import('./GachaCollection.jsx'));
const RelationshipConstellation = lazy(() => import('./RelationshipConstellation.jsx'));
const EncyclopediaView = lazy(() => import('./EncyclopediaView.jsx'));
const CharacterSocialFeed = lazy(() => import('./CharacterSocialFeed.jsx'));
const MusicPlayer = lazy(() => import('./MusicPlayer.jsx'));
const PopupPhone = lazy(() => import('./PopupPhone.jsx'));
// The two heaviest views load on first visit to their tab, not at startup —
// keeps the main chunk (and mobile first paint) lean.
const ChannelView = lazy(() => import("./ChannelView.jsx"));
const DMView = lazy(() => import("./DMView.jsx"));
const DailyFateCard = lazy(() => import('./DailyFateCard.jsx'));
const PrivacyPolicyPage = lazy(() => import('./legal/PrivacyPolicy.jsx'));
const TermsOfServicePage = lazy(() => import('./legal/TermsOfService.jsx'));
const AIDisclosurePage = lazy(() => import('./legal/AIDisclosurePage.jsx'));
const SafetyPage = lazy(() => import('./legal/SafetyPage.jsx'));
const ContactPage = lazy(() => import('./legal/ContactPage.jsx'));
const DeleteAccountPage = lazy(() => import('./legal/DeleteAccount.jsx'));
const GachaOddsPage = lazy(() => import('./legal/GachaOdds.jsx'));

// ─── BUILD VERSION + DEPLOY RECOVERY ────────────────────────────────────────
// Increment this on each deploy. If the stored version doesn't match,
// the app clears stale auth tokens and reloads once to prevent stuck sessions.
const BUILD_VERSION = "2026-10-29-a-dropped-draft-stays-dropped-and-ooc-is-heard-v429";

// Crash catcher arms FIRST — module scope, zero conditions. It was gated
// three levels deep behind session + a 4s profile race (finding 15), i.e.
// disarmed in exactly the boot-break cases it exists for. Both 2026-07-30
// TDZ outages were only diagnosable because the OLD arming had happened on a
// prior healthy boot; a fresh session would have crashed silently.
initCrashCatcher(BUILD_VERSION);

(function deployRecovery() {
  try {
    const stored = localStorage.getItem("mfml_build_version");
    if (stored && stored !== BUILD_VERSION) {
      console.log("[Deploy Recovery] New version detected:", BUILD_VERSION, "(was:", stored + ")");
      // Clear stale Supabase cache that might reference old schema, but NEVER
      // the auth token / PKCE verifier — clearing those signed users out on
      // every single deploy. Preserve anything matching `auth-token`.
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("sb-") && !key.includes("auth-token")) localStorage.removeItem(key);
      });
      // Clear app backup keys that might reference old schema
      localStorage.removeItem("sixguys_backup_story");
      localStorage.removeItem("sixguys_failed_writes");
      // Store new version
      localStorage.setItem("mfml_build_version", BUILD_VERSION);
      // A new bundle actually booted — the update landed. Clear the reload
      // attempt counters (and the legacy once-per-version guards) so the next
      // deploy starts with a clean slate.
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("mfml_reload_attempts_") || key.startsWith("mfml_reloaded_for_")) localStorage.removeItem(key);
      });
      // Tell SW to clear caches
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage("CLEAR_CACHES");
      }
      // Reload once to pick up new code with clean state
      if (!sessionStorage.getItem("mfml_deploy_reload")) {
        sessionStorage.setItem("mfml_deploy_reload", "1");
        window.location.reload();
      }
    } else if (!stored) {
      // First visit or cleared storage — just store version
      localStorage.setItem("mfml_build_version", BUILD_VERSION);
    }
    // Clear the reload flag (so future manual reloads work normally)
    sessionStorage.removeItem("mfml_deploy_reload");
  } catch (e) {
    console.error("[Deploy Recovery] Error:", e);
  }
})();

// ─── LIVE UPDATE POLL + STALE-CLIENT RECOVERY ───────────────────────────────
// The deploy-recovery block above only runs when a NEW bundle loads — but the
// iOS WebView and long-lived Mac PWA windows can keep running an old bundle
// indefinitely, so the new version never arrives and users get stuck on old
// builds. This actively asks the server what the current version is
// (no-store, cache-proof) on launch, on foreground, and every 5 minutes.
//
// v136: the old once-per-version reload guard could strand a client on an old
// build FOREVER — if its single reload raced the async cache clear (or any
// cache layer served the old document again; prod has returned `age:` headers
// on no-store documents) and booted the same old bundle, the guard was
// already set and that version was never retried. Haley's Mac PWA sat on v131
// through four deploys exactly this way, then crashed opening Group Chats on
// a v131 bug fixed two deploys earlier, then crashed again lazy-loading a
// chunk the CDN had deleted. The guard is now an attempt COUNTER (max 3), the
// reload is a cache-busted `/?v=<version>` document request no cache can
// satisfy from the old copy, and from the second attempt on the service
// worker is unregistered outright (it has no fetch handler; the app
// re-registers it on next boot).
(function liveUpdatePoll() {
  const check = async () => {
    try {
      const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const { version } = await res.json();
      if (!version || version === BUILD_VERSION) return;
      // v155: NEVER auto-reload on a version mismatch — same-day deploy
      // bursts were force-refreshing both players mid-play. Announce it; the
      // UpdatePill (main.jsx) renders the notice and the refresh happens
      // only when the user taps it. Broken-chunk recovery (ErrorBoundary →
      // purgeAndReload) stays automatic — that app state is unusable anyway.
      window.dispatchEvent(new CustomEvent('mfml:update-available', { detail: { version } }));
    } catch { /* offline etc — try again next tick */ }
  };
  setTimeout(check, 4000); // shortly after launch (let boot finish first)
  setInterval(check, 5 * 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
})();

// Stale-chunk errors at runtime (a lazy view failing to load) are handled by
// the ErrorBoundary + the global unhandledrejection listener below, which
// route through isChunkLoadError (src/staleClient.js — now also matching the
// React.lazy interop "reading 'default'" spelling that production actually
// produced) into purgeAndReload.

// Force reset function — available globally for emergency recovery
window.__mfmlForceReset = function() {
  try {
    // Nuke all localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("sb-") || key.startsWith("sixguys")) localStorage.removeItem(key);
    });
    localStorage.removeItem("mfml_build_version");
    // Unregister all service workers
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        regs.forEach(r => r.unregister());
      });
    }
    // Clear caches
    if ("caches" in window) {
      caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
    }
    // Reload
    setTimeout(() => window.location.reload(), 300);
  } catch (e) {
    window.location.reload();
  }
};

// ─── PROFILE CACHE ──────────────────────────────────────────────────────────
// Persists the user's display name, avatar, and role in localStorage so the
// correct name/avatar loads INSTANTLY on every page reload — no more "thescheerers"
// flash while the DB fetch is in-flight.
const PROFILE_CACHE_KEY = 'mfml_profile_v1';
const PROFILE_CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days

function setCachedProfile(userId, profile) {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
      userId,
      display_name: profile.display_name,
      avatar_url: profile.avatar_url,
      role: profile.role,
      accent_color: profile.accent_color,
      profile_frame: profile.profile_frame,
      is_admin: profile.is_admin,
      cachedAt: Date.now(),
    }));
  } catch {}
}

function getCachedProfile(userId) {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Reject if wrong user or stale
    if (!data || data.userId !== userId) return null;
    if (Date.now() - data.cachedAt > PROFILE_CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function clearProfileCache() {
  try { localStorage.removeItem(PROFILE_CACHE_KEY); } catch {}
}

// ─── HELPERS ────────────────────────────────────────────────────────────────

// Format *italic* and **bold** in message text
const formatMessageText = (text) => {
  if (!text) return text;
  // Strip AI new character tags before display
  text = text.replace(/\n?\[NEW_CHARACTER:[^\]]*\]/g, '').trim();
  // ||spoiler|| hides until tapped (and re-hides after the reveal window) —
  // parsed at render time, so bars added by a later edit hide on the next
  // paint. (formatInlineText is hoisted — declaration below.)
  return renderWithSpoilers(text, formatInlineText);
};

function formatInlineText(text) {
  if (!text) return text;
  const parts = [];
  // Match: **bold**, *italic*, ~~strikethrough~~, __underline__
  const regex = /(\*\*(.+?)\*\*|~~(.+?)~~|__(.+?)__|\*(.+?)\*)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<s key={key++}>{match[3]}</s>);
    } else if (match[4]) {
      parts.push(<u key={key++}>{match[4]}</u>);
    } else if (match[5]) {
      parts.push(<em key={key++}>{match[5]}</em>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

// isOOCMessage is shared with the DM surface (oocDetect.js, v429): ((…)),
// "ooc:", "(ooc)", "[ooc]" and "//" all mark an aside.

const formatTimestamp = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" });
};

// ─── ERROR BOUNDARY ────────────────────────────────────────────────────────
// Prevents the entire app from going blank on uncaught errors. Has special
// handling for Vite "Failed to fetch dynamically imported module" errors,
// which happen after a deploy when a user's tab still has the old bundle and
// tries to lazy-load a chunk file Netlify already replaced. We auto-purge
// caches + hard-reload (once, gated by sessionStorage to avoid reload loops).

// isChunkLoadError now lives in src/staleClient.js (imported above) so the
// classifier is unit-tested against the exact strings production produced —
// including the React.lazy interop "reading 'default'" spelling from the
// 2026-07-30 stale-v131 crash, which this local copy used to miss.

async function purgeAndReload() {
  // Track recent attempts so a permanently-broken deploy doesn't loop forever.
  const KEY = 'mfml_chunk_reload_at';
  try {
    const last = parseInt(sessionStorage.getItem(KEY) || '0', 10);
    if (Date.now() - last < 10000) return false; // already tried in last 10s — show manual UI
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {}
  // Tell the service worker to clear caches (defined in /public/sw.js).
  try { navigator.serviceWorker?.controller?.postMessage('CLEAR_CACHES'); } catch {}
  // Also nuke browser-side caches if we have access.
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch {}
  // Hard reload with cache-busting query param.
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_v', String(Date.now()));
    window.location.replace(u.toString());
  } catch {
    window.location.reload();
  }
  return true;
}

// Global listener for Vite's preload error event — fires before React even
// gets a chance to error-boundary the failure on some browsers.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault?.();
    purgeAndReload();
  });
  // Also catch unhandled promise rejections that look like chunk failures.
  window.addEventListener('unhandledrejection', (e) => {
    if (isChunkLoadError(e.reason)) {
      e.preventDefault?.();
      purgeAndReload();
    }
  });
}

export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, newVersion: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentWillUnmount() {
    if (this._onUpdate) window.removeEventListener('mfml:update-available', this._onUpdate);
  }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
    // Ship the stack home — device-specific crashes were invisible before.
    logClientError('react_boundary', error, info?.componentStack || '');
    // Chunk load failures: try to recover automatically (once per 10s) before
    // showing the user the error UI. Most of the time this gets them back into
    // the app within a second of a stale-bundle crash.
    if (isChunkLoadError(error)) {
      purgeAndReload();
      return;
    }
    // A crash is not always a stale chunk — the 2026-08-02 white screen was a
    // shadowed import, which the chunk test does not match. But the recovery
    // is identical: this app state is unusable, and if a FIXED build is
    // already live the kindest thing is to say so on the screen the player is
    // actually looking at. The version poll is module scope and survives the
    // crash, so this listener will hear it.
    this._onUpdate = (e) => {
      const v = e?.detail?.version;
      if (v) this.setState({ newVersion: v });
    };
    window.addEventListener('mfml:update-available', this._onUpdate);
    // Ask immediately rather than waiting out the 5-minute poll interval.
    fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j?.version && j.version !== BUILD_VERSION) this.setState({ newVersion: j.version }); })
      .catch(() => { /* offline — the poll retries */ });
  }
  render() {
    if (this.state.hasError) {
      const isChunk = isChunkLoadError(this.state.error);
      const fixed = !!this.state.newVersion;
      return React.createElement("div", { style: { background: "var(--ds-surface-0, #0a0a0a)", color: "var(--ds-ink-hi)", minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", textAlign: "center", fontFamily: "'Cormorant Garamond', serif" } },
        React.createElement("h1", { style: { color: "var(--ds-accent, #c62828)", fontSize: "24px", marginBottom: "12px" } }, (isChunk || fixed) ? "New version available" : "Something went wrong"),
        React.createElement("p", { style: { color: "#aaa", fontSize: "14px", marginBottom: "20px", maxWidth: "400px" } }, (isChunk || fixed)
          ? "Please refresh — an updated version is ready and it fixes this."
          : (this.state.error?.message || "An unexpected error occurred.")),
        React.createElement("button", { onClick: () => { this.setState({ hasError: false, error: null }); purgeAndReload(); }, style: { background: "linear-gradient(135deg, var(--ds-accent-deeper, #8b0000), var(--ds-accent, #c62828))", color: "var(--ds-ink-hi)", border: "none", borderRadius: "8px", padding: "10px 24px", fontSize: "14px", cursor: "pointer", fontFamily: "'Cormorant Garamond', serif" } }, fixed ? "Refresh now" : "Reload App")
      );
    }
    return this.props.children;
  }
}

// ─── FLOATING PARTICLES ─────────────────────────────────────────────────────
// Perf #16: Stabilize random positions so they don't re-randomize every render
function FloatingParticles({ count = 15, color, t }) {
  const particleColor = color || (t?.accentDanger) || "var(--ds-accent, #c62828)";
  const [particles] = useState(() => Array.from({ length: count }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    animDuration: `${8 + Math.random() * 12}s`,
    animDelay: `${Math.random() * 8}s`,
    size: `${2 + Math.random() * 3}px`,
    blur: Math.random() > 0.5 ? 1 : 0,
    colorIdx: i % 3,
  })));
  return (
    <div className="particles-container">
      {particles.map((p, i) => (
        <div key={i} className="particle" style={{
          left: p.left,
          animationDuration: p.animDuration,
          animationDelay: p.animDelay,
          width: p.size,
          height: p.size,
          background: p.colorIdx === 0 ? particleColor : (p.colorIdx === 1 ? (t?.accentGold || "var(--ds-gold, #c4a882)") : `${particleColor}88`),
          filter: `blur(${p.blur}px)`,
        }} />
      ))}
    </div>
  );
}

// ─── CONFETTI CELEBRATION ────────────────────────────────────────────────────
// Fires confetti particles for milestones (first story, affinity levels, etc.)
function Confetti({ count = 40, duration = 3000, onDone, t }) {
  const [particles] = useState(() => Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${10 + Math.random() * 80}%`,
    color: [t?.accentDanger || 'var(--ds-accent, #c62828)', t?.accentGold || 'var(--ds-gold, #c4a882)', '#e91e63', '#ff9800', '#4caf50', '#2196f3', '#9c27b0'][Math.floor(Math.random() * 7)],
    delay: `${Math.random() * 0.8}s`,
    dur: `${2 + Math.random() * 1.5}s`,
    size: `${6 + Math.random() * 6}px`,
    shape: Math.random() > 0.5 ? '50%' : '2px',
    rotation: `${Math.random() * 360}deg`,
  })));
  useEffect(() => {
    const t = setTimeout(() => onDone && onDone(), duration);
    return () => clearTimeout(t);
  }, [duration, onDone]);
  return (
    <div className="confetti-container" aria-hidden="true">
      {particles.map(p => (
        <div key={p.id} className="confetti-piece" style={{
          left: p.left, width: p.size, height: p.size,
          background: p.color, borderRadius: p.shape,
          animationDelay: p.delay, animationDuration: p.dur,
          transform: `rotate(${p.rotation})`,
        }} />
      ))}
    </div>
  );
}

// ─── TOAST NOTIFICATION ─────────────────────────────────────────────────────
// Beautiful in-app toast for feedback moments
function Toast({ message, type = "success", icon, onDone, t }) {
  useEffect(() => {
    const timer = setTimeout(() => onDone && onDone(), 3000);
    return () => clearTimeout(timer);
  }, [onDone]);
  const colors = { success: t?.accentSuccess || "#43b581", error: t?.accentDanger || "var(--ds-accent, #c62828)", info: t?.accentGold || "var(--ds-gold, #c4a882)", celebration: "#e91e63" };
  const bg = colors[type] || colors.info;
  return (
    <div className="slide-up" style={{
      position: "fixed", bottom: "24px", left: "50%", transform: "translateX(-50%)",
      zIndex: 99998, padding: "12px 24px", borderRadius: "12px",
      background: `linear-gradient(135deg, ${bg}22, ${bg}11)`, backdropFilter: "blur(16px)",
      border: `1px solid ${bg}44`, color: t?.textPrimary || "#dcddde",
      fontSize: "14px", fontFamily: "'Cormorant Garamond', serif",
      display: "flex", alignItems: "center", gap: "8px",
      boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 20px ${bg}22`,
      maxWidth: "90vw", textAlign: "center",
    }}>
      {icon && <span style={{ fontSize: "18px" }}>{icon}</span>}
      <span>{message}</span>
    </div>
  );
}

// ─── ORNATE DIVIDER ─────────────────────────────────────────────────────────
function OrnateDivider({ width = "60%", margin = "20px auto" }) {
  const { theme: t } = useContext(ThemeContext);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width, margin, gap: "12px" }}>
      <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, transparent, var(--ds-accent-a44, #c6282844))" }} />
      <div style={{ width: "8px", height: "8px", transform: "rotate(45deg)", background: t.accentDanger, opacity: 0.6 }} />
      <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, var(--ds-accent-a44, #c6282844), transparent)" }} />
    </div>
  );
}

// ─── CHAR AVATAR ───────────────────────────────────────────────────────────
function CharAvatar({ char, size = 38, fontSize, showFullArt = false }) {
  const { theme: t } = useContext(ThemeContext);
  const artUrl = char?.id ? getCharacterAvatar(char.id) : null;
  const fullArtUrl = char?.id ? getCharacterArt(char.id, showFullArt ? 'full' : 'thumb') : null;
  const displayUrl = fullArtUrl || artUrl;
  const fs = fontSize || Math.round(size * 0.47);

  if (displayUrl) {
    return (
      <div style={{
        width: `${size}px`, height: `${size}px`, borderRadius: showFullArt ? "12px" : "50%",
        border: `2px solid ${char?.color || t.accentGold}44`,
        overflow: "hidden", flexShrink: 0, background: t.bgDeepest,
        boxShadow: `0 0 12px ${char?.color || t.accentGold}22`,
        transition: "all 0.3s ease",
      }}>
        <img src={displayUrl} alt={char?.name || ""} style={{
          width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center",
        }} loading="lazy" />
      </div>
    );
  }

  // Fallback: styled initial letter
  const initial = char?.name?.charAt(0)?.toUpperCase() || "?";
  return (
    <div style={{
      width: `${size}px`, height: `${size}px`, borderRadius: "50%",
      background: `linear-gradient(135deg, ${char?.color || t.accentGold}55, ${char?.color || t.accentGold}22)`,
      border: `2px solid ${char?.color || t.accentGold}44`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: `${fs}px`, flexShrink: 0, color: char?.color || t.accentGold,
      fontWeight: 700, fontFamily: "'Cinzel', serif",
      boxShadow: `0 0 12px ${char?.color || t.accentGold}22`,
    }}>
      {initial}
    </div>
  );
}

// ─── INCOGNITO WINDOW ───────────────────────────────────────────────────────
// A floating, draggable, neutral-styled "work mode" chat overlay.
// Looks like a regular messaging client — no fantasy branding visible.
function IncognitoWindow({ user, onClose, allChars = [], myStories = [], onlineProfiles: _onlineProfiles = [], standalone = false, chatHint }) {
  const POS_KEY = 'mfml_incog_pos';
  const getSaved = () => { try { return JSON.parse(localStorage.getItem(POS_KEY)) || { x: window.innerWidth - 380, y: 60 }; } catch { return { x: window.innerWidth - 380, y: 60 }; } };

  const [pos, setPos] = React.useState(getSaved);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ mx: 0, my: 0, px: 0, py: 0 });
  const [minimized, setMinimized] = React.useState(false);
  const [selectedConvo, setSelectedConvo] = React.useState(null);
  const [convos, setConvos] = React.useState([]);
  const [messages, setMessages] = React.useState([]);
  const [inputText, setInputText] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [sendingMsg, setSendingMsg] = React.useState(false);
  // Disappearing messages: null = off, number = seconds until fade
  const [disappearSecs, setDisappearSecs] = React.useState(null);
  const [hiddenMsgIds, setHiddenMsgIds] = React.useState(new Set());
  const disappearTimers = React.useRef({});
  const subRef = React.useRef(null);
  const msgEndRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const posRef = React.useRef(pos);
  posRef.current = pos;

  // Auto-grow the compose textarea as the user types (and on draft load).
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    autoGrowTextarea(el);
  }, [inputText]);
  // Message actions
  const [hoveredMsgId, setHoveredMsgId] = React.useState(null);
  const [editingMsgId, setEditingMsgId] = React.useState(null);
  const [editText, setEditText] = React.useState('');
  // SW-6: a failed delete/edit/regen has to be VISIBLE here. This surface is
  // the one built for discretion, so "it looked deleted and came back" is the
  // worst possible failure mode.
  const [incogError, setIncogError] = React.useState('');
  const [regenLoading, setRegenLoading] = React.useState(false);

  const W = standalone ? '100%' : 360;

  // ── Ultra-understated "work messenger" palette — all grays, no color ──────
  // Looks like a generic internal HR/ops tool from any distance.
  const N = {
    bg: '#ffffff', header: '#f8f9fa', border: '#e9ecef',
    text: '#212529', textMuted: '#6c757d', textFaint: '#adb5bd',
    accent: '#495057',        // charcoal — no bright blue
    accentHover: '#343a40',
    msgBg: '#f1f3f4',         // my messages: very light gray (not blue!)
    myBg: '#e2e6ea',          // other side: slightly different gray
    myText: '#212529',        // dark text on gray bg
    inputBg: '#f8f9fa', btnDisabled: '#ced4da',
    shadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
    sendBtn: '#6c757d',       // muted gray send button
  };

  // ── Document title swap when incognito is open ───────────────────────────
  // In `standalone` mode the parent (IncognitoStandalone) owns the title and
  // toggles it between the disguise name and 'Messages — Workspace' based on
  // chatVisible. If this effect runs in standalone, it permanently pins the
  // title to 'Messages — Workspace' even when the user is viewing the
  // disguise — defeating the cover on the browser tab. So we only swap the
  // title when this component is used inline (non-standalone floating chat).
  React.useEffect(() => {
    if (standalone) return;
    const orig = document.title;
    document.title = 'Messages — Workspace';
    return () => { document.title = orig; };
  }, [standalone]);

  // ── Boss-key: Escape minimizes ───────────────────────────────────────────
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMinimized(m => !m); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Dragging ─────────────────────────────────────────────────────────────
  const onHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ mx: e.clientX, my: e.clientY, px: posRef.current.x, py: posRef.current.y });
    e.preventDefault();
  };
  React.useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const nx = Math.max(0, Math.min(window.innerWidth - W, dragStart.px + e.clientX - dragStart.mx));
      const ny = Math.max(0, Math.min(window.innerHeight - 44, dragStart.py + e.clientY - dragStart.my));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      setIsDragging(false);
      try { localStorage.setItem(POS_KEY, JSON.stringify(posRef.current)); } catch {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isDragging, dragStart]);

  // ── Load conversation list ────────────────────────────────────────────────
  React.useEffect(() => {
    if (!user) return;
    const load = async () => {
      const list = [];
      // DM conversations (character DMs)
      const { data: dmData } = await supabase
        .from('dm_conversations')
        .select('id, character_id, title, last_message_at')
        .eq('user_id', user.id)
        .order('last_message_at', { ascending: false })
        .limit(15);
      (dmData || []).forEach(dm => {
        const charObj = allChars.find(c => c.id === dm.character_id);
        list.push({ type: 'dm', id: dm.id, name: charObj?.name || dm.title || 'DM', icon: '💬', charName: charObj?.name, charId: dm.character_id });
      });
      // Channels (public only)
      const { data: chData } = await supabase
        .from('channels')
        .select('id, name')
        .eq('is_private', false)
        .limit(20);
      (chData || []).forEach(ch => list.push({ type: 'channel', id: ch.id, name: '#' + ch.name, icon: '📢' }));
      // My stories (RP sessions)
      myStories.forEach(s => list.push({ type: 'story', id: s.id, name: s.name || 'Story', icon: '📖' }));
      setConvos(list);
      if (list.length > 0 && !selectedConvo) setSelectedConvo(list[0]);
    };
    load().catch(() => {});
  }, [user?.id, allChars, myStories]);

  // ── Load messages for selected convo ─────────────────────────────────────
  React.useEffect(() => {
    if (subRef.current) { supabase.removeChannel(subRef.current); subRef.current = null; }
    if (!selectedConvo) return;
    setMessages([]); setLoading(true);

    const fetch = async () => {
      try {
        if (selectedConvo.type === 'dm') {
          const { data } = await supabase.from('dm_messages').select('id, sender_type, sender_id, content, created_at').eq('conversation_id', selectedConvo.id).order('created_at', { ascending: false }).limit(50);
          setMessages((data || []).reverse());
        } else if (selectedConvo.type === 'channel') {
          const { data } = await supabase.from('channel_messages').select('id, user_id, content, created_at').eq('channel_id', selectedConvo.id).order('created_at', { ascending: false }).limit(50);
          setMessages((data || []).reverse());
        } else if (selectedConvo.type === 'story') {
          const { data } = await supabase.from('story_messages').select('id, user_id, char_id, char_name, text, is_player, created_at').eq('story_id', selectedConvo.id).order('created_at', { ascending: false }).limit(50);
          setMessages((data || []).reverse());
        }
      } catch {} finally { setLoading(false); }
    };
    fetch();

    // Realtime subscription
    const tbl = selectedConvo.type === 'dm' ? 'dm_messages' : selectedConvo.type === 'channel' ? 'channel_messages' : 'story_messages';
    const col = selectedConvo.type === 'dm' ? 'conversation_id' : selectedConvo.type === 'channel' ? 'channel_id' : 'story_id';
    const ch = supabase.channel(`incog-${selectedConvo.type}-${selectedConvo.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: tbl, filter: `${col}=eq.${selectedConvo.id}` }, (payload) => {
        setMessages(prev => [...prev, payload.new]);
      }).subscribe();
    subRef.current = ch;
    return () => { if (subRef.current) { supabase.removeChannel(subRef.current); subRef.current = null; } };
  }, [selectedConvo?.id, selectedConvo?.type]);

  // Auto-scroll
  React.useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // ── Disappearing messages — start timer for each new message when mode is on ──
  React.useEffect(() => {
    if (!disappearSecs || messages.length === 0) return;
    messages.forEach(msg => {
      const id = msg.id;
      if (!id || hiddenMsgIds.has(id) || disappearTimers.current[id]) return;
      disappearTimers.current[id] = setTimeout(() => {
        setHiddenMsgIds(prev => new Set([...prev, id]));
        delete disappearTimers.current[id];
      }, disappearSecs * 1000);
    });
  }, [messages, disappearSecs, hiddenMsgIds]);

  // Clear timers when disappear mode is turned off
  React.useEffect(() => {
    if (!disappearSecs) {
      Object.values(disappearTimers.current).forEach(clearTimeout);
      disappearTimers.current = {};
      setHiddenMsgIds(new Set());
    }
  }, [disappearSecs]);

  // ── Message actions ───────────────────────────────────────────────────────
  // AUDIT SW-6: these three were bare awaits behind catches that are DEAD for
  // resolved supabase errors, each followed by an unconditional optimistic
  // state change. In the one surface built for discretion, a deleted sensitive
  // message vanished locally and came back on next open; edits showed then
  // reverted. Every write is verified now, and a failure says so instead of
  // pretending. RLS permits these ops, so the real failure class is
  // auth/offline — precisely when the player most needs to know.
  const deleteMsg = async (msg) => {
    try {
      const table = selectedConvo?.type === 'dm' ? 'dm_messages'
        : selectedConvo?.type === 'channel' ? 'channel_messages'
        : selectedConvo?.type === 'story' ? 'story_messages' : null;
      if (table) {
        const { data: gone, error: delErr } = await supabase.from(table).delete().eq('id', msg.id).select('id');
        if (delErr || !gone?.length) {
          setIncogError(`Couldn't delete that message${delErr ? `: ${delErr.message.slice(0, 60)}` : ''} — it's still there.`);
          return;
        }
      }
      if (selectedConvo?.type === 'story') {
        // The row is already deleted above; also prune it from the story's
        // blob, or the main RP view's blob+table merge re-adds it next load.
        try {
          const { data: srow } = await supabase.from('stories').select('data').eq('id', selectedConvo.id).single();
          const blobMsgs = srow?.data?.messages;
          if (Array.isArray(blobMsgs)) {
            const pruned = blobMsgs.filter(m => m.text !== msg.text);
            if (pruned.length !== blobMsgs.length) {
              await supabase.from('stories')
                .update({ data: { ...srow.data, messages: pruned }, message_count: pruned.length })
                .eq('id', selectedConvo.id);
            }
          }
        } catch (blobErr) { console.warn('[Incognito] blob prune failed:', blobErr?.message || blobErr); }
      }
      setMessages(prev => prev.filter(m => m.id !== msg.id));
    } catch (e) { console.error('[Incognito] delete failed:', e); }
    setHoveredMsgId(null);
  };

  const saveEdit = async (msg) => {
    const newText = editText.trim();
    if (!newText) return;
    try {
      const editTable = selectedConvo?.type === 'dm' ? 'dm_messages'
        : selectedConvo?.type === 'channel' ? 'channel_messages' : null;
      if (editTable) {
        const { data: upd, error: updErr } = await supabase
          .from(editTable)
          .update({ content: newText, edited: true, edited_at: new Date().toISOString() })
          .eq('id', msg.id).select('id');
        if (updErr || !upd?.length) {
          setIncogError(`Couldn't save that edit${updErr ? `: ${updErr.message.slice(0, 60)}` : ''}.`);
          setEditingMsgId(null); setEditText('');
          return;
        }
      }
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, content: newText, text: newText } : m));
    } catch (e) { console.error('[Incognito] edit failed:', e); }
    setEditingMsgId(null); setEditText('');
  };

  const regenMsg = async (msg) => {
    if (!selectedConvo || selectedConvo.type !== 'dm' || regenLoading) return;
    setRegenLoading(true);
    try {
      const { data: { session } } = await safeGetSession();
      if (!session?.access_token) return;
      // Regenerate from the WHOLE thread, ended at the message being
      // replaced — the display page's last 10 was a different (smaller)
      // memory than the send path's, and a transcript that runs past the
      // regenerated reply asks the model to continue, not replace.
      let regenSource = messages;
      try {
        const { data: allRows } = await supabase
          .from('dm_messages')
          .select('id, sender_type, sender_id, content, created_at')
          .eq('conversation_id', selectedConvo.id)
          .order('created_at', { ascending: false })
          .limit(FULL_STORY_MSGS);
        if (allRows && allRows.length > regenSource.length) regenSource = allRows.reverse();
      } catch (e) { console.warn('[Incognito] regen full-history fetch failed:', e); }
      const cutIdx = regenSource.findIndex(m => m.id === msg.id);
      const recent = cutIdx >= 0 ? regenSource.slice(0, cutIdx) : regenSource.filter(m => m.id !== msg.id);
      const msgHistory = scopeFullHistory(recent).map(m => ({
        role: (m.sender_type === 'character' || m.is_player === false) ? 'assistant' : 'user',
        content: m.content || m.text || '',
      })).filter(m => m.content);
      const charObj = allChars.find(c => c.id === selectedConvo.charId) || { name: selectedConvo.name || 'Character' };
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const regenAbort = new AbortController();
      const regenTimer = setTimeout(() => regenAbort.abort(), AI_ABORT_MS);
      const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
        method: 'POST',
        signal: regenAbort.signal,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}`, 'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY },
        body: JSON.stringify({
          provider: 'claude-haiku',
          feature: 'dm_regen',
          // Audit P1-6: this regen OVERWRITES the stored message and used to
          // run on a 12-word prompt — canon written by a character with no
          // canon. It now carries the character's merged profile + the hard
          // rules. 4096 not 300: the zero-text class (reasoning models).
          systemPrompt: `You are ${charObj.name}${charObj.species ? ` (${charObj.species})` : ''} from the MFML universe. Continue the conversation naturally. Stay in character.
${charObj.personality ? `\nPERSONALITY: ${String(charObj.personality).slice(0, 900)}` : ''}${charObj.speechPattern ? `\nHOW YOU SPEAK: ${String(charObj.speechPattern).slice(0, 600)}` : ''}${charObj.relationships ? `\nRELATIONSHIPS: ${String(charObj.relationships).slice(0, 600)}` : ''}

${buildBoundaries(charObj)}`,
          messages: [...msgHistory, { role: 'user', content: 'Please give a different response to what came before.' }],
          maxTokens: 4096,
        }),
      });
      clearTimeout(regenTimer);
      if (!res.ok) throw new Error(`ai-proxy ${res.status}`); // audit P3-1: a 5xx body parsed as JSON used to read as a silent no-op
      const data = await res.json();
      if (data.text) {
        const { data: rg, error: rgErr } = await supabase
          .from('dm_messages').update({ content: data.text }).eq('id', msg.id).select('id');
        if (rgErr || !rg?.length) {
          setIncogError(`Couldn't save the new reply${rgErr ? `: ${rgErr.message.slice(0, 60)}` : ''}.`);
        } else {
          setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, content: data.text, text: data.text } : m));
        }
      }
    } catch (e) { console.error('[Incognito] regen failed:', e); }
    setRegenLoading(false); setHoveredMsgId(null);
  };

  const copyMsg = (msg) => {
    const text = msg.content || msg.text || '';
    navigator.clipboard?.writeText(text).catch(() => {});
    setHoveredMsgId(null);
  };

  // ── AI response for DM conversations — full identity anchor + user's API key ──
  const [aiTyping, setAiTyping] = React.useState(false);

  const triggerDMResponse = React.useCallback(async (convo, history) => {
    if (!convo || convo.type !== 'dm' || !convo.charId) return;
    setAiTyping(true);
    try {
      const { data: { session } } = await safeGetSession();
      if (!session?.access_token) return;

      // Get user's personal API key from localStorage (same key DMView uses)
      const userApiKey = localStorage.getItem('sixguys_apiKey') || '';
      if (!userApiKey) {
        // No personal key — skip AI response in incognito (global key = no NSFW, keep simple)
        return;
      }

      // Build character profile with full identity anchor (same as DMView)
      let charObj = allChars.find(c => c.id === convo.charId);
      if (!charObj) return;
      try {
        const overrides = await loadCharacterOverrides();
        charObj = mergeCharWithOverrides(charObj, overrides);
      } catch {}

      const playerLabel = user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'you';
      const identityAnchor = buildIdentityAnchor(charObj, {
        playerLabel,
        mode: 'dm',
      });

      // The character's memory is the WHOLE conversation, not the widget's
      // display page (Haley 2026-08-30: "I would prefer the character gets
      // access to all the messages"). Display stays at 50 rows; the AI turn
      // fetches full, same pattern as DMView's send path.
      let fullHistory = history || [];
      try {
        if (convo?.id) {
          // Stepped window — same history, cacheable prefix (historyWindow.js).
          const { data: steppedRows, error: histErr } = await fetchSteppedWindow(
            supabase, 'dm_messages', (q) => q.eq('conversation_id', convo.id));
          // The code below expects newest-first, as the old query returned it.
          const allRows = steppedRows ? [...steppedRows].reverse() : steppedRows;
          if (histErr) console.error('[Incognito] full history fetch failed:', histErr.message);
          else if (allRows && allRows.length > fullHistory.length) {
            const seen = new Set(allRows.map(m => m.id));
            const pending = (history || []).filter(m => !m?.id || !seen.has(m.id));
            fullHistory = [...allRows.reverse(), ...pending];
          }
        }
      } catch (e) { console.error('[Incognito] full history fetch threw:', e); }

      // Build message history for the AI
      const msgs = scopeFullHistory(fullHistory).filter(m => (m.content || m.text || '').trim());
      const apiMsgs = msgs.map(m => ({
        role: (m.sender_type === 'character' || m.sender_type === 'assistant') ? 'assistant' : 'user',
        content: m.content || m.text || '',
      }));
      // Ensure it doesn't start with assistant
      while (apiMsgs.length > 0 && apiMsgs[0].role === 'assistant') apiMsgs.shift();
      if (apiMsgs.length === 0) return;

      const systemPrompt = `${identityAnchor}

${buildBirthdayContext(user?.id)}

DM FORMAT — ABSOLUTE RULES:

═══ TEXT MODE (default — this is how virtually all DMs are) ═══

You are TEXTING. Like a real person typing into iMessage. Output is the literal words you would send as text messages — nothing else. No narrator. No author voice. No descriptive prose. Just texts.

ABSOLUTELY FORBIDDEN IN TEXT MODE:
• Action beats in any wrapper: *sighs*, *runs hand through hair*, *paces*, (scoffs), [eyebrow raise], —laughs—
• Narrator description: "He texts back immediately", "Her response comes back sharp", "There's a long pause", "His tone is flat"
• Stage directions: parentheses around actions, brackets, asterisks, em-dashes wrapping movement
• Third-person narration of yourself: "She walks to the window…", "His jaw tightens…"
• Voice-quality descriptions: "in a teasing tone", "*coldly*", "(softly)"
• Scene-setting prose: "The room is quiet…", "Patrol just ended and he's exhausted…"
• Markdown for narrative emphasis (asterisks around actions)
• Replying with a paragraph of fiction prose when the user just sent a casual text

PERMITTED IN TEXT MODE:
• The literal words you would type into a text message
• Real-text emoji used the way real people use them (only if the character actually texts that way)
• Real-text shorthand (lol, wym, idk, fr, ngl) ONLY if it matches your character's voice
• Short-to-medium chunks. 1–4 sentences per chunk. Multiple texts in a row are natural — separate with a blank line
• Keep it under 200 words total
• Personality drives length: chatty characters send more, brooding ones send less

═══ MODE SWITCH — TEXT → SCENE ═══

Switch to SCENE MODE *only* when the user themselves starts writing prose narration (their own actions, body, or environment described in third person) in their most recent message. If they're still texting, you're still texting. When unsure: stay in TEXT MODE.

═══ SCENE MODE (only when user has explicitly started a physical scene) ═══

Third-person narrative, 1–3 short paragraphs. Plain prose for actions/narration — NEVER asterisks. Quoted dialogue when characters speak. Return to TEXT MODE the next time the conversation drops back to plain texting.

OTHER RULES:
- Stay COMPLETELY in character. Never break character, never speak as the AI.
- NEVER control the player's actions or narrate for them.`;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const incogAbort = new AbortController();
      const incogTimer = setTimeout(() => incogAbort.abort(), AI_ABORT_MS);
      const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
        method: 'POST',
        signal: incogAbort.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          provider: 'claude',
          apiKey: userApiKey,
          systemPrompt,
          messages: apiMsgs,
          maxTokens: 300,
          feature: 'incognito_dm',
          characterName: charObj.name,
        }),
      });

      clearTimeout(incogTimer);
      if (!res.ok) return;
      const data = await res.json();
      const responseText = (data.response || data.content || data.text || '').trim();
      if (!responseText) return;

      // Save AI response to dm_messages so it's synced back via Realtime.
      // Multi-text burst support: blank-line-separated chunks become separate
      // bubbles. Single-chunk responses (incl. SCENE MODE prose) stay as one
      // row. Same heuristic as DMView's splitDMTextChunks (kept inline here
      // to avoid importing UI from DMView into the main shell).
      const splitChunks = (() => {
        const t = (responseText || '').trim();
        if (!t) return [];
        const parts = t.split(/\n\s*\n+/).map((c) => c.trim()).filter(Boolean);
        if (parts.length < 2) return [t];
        if (parts.some((c) => c.length > 250)) return [t];
        return parts;
      })();
      // ── BULLETPROOF INSERT (silent-drop fix, May 20) ──────────────
      // Each chunk: up to 3 attempts. Without retry, transient session/
      // RLS races during AI generation can drop responses entirely.
      for (let ci = 0; ci < splitChunks.length; ci++) {
        if (ci > 0) {
          await new Promise((r) => setTimeout(r, 700 + Math.floor(Math.random() * 500)));
        }
        const payload = {
          conversation_id: convo.id,
          sender_type: 'character',
          sender_id: null,
          content: splitChunks[ci],
        };
        let saved = false;
        for (let attempt = 0; attempt < 3 && !saved; attempt++) {
          if (attempt > 0) await safeRefreshSession();
          const { error } = await supabase.from('dm_messages').insert(payload);
          if (!error) { saved = true; break; }
          console.warn(`[Incognito] chunk ${ci + 1}/${splitChunks.length} insert attempt ${attempt + 1}/3 failed:`, error.message);
        }
        if (!saved) {
          console.error(`[Incognito] CRITICAL: chunk ${ci + 1}/${splitChunks.length} dropped after 3 attempts (convo=${convo.id})`);
          supabase.from('dm_message_drops').insert({
            user_id: user?.id || null,
            conversation_id: convo?.id || null,
            character_id: null,
            character_name: null,
            content_length: (splitChunks[ci] || '').length,
            source: 'sixguys_incognito',
            attempt_count: 3,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
            notes: `Incognito chunk ${ci + 1}/${splitChunks.length} dropped`,
          }).then(({ error: dropLogErr }) => {
            if (dropLogErr) console.warn('[Incognito] dm_message_drops telemetry insert also failed:', dropLogErr.message);
          });
        }
      }
      // Update conversation's last_message_at
      await supabase.from('dm_conversations').update({ last_message_at: new Date().toISOString() }).eq('id', convo.id);
    } catch (err) {
      console.error('[Incognito] AI response failed:', err);
    } finally {
      setAiTyping(false);
    }
  }, [allChars, user]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const sendMsg = async () => {
    const text = inputText.trim();
    if (!text || !selectedConvo || selectedConvo.type === 'story' || sendingMsg) return;
    setSendingMsg(true); setInputText('');
    try {
      // SW-2: supabase-js RESOLVES failures ({ error }), so the old bare
      // awaits made the catch dead — a stale-session send evaporated the
      // typed text with zero feedback while triggerDMResponse still fired,
      // and the AI's persisted answer replied to a message that doesn't
      // exist. Read the error; on failure restore the input and do NOT
      // generate.
      if (selectedConvo.type === 'dm') {
        const { error: insErr } = await supabase.from('dm_messages').insert({ conversation_id: selectedConvo.id, sender_type: 'user', sender_id: user.id, content: text });
        if (insErr) {
          console.error('[Incognito] send failed:', insErr.message);
          setInputText(text);
          alert('Message failed to send — check your connection and try again.');
          return;
        }
        // Trigger full AI response (same quality as DMView)
        const updatedHistory = [...messages, { sender_type: 'user', content: text }];
        triggerDMResponse(selectedConvo, updatedHistory);
      } else if (selectedConvo.type === 'channel') {
        const { error: insErr } = await supabase.from('channel_messages').insert({ channel_id: selectedConvo.id, user_id: user.id, content: text });
        if (insErr) {
          console.error('[Incognito] send failed:', insErr.message);
          setInputText(text);
          alert('Message failed to send — check your connection and try again.');
          return;
        }
      }
    } catch (err) { console.error('[Incognito] send failed:', err); setInputText(text); } finally { setSendingMsg(false); }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderMsg = (msg, i) => {
    if (hiddenMsgIds.has(msg.id)) return null; // disappeared
    const isMe = (msg.sender_id || msg.user_id) === user?.id;
    const isAI = msg.sender_type === 'character' || (msg.is_player === false);
    const isStory = selectedConvo?.type === 'story';
    const senderName = isMe ? 'You' : (msg.char_name || (isAI ? 'Assistant' : 'User'));
    const rawText = msg.content || msg.text || '';
    const text = rawText.replace(/^\*\*([^*]+)\*\*\s*/, '').trim();
    if (!text) return null;
    const isHovered = hoveredMsgId === msg.id;
    const isEditing = editingMsgId === msg.id;
    const isLastAI = isAI && i === messages.filter(m => !hiddenMsgIds.has(m.id)).length - 1;

    // Action bar — disguised as standard message reaction/action strip
    const actionBar = !isStory && isHovered && !isEditing && (
      <div
        onMouseEnter={() => setHoveredMsgId(msg.id)}
        onMouseLeave={() => setHoveredMsgId(null)}
        style={{
          display: 'flex', gap: '2px', marginTop: '3px',
          justifyContent: isMe ? 'flex-end' : 'flex-start',
        }}
      >
        {/* Copy — always available */}
        <button onClick={() => copyMsg(msg)} title="Copy" style={{ background: '#e9ecef', border: 'none', borderRadius: '4px', padding: '2px 7px', fontSize: '10px', color: '#495057', cursor: 'pointer', fontFamily: 'inherit' }}>Copy</button>
        {/* Edit — own messages only */}
        {isMe && <button onClick={() => { setEditingMsgId(msg.id); setEditText(rawText); }} title="Edit" style={{ background: '#e9ecef', border: 'none', borderRadius: '4px', padding: '2px 7px', fontSize: '10px', color: '#495057', cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>}
        {/* Regenerate — AI messages in DMs only */}
        {isAI && selectedConvo?.type === 'dm' && (
          <button onClick={() => regenMsg(msg)} disabled={regenLoading} title="Regenerate response" style={{ background: '#e9ecef', border: 'none', borderRadius: '4px', padding: '2px 7px', fontSize: '10px', color: '#495057', cursor: regenLoading ? 'default' : 'pointer', fontFamily: 'inherit', opacity: regenLoading ? 0.5 : 1 }}>
            {regenLoading && isLastAI ? '…' : 'Retry'}
          </button>
        )}
        {/* Delete — own messages or AI messages in DMs */}
        {(isMe || (isAI && selectedConvo?.type === 'dm')) && (
          <button onClick={() => deleteMsg(msg)} title="Delete" style={{ background: '#f8d7da', border: 'none', borderRadius: '4px', padding: '2px 7px', fontSize: '10px', color: '#842029', cursor: 'pointer', fontFamily: 'inherit' }}>Del</button>
        )}
      </div>
    );

    return (
      <div
        key={msg.id || i}
        onMouseEnter={() => { if (msg.id) setHoveredMsgId(msg.id); }}
        onMouseLeave={() => setHoveredMsgId(null)}
        style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: '6px', marginBottom: '6px', opacity: 1, transition: 'opacity 0.5s' }}
      >
        {!isMe && (
          <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#adb5bd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#fff', flexShrink: 0, marginBottom: 2 }}>
            {(senderName[0] || '?').toUpperCase()}
          </div>
        )}
        <div style={{ maxWidth: '76%' }}>
          {!isMe && <div style={{ fontSize: '10px', color: N.textMuted, marginBottom: '3px', marginLeft: '2px' }}>{senderName}</div>}
          {/* All bubbles are gray — looks like a plain internal tool, nothing colorful */}
          {isEditing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <textarea
                autoFocus
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(msg); } if (e.key === 'Escape') { setEditingMsgId(null); setEditText(''); } }}
                rows={3}
                style={{ width: '100%', border: '1px solid #adb5bd', borderRadius: '8px', padding: '7px 10px', fontSize: '12.5px', fontFamily: 'inherit', resize: 'none', outline: 'none', background: '#fff', color: N.text, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                <button onClick={() => { setEditingMsgId(null); setEditText(''); }} style={{ background: '#e9ecef', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', color: '#495057', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                <button onClick={() => saveEdit(msg)} style={{ background: '#495057', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Save</button>
              </div>
            </div>
          ) : (
            <div style={{ padding: '8px 11px', borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: isMe ? N.msgBg : N.myBg, color: N.text, fontSize: '12.5px', lineHeight: '1.45', wordBreak: 'break-word', border: `1px solid ${isHovered ? '#adb5bd' : '#dee2e6'}`, transition: 'border-color 0.15s', cursor: 'default' }}>
              {text}
            </div>
          )}
          {actionBar}
          {disappearSecs && msg.id && !isEditing && (
            <div style={{ fontSize: '9px', color: N.textFaint, marginTop: '2px', textAlign: isMe ? 'right' : 'left', marginLeft: isMe ? 0 : '2px', marginRight: isMe ? '2px' : 0 }}>
              ⏱ auto-archiving in {disappearSecs}s
            </div>
          )}
        </div>
      </div>
    );
  };

  const msgAreaH = standalone ? 'calc(100dvh - 145px)' : (minimized ? 0 : 380);

  return (
    <div style={{ position: standalone ? 'relative' : 'fixed', left: standalone ? undefined : pos.x, top: standalone ? undefined : pos.y, width: W, height: standalone ? '100%' : undefined, zIndex: standalone ? undefined : 99998, fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", borderRadius: standalone ? 0 : '10px', boxShadow: standalone ? 'none' : N.shadow, overflow: 'hidden', background: N.bg, border: standalone ? 'none' : `1px solid ${N.border}`, userSelect: isDragging ? 'none' : 'auto', display: standalone ? 'flex' : undefined, flexDirection: standalone ? 'column' : undefined }}>
      {/* ── Title bar ── */}
      <div onMouseDown={standalone ? undefined : onHeaderMouseDown} style={{ background: N.header, borderBottom: `1px solid ${N.border}`, padding: '9px 12px', display: 'flex', alignItems: 'center', gap: '7px', cursor: standalone ? 'default' : (isDragging ? 'grabbing' : 'grab') }}>
        {/* macOS-style traffic lights */}
        <div onClick={onClose} title="Close" style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f57', border: '1px solid #e0443e', cursor: 'pointer', flexShrink: 0 }} />
        <div onClick={() => setMinimized(m => !m)} title={minimized ? 'Restore' : 'Minimize'} style={{ width: 12, height: 12, borderRadius: '50%', background: '#febc2e', border: '1px solid #d4a017', cursor: 'pointer', flexShrink: 0 }} />
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#28c840', border: '1px solid #1aab34', flexShrink: 0 }} />
        <span style={{ flex: 1, textAlign: 'center', fontSize: '12px', fontWeight: 600, color: N.text, letterSpacing: '0.2px' }}>Messages</span>
        <span style={{ fontSize: '10px', color: N.textFaint, cursor: 'default' }} title="Press Esc to toggle">Esc</span>
      </div>

      {!minimized && (
        <>
          {/* ── Conversation picker ── */}
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${N.border}`, position: 'relative' }}>
            <div onClick={() => setShowDropdown(d => !d)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '7px', background: N.inputBg, border: `1px solid ${N.border}`, cursor: 'pointer' }}>
              <span style={{ fontSize: '14px' }}>{selectedConvo?.icon || '💬'}</span>
              <span style={{ flex: 1, fontSize: '12px', fontWeight: 500, color: N.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedConvo?.name || 'Select conversation…'}</span>
              <span style={{ color: N.textMuted, fontSize: '10px', transform: showDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
            </div>
            {showDropdown && (
              <div style={{ position: 'absolute', left: 10, right: 10, top: '100%', marginTop: 2, background: '#fff', border: `1px solid ${N.border}`, borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}>
                {convos.length === 0 ? (
                  <div style={{ padding: '14px', textAlign: 'center', color: N.textMuted, fontSize: '12px' }}>No conversations yet</div>
                ) : convos.map(c => (
                  <div key={`${c.type}-${c.id}`} onClick={() => { setSelectedConvo(c); setShowDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 12px', cursor: 'pointer', background: selectedConvo?.id === c.id ? '#e9ecef' : 'transparent', borderRadius: '5px', margin: '2px 4px', transition: 'background 0.1s' }} onMouseEnter={e => { if (selectedConvo?.id !== c.id) e.currentTarget.style.background = '#f8f9fa'; }} onMouseLeave={e => { e.currentTarget.style.background = selectedConvo?.id === c.id ? '#e9ecef' : 'transparent'; }}>
                    <span style={{ fontSize: '14px', flexShrink: 0 }}>{c.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: N.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                      <div style={{ fontSize: '10px', color: N.textFaint, textTransform: 'capitalize' }}>{c.type === 'dm' ? 'Direct Message' : c.type === 'channel' ? 'Channel' : 'Story'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Messages ── */}
          <div style={{ height: msgAreaH, overflowY: 'auto', padding: '12px 10px 8px', background: '#fafafa', display: 'flex', flexDirection: 'column' }} onClick={() => showDropdown && setShowDropdown(false)}>
            {incogError && (
              <div style={{ margin: '0 0 8px', padding: '7px 9px', borderRadius: '8px', background: 'rgba(198,40,40,0.10)', border: '1px solid rgba(198,40,40,0.30)', color: '#a33', fontSize: '11px', display: 'flex', gap: '8px', alignItems: 'flex-start', flexShrink: 0 }}>
                <span style={{ flex: 1 }}>{incogError}</span>
                <button onClick={() => setIncogError('')} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: '#a33', cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
              </div>
            )}
            {!selectedConvo ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: N.textMuted, fontSize: '12px', textAlign: 'center', padding: '20px' }}>Pick a conversation above to read it here</div>
            ) : loading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: N.textMuted, fontSize: '12px' }}>Loading…</div>
            ) : messages.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: N.textMuted, fontSize: '12px' }}>No messages yet</div>
            ) : messages.map(renderMsg)}
            {/* Typing indicator — shows while AI is generating */}
            {aiTyping && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', marginBottom: '6px' }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#adb5bd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                  {(selectedConvo?.name?.[0] || '?').toUpperCase()}
                </div>
                <div style={{ padding: '8px 13px', borderRadius: '14px 14px 14px 4px', background: '#e2e6ea', color: '#6c757d', fontSize: '12.5px', border: '1px solid #dee2e6', letterSpacing: '2px' }}>
                  ···
                </div>
              </div>
            )}
            <div ref={msgEndRef} />
          </div>

          {/* ── Input ── */}
          <div style={{ padding: '8px 10px', borderTop: `1px solid ${N.border}`, background: N.bg, display: 'flex', gap: '7px', alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={e => { if (enterShouldSend(e)) { e.preventDefault(); sendMsg(); } }}
              placeholder={!selectedConvo ? 'Select a conversation…' : selectedConvo.type === 'story' ? 'Stories are read-only here' : 'Message…'}
              disabled={!selectedConvo || selectedConvo.type === 'story' || sendingMsg}
              rows={1}
              style={{ flex: 1, resize: 'none', border: `1px solid ${N.border}`, borderRadius: '8px', padding: '7px 13px', fontSize: '12.5px', background: N.inputBg, color: N.text, outline: 'none', fontFamily: 'inherit', lineHeight: '1.4', minHeight: '32px', maxHeight: '50vh', overflowY: 'auto', transition: 'border-color 0.15s' }}
              onFocus={e => { e.target.style.borderColor = '#adb5bd'; }}
              onBlur={e => { e.target.style.borderColor = N.border; }}
            />
            <button onClick={sendMsg} disabled={!inputText.trim() || !selectedConvo || selectedConvo.type === 'story' || sendingMsg} aria-label="Send message" style={{ width: 44, height: 44, minWidth: 44, minHeight: 44, borderRadius: '8px', border: 'none', background: inputText.trim() && selectedConvo?.type !== 'story' ? N.sendBtn : N.btnDisabled, color: '#fff', cursor: inputText.trim() && selectedConvo?.type !== 'story' ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0, transition: 'background 0.15s', touchAction: 'manipulation' }}>
              →
            </button>
          </div>

          {/* ── Footer — disappear toggle disguised as "Auto-archive" ── */}
          <div style={{ padding: '4px 12px 6px', background: N.header, borderTop: `1px solid ${N.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* Auto-archive toggle — looks like a standard productivity feature */}
              <button
                onClick={() => setDisappearSecs(d => d ? null : 30)}
                title={disappearSecs ? 'Auto-archive: ON — click to disable' : 'Auto-archive: OFF'}
                style={{ fontSize: '10px', color: disappearSecs ? '#495057' : N.textFaint, background: disappearSecs ? '#e9ecef' : 'transparent', border: `1px solid ${disappearSecs ? '#ced4da' : 'transparent'}`, borderRadius: '4px', padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
              >
                {disappearSecs ? `⏱ Archive ${disappearSecs}s` : '⏱ Archive off'}
              </button>
              {disappearSecs && (
                <select value={disappearSecs} onChange={e => setDisappearSecs(Number(e.target.value))} style={{ fontSize: '10px', border: `1px solid ${N.border}`, borderRadius: '4px', padding: '1px 4px', background: N.inputBg, color: N.text, fontFamily: 'inherit', cursor: 'pointer' }}>
                  <option value={15}>15s</option>
                  <option value={30}>30s</option>
                  <option value={60}>1m</option>
                  <option value={300}>5m</option>
                </select>
              )}
            </div>
            <span style={{ fontSize: '10px', color: N.textFaint }}>{chatHint || 'Workspace'}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ─── FAKE OFFICE DISGUISE ────────────────────────────────────────────────────
const INCOG_SHEET_KEY = 'mfml_incog_spreadsheet_v2';
function FakeOfficeView({ hint }) {
  const cols = 'ABCDEFGHIJ'.split('');
  const rows = Array.from({ length: 22 }, (_, i) => i + 1);
  const DEFAULT_DATA = {
    'A1':'Employee','B1':'Department','C1':'Jan','D1':'Feb','E1':'Mar','F1':'Q1 Total','G1':'Budget','H1':'Variance','I1':'Notes',
    'A2':'Anderson, J.','B2':'Marketing','C2':'4,250','D2':'4,250','E2':'4,625','F2':'13,125','G2':'13,000','H2':'(125)','I2':'On track',
    'A3':'Chen, M.','B3':'Engineering','C3':'6,800','D3':'6,800','E3':'7,000','F3':'20,600','G3':'21,000','H3':'400','I3':'Under budget',
    'A4':'Davis, S.','B4':'HR','C4':'3,900','D4':'3,900','E4':'3,900','F4':'11,700','G4':'11,700','H4':'—','I4':'',
    'A5':'Evans, P.','B5':'Finance','C5':'5,400','D5':'5,400','E5':'5,600','F5':'16,400','G5':'16,000','H5':'(400)','I5':'Review Q2',
    'A6':'Foster, K.','B6':'Marketing','C6':'4,100','D6':'4,100','E6':'4,350','F6':'12,550','G6':'12,500','H6':'(50)','I6':'',
    'A7':'Garcia, R.','B7':'Engineering','C7':'7,200','D7':'7,200','E7':'7,500','F7':'21,900','G7':'22,000','H7':'100','I7':'',
    'A8':'Hill, T.','B8':'Operations','C8':'5,100','D8':'5,100','E8':'5,100','F8':'15,300','G8':'15,000','H8':'(300)','I8':'Pending review',
    'A9':'Imai, Y.','B9':'Finance','C9':'5,900','D9':'5,900','E9':'6,100','F9':'17,900','G9':'18,000','H9':'100','I9':'',
    'A10':'Johnson, B.','B10':'HR','C10':'3,750','D10':'3,750','E10':'3,750','F10':'11,250','G10':'11,250','H10':'—','I10':'',
    'A11':'Kim, J.','B11':'Engineering','C11':'6,500','D11':'6,500','E11':'6,800','F11':'19,800','G11':'20,000','H11':'200','I11':'',
    'A12':'Lopez, A.','B12':'Operations','C12':'4,800','D12':'4,800','E12':'4,900','F12':'14,500','G12':'14,500','H12':'—','I12':'',
    'A13':'TOTAL','C13':'63,700','D13':'63,700','E13':'65,625','F13':'193,025','G13':'193,950','H13':'925',
  };
  // Load from localStorage (persists custom AI-generated sheet)
  const [D, setD] = React.useState(() => {
    try { const s = localStorage.getItem(INCOG_SHEET_KEY); return s ? JSON.parse(s) : DEFAULT_DATA; } catch { return DEFAULT_DATA; }
  });
  const [selCell, setSelCell] = React.useState('A1');
  const [showAIPrompt, setShowAIPrompt] = React.useState(false);
  const [aiPromptText, setAIPromptText] = React.useState('');
  const [aiGenerating, setAIGenerating] = React.useState(false);
  const [aiError, setAIError] = React.useState('');
  const selVal = D[selCell] || '';
  const setCell = (k, v) => setD(prev => ({ ...prev, [k]: v }));
  const cellW = c => c==='A'?140:c==='B'?110:c==='I'?110:72;

  // Auto-save any edits to localStorage
  React.useEffect(() => {
    try { localStorage.setItem(INCOG_SHEET_KEY, JSON.stringify(D)); } catch {}
  }, [D]);

  const generateWithAI = async () => {
    if (!aiPromptText.trim()) return;
    setAIGenerating(true); setAIError('');
    try {
      // Use the shared ai-proxy edge function — picks up the global admin key automatically
      const { data: { session } } = await safeGetSession();
      if (!session?.access_token) {
        setAIError('Not logged in. Please refresh and try again.');
        setAIGenerating(false); return;
      }
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const ssAbort = new AbortController();
      const ssTimer = setTimeout(() => ssAbort.abort(), AI_ABORT_MS);
      let res;
      try {
        res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
        method: 'POST',
        signal: ssAbort.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          provider: 'gemini', // spreadsheet generation, never NSFW → free tier
          feature: 'spreadsheet_gen',
          systemPrompt: 'You are a spreadsheet generator. Return ONLY valid JSON, no markdown, no explanation.',
          messages: [{
            role: 'user',
            content: `Create realistic spreadsheet data for someone who works as: "${aiPromptText}".
Generate a plausible, work-appropriate Excel spreadsheet with 10-12 rows of data.
Return ONLY a JSON object with cell keys like "A1","B1","C1" etc (columns A-I, rows 1-13 max).
Row 1 = headers, rows 2-12 = data rows, row 13 = totals if applicable.
Make it look real for their job. Return raw JSON only, no markdown.`
          }],
          maxTokens: 1200,
        })
        });
      } finally { clearTimeout(ssTimer); }
      if (!res.ok) throw new Error(`ai-proxy ${res.status}`); // audit P3-1: a 5xx body parsed as JSON used to read as a silent no-op
      const data = await res.json();
      const raw = data.text || '';
      const match = raw.match(/\{[\s\S]+\}/);
      if (match) {
        const newData = JSON.parse(match[0]);
        setD(newData);
        setShowAIPrompt(false);
        setAIPromptText('');
      } else { setAIError('Could not parse AI response. Try again.'); }
    } catch (e) { setAIError('Generation failed: ' + e.message); }
    setAIGenerating(false);
  };
  return (
    <div style={{ width:'100%', height:'100%', background:'#f5f5f5', fontFamily:"'Segoe UI',Arial,sans-serif", fontSize:'13px', display:'flex', flexDirection:'column' }}>
      {/* Menu bar */}
      <div style={{ background:'#217346', color:'#fff', padding:'3px 8px', fontSize:'12px', display:'flex', alignItems:'center', gap:'16px', flexShrink:0 }}>
        <strong style={{ marginRight:'12px' }}>{D['A1'] ? (D['A1'] !== 'Employee' ? 'custom_data.xlsx' : 'Q1_Payroll_Final_v2.xlsx') : 'Q1_Payroll_Final_v2.xlsx'}</strong>
        {['File','Edit','View','Insert','Format','Data','Review','Help'].map(m=><span key={m} style={{ cursor:'default', padding:'2px 6px', borderRadius:'2px' }} onMouseEnter={e=>e.target.style.background='var(--ds-line-strong)'} onMouseLeave={e=>e.target.style.background='transparent'}>{m}</span>)}
        <div style={{ flex:1 }} />
        {/* AI customize — looks like a data tools button */}
        <button onClick={()=>setShowAIPrompt(v=>!v)} title="Generate data for my job" style={{ background:'var(--ds-hover)', border:'1px solid var(--ds-line-strong)', color:'var(--ds-ink-hi)', borderRadius:'3px', padding:'2px 8px', fontSize:'11px', cursor:'pointer', fontFamily:'inherit' }}>
          {aiGenerating ? '⏳ Generating…' : '✦ Customize'}
        </button>
        <button onClick={()=>{ setD(DEFAULT_DATA); try{localStorage.removeItem(INCOG_SHEET_KEY);}catch{} }} title="Reset to default data" style={{ background:'transparent', border:'1px solid var(--ds-line-strong)', color:'var(--ds-ink)', borderRadius:'3px', padding:'2px 6px', fontSize:'11px', cursor:'pointer', fontFamily:'inherit' }}>
          ↺
        </button>
      </div>
      {/* AI prompt panel */}
      {showAIPrompt && (
        <div style={{ background:'#f0f7f0', borderBottom:'2px solid #217346', padding:'10px 14px', flexShrink:0 }}>
          <div style={{ fontSize:'12px', fontWeight:600, color:'#1a5c2e', marginBottom:'6px' }}>✦ AI Spreadsheet Customizer — Saved automatically</div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <input value={aiPromptText} onChange={e=>setAIPromptText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&generateWithAI()} placeholder='Describe your job (e.g. "automotive parts manager" or "school librarian")' style={{ flex:1, border:'1px solid #c3d9c8', borderRadius:'4px', padding:'5px 10px', fontSize:'12px', outline:'none', fontFamily:'inherit' }} />
            <button onClick={generateWithAI} disabled={aiGenerating||!aiPromptText.trim()} style={{ background:'#217346', color:'#fff', border:'none', borderRadius:'4px', padding:'5px 14px', fontSize:'12px', cursor:'pointer', fontWeight:600, fontFamily:'inherit', whiteSpace:'nowrap' }}>
              {aiGenerating ? 'Generating…' : 'Generate & Save'}
            </button>
            <button onClick={()=>setShowAIPrompt(false)} style={{ background:'transparent', border:'none', color:'#6b7280', cursor:'pointer', fontSize:'16px', padding:'0 4px' }}>✕</button>
          </div>
          {aiError && <div style={{ fontSize:'11px', color:'#c00', marginTop:'5px' }}>{aiError}</div>}
          <div style={{ fontSize:'10px', color:'#6b7280', marginTop:'4px' }}>Your custom sheet saves to this browser and reloads every time you open this mode.</div>
        </div>
      )}
      {/* Toolbar */}
      <div style={{ background:'#fff', borderBottom:'1px solid #d0d0d0', padding:'4px 8px', display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
        {['↩','↪','⊟','⊞','B','I','U'].map(b=><button key={b} style={{ background:'none', border:'1px solid transparent', borderRadius:'3px', padding:'2px 7px', cursor:'default', fontWeight: ['B','I','U'].includes(b)?700:400 }}>{b}</button>)}
        <div style={{ width:'1px', height:'20px', background:'#d0d0d0', margin:'0 4px' }} />
        <span style={{ fontSize:'12px', color:'#444' }}>General</span>
        <div style={{ width:'1px', height:'20px', background:'#d0d0d0', margin:'0 4px' }} />
        {['%','$','↑','↓'].map(b=><button key={b} style={{ background:'none', border:'1px solid transparent', borderRadius:'3px', padding:'2px 7px', cursor:'default' }}>{b}</button>)}
      </div>
      {/* Formula bar — editable, updates selected cell */}
      <div style={{ background:'#fff', borderBottom:'1px solid #d0d0d0', padding:'3px 8px', display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
        <div style={{ width:'60px', background:'#f5f5f5', border:'1px solid #ccc', padding:'2px 6px', fontSize:'12px', textAlign:'center', flexShrink:0 }}>{selCell}</div>
        <span style={{ color:'#888', flexShrink:0 }}>fx</span>
        <input
          value={selVal}
          onChange={e => setCell(selCell, e.target.value)}
          style={{ flex:1, border:'1px solid #1a73e8', padding:'2px 8px', fontSize:'12px', outline:'none', borderRadius:0 }}
        />
      </div>
      {/* Grid */}
      <div style={{ flex:1, overflow:'auto', display:'flex', minHeight:0 }}>
        {/* Row numbers */}
        <div style={{ flexShrink:0 }}>
          <div style={{ width:'40px', height:'22px', background:'#f5f5f5', borderRight:'1px solid #d0d0d0', borderBottom:'1px solid #d0d0d0' }} />
          {rows.map(r=><div key={r} style={{ width:'40px', height:'22px', background:'#f5f5f5', borderRight:'1px solid #d0d0d0', borderBottom:'1px solid #e8e8e8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', color:'#666' }}>{r}</div>)}
        </div>
        <div style={{ flex:1, overflow:'auto' }}>
          {/* Column headers */}
          <div style={{ display:'flex', position:'sticky', top:0, zIndex:1 }}>
            {cols.map(c=><div key={c} style={{ minWidth:cellW(c), height:'22px', background:'#f5f5f5', border:'1px solid #d0d0d0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:600, color:'#444', flexShrink:0 }}>{c}</div>)}
          </div>
          {/* Data rows — all cells are editable inputs */}
          {rows.map(r=>(
            <div key={r} style={{ display:'flex' }}>
              {cols.map(c=>{
                const k=`${c}${r}`;
                const v=D[k]||'';
                const isHeader=r===1;
                const isTotal=r===13;
                const isNum=['C','D','E','F','G','H'].includes(c)&&r>1;
                const isSel=selCell===k;
                return (
                  <input
                    key={c}
                    value={v}
                    onChange={e => setCell(k, e.target.value)}
                    onFocus={() => setSelCell(k)}
                    style={{
                      minWidth:cellW(c), width:cellW(c), height:'22px',
                      border: isSel ? '2px solid #1a73e8' : '1px solid #e8e8e8',
                      padding:'0 5px', fontSize:'12px', boxSizing:'border-box',
                      background: isSel?'#e8f0fe': isHeader?'#e8f0e8': isTotal?'#e8f0e8':'#fff',
                      fontWeight: isHeader||isTotal?600:400,
                      color: v.startsWith('(')?'#c00':'#1a1a1a',
                      textAlign: isNum?'right':'left',
                      outline:'none', cursor:'cell', flexShrink:0,
                      fontFamily:"'Segoe UI',Arial,sans-serif",
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {/* Sheet tabs */}
      <div style={{ background:'#f5f5f5', borderTop:'1px solid #d0d0d0', padding:'0 8px', display:'flex', alignItems:'center', gap:'2px', flexShrink:0 }}>
        {['Q1 Payroll','Q2 Forecast','Annual Budget','Headcount'].map((tab,i)=><div key={tab} style={{ padding:'4px 16px', fontSize:'12px', background:i===0?'#fff':'transparent', borderTop:i===0?'2px solid #217346':'none', borderLeft:'1px solid #d0d0d0', borderRight:'1px solid #d0d0d0', cursor:'default', color:i===0?'#217346':'#555' }}>{tab}</div>)}
        <div style={{ flex:1 }} />
        {hint && <span style={{ fontSize:'10px', color:'#999', padding:'2px 8px' }}>{hint}</span>}
      </div>
    </div>
  );
}

// ─── FAKE EDUCATION DISGUISE ─────────────────────────────────────────────────
function FakeEducationView({ hint }) {
  const [activeUnit, setActiveUnit] = React.useState('3.2');
  const units = [
    { id:'1', label:'Unit 1: Foundations', items:['1.1 Introduction','1.2 Core Concepts','1.3 Assessment'] },
    { id:'2', label:'Unit 2: Intermediate Topics', items:['2.1 Analysis Methods','2.2 Case Studies','2.3 Quiz'] },
    { id:'3', label:'Unit 3: Advanced Applications', items:['3.1 Theory Review','3.2 Research Methods','3.3 Final Project'] },
  ];
  const content = {
    '3.2': {
      title: 'Research Methods in Applied Studies',
      due: 'Assignment due: May 15, 2026',
      body: `In this module, we examine the foundational principles of qualitative and quantitative research methodology as applied to modern organizational contexts.

**Learning Objectives**
Upon completion of this section, students should be able to:
• Distinguish between primary and secondary research approaches
• Apply systematic data collection methods in professional settings
• Evaluate research validity using peer-reviewed frameworks
• Construct a coherent literature review aligned with APA 7th edition standards

**Required Reading**
Creswell, J.W. & Creswell, J.D. (2023). Research Design: Qualitative, Quantitative, and Mixed Methods Approaches (6th ed.). SAGE Publications.

Chapter 8 — pp. 201–247: Mixed-Methods Design Considerations
Chapter 9 — pp. 248–279: Validity and Reliability in Applied Research

**Lecture Notes**
This week we continue our exploration of research design, focusing specifically on how organizational constraints influence methodology selection. When resources are limited, researchers often default to convenience sampling — however, this introduces systematic bias that must be accounted for in the discussion section...

**Discussion Prompt (Due Friday)**
Respond in 300–400 words: How does researcher positionality affect the interpretation of qualitative data? Provide one example from your professional experience or from the assigned reading.`,
    },
  };
  const c = content[activeUnit] || content['3.2'];
  return (
    <div style={{ width:'100%', height:'100%', background:'#f8f9fa', fontFamily:"'Segoe UI',Arial,sans-serif", fontSize:'13px', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <div style={{ background:'#1a3a5c', color:'#fff', padding:'8px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
        <div style={{ width:32, height:32, background:'#fff', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px' }}>🎓</div>
        <div>
          <div style={{ fontWeight:700, fontSize:'14px' }}>Academic Learning Portal</div>
          <div style={{ fontSize:'11px', opacity:0.8 }}>PROF 4820 · Applied Research Methods · Spring 2026</div>
        </div>
        <div style={{ flex:1 }} />
        <div style={{ fontSize:'12px', opacity:0.8 }}>Student: Guest User &nbsp;|&nbsp; Grade: A– &nbsp;|&nbsp; Progress: 67%</div>
      </div>
      {/* Progress bar */}
      <div style={{ background:'#e9ecef', height:'6px' }}>
        <div style={{ width:'67%', height:'100%', background:'#1a3a5c' }} />
      </div>
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
        {/* Sidebar */}
        <div style={{ width:220, background:'#fff', borderRight:'1px solid #dee2e6', overflowY:'auto', flexShrink:0 }}>
          <div style={{ padding:'10px 12px', fontWeight:700, fontSize:'11px', color:'#6c757d', textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:'1px solid #dee2e6' }}>Course Navigation</div>
          {units.map(u=>(
            <div key={u.id}>
              <div style={{ padding:'8px 12px', fontWeight:600, fontSize:'12px', color:'#1a3a5c', background:'#f8f9fa', borderBottom:'1px solid #dee2e6' }}>{u.label}</div>
              {u.items.map(item=>{
                const id = `${u.id}.${u.items.indexOf(item)+1}`;
                const isActive = activeUnit === id;
                return (
                  <div key={item} onClick={()=>setActiveUnit(id)} style={{ padding:'7px 12px 7px 20px', fontSize:'12px', cursor:'pointer', background:isActive?'#e8f0f8':'transparent', color:isActive?'#1a3a5c':'#495057', borderLeft:isActive?'3px solid #1a3a5c':'3px solid transparent', fontWeight:isActive?600:400 }}>
                    {isActive?'▶ ':''}{item}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {/* Content */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 28px' }}>
          <div style={{ maxWidth:680 }}>
            <div style={{ fontSize:'11px', color:'#6c757d', marginBottom:'6px' }}>Unit 3 › Section {activeUnit}</div>
            <h1 style={{ fontSize:'20px', fontWeight:700, color:'#1a1a2e', margin:'0 0 4px' }}>{c.title}</h1>
            <div style={{ fontSize:'12px', color:'#e74c3c', marginBottom:'20px', padding:'6px 10px', background:'#fff5f5', borderRadius:'4px', border:'1px solid #f5c6cb', display:'inline-block' }}>⏰ {c.due}</div>
            <div style={{ background:'#fff', border:'1px solid #dee2e6', borderRadius:'6px', padding:'20px', lineHeight:'1.6', fontSize:'13px', color:'#343a40', whiteSpace:'pre-line' }}>{c.body}</div>
            <div style={{ marginTop:'20px', display:'flex', gap:'10px' }}>
              <button style={{ padding:'8px 18px', background:'#1a3a5c', color:'#fff', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'13px', fontWeight:600 }}>Submit Assignment</button>
              <button style={{ padding:'8px 18px', background:'#fff', color:'#1a3a5c', border:'1px solid #1a3a5c', borderRadius:'4px', cursor:'pointer', fontSize:'13px' }}>Mark as Complete</button>
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div style={{ background:'#fff', borderTop:'1px solid #dee2e6', padding:'6px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:'11px', color:'#6c757d' }}>Academic Learning Portal · © 2026 · Privacy · Accessibility</span>
        {hint && <span style={{ fontSize:'10px', color:'#adb5bd' }}>{hint}</span>}
      </div>
    </div>
  );
}

// ─── FAKE SCHOOL STAFF DISGUISE ──────────────────────────────────────────────
// Rotating daily articles for teachers, librarians, school staff
function FakeSchoolStaffView({ hint }) {
  const ARTICLES = [
    {
      category: 'Library Science', source: 'School Library Journal', date: 'May 2026',
      title: 'Collection Development in the Age of Book Challenges: A Practical Guide',
      author: 'Renata Delacroix, MLS', readTime: '7 min read',
      body: `Book challenges reached a record high for the third consecutive year in 2025. For school librarians, this has transformed collection development from a curatorial role into one requiring legal awareness, community engagement skills, and institutional support navigation.

The librarians most prepared for challenge situations are those who can clearly articulate the criteria used to select each title. Selection policies—when they exist, are current, and have board approval—serve as the first and most important line of defense.

Key elements of a defensible selection policy:
• Clear articulation of educational purpose and community values
• Review source criteria (Kirkus, Booklist, School Library Journal starred reviews)
• A formal reconsideration process with a standing committee
• Board-level approval and annual review dates

The ALA's Library Bill of Rights remains the professional foundation for collection defense. Familiarity with the legal distinction between parental rights over their own child's reading and removal of materials from public collections is increasingly essential for practicing school librarians.`,
    },
    {
      category: 'EdTech', source: 'Education Week', date: 'May 2026',
      title: 'AI in the Classroom: What Teachers Actually Think After Year Two',
      author: 'Marcus Holloway, Staff Writer', readTime: '8 min read',
      body: `Two years into widespread AI tool adoption in K–12 schools, the picture is more nuanced than either enthusiasts or skeptics predicted. The most significant finding from a 14-state longitudinal study: teacher satisfaction with AI tools is strongly correlated not with the tools themselves, but with whether teachers had meaningful input in selecting them.

Districts that imposed AI platforms top-down consistently showed lower teacher buy-in and higher rates of tool abandonment. Districts that piloted tools with small teacher cohorts first showed 3x higher engagement after 24 months.

Where AI is actually helping:
• Differentiated reading materials generated on demand
• First-draft feedback on student writing before teacher review
• Lesson plan ideation (not replacement)
• IEP documentation drafting

AI-generated assessments remain a sore point. Most teachers report that auto-generated quiz questions require significant editing before use, and that the tools struggle with local curriculum standards not well-represented in training data.`,
    },
    {
      category: 'Professional Development', source: 'ASCD SmartBrief', date: 'April 2026',
      title: 'Vertical Curriculum Alignment: Fixing the 6th-Grade Drop-Off',
      author: 'Dr. Simone Okafor', readTime: '5 min read',
      body: `The transition from 5th to 6th grade represents one of the most studied inflection points in K–12 education—and one of the least effectively addressed. Students who were thriving readers in elementary school routinely arrive in middle school unprepared for the vocabulary load and disciplinary literacy demands of content-area classes.

The culprit is rarely the students themselves. More often, curriculum developers at the elementary and middle levels simply haven't spoken to each other.

Vertical curriculum alignment isn't about dictating what individual teachers do in their classrooms. It's about ensuring that the conceptual frameworks, vocabulary, and reading/writing expectations in grade 5 connect meaningfully to what students encounter in grade 6.

One mid-sized district in the Pacific Northwest piloted a vertical alignment initiative that brought elementary and middle school ELA teachers together for three days each summer. After two years, 6th-grade reading benchmark scores improved by 11 percentile points. "We discovered we were teaching the same vocabulary strategy three times across grades 4, 5, and 6—and calling it different things each time," said the district's ELA coordinator.`,
    },
    {
      category: 'Library Science', source: 'American Libraries Magazine', date: 'April 2026',
      title: 'Reimagining the School Library: Makerspaces, Quiet Zones, and Everything Between',
      author: 'Dr. Patricia Wren', readTime: '6 min read',
      body: `As school libraries evolve from quiet reading rooms into dynamic learning hubs, administrators and librarians are grappling with a central tension: how to serve both the student who needs silence for deep focus and the cohort who thrives in collaborative, hands-on environments.

A 2025 survey by the American Library Association found that 67% of school librarians who implemented structured makerspace programming reported increased library usage across all grade levels. More striking: 74% reported measurable improvements in student confidence when presenting research findings to peers.

The challenge for most library professionals isn't whether to embrace active learning spaces—it's how to zone them effectively within existing square footage. Acoustic panels, strategic shelving placement, and scheduled use windows have emerged as the most common solutions.

Librarians increasingly report needing training in facilitation techniques more than in cataloging systems. The shift from information keeper to learning designer is well underway, and professional development programs are only beginning to catch up.`,
    },
    {
      category: 'School Administration', source: 'Principal Leadership', date: 'March 2026',
      title: 'Substitute Teacher Pipelines: Why Districts Are Growing Their Own',
      author: 'Darlene McComb', readTime: '5 min read',
      body: `The substitute teacher shortage that emerged during the pandemic never fully resolved. In many districts—particularly rural and high-needs urban schools—the availability of qualified substitutes remains one of the most operationally disruptive challenges administrators face.

In response, a growing number of districts have moved away from dependence on external placement agencies and begun developing internal pipelines: programs that actively recruit, train, and cultivate long-term substitute relationships with community members.

Districts that have built successful internal substitute programs share several characteristics:
• Active recruitment from the school community (paraeducators, parent volunteers, retired teachers)
• Paid orientation and training specific to the district's instructional systems
• Preferred sub arrangements that give reliable substitutes first access to available days
• A dedicated coordinator who maintains relationships and handles placement

One mid-sized district in Colorado reports that after implementing this model in 2024, unfilled substitute requests dropped from 23% to under 6% of available positions within 18 months.`,
    },
    {
      category: 'Curriculum', source: 'ISTE Connects', date: 'March 2026',
      title: 'Micro-Credentialing for Educators: Where It Actually Works',
      author: 'Yolanda Ferris-Thomas', readTime: '6 min read',
      body: `When micro-credentials for educators emerged as a professional development model in the early 2010s, proponents promised a flexible, competency-based alternative to seat-time PD. Fifteen years later, the results are mixed—but the core concept has proven durable enough to examine where it's working and why.

The most common failure mode: treating micro-credentials as a replacement for coherent, sustained professional development rather than as a supplement. Teachers who earn micro-credentials in isolation—without collegial communities of practice or instructional coaching—show lower rates of classroom implementation.

The strongest outcomes appear in districts that use micro-credentials as entry points to deeper learning communities. When earning a micro-credential in project-based learning connects a teacher to a cohort of colleagues engaged in ongoing PBL implementation, the badge becomes a launchpad rather than a destination.

Specialized tracks—particularly in school librarianship, special education technology tools, and multilingual learner support—show strong outcomes, likely because these populations have fewer built-in professional learning communities.`,
    },
    {
      category: 'Library Science', source: 'Library Journal', date: 'February 2026',
      title: 'Cataloging in Crisis: The Hidden Workload Behind Every MARC Record',
      author: 'Dr. Helena Fuentes', readTime: '7 min read',
      body: `Cataloging—the invisible infrastructure of every functional library—is in quiet crisis. Driven by retirements, budget cuts to technical services departments, and an industry-wide shift toward copy cataloging, original cataloging expertise is becoming scarcer precisely as collection diversity demands it most.

For most items added to a library collection, copy cataloging from OCLC WorldCat is both efficient and adequate. The system works well for mainstream English-language print materials that are already widely held.

It works significantly less well for:
• Materials in less commonly cataloged languages
• Indigenous and community-produced materials
• Digitized special collections with no existing bibliographic record
• Non-standard formats and emerging media types

Poor catalog records don't simply inconvenience users—they render materials effectively invisible. A digital oral history collection cataloged with minimal subject headings will consistently underperform in discovery, regardless of the quality or significance of the content itself. Grant-funded IMLS projects are beginning to address this gap with specialized metadata training for rural and tribal libraries.`,
    },
  ];
  const dayIdx = Math.floor(Date.now() / 86400000);
  const art = ARTICLES[dayIdx % ARTICLES.length];
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div style={{ width:'100%', height:'100%', background:'#f4f4f0', fontFamily:"Georgia,'Times New Roman',serif", fontSize:'14px', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      {/* Masthead */}
      <div style={{ background:'#1c1c1c', color:'#fff', padding:'8px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:'15px', letterSpacing:'0.5px' }}>EdConnect Professional</div>
          <div style={{ fontSize:'10px', color:'#aaa', letterSpacing:'1px', textTransform:'uppercase' }}>Faculty Resources &amp; Professional Reading</div>
        </div>
        <div style={{ fontSize:'11px', color:'#aaa' }}>{art.date}</div>
      </div>
      {/* Category strip */}
      <div style={{ background:'#2c5f8a', color:'#fff', padding:'4px 16px', display:'flex', alignItems:'center', gap:'16px', fontSize:'11px', flexShrink:0 }}>
        {['Latest','Curriculum','Library Science','EdTech','Administration','PD'].map((tab,i)=>
          <span key={tab} style={{ cursor:'default', paddingBottom:'2px', borderBottom: i===0?'2px solid #fff':'none', opacity: i===0?1:0.75 }}>{tab}</span>
        )}
      </div>
      {/* Article */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px 20px 16px' }}>
        <div style={{ maxWidth:640, margin:'0 auto' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
            <span style={{ background:'#2c5f8a', color:'#fff', fontSize:'10px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', padding:'2px 8px', borderRadius:'2px' }}>{art.category}</span>
            <span style={{ fontSize:'11px', color:'#888' }}>{art.source} · {art.readTime}</span>
          </div>
          <h1 style={{ fontSize:'20px', fontWeight:700, lineHeight:'1.3', color:'#1a1a1a', margin:'0 0 10px', fontFamily:"Georgia,serif" }}>{art.title}</h1>
          <div style={{ fontSize:'12px', color:'#666', marginBottom:'16px', paddingBottom:'12px', borderBottom:'1px solid #ddd' }}>By {art.author}</div>
          <div style={{ lineHeight:'1.75', color:'#2a2a2a', fontSize:'14px', whiteSpace:'pre-line' }}>
            {expanded ? art.body : art.body.slice(0, 420) + '…'}
          </div>
          <button onClick={()=>setExpanded(v=>!v)} style={{ marginTop:'14px', background:'none', border:'1px solid #2c5f8a', color:'#2c5f8a', padding:'5px 14px', borderRadius:'3px', cursor:'pointer', fontSize:'12px', fontFamily:'inherit' }}>
            {expanded ? 'Show less ▲' : 'Continue reading ▼'}
          </button>
          {/* Related articles sidebar */}
          <div style={{ marginTop:'24px', paddingTop:'16px', borderTop:'2px solid #1c1c1c' }}>
            <div style={{ fontSize:'11px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', color:'#888', marginBottom:'10px' }}>Also in this issue</div>
            {ARTICLES.filter((_,i)=>i !== dayIdx % ARTICLES.length).slice(0,3).map((a,i)=>(
              <div key={i} style={{ marginBottom:'12px', paddingBottom:'12px', borderBottom:'1px solid #e8e8e4' }}>
                <div style={{ fontSize:'10px', color:'#2c5f8a', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'2px' }}>{a.category}</div>
                <div style={{ fontSize:'13px', fontWeight:600, color:'#1a1a1a', lineHeight:'1.3' }}>{a.title}</div>
                <div style={{ fontSize:'11px', color:'#888', marginTop:'2px' }}>{a.author} · {a.readTime}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Footer */}
      <div style={{ background:'#1c1c1c', color:'#aaa', padding:'5px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:'10px', flexShrink:0 }}>
        <span>EdConnect Professional · Faculty Edition · © 2026</span>
        {hint && <span style={{ color:'#555' }}>{hint}</span>}
      </div>
    </div>
  );
}

// ─── FAKE TASK/PARTS MANAGER DISGUISE ────────────────────────────────────────
function FakeTaskView({ hint }) {
  const [tasks, setTasks] = React.useState([
    { id:1, wo:'WO-2847', desc:'Replace brake pads — Unit 14', part:'Part #BP-7712', status:'In Progress', priority:'High', due:'May 2' },
    { id:2, wo:'WO-2848', desc:'Oil change & filter — Unit 09', part:'Part #OF-2201', status:'Pending Parts', priority:'Medium', due:'May 3' },
    { id:3, wo:'WO-2849', desc:'Transmission fluid flush — Unit 22', part:'Part #TF-0093', status:'Open', priority:'Medium', due:'May 5' },
    { id:4, wo:'WO-2851', desc:'Check engine light diag — Unit 07', part:'—', status:'Open', priority:'Low', due:'May 6' },
    { id:5, wo:'WO-2844', desc:'New tires (4x) — Unit 03', part:'Part #TR-1144', status:'Complete', priority:'High', due:'Apr 30' },
    { id:6, wo:'WO-2843', desc:'Battery replacement — Unit 18', part:'Part #BA-5560', status:'Complete', priority:'Medium', due:'Apr 29' },
  ]);
  const [newTask, setNewTask] = React.useState('');
  const statusColor = s => s==='Complete'?'#16a34a':s==='In Progress'?'#2563eb':s==='Pending Parts'?'#d97706':'#6b7280';
  const priColor = p => p==='High'?'#dc2626':p==='Medium'?'#d97706':'#6b7280';
  const addTask = () => {
    if (!newTask.trim()) return;
    const id = Date.now();
    setTasks(prev => [{ id, wo:`WO-${2851+prev.length}`, desc:newTask, part:'—', status:'Open', priority:'Medium', due:'TBD' }, ...prev]);
    setNewTask('');
  };
  const cycleStatus = (id) => {
    const cycle = ['Open','In Progress','Pending Parts','Complete'];
    setTasks(prev => prev.map(t => t.id===id ? { ...t, status: cycle[(cycle.indexOf(t.status)+1)%cycle.length] } : t));
  };
  return (
    <div style={{ width:'100%', height:'100%', background:'#f8fafc', fontFamily:"'Segoe UI',Arial,sans-serif", fontSize:'13px', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <div style={{ background:'#1e3a5f', color:'#fff', padding:'8px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:'14px' }}>🔧 Work Order Manager</div>
          <div style={{ fontSize:'10px', color:'#93c5fd', marginTop:'1px' }}>Service &amp; Parts Tracker — {new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
        </div>
        <div style={{ textAlign:'right', fontSize:'11px', color:'#93c5fd' }}>
          <div>{tasks.filter(t=>t.status!=='Complete').length} Open</div>
          <div>{tasks.filter(t=>t.status==='Complete').length} Complete</div>
        </div>
      </div>
      {/* Summary badges */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding:'6px 14px', display:'flex', gap:'10px', flexShrink:0 }}>
        {[['Open',tasks.filter(t=>t.status==='Open').length,'#6b7280'],['In Progress',tasks.filter(t=>t.status==='In Progress').length,'#2563eb'],['Pending Parts',tasks.filter(t=>t.status==='Pending Parts').length,'#d97706'],['Complete',tasks.filter(t=>t.status==='Complete').length,'#16a34a']].map(([label,count,color])=>(
          <div key={label} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'3px 10px', background:'#f8fafc', border:`1px solid ${color}33`, borderRadius:'20px' }}>
            <span style={{ width:'7px', height:'7px', borderRadius:'50%', background:color, display:'inline-block' }} />
            <span style={{ fontSize:'11px', color:'#374151' }}>{label}</span>
            <span style={{ fontSize:'11px', fontWeight:700, color }}>{count}</span>
          </div>
        ))}
      </div>
      {/* Add task bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding:'6px 14px', display:'flex', gap:'8px', flexShrink:0 }}>
        <input value={newTask} onChange={e=>setNewTask(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addTask()} placeholder="Add new work order description…" style={{ flex:1, border:'1px solid #d1d5db', borderRadius:'4px', padding:'5px 10px', fontSize:'12px', outline:'none' }} />
        <button onClick={addTask} style={{ background:'#1e3a5f', color:'#fff', border:'none', borderRadius:'4px', padding:'5px 14px', fontSize:'12px', cursor:'pointer', fontWeight:600 }}>+ Add</button>
      </div>
      {/* Task list */}
      <div style={{ flex:1, overflowY:'auto', padding:'8px 14px', display:'flex', flexDirection:'column', gap:'6px' }}>
        {tasks.map(t=>(
          <div key={t.id} style={{ background:'#fff', border:'1px solid #e2e8f0', borderLeft:`3px solid ${statusColor(t.status)}`, borderRadius:'6px', padding:'8px 12px', display:'flex', alignItems:'flex-start', gap:'10px', opacity:t.status==='Complete'?0.65:1 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px' }}>
                <span style={{ fontSize:'10px', fontWeight:700, color:'#6b7280', fontFamily:'monospace' }}>{t.wo}</span>
                <span style={{ fontSize:'10px', fontWeight:700, color:priColor(t.priority), textTransform:'uppercase' }}>{t.priority}</span>
              </div>
              <div style={{ fontWeight:600, color:'#1e293b', fontSize:'12px', textDecoration:t.status==='Complete'?'line-through':'none' }}>{t.desc}</div>
              <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'2px' }}>{t.part} · Due: {t.due}</div>
            </div>
            <button onClick={()=>cycleStatus(t.id)} style={{ background:`${statusColor(t.status)}18`, border:`1px solid ${statusColor(t.status)}44`, color:statusColor(t.status), borderRadius:'4px', padding:'3px 8px', fontSize:'10px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>{t.status}</button>
          </div>
        ))}
      </div>
      {/* Footer */}
      <div style={{ background:'#fff', borderTop:'1px solid #e2e8f0', padding:'5px 14px', display:'flex', justifyContent:'space-between', fontSize:'10px', color:'#94a3b8', flexShrink:0 }}>
        <span>WorkOrder Pro · Fleet &amp; Parts Management System</span>
        {hint && <span>{hint}</span>}
      </div>
    </div>
  );
}

// Capture URL params AND hash tokens ONCE at page load — before any
// history.replaceState call (which would wipe them on first render).
const _POPUP_PARAMS = new URLSearchParams(window.location.search);
const _POPUP_HASH   = new URLSearchParams(window.location.hash.replace(/^#/, ''));
const _INCOGNITO_HOSTNAME = ['resourcehub.cloud', 'www.resourcehub.cloud'];
const IS_INCOGNITO_POPUP =
  _POPUP_PARAMS.get('incognito') === '1' ||
  _INCOGNITO_HOSTNAME.includes(window.location.hostname);
// Masked cover paths ⇄ disguise. Opening the popup AT a masked path (not "/")
// matters: Vercel never applies a rewrite when a static file matches, and "/"
// maps to the real index.html — so only the masked paths get rewritten to the
// neutral incognito.html (link previews / view-source stay clean). The path
// also recovers the disguise on reload after replaceState wipes ?disguise.
const _MASK_PATHS     = { '/dashboard': 'office', '/portal': 'education', '/faculty-resources': 'staff', '/workspace': 'tasks' };
const _DISGUISE_PATHS = { office: '/dashboard', education: '/portal', staff: '/faculty-resources', tasks: '/workspace' };
const INCOGNITO_DISGUISE      = _POPUP_PARAMS.get('disguise') || _MASK_PATHS[window.location.pathname] || 'office';
// Auth tokens passed via hash from cross-origin main app
const INCOGNITO_ACCESS_TOKEN  = decodeURIComponent(_POPUP_HASH.get('access_token')  || '');
const INCOGNITO_REFRESH_TOKEN = decodeURIComponent(_POPUP_HASH.get('refresh_token') || '');

// Helper: open incognito popup, passing auth tokens via URL hash for cross-origin
async function openIncognitoPopup(mode) {
  try {
    const { data: { session } } = await safeGetSession();
    const tokenFrag = session
      ? `#access_token=${encodeURIComponent(session.access_token)}&refresh_token=${encodeURIComponent(session.refresh_token)}&token_type=bearer`
      : '';
    window.open(
      // Open AT the masked path (not "/") so Vercel's host rewrite serves the
      // neutral incognito.html — "/" would hit the real static index.html.
      `https://resourcehub.cloud${_DISGUISE_PATHS[mode] || '/dashboard'}?disguise=${mode}${tokenFrag}`,
      'mfml_incognito',
      'width=460,height=620,resizable=yes,scrollbars=no,status=no,location=no,toolbar=no,menubar=no'
    );
  } catch {
    // Fallback: same-origin popup (dev / domain not yet live)
    window.open(`/?incognito=1&disguise=${mode}`, 'mfml_incognito', 'width=460,height=620,resizable=yes,scrollbars=no,status=no,location=no,toolbar=no,menubar=no');
  }
}

// ─── INCOGNITO LOGIN (shown when no session on resourcehub.cloud standalone) ──
// Lets users sign in directly on resourcehub.cloud for mobile PWA use.
function IncognitoLogin({ disguise, onLogin }) {
  const bg = { office:'#f5f5f5', education:'#f8f9fa', staff:'#f4f4f0', tasks:'#f8fafc' }[disguise] || '#f8f9fa';
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true); setError('');
    try {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
      onLogin(data.user);
    } catch (err) {
      setError(err.message || 'Login failed. Check your credentials.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100dvh', background:bg, fontFamily:"'Segoe UI',Arial,sans-serif", padding:'20px' }}>
      <div style={{ width:'100%', maxWidth:'340px' }}>
        {/* Looks like a generic portal login */}
        <div style={{ textAlign:'center', marginBottom:'28px' }}>
          <div style={{ width:48, height:48, borderRadius:'10px', background:'#e9ecef', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px', margin:'0 auto 12px' }}>🔐</div>
          <div style={{ fontWeight:700, fontSize:'18px', color:'#212529' }}>ResourceHub Portal</div>
          <div style={{ fontSize:'12px', color:'#6c757d', marginTop:'4px' }}>Sign in to access your workspace</div>
        </div>
        <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Work email" required autoComplete="email"
            style={{ border:'1px solid #ced4da', borderRadius:'6px', padding:'10px 14px', fontSize:'14px', outline:'none', fontFamily:'inherit', background:'#fff', color:'#212529' }}
          />
          <input
            type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Password" required autoComplete="current-password"
            style={{ border:'1px solid #ced4da', borderRadius:'6px', padding:'10px 14px', fontSize:'14px', outline:'none', fontFamily:'inherit', background:'#fff', color:'#212529' }}
          />
          {error && <div style={{ fontSize:'12px', color:'#dc3545', background:'#fff5f5', border:'1px solid #f5c6cb', borderRadius:'4px', padding:'6px 10px' }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ background:'#495057', color:'#fff', border:'none', borderRadius:'6px', padding:'11px', fontSize:'14px', fontWeight:600, cursor:loading?'default':'pointer', fontFamily:'inherit', transition:'background 0.15s' }}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <div style={{ textAlign:'center', marginTop:'20px', fontSize:'10px', color:'#adb5bd' }}>
          Secure workspace portal · resourcehub.cloud
        </div>
      </div>
    </div>
  );
}

// ─── INCOGNITO STANDALONE (runs in popup window) ──────────────────────────────
// Launched via window.open('/?incognito=1&disguise=office|education|staff|tasks')
// Shares Supabase auth via localStorage (same origin).
function IncognitoStandalone() {
  // Use module-level constant — NOT window.location.search — so replaceState doesn't break it
  const disguise = INCOGNITO_DISGUISE;

  const [standaloneUser, setStandaloneUser] = React.useState(null);
  const [standaloneLoading, setStandaloneLoading] = React.useState(true);
  const [chatVisible, setChatVisible] = React.useState(false); // start in disguise view
  const [myStories, setMyStories] = React.useState([]);

  // Mask URL path so query params don't show in address bar
  React.useEffect(() => {
    const paths = { office: '/dashboard', education: '/portal', staff: '/faculty-resources', tasks: '/workspace' };
    window.history.replaceState(null, '', paths[disguise] || '/portal');
  }, [disguise]);

  // Update title for each view
  React.useEffect(() => {
    const titles = { office: 'Q1_Payroll_Final_v2.xlsx', education: 'Academic Learning Portal', staff: 'EdConnect Professional', tasks: 'Work Order Manager' };
    document.title = chatVisible ? 'Messages — Workspace' : (titles[disguise] || 'Dashboard');
  }, [chatVisible, disguise]);

  // Boss key: Option/Alt toggles between disguise and chat
  // Alt alone won't conflict with system shortcuts (Cmd+Tab uses Meta, not Alt)
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Alt' && !e.repeat && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        setChatVisible(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auth — supports both same-origin (shared localStorage) and cross-origin (hash tokens)
  React.useEffect(() => {
    const loadStories = (userId) => {
      // NAMES ONLY (audit PERF-5). This result is rendered as ten names in
      // the link picker and nothing else — the one consumer that needs a
      // blob refetches `data` itself. Selecting `data` here transferred and
      // parsed megabytes of story JSON (~4.8 MB for Nicole's newest ten) just
      // to open the incognito popup.
      supabase.from('stories').select('id, name').eq('user_id', userId)
        .order('updated_at', { ascending: false }).limit(10)
        .then(({ data }) => setMyStories(data || []));
    };

    const init = async () => {
      // Use module-level constants — captured before any replaceState wipes the hash
      if (INCOGNITO_ACCESS_TOKEN && INCOGNITO_REFRESH_TOKEN) {
        // Cross-origin popup (resourcehub.cloud): set session from hash tokens
        try {
          const { data: { session } } = await supabase.auth.setSession({
            access_token: INCOGNITO_ACCESS_TOKEN,
            refresh_token: INCOGNITO_REFRESH_TOKEN,
          });
          setStandaloneUser(session?.user || null);
          if (session?.user) loadStories(session.user.id);
        } catch (e) {
          console.warn('[Incognito] setSession failed:', e);
        }
      } else {
        // Same-origin popup: tokens are in shared localStorage automatically
        const { data: { session } } = await safeGetSession();
        setStandaloneUser(session?.user || null);
        if (session?.user) loadStories(session.user.id);
      }
      setStandaloneLoading(false);
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setStandaloneUser(session?.user || null);
    });
    return () => subscription?.unsubscribe();
  }, []);

  const hint = `Press Option (⌥) to switch views`;

  if (standaloneLoading) {
    return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100dvh', fontFamily:'sans-serif', fontSize:'13px', color:'#666' }}>Loading…</div>;
  }

  if (!standaloneUser) {
    return <IncognitoLogin disguise={disguise} onLogin={setStandaloneUser} />;
  }

  return (
    <div style={{ width:'100%', height:'100dvh', overflow:'hidden', position:'relative', fontFamily:'sans-serif', boxSizing:'border-box' }}>
      {/* Disguise layer */}
      <div style={{ position:'absolute', inset:0, zIndex: chatVisible ? 0 : 10, pointerEvents: chatVisible ? 'none' : 'auto' }}>
        {disguise === 'education' ? <FakeEducationView hint={hint} />
         : disguise === 'staff' ? <FakeSchoolStaffView hint={hint} />
         : disguise === 'tasks' ? <FakeTaskView hint={hint} />
         : <FakeOfficeView hint={hint} />}
      </div>
      {/* Chat layer — IncognitoWindow in standalone/fullscreen mode */}
      <div style={{ position:'absolute', inset:0, zIndex: chatVisible ? 10 : 0, pointerEvents: chatVisible ? 'auto' : 'none' }}>
        <IncognitoWindow user={standaloneUser} onClose={() => window.close()} allChars={CHARACTERS || []} myStories={myStories} onlineProfiles={[]} standalone chatHint={hint} />
      </div>
      {/* Toggle hint — visible floating pill so user always knows how to switch */}
      <div
        onClick={() => setChatVisible(v => !v)}
        title={chatVisible ? 'Switch to disguise view' : 'Switch to chat'}
        style={{
          position:'fixed', bottom:14, right:14, zIndex:9999,
          background: chatVisible ? 'rgba(73,80,87,0.85)' : 'rgba(33,115,70,0.85)',
          color:'#fff', borderRadius:'20px', padding:'5px 14px',
          fontSize:'11px', fontWeight:600, cursor:'pointer',
          backdropFilter:'blur(4px)', boxShadow:'0 2px 8px rgba(0,0,0,0.25)',
          userSelect:'none', letterSpacing:'0.3px',
          transition:'background 0.2s',
        }}
      >
        {chatVisible ? '📋 Show work view  ⌥' : '💬 Show chat  ⌥'}
      </div>
    </div>
  );
}

// ─── CSS ANIMATIONS ─────────────────────────────────────────────────────────
const styleTag = document.createElement("style");
styleTag.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Inter:wght@400;500;600&family=Lora:ital,wght@0,400;0,600;1,400&family=Merriweather:ital,wght@0,400;0,700;1,400&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Source+Sans+3:wght@400;500;600&family=Nunito:wght@400;500;600;700&family=Literata:ital,opsz,wght@0,7..72,400;0,7..72,600;1,7..72,400&family=EB+Garamond:ital,wght@0,400;0,600;1,400&display=swap');
  @import url('https://fonts.cdnfonts.com/css/open-dyslexic');

  /* ── Core Animations ── */
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideInRight {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes typingDot {
    0%, 80%, 100% { opacity: 0.3; }
    40% { opacity: 1; }
  }
  @keyframes subtlePulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  @keyframes chainDrift {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-3px) rotate(0.5deg); }
  }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes modalFadeIn { from { opacity: 0; transform: translateY(10px) scale(0.965); } to { opacity: 1; transform: translateY(0) scale(1); } }

  /* ── Atmospheric Animations ── */
  @keyframes floatParticle {
    0% { transform: translateY(100vh) translateX(0) scale(0); opacity: 0; }
    10% { opacity: 1; transform: translateY(90vh) translateX(10px) scale(1); }
    90% { opacity: 0.6; }
    100% { transform: translateY(-10vh) translateX(-20px) scale(0.5); opacity: 0; }
  }
  @keyframes glowPulse {
    0%, 100% { box-shadow: 0 0 5px var(--ds-accent-a00, #c6282800), 0 0 20px var(--ds-accent-a00, #c6282800); }
    50% { box-shadow: 0 0 8px var(--ds-accent-a44, #c6282844), 0 0 25px var(--ds-accent-a22, #c6282822); }
  }
  @keyframes borderGlow {
    0%, 100% { border-color: var(--ds-accent-a33, #c6282833); }
    50% { border-color: var(--ds-accent-a66, #c6282866); }
  }
  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes breathe {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
  @keyframes redEmber {
    0%, 100% { opacity: 0.3; filter: blur(1px); }
    50% { opacity: 0.8; filter: blur(0.5px); }
  }
  @keyframes driftSlow {
    0% { transform: translate(0, 0) rotate(0deg); }
    25% { transform: translate(5px, -8px) rotate(1deg); }
    50% { transform: translate(-3px, -15px) rotate(-0.5deg); }
    75% { transform: translate(8px, -8px) rotate(0.5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
  }
  @keyframes cardReveal {
    from { opacity: 0; transform: translateY(12px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes navSlideIn {
    from { opacity: 0; transform: translateX(-10px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes textReveal {
    from { opacity: 0; letter-spacing: 12px; }
    to { opacity: 1; letter-spacing: 8px; }
  }
  @keyframes logoReveal {
    from { opacity: 0; transform: scale(0.9); filter: blur(4px); }
    to { opacity: 1; transform: scale(1); filter: blur(0); }
  }

  /* ── Applied Classes ── */
  .msg-animate { animation: fadeSlideIn 0.3s ease-out; }
  .msg-sent { animation: fadeSlideIn 0.25s ease-out; }
  .msg-received { animation: fadeSlideIn 0.35s ease-out; }
  .modal-animate { animation: modalFadeIn 0.32s var(--ds-ease-fated, cubic-bezier(0.16, 1, 0.3, 1)) both; }
  [role="dialog"] { animation: modalFadeIn 0.32s var(--ds-ease-fated, cubic-bezier(0.16, 1, 0.3, 1)) both; }
  .modal-closing { animation: modalFadeOut 0.18s ease-in forwards; pointer-events: none; }
  .backdrop-closing { animation: backdropFadeOut 0.18s ease-in forwards; }
  .card-animate { animation: cardReveal 0.4s ease-out both; }
  .glow-hover:hover { animation: glowPulse 2s ease-in-out infinite; }
  .view-enter { animation: viewFadeIn 0.25s ease-out; }

  /* ── Hover Effects ── */
  .char-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border-radius: var(--ds-r-md, 14px) !important; }
  .char-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px var(--ds-accent-a26, rgba(198,40,40,0.15)), 0 2px 8px rgba(0,0,0,0.4), inset 0 0 0 1px var(--ds-gold-a40, #c4a88240); }
  .nav-btn { transition: all 0.2s ease; position: relative; }
  .nav-btn::after { content: ''; position: absolute; bottom: -2px; left: 50%; width: 0; height: 2px; background: linear-gradient(90deg, var(--ds-accent, #c62828), var(--ds-gold, #c4a882)); transition: all 0.3s ease; transform: translateX(-50%); }
  .nav-btn:hover::after { width: 80%; }
  .nav-btn.active::after { width: 100%; }
  .art-frame { transition: all 0.4s ease; }
  .art-frame:hover { transform: scale(1.03); filter: brightness(1.1); }

  /* ── Story Card: frosted slab with a gold-kissed lift ── */
  .story-card {
    transition: transform 0.2s ease, box-shadow 0.25s ease, border-color 0.2s ease, background 0.2s ease;
    cursor: pointer;
    border-radius: var(--ds-r-md, 14px) !important;
    backdrop-filter: blur(10px) saturate(1.3);
    -webkit-backdrop-filter: blur(10px) saturate(1.3);
  }
  .story-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.35), 0 0 18px -6px var(--ds-gold-a40, #c4a88240);
    border-color: var(--ds-gold-a66, rgba(196,168,130,0.4)) !important;
    background: var(--ds-hover, rgba(255,255,255,0.06)) !important;
  }

  /* ── Sidebar Animation ── */
  .sidebar-panel {
    transition: width 0.25s ease, opacity 0.2s ease, transform 0.25s ease;
    overflow: hidden;
    backdrop-filter: blur(14px) saturate(1.4);
    -webkit-backdrop-filter: blur(14px) saturate(1.4);
    box-shadow: inset -1px 0 0 var(--ds-gold-a26, #c4a88226);
  }
  @media (max-width: 768px) {
    .sidebar-overlay {
      position: fixed; inset: 0; z-index: 50;
      background: rgba(0,0,0,0.5);
      animation: backdropFadeIn 0.2s ease-out;
    }
    .sidebar-mobile {
      position: fixed; left: 0; top: 0; bottom: 0; z-index: 51;
      width: 280px; max-width: 80vw;
      animation: slideInLeft 0.25s ease-out;
      box-shadow: 4px 0 24px rgba(0,0,0,0.5);
    }
    /* Mobile touch optimization — prevent accidental double-tap zoom */
    button, a, [role="button"], [role="menuitem"] {
      touch-action: manipulation;
    }
    /* Ensure inputs don't zoom on iOS (font-size must be >= 16px) */
    input, textarea, select {
      font-size: 16px !important;
    }
  }
  @keyframes slideInLeft {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
  @keyframes modalFadeOut {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0.95); }
  }
  @keyframes backdropFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes backdropFadeOut { from { opacity: 1; } to { opacity: 0; } }
  @keyframes viewFadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ── Skeleton Loader ── */
  .skeleton {
    background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 75%);
    background-size: 200% 100%;
    animation: skeletonShimmer 1.5s ease-in-out infinite;
    border-radius: 6px;
  }
  @keyframes skeletonShimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* ── Typing Indicator Dots ── */
  .typing-dot {
    display: inline-block;
    width: 6px; height: 6px;
    background: var(--ds-accent, #c62828);
    border-radius: 50%;
    animation: typingBounce 1.2s ease-in-out infinite;
  }
  .typing-dot:nth-child(2) { animation-delay: 0.15s; }
  .typing-dot:nth-child(3) { animation-delay: 0.3s; }
  @keyframes typingBounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
    40% { transform: translateY(-4px); opacity: 1; }
  }

  /* ── Save Button Success Flash ── */
  .save-success {
    animation: saveFlash 0.6s ease-out;
  }
  @keyframes saveFlash {
    0% { background: rgba(76,175,80,0.3); }
    100% { background: transparent; }
  }

  /* ── Notification Dot ── */
  .notif-dot {
    width: 8px; height: 8px;
    background: var(--ds-accent, #c62828);
    border-radius: 50%;
    animation: notifPulse 2s ease-in-out infinite;
  }
  @keyframes notifPulse {
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 var(--ds-accent-a80, rgba(198,40,40,0.5)); }
    50% { transform: scale(1.15); box-shadow: 0 0 0 4px var(--ds-accent-a00, rgba(198,40,40,0)); }
  }

  /* ── Input Glow on Focus ── */
  .input-glow:focus {
    border-color: var(--ds-gold, #c4a882) !important;
    box-shadow: 0 0 0 2px var(--ds-gold-a26, rgba(196,168,130,0.15)), 0 0 12px var(--ds-gold-a0f, rgba(196,168,130,0.06));
    outline: none;
  }

  /* ── Tooltip ── */
  .tooltip-hint {
    position: relative;
  }
  .tooltip-hint::after {
    content: attr(data-tooltip);
    position: absolute; bottom: 100%; left: 50%;
    transform: translateX(-50%) translateY(-4px);
    padding: 4px 10px;
    background: #1a1a1a; color: var(--ds-gold, #c4a882);
    font-size: 11px; border-radius: 6px;
    white-space: nowrap; pointer-events: none;
    opacity: 0; transition: opacity 0.2s ease;
    border: 1px solid var(--ds-accent-a33, rgba(198,40,40,0.2));
  }
  .tooltip-hint:hover::after { opacity: 1; }

  /* ── Utilities ── */
  *:focus-visible { outline: 2px solid var(--ds-gold-a80, rgba(196,168,130,0.5)); outline-offset: 2px; border-radius: 4px; }
  .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }

  /* Scrollbars are styled once, in index.css §REFRESH (the #0d0d0d track
     here painted a black gutter down every light-theme panel). */
  ::selection { background: var(--ds-accent-a55, #c6282855); color: var(--ds-on-accent, #fff); }

  /* ── Floating Particles Container ── */
  .particles-container { position: fixed; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
  .particle { position: absolute; width: 3px; height: 3px; background: var(--ds-accent, #c62828); border-radius: 50%; animation: floatParticle linear infinite; opacity: 0; }

  /* ── Live Notification Feed ── */
  @keyframes notifSlideIn {
    from { transform: translateX(120%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes notifSlideOut {
    from { transform: translateX(0); opacity: 1; max-height: 100px; margin-bottom: 8px; padding: 10px 14px; }
    to { transform: translateX(120%); opacity: 0; max-height: 0; margin-bottom: 0; padding: 0 14px; }
  }
  @keyframes bellShake {
    0%, 100% { transform: rotate(0deg); }
    10% { transform: rotate(14deg); }
    20% { transform: rotate(-12deg); }
    30% { transform: rotate(10deg); }
    40% { transform: rotate(-8deg); }
    50% { transform: rotate(0deg); }
  }
  @keyframes notifPing {
    0% { transform: scale(1); box-shadow: 0 0 0 0 var(--ds-accent-a99, rgba(198,40,40,0.6)); }
    70% { transform: scale(1); box-shadow: 0 0 0 8px var(--ds-accent-a00, rgba(198,40,40,0)); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 var(--ds-accent-a00, rgba(198,40,40,0)); }
  }
  @keyframes progressShrink {
    from { width: 100%; }
    to { width: 0%; }
  }
  .notif-item { animation: notifSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
  .notif-item-exit { animation: notifSlideOut 0.3s ease-in forwards; }
  .bell-shake { animation: bellShake 0.6s ease-in-out; }
  .notif-badge-ping { animation: notifPing 1.5s ease-out infinite; }

  /* ── Story Channel Notification Bell ── */
  .story-bell-glow { animation: storyBellGlow 2.5s ease-in-out infinite; }
  @keyframes storyBellGlow {
    0%, 100% { filter: drop-shadow(0 0 2px var(--ds-gold-a33, rgba(196,168,130,0.2))); }
    50% { filter: drop-shadow(0 0 8px var(--ds-gold-a99, rgba(196,168,130,0.6))) drop-shadow(0 0 16px var(--ds-gold-a4c, rgba(196,168,130,0.3))); }
  }
  @keyframes storyBadgePulse {
    0%, 100% { transform: scale(1); box-shadow: 0 0 6px var(--ds-gold-a66, rgba(196,168,130,0.4)); }
    50% { transform: scale(1.1); box-shadow: 0 0 12px var(--ds-gold-a99, rgba(196,168,130,0.6)), 0 0 20px var(--ds-gold-a33, rgba(196,168,130,0.2)); }
  }

  /* ── Nav active indicator ── */
  /* Active tab ink is a token (the pale-pink literal vanished on the light
     themes); the sliding gold→accent hairline lives in index.css §REFRESH. */
  .nav-btn.active { color: var(--ds-ink-hi, #e8d5d5) !important; }

  /* ── Smooth View Transitions ── */
  .view-transition { animation: viewFadeIn 0.2s ease-out; }

  /* ── Button press feedback ── */
  .btn-press:active { transform: scale(0.97); transition: transform 0.1s; }
`;
if (!document.querySelector("[data-sixguys-styles]")) {
  styleTag.setAttribute("data-sixguys-styles", "true");
  document.head.appendChild(styleTag);
}

// ─── LAZY SUSPENSE WRAPPER ────────────────────────────────────────────────
// Wraps any lazy-loaded component with a Suspense boundary + fallback
const LazyFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#888' }}>
    <SectionLoader message="Loading..." />
  </div>
);
function Lazy({ children }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>;
}
// For modal components: dark overlay fallback prevents black flash during lazy load
const ModalLazyFallback = () => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <SectionLoader message="" />
  </div>
);
function ModalLazy({ children }) {
  return <Suspense fallback={<ModalLazyFallback />}>{children}</Suspense>;
}

// ─── RP INPUT BAR ──────────────────────────────────────────────────────────
// Extracted into its own memoized component so typing into the RP input
// doesn't re-render the entire 10K-line SixGuysRP tree on every keystroke.
// Previously every keystroke set parent state → full re-render → ~50-100ms
// commit on iOS Safari → visible typing lag (text would land ~1s after the
// keystroke). Now the input owns its own state; the parent only learns
// about it via onSend(text) when the user actually presses Enter / Send.
//
// Imperative API exposed via ref so the parent can still seed text from
// suggestion buttons or load a draft on story switch.
const RPInputBar = React.memo(React.forwardRef(function RPInputBar({
  activeStoryId,
  currentChar,
  playerName,        // fallback label for play-as-yourself (prompt-driven) stories
  aiPaused,
  setAiPaused,
  onSend,             // async (text) => boolean | undefined  — return false to restore text
  onTypingBroadcast,  // optional () => void — called when input becomes non-empty
  inputStyle,
  btnPrimary,
  isSendingRef,
  t,
  isMobile,           // when true, textarea wraps to its own row and buttons sit below
}, ref) {
  const [localText, setLocalText] = React.useState('');
  const localSendingRef = React.useRef(false);
  const inputDomRef = React.useRef(null);
  // Light themes (Haley, 2026-09-08): the bar was hardcoded near-black while
  // the rest of the page went ivory. It now follows the theme's input surface
  // unless the writer keeps the dark bar on purpose (Settings → Appearance).
  const themeCtxBar = React.useContext(ThemeContext);
  const lightBar = !!themeCtxBar?.theme?.light && !themeCtxBar?.composerDark;

  // Load the persisted draft when switching stories. Parent no longer
  // does this — owns no inputText state anymore.
  React.useEffect(() => {
    setLocalText(activeStoryId ? (loadDraft('rp', activeStoryId) || '') : '');
  }, [activeStoryId]);
  // v425: the same draft on every device
  const localTextRef = React.useRef('');
  localTextRef.current = localText;
  useSyncedDraft('rp', activeStoryId, { getText: () => localTextRef.current, setText: (txt) => setLocalText(txt || '') });

  React.useImperativeHandle(ref, () => ({
    setText: (txt) => setLocalText(txt || ''),
    appendText: (txt) => setLocalText(prev => (prev ? prev + ' ' : '') + (txt || '')),
    getText: () => localText,
    clear: () => setLocalText(''),
    focus: () => inputDomRef.current?.focus(),
  }), [localText]);

  const doSend = React.useCallback(async () => {
    const text = localText.trim();
    if (!text || localSendingRef.current) return;
    localSendingRef.current = true;
    setLocalText(''); // optimistic clear — prevents double-send
    try {
      const ok = await onSend(text);
      if (ok === false) setLocalText(text); // parent says nope (e.g. session check failed)
    } finally {
      localSendingRef.current = false;
    }
  }, [localText, onSend]);

  const hasText = !!localText.trim();
  const charColor = currentChar?.color;

  // Auto-grow textarea as the user types. CSS maxHeight: 50vh caps it.
  React.useEffect(() => {
    const el = inputDomRef.current;
    if (!el) return;
    autoGrowTextarea(el);
  }, [localText]);

  return (
    // Desktop: single row (avatar + textarea + pause + Send).
    // Mobile: textarea wraps to its own full-width row (forced by minWidth:100%
    // + order:1), with avatar + pause + Send sitting on the row below.
    <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: '8px', alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: charColor, fontSize: '13px', fontWeight: 700, flexShrink: 0, padding: '0 4px', minHeight: '44px', order: isMobile ? 2 : 0 }}>
        {currentChar?.avatar}
      </div>
      <textarea
        ref={inputDomRef}
        value={localText}
        rows={1}
        onChange={(e) => {
          const v = e.target.value;
          setLocalText(v);
          saveDraft('rp', activeStoryId, v); // already debounced inside saveDraft
          if (v.trim() && onTypingBroadcast) onTypingBroadcast();
        }}
        onKeyDown={(e) => { if (enterShouldSend(e)) { e.preventDefault(); doSend(); } }}
        className="input-glow"
        style={{ ...inputStyle, flex: 1, padding: '10px 14px', borderRadius: '10px', border: lightBar ? '1px solid var(--ds-line, #dcc8b8)' : '1px solid var(--ds-accent-a1f, rgba(198,40,40,0.12))', background: lightBar ? 'var(--ds-input, #ffffff)' : 'rgba(18,14,14,0.8)', color: lightBar ? 'var(--ds-ink-hi, #3a2c28)' : (inputStyle?.color || '#dcddde'), minHeight: '44px', maxHeight: '50vh', resize: 'none', overflow: 'auto', lineHeight: '1.4', fontFamily: 'inherit', order: isMobile ? 1 : 0, minWidth: isMobile ? '100%' : 0 }}
        placeholder={aiPaused ? `Write as ${currentChar?.name || playerName || 'yourself'}... (AI paused)` : `Write as ${currentChar?.name || playerName || 'yourself'}... (use (( )) for OOC)`}
      />
      <button
        onClick={(e) => { e.stopPropagation(); setAiPaused(p => !p); }}
        title={aiPaused ? 'Resume AI responses' : 'Pause AI — RP with other players'}
        style={{ padding: '10px 12px', borderRadius: '10px', border: `1px solid ${aiPaused ? '#4a9e6e55' : 'var(--ds-gold-a33, #c4a88233)'}`, background: aiPaused ? 'rgba(74, 158, 110, 0.12)' : 'var(--ds-gold-a14, rgba(196, 168, 130, 0.08))', color: aiPaused ? (t?.accentSuccess || '#69f0ae') : (t?.accentGold || 'var(--ds-gold, #c4a882)'), fontSize: '16px', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0, lineHeight: 1, minWidth: '44px', minHeight: '44px', touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none', order: isMobile ? 2 : 0 }}
      >
        <span style={{ pointerEvents: 'none' }}>{aiPaused ? '▶' : '⏸'}</span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); doSend(); }}
        disabled={!hasText || isSendingRef?.current}
        style={{ ...btnPrimary, padding: '12px 22px', borderRadius: '10px', opacity: isSendingRef?.current ? 0.5 : 1, background: hasText ? 'linear-gradient(135deg, var(--ds-accent-deeper, #8b0000), var(--ds-accent, #c62828))' : '#2a1a1a', border: '1px solid ' + (hasText ? 'var(--ds-accent-a66, #c6282866)' : '#2a1a1a'), transition: 'all 0.3s ease', boxShadow: hasText ? '0 2px 12px var(--ds-accent-a40, rgba(198,40,40,0.25))' : 'none', minWidth: '48px', minHeight: '48px', WebkitTapHighlightColor: 'var(--ds-accent-a33, rgba(198,40,40,0.2))', touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none', order: isMobile ? 2 : 0, marginLeft: isMobile ? 'auto' : 0 }}
      >
        Send
      </button>
    </div>
  );
}));

// Relative "last active" timestamp. Module-level so BOTH SixGuysRP (which renders
// <UserProfileModal formatLastActive={formatLastActive} />) and MainNav (which uses
// it in the friend/user list) can reference it. Pure — no component state.
// Every profile column the app reads for the signed-in user. `phone` is
// deliberately absent: it is collected at sign-up and is leaving the public
// SELECT grant (2026-10-19 audit; the privacy policy calls it optional and the
// app shows it to the admin only).
const PROFILE_COLUMNS = "id, display_name, role, created_at, avatar_url, status, is_banned, banned_at, banned_by, bio, is_online, last_seen, accent_color, theme, status_message, hide_online_status, profile_frame, unlocked_frames, xp, level, login_streak, last_login_date, longest_streak, fate_last_drawn, fate_last_card_id, last_client_version, last_seen_at, message_color, rp_defaults, text_color, composer_dark";

function formatLastActive(lastSeen) {
  if (!lastSeen) return "";
  const diff = Date.now() - new Date(lastSeen).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(lastSeen).toLocaleDateString();
}

export default function SixGuysRP() {
  // ═══════════════════════════════════════════════════════════════════════════
  // THEME
  // ═══════════════════════════════════════════════════════════════════════════
  const themeCtx = useContext(ThemeContext);
  const T = themeCtx?.theme || {};
  const t = T;
  const bgApp = T.bgDeepest || t.bgDeepest;
  const bgCard = T.bgBase || t.bgBase;
  const _bgSurface = T.bgSecondary || t.bgSecondary;
  const textMain = T.textPrimary || t.textPrimary;
  const _textSub = T.textSecondary || t.textSecondary;
  const _textMuted = T.textMuted || t.textMuted;
  const _borderColor = T.borderMedium || t.borderMedium;

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTH STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginMode, setLoginMode] = useState("signin");
  const [authEmail, setAuthEmail] = useState(() => {
    try { return localStorage.getItem('sixguys_last_email') || ""; } catch { return ""; }
  });
  const [authPassword, setAuthPassword] = useState("");
  const [authDisplayName, setAuthDisplayName] = useState("");
  const [authPhone, setAuthPhone] = useState("");
  const [authInviteCode, setAuthInviteCode] = useState("");
  const [authError, setAuthError] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showForceReset, setShowForceReset] = useState(false);
  // Legal route — check on mount; renders standalone legal page if matched
  const [legalRoute] = useState(() => {
    try {
      const path = window.location.pathname;
      const paths = ['/privacy', '/terms', '/ai-disclosure', '/safety', '/contact', '/delete-account', '/gacha-odds'];
      return paths.find(p => path === p) || null;
    } catch { return null; }
  });
  // Age gate — must be confirmed before signup proceeds
  const [ageGateConfirmed, setAgeGateConfirmed] = useState(false);
  // Content consent — gates dark/mature adult fiction. Initializes from localStorage so
  // returning users who already agreed don't see it again. Bump CONSENT_VERSION in
  // ContentConsentModal.jsx to force re-acceptance after policy changes.
  const [showContentConsent, setShowContentConsent] = useState(() => !hasConsented());

  // Fast stuck-detection: if session is still expired after 5s, show force reset
  useEffect(() => {
    if (!sessionExpired) { setShowForceReset(false); return; }
    const timer = setTimeout(() => setShowForceReset(true), 2000);
    return () => clearTimeout(timer);
  }, [sessionExpired]);

  // ═══════════════════════════════════════════════════════════════════════════
  // MAINTENANCE MODE
  // ═══════════════════════════════════════════════════════════════════════════
  const [maintenanceMode, setMaintenanceMode] = useState(null); // null = loading, false = off, object = on
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'maintenance_mode')
          .single();
        if (!error && data?.value?.enabled) {
          setMaintenanceMode(data.value);
        } else {
          setMaintenanceMode(false);
        }
      } catch {
        setMaintenanceMode(false);
      }
    };
    checkMaintenance();
    // Also subscribe to realtime changes so the screen clears instantly when maintenance ends
    const channel = supabase.channel('maintenance-watch')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'site_settings', filter: 'key=eq.maintenance_mode' }, (payload) => {
        const val = payload.new?.value;
        if (val?.enabled) {
          setMaintenanceMode(val);
        } else {
          setMaintenanceMode(false);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // MOBILE DETECTION
  // ═══════════════════════════════════════════════════════════════════════════
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // APP STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [view, setView] = useState("home");
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [playingAs, setPlayingAs] = useState(null);
  const [sceneChars, setSceneChars] = useState([]);
  // Nicole, 2026-08-10: "still no way to designate relationships". The RP
  // section was the one surface that never mounted RelationshipsPanel —
  // DMView, ChannelView, GroupChatView and CharacterEditor all did. Worse,
  // the Scene Characters blurb told her to "tag their relationship below",
  // a promise left behind when charTags was purged in item 16.
  const [showRpRelationships, setShowRpRelationships] = useState(false);
  // Characters explicitly removed from a story stay out until re-added. Auto-
  // casting (below) may pull in ANY character the scene calls for, so there has
  // to be a way to say "not this one" and have it stick.
  const [blockedChars, setBlockedChars] = useState([]);
  // Surfaced when a send produces no AI reply, so the room can never go
  // silently dead again.
  const [rpAIError, setRpAIError] = useState('');
  const [messages, setMessages] = useState([]);
  // inputText state lives inside <RPInputBar /> so keystrokes don't re-render
  // the giant SixGuysRP tree. Parent communicates via the ref below.
  const rpInputBarRef = useRef(null);
  const [sceneType, setSceneType] = useState("chat");
  // Prompt-driven "narrator mode" — story was started from a free-form
  // route description rather than by picking a specific character to play.
  // The AI brings in any universe characters that fit the scene; the player
  // types as themselves (or their described persona). Persisted in
  // stories.data.narratorMode so the flag survives reload.
  const [narratorMode, setNarratorMode] = useState(false);
  // AU = this story was branched off another one. Canon blood/species still
  // hold, but relationship STATE belongs to this route (Haley, 2026-08-01:
  // "if it's a first meeting that needs to be known as well, or if it's an
  // AU"). Derived from data.branchedFrom, which branchStory already writes.
  const [storyIsAU, setStoryIsAU] = useState(false);
  // Extracted "my name is X" / "I'm Y" from the route prompt; falls back to
  // currentPlayer / display name. Used as the "isPlayer" sender name when
  // playingAs is null.
  const [narratorPlayerName, setNarratorPlayerName] = useState('');
  // Whether to show the optional advanced character-picker grid on the
  // Start an RP screen. Default is collapsed — prompt-driven start is
  // the new primary path.
  const [advancedCharPickOpen, setAdvancedCharPickOpen] = useState(false);
  const [startingPromptRP, setStartingPromptRP] = useState(false);
  const [savedSessions, setSavedSessions] = useState([]);
  // True when the last stories fetch failed. Lets the RP list say "couldn't
  // load" instead of showing an empty list, which reads as "your stories are
  // gone" — the reported symptom.
  const [storiesLoadFailed, setStoriesLoadFailed] = useState(false);
  // First successful load finished — until then the home list shows a loading
  // line, never "No stories yet" (a slow query looked like lost stories).
  const [storiesLoaded, setStoriesLoaded] = useState(false);
  const [jumpStories, setJumpStories] = useState([]); // Stories user joined but doesn't own
  const [storySharedMap, setStorySharedMap] = useState({}); // storyId → shared_stories row (or null)
  const [invitePanelStoryId, setInvitePanelStoryId] = useState(null); // story invite panel open for this story
  const [storyInvitesMap, setStoryInvitesMap] = useState({}); // storyId → invite rows
  const [inviteUserSearch, setInviteUserSearch] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [invitedStories, setInvitedStories] = useState([]); // stories user was invited to
  const [currentSessionName, setCurrentSessionName] = useState("");
  const [renamingStoryId, setRenamingStoryId] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [branchingStory, setBranchingStory] = useState(null); // story object when picking branch point
  const [viewingChar, setViewingChar] = useState(null);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("sixguys_apiKey") || "");
  const [aiProvider, setAiProvider] = useState(() => localStorage.getItem("sixguys_aiProvider") || "none");
  // Optional model override for OpenAI-compatible personal providers
  // (OpenRouter / DeepSeek / OpenAI). Blank = the provider's sensible default.
  const [aiModel, setAiModel] = useState(() => localStorage.getItem("sixguys_aiModel") || "");
  // Cruz's Movie Night on the doorstep: the next Pack House night, if any.
  // Polled gently — the card itself is a one-liner and the channel holds
  // the live state; this only has to be right when you open the app.
  const [movieNight, setMovieNight] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const load = async () => {
      // No channel, no doorstep card. Without this the query would run
      // `.eq('channel_id', null)` on every load and every five minutes after.
      if (!MOVIE_NIGHT_CHANNEL_ID) { setMovieNight(null); return; }
      try {
        const { data } = await supabase.from('movie_nights')
          .select('id, channel_id, night_at, picks, locked_idx, status, video')
          .eq('channel_id', MOVIE_NIGHT_CHANNEL_ID)
          .in('status', ['picks', 'locked', 'live'])
          .order('night_at', { ascending: true }).limit(1).maybeSingle();
        if (alive) setMovieNight(data || null);
      } catch { /* the doorstep stays quiet */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [user?.id]);
  // On mount: load personal-key STATUS from the BYOK RPC. The actual key
  // value lives vault-encrypted server-side; ai-proxy decrypts it via service
  // role. Client state holds a hint sentinel ("sk-ant-...XXXX") so existing
  // truthiness checks (`if (apiKey)`) keep working without exposing the key.
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      await safeRefreshSession().catch(() => {});
      try {
        const { data, error } = await supabase.rpc('get_my_ai_status');
        if (error) { console.warn('[apiKey] status RPC error:', error.message); return; }
        if (data?.has_key && data?.enabled) {
          setApiKey(data.hint || 'sk-...****');
          // Reflect the stored provider (server is authoritative). anthropic
          // maps to the existing 'claude-haiku' UI id; the rest pass through.
          const prov = data.provider || 'anthropic';
          const uiProvider = prov === 'anthropic' ? 'claude-haiku' : prov;
          setAiProvider(uiProvider);
          localStorage.setItem("sixguys_aiProvider", uiProvider);
          if (data.model) { setAiModel(data.model); localStorage.setItem("sixguys_aiModel", data.model); }
        } else {
          setApiKey("");
        }
      } catch (e) { console.warn('[apiKey] status fetch threw:', e); }
    })();
  }, [user?.id]);
  // showReaderSettings / showStoryPrefs are gone (queue item 17): reading
  // prefs and story prefs are edited in the ONE settings menu (Appearance /
  // Story tabs); this view syncs via the mfml-*-changed window events above.
  // Story-linking modal (Phase 1 — May 20). Surfaced from inside
  // SettingsModal when an active story is loaded, so it's discoverable
  // from any view without polluting the chat header. Renders once at
  // the end of the top-level return.
  const [showStoryLinks, setShowStoryLinks] = useState(false);
  const [storyPreferences, setStoryPreferences] = useState({});
  const [routeDescription, setRouteDescription] = useState('');
  const [backstorySummary, setBackstorySummary] = useState(null);
  const [readerPrefs, setReaderPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem("sixguys_reader_prefs");
      return saved ? JSON.parse(saved) : { fontFamily: "serif", fontSize: 15, lineHeight: 1.6, theme: "dark" };
    } catch { return { fontFamily: "serif", fontSize: 15, lineHeight: 1.6, theme: "dark" }; }
  });
  const [isTyping, setIsTyping] = useState(false);
  const [typingCharName, setTypingCharName] = useState("");
  const [aiPaused, setAiPaused] = useState(false); // Pause AI responses so users can RP together
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  // Dead code #20: selectedGen removed — was never set to anything other than null
  const [charSearch, setCharSearch] = useState("");
  const charSearchRef = useRef(null);
  const [sceneSearch, setSceneSearch] = useState("");
  // charTags purged (queue item 16, 2026-08-04): per-story relationship
  // kinds live in character_dynamics rows (scope_type='story'), edited via
  // the Relationships panel. rp_defaults now persists only playingAs; old
  // blobs may still carry a charTags field, which loaders simply ignore.
  useEffect(() => {
    if (!user?.id) return;
    if (!playingAs) return;
    const t = setTimeout(() => {
      const d = { playingAs: playingAs || null };
      try { localStorage.setItem('mfml_rp_defaults', JSON.stringify(d)); } catch {}
      supabase.from('profiles').update({ rp_defaults: d }).eq('id', user.id).then(null, () => {});
    }, 800);
    return () => clearTimeout(t);
  }, [user?.id, playingAs]);
  // Dead code #19: showOtherInput/otherName removed — never read or set anywhere
  const [customChars, setCustomChars] = useState([]);
  const [showCreateChar, setShowCreateChar] = useState(false);
  const [newChar, setNewChar] = useState({ name: "", species: "", personality: "", speechPattern: "", backstory: "" });
  const [activeStoryId, setActiveStoryId] = useState(null);
  const [storyOwnerId, setStoryOwnerId] = useState(null); // Track story owner for push notifications
  const [showRpLinkPanel, setShowRpLinkPanel] = useState(false); // 🔗 link this RP to other DMs/RPs
  const [mainTab, setMainTab] = useState(() => {
    // Parse initial URL for push notification deep links (e.g., /dm, /phone)
    try {
      const path = window.location.pathname;
      if (path === '/dm') { console.log('[PushNav] Startup deep link → dm'); return 'dm'; }
      if (path === '/phone' || path === '/phones') { console.log('[PushNav] Startup deep link → phones'); return 'phones'; }
      if (path === '/channels') { console.log('[PushNav] Startup deep link → channels'); return 'channels'; }
      if (path === '/community') { console.log('[PushNav] Startup deep link → community'); return 'community'; }
    } catch {}
    // No deep link: come back to where this tab was (a browser discard or a
    // reload of the same tab). A fresh tab has no record and lands on RP.
    try {
      const lv = readLastView();
      if (lv?.tab && ['rp', 'channels', 'stories', 'dm', 'search', 'community', 'phones'].includes(lv.tab)) {
        console.log(`[Resume] restoring last view → ${lv.tab}${wasDiscarded() ? ' (the browser discarded this tab)' : ''}`);
        return lv.tab;
      }
    } catch { /* fall through */ }
    return 'rp';
  }); // "rp" | "channels" | "stories" | "dm" | "search" | "admin"
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showCharacterEditor, setShowCharacterEditor] = useState(false);
  // Which character the WORLD interface asked to edit (deep-link into the editor).
  const [worldCharEditId, setWorldCharEditId] = useState(null);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [profileSettingsInitialTab, setProfileSettingsInitialTab] = useState("profile");
  // Deep-link scope for the ONE settings menu (queue item 17): which reader
  // surface / channel / conversation / story the entry point was looking at.
  const [profileSettingsScope, setProfileSettingsScope] = useState(null);

  // Child views (DMView, ChannelView) open the one settings menu through this
  // event instead of owning their own settings panels.
  useEffect(() => {
    const onOpenSettings = (e) => {
      const { tab, scope } = e.detail || {};
      setProfileSettingsInitialTab(tab || "profile");
      setProfileSettingsScope(scope || null);
      setShowProfileSettings(true);
    };
    window.addEventListener("mfml-open-settings", onOpenSettings);
    return () => window.removeEventListener("mfml-open-settings", onOpenSettings);
  }, []);

  // The menu edits reader prefs / story prefs that THIS view renders with —
  // sync the local copies live when it saves them.
  useEffect(() => {
    const onReaderPrefs = (e) => {
      if (e.detail?.surface === "rp" && e.detail.prefs) setReaderPrefs(e.detail.prefs);
    };
    window.addEventListener("mfml-reader-prefs-changed", onReaderPrefs);
    return () => window.removeEventListener("mfml-reader-prefs-changed", onReaderPrefs);
  }, []);
  useEffect(() => {
    const onStoryPrefs = (e) => {
      const d = e.detail || {};
      if (d.type === "story" && d.id === activeStoryId && d.prefs) setStoryPreferences(d.prefs);
    };
    window.addEventListener("mfml-story-prefs-changed", onStoryPrefs);
    return () => window.removeEventListener("mfml-story-prefs-changed", onStoryPrefs);
  }, [activeStoryId]);
  const [musicPlayerVisible, setMusicPlayerVisible] = useState(() => {
    try { return localStorage.getItem("mfml_music_visible") === "true"; } catch { return false; }
  });
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [showFateCard, setShowFateCard] = useState(false);
  const [showCrosstalk, setShowCrosstalk] = useState(false); // "characters texting each other" panel
  const [fateDrawnToday, setFateDrawnToday] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('sixguys_daily_fate') || '{}');
      const today = new Date().toISOString().split('T')[0];
      return !!saved[today];
    } catch { return false; }
  });
  // Cross-device fate card sync — check Supabase after login in case another device drew today
  useEffect(() => {
    if (!user?.id || fateDrawnToday) return;
    const today = new Date().toISOString().split('T')[0];
    supabase.from('profiles').select('fate_last_drawn').eq('id', user.id).single()
      .then(({ data }) => { if (data?.fate_last_drawn === today) setFateDrawnToday(true); })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [incomingCallChar, setIncomingCallChar] = useState(null);   // character object for ring overlay
  const [phoneInitialCharKey, setPhoneInitialCharKey] = useState(null); // pre-dial key for PopupPhone
  const [phoneLinkedConvoId, setPhoneLinkedConvoId] = useState(null); // DM conversation to link for context
  const [onlineProfiles, setOnlineProfiles] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  // Friend system — real-time friendships data
  const { friends: friendsList, incoming: friendIncoming, outgoing: friendOutgoing, refresh: refreshFriends } = useFriendships(user?.id);
  const [viewingProfile, setViewingProfile] = useState(null); // profile object to show in UserProfileModal
  // v421 (Haley, 2026-10-19: "click profile pics to see the users bio and
  // profile pic"): any surface hands a user id here; the card gets the live
  // row (bio, status, frame, presence), not whatever the caller cached.
  const openUserProfile = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url, accent_color, bio, status_message, is_online, last_seen, hide_online_status, profile_frame, level, xp, created_at')
        .eq('id', userId)
        .maybeSingle();
      if (data) setViewingProfile(data);
    } catch (e) { console.warn('[Profile] could not load:', e?.message || e); }
  }, []);
  const [showIOSBanner, setShowIOSBanner] = useState(() => {
    // Show iOS install banner if: on iOS Safari, NOT already in PWA mode, and not previously dismissed
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.navigator.standalone === true;
    const dismissed = localStorage.getItem("sixguys_ios_banner_dismissed");
    return isIOS && !isStandalone && !dismissed;
  });
  const [spectatingStory, setSpectatingStory] = useState(null);
  const [spectateMessages, setSpectateMessages] = useState([]);
  // Pagination + scroll-anchor state for the spectate view (long stories take
  // forever to load all at once and dump the user at the top). We load only
  // the latest N on open, scroll to bottom, and load older batches when the
  // user scrolls to the top.
  const [spectateHasMore, setSpectateHasMore] = useState(false);
  const [spectateLoadingOlder, setSpectateLoadingOlder] = useState(false);
  const [spectateInitialLoaded, setSpectateInitialLoaded] = useState(false);
  const spectateScrollRef = useRef(null);
  const spectateAtBottomRef = useRef(true); // tracks whether user is pinned to bottom (for realtime auto-scroll)
  const SPECTATE_PAGE_SIZE = 60;
  const [feedbackContexts, setFeedbackContexts] = useState({});
  // UNDO REGENERATE. Haley: "when we regenerate a message from ai the old
  // message that we regenerate from should be completely replaced but also
  // have an undo regenerate button as a failsafe."
  //
  // Regenerate already replaces completely — it deletes the old row and
  // everything after it from story_messages BEFORE generating, so the rejected
  // text cannot come back on the next merge. That is exactly what makes an
  // undo necessary: until now the previous reply was unrecoverable the instant
  // you asked for a new one, and a regenerate you did not like left you with
  // nothing to go back to.
  //
  // Held in memory only, and only for the most recent regenerate. It is a
  // failsafe for "that was worse, give me the old one back", not a history.
  // { storyId, msgId (the REPLACEMENT's id), previous (the whole old message) }
  const [lastRegen, setLastRegen] = useState(null);
  const [detectedNewChars, setDetectedNewChars] = useState([]); // AI-introduced characters pending save
  const [showNewCharPrompt, setShowNewCharPrompt] = useState(null); // Character being prompted for save
  const [editingMsgIdx, setEditingMsgIdx] = useState(null);
  // Surfaced above the composer when a resend attempt fails again.
  const [rpSendError, setRpSendError] = useState(null);
  // Visibility chosen BEFORE the story exists. DEFAULT PRIVATE — restored
  // 2026-07-30 after one hour of public-default put two of Haley's stories
  // in Community Stories "without permission" (her words, same day she asked
  // for the public default). The v137 rationale proved itself live: a story
  // must never become community-visible because nobody registered a toggle.
  // The clear two-sided toggle stays; sharing is one deliberate tap.
  const [newRpPublic, setNewRpPublic] = useState(false);
  // Today's placeholder suggestion for the new-RP box (src/dailyRpIdea.js).
  // null = static examples. Generated at most once per user per local day.
  const [dailyIdea, setDailyIdea] = useState(null);
  const dailyIdeaAttemptedRef = useRef(false);
  const [editingMsgText, setEditingMsgText] = useState('');
  const [showPinPanel, setShowPinPanel] = useState(false);
  const [showStoryLore, setShowStoryLore] = useState(false);   // v422: this story's lore (ThreadLorePanel)
  const [pinPopoverMsgIdx, setPinPopoverMsgIdx] = useState(null);
  // Story message actions on touch: hidden by default, DOUBLE-TAP or
  // LONG-PRESS toggles them (2026-07-27 — the v92 always-visible CSS sat on
  // top of the prose with no way to dismiss). Desktop keeps hover.
  const [rpActionsIdx, setRpActionsIdx] = useState(null);
  const rpTapRef = useRef(null);
  const rpPressTimerRef = useRef(null);
  const rpPressStartRef = useRef(null);
  const rpBubbleTouchHandlers = (idx, msgId) => ({
    onPointerDown: (e) => {
      if (e.pointerType === 'mouse') return;
      clearTimeout(rpPressTimerRef.current);
      rpPressStartRef.current = { x: e.clientX, y: e.clientY };
      rpPressTimerRef.current = setTimeout(() => {
        setRpActionsIdx(cur => cur === idx ? null : idx);
        if (navigator.vibrate) navigator.vibrate(30);
      }, 400);
    },
    onPointerUp: (e) => {
      clearTimeout(rpPressTimerRef.current);
      // Haley (2026-07-30): actions are ON-DEMAND everywhere — desktop
      // double-CLICK now toggles them exactly like mobile double-tap (hover
      // no longer reveals). Mouse long-press stays disabled (drag conflicts).
      const cur = { id: msgId ?? idx, x: e.clientX, y: e.clientY, t: Date.now() };
      if (isDoubleTap(rpTapRef.current, cur)) {
        setRpActionsIdx(v => v === idx ? null : idx);
        if (navigator.vibrate) navigator.vibrate(20);
        rpTapRef.current = null;
      } else {
        rpTapRef.current = cur;
      }
    },
    onPointerCancel: () => clearTimeout(rpPressTimerRef.current),
    onPointerLeave: () => clearTimeout(rpPressTimerRef.current),
    onPointerMove: (e) => {
      const s0 = rpPressStartRef.current;
      if (!s0) return;
      if (Math.abs(e.clientX - s0.x) > 10 || Math.abs(e.clientY - s0.y) > 10) clearTimeout(rpPressTimerRef.current);
    },
  });
  // ─── RP Message Highlights ───────────────────────────────────────────────────
  // Stored directly on the message object (msg.highlighted = true) so it persists
  // automatically with every blob save. No extra state needed — just a toggle helper.
  const [rpHighlightBanner, setRpHighlightBanner] = useState(null); // { idx, label }
  const rpHighlightBannerTimerRef = useRef(null);
  const toggleRPHighlight = (idx) => {
    setMessages(prev => {
      const next = prev.map((m, i) => i === idx ? { ...m, highlighted: !m.highlighted } : m);
      const msg = next[idx];
      if (msg.highlighted) {
        const preview = (msg.text || '').substring(0, 60) + ((msg.text || '').length > 60 ? '…' : '');
        clearTimeout(rpHighlightBannerTimerRef.current);
        setRpHighlightBanner({ idx, label: `${msg.name || 'Message'} — "${preview}"` });
        rpHighlightBannerTimerRef.current = setTimeout(() => setRpHighlightBanner(null), 12000);
      } else {
        setRpHighlightBanner(null);
      }
      return next;
    });
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveStory(), 1500);
  };
  const chatEndRef = useRef(null);
  const rpChatContainerRef = useRef(null);
  useBottomAnchor(rpChatContainerRef); // the end of the post you are answering stays in view while the composer grows (2026-10-23)
  const [rpIsAtBottom, setRpIsAtBottom] = useState(true);
  // RP pagination: heavy stories with hundreds of messages were locking up
  // the UI. We now load only the latest 100 messages on story switch and
  // load older pages when the user scrolls near the top. The AI prompt
  // still has full memory of older events via storyMemory's extracted arc
  // — see buildRPTimeline + getCharacterStoryArc in the prompt builder.
  const [hasMoreRpMessages, setHasMoreRpMessages] = useState(false);
  const [loadingOlderRp, setLoadingOlderRp] = useState(false);
  const rpScrollAnchorRef = useRef(null); // { scrollTop, scrollHeight } before prepend
  // Full embedded-blob backup so paginated load-older can pull from the blob
  // for old stories whose history predates the story_messages table. Without
  // this, scrolling up only reaches the table-backed messages and the user
  // gets stuck after 1-2 pages.
  const fullEmbeddedMsgsRef = useRef([]);
  const allowEmptySaveRef = useRef(false); // armed ONLY by an explicit message delete
  // Same identity rule saveStory's reunite uses: row id, else speaker+text.
  const storyMsgFp = (m) => (m?.id ? `id:${m.id}` : `t:${m?.charId || m?.name || ''}|${String(m?.text || '').slice(0, 80)}`);
  // MDI-3: fullEmbeddedMsgsRef was set once at load and never pruned, so the
  // reunite in saveStory resurrected every deleted line and regen-discarded
  // branch back into the blob ~600ms after the player removed it. Every
  // delete/regen/resend must drop its victims from the ref too.
  const pruneFullEmbeddedRef = (victims) => {
    const list = fullEmbeddedMsgsRef.current;
    if (!Array.isArray(list) || !list.length || !victims?.length) return;
    const gone = new Set(victims.map(storyMsgFp));
    fullEmbeddedMsgsRef.current = list.filter(m => !gone.has(storyMsgFp(m)));
  };
  const RP_PAGE_SIZE = 100;
  const isSendingRef = useRef(false);
  const viewRef = useRef(view); // tracks current view to detect stale API calls
  const saveTimerRef = useRef(null); // debounced auto-save timer
  const messagesRef = useRef(messages); // always-current messages for beforeunload save
  const activeStoryIdRef = useRef(activeStoryId);
  const lastShownRef = useRef(''); // what the model was shown on the last turn (name guard, v410)
  // A reply becomes memory only when the player answers it (src/memoryGate.js, v409).
  const memoryGate = useRef(createMemoryGate()).current;
  // ── RP SETTINGS AUTOSAVE (live 2026-07-30, Haley: "character choices are
  // not being saved") ─────────────────────────────────────────────────────
  // Cast / played-character / relationship-tag / narrator changes used to
  // reach the story blob only when the NEXT message save happened to fire —
  // change a setting and leave (or crash, or switch stories) and the choice
  // silently reverted. Persist settings on their own debounce. The epoch ref
  // suppresses the echo save right after a story load restores these states,
  // and saveStory is reached through a ref kept current after its definition
  // (it is declared far below; a direct dep would re-arm every render).
  const settingsLoadEpochRef = useRef(0);
  const saveStoryFnRef = useRef(null);
  // Route-gated identity latches for the ACTIVE story (identityGates.js).
  // Seeded from story.data.routeFlags on load, persisted back inside
  // storyData on every save — one-way, story-scoped, like scene flags.
  const storyRouteFlagsRef = useRef({});
  // Re-rolling a prompt-driven opening used to INSERT a brand-new story per
  // attempt — three generations in four minutes minted identical-named twins
  // (live 2026-07-30). While the latest prompt-story has no player message
  // yet, regenerating with the same prompt replaces it instead. Any send
  // clears the ref.
  const lastPromptStoryRef = useRef(null);
  useEffect(() => {
    if (!user?.id || !activeStoryId) return;
    if (Date.now() - settingsLoadEpochRef.current < 1500) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveStoryFnRef.current?.(), 1200);
  }, [user?.id, activeStoryId, playingAs, sceneChars, narratorMode, sceneType]);
  const [showConfirmModal, setShowConfirmModal] = useState(null); // custom confirm modal state
  const [showConfetti, setShowConfetti] = useState(false); // celebration confetti
  const [toastMsg, setToastMsg] = useState(null); // toast notifications (legacy)
  // Dead code #21: toastContainerRef removed — addToast() was never called, legacy toastMsg used instead

  // ─── INCOGNITO MODE ─────────────────────────────────────────────────────────
  const [_showIncognito, _setShowIncognito] = useState(false);

  // ─── STORY INTEL PANEL ─────────────────────────────────────────────────────
  const [storyIntel, setStoryIntel] = useState(null); // { timeline, summary, characters, currentScene, plotIdeas, mood, themes }
  const [storyIntelOpen, setStoryIntelOpen] = useState(false);
  const [storyIntelLoading, setStoryIntelLoading] = useState(false);

  // ─── STORY PRESENCE (who has the story open — always visible) ──────────────
  const [storyPresence, setStoryPresence] = useState([]); // [{ userId, userName, avatarUrl, charName, charAvatar }]
  const storyPresenceChannelRef = useRef(null);

  // ─── SCENE RECAP & SUGGESTIONS (for jump-in guests) ──────────────────────────
  const [sceneRecap, setSceneRecap] = useState(null); // { recap, suggestions: [string, string, string] }
  const [sceneRecapLoading, setSceneRecapLoading] = useState(false);

  // ─── STORY TYPING INDICATOR (broadcast to other players) ────────────────────
  const [remoteTyping, setRemoteTyping] = useState(null); // { userName, charName, timestamp }
  const storyTypingChannelRef = useRef(null);
  const typingBroadcastTimerRef = useRef(null);

  // ─── LIVE NOTIFICATION FEED STATE ──────────────────────────────────────────
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [notifMinimized, _setNotifMinimized] = useState(false);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const notifIdCounter = useRef(0);
  const toastRef = useRef(null); // upper-right message-preview toasts
  const liveNotifChannelsRef = useRef([]);
  // Tracks which specific channel/conversation is currently being viewed
  // so we only suppress bell notifications for THAT item, not the whole tab.
  // Shape: { type: 'channel'|'dm'|'user_dm', id: string } or null
  const activeViewRef = useRef(null);

  // ─── NOTIFICATION PREFERENCES STATE ───────────────────────────────────────
  const [notifPrefs, setNotifPrefs] = useState({ ...DEFAULT_PREFS });
  const notifPrefsRef = useRef({ ...DEFAULT_PREFS }); // always-current ref for async callbacks
  const onlineProfilesRef = useRef([]); // always-current ref so realtime callbacks read profiles without re-subscribing
  const allCharsRef = useRef([]); // same pattern for base+custom characters
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [dmStartNew, setDmStartNew] = useState(false);
  const [dmTargetUserId, setDmTargetUserId] = useState(null); // For user-to-user DMs from the user list
  // Track which nav tabs have unread activity (red dot indicators)
  const [unreadTabs, setUnreadTabs] = useState({ dm: false, channels: false, community: false, rp: false, stories: false, phones: false });
  // ─── STORY CHANNEL NOTIFICATION STATE ─────────────────────────────────────
  const [storyChannelUnread, setStoryChannelUnread] = useState(0);
  const storyChannelIdsRef = useRef(new Set()); // IDs of channels in "STORIES" category
  const [showStoryNotifPanel, setShowStoryNotifPanel] = useState(false);
  const [storyNotifMessages, setStoryNotifMessages] = useState([]); // recent story channel messages for the panel
  const [jumpToChannelId, setJumpToChannelId] = useState(() => {
    // Same restore as mainTab: the channel this tab had open, if any.
    try { const lv = readLastView(); return lv?.tab === 'channels' && lv?.channelId ? lv.channelId : null; } catch { return null; }
  }); // channel to auto-select when switching to channels tab
  useEffect(() => { saveLastView({ tab: mainTab }); }, [mainTab]);
  const mainTabRef = useRef(mainTab);
  useEffect(() => {
    mainTabRef.current = mainTab;
    // Clear red dot when user switches to a tab
    setUnreadTabs(prev => prev[mainTab] ? { ...prev, [mainTab]: false } : prev);
    scheduleBadgeSync(400);
    // Clear specific-view tracking when switching top-level tabs
    activeViewRef.current = null;
  }, [mainTab]);

  // ─── PUSH NOTIFICATION DEEP LINK LISTENER ─────────────────────────────────
  // When the SW focuses an existing window after a notification tap, it posts
  // { type: "PUSH_NAV", url: "/dm" }. We route to the right tab here.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    // Detect call deeplink in the initial URL (cold-tap from a closed app)
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const callKey = sp.get("call") || (window.location.pathname.startsWith("/call/") ? window.location.pathname.slice(6).split("/")[0] : null);
      const convId = sp.get("conv");
      if (callKey) {
        const char = CHARACTERS.find(c => c.id === callKey) || { id: callKey, name: callKey };
        handleIncomingCall(char, { reason: convId ? 'random' : 'milestone', conversationId: convId });
        setMainTab("phones");
      }
    } catch {}
    // Clean up deep-link URL on startup so browser history stays at /.
    // Skip this in incognito-popup mode — IncognitoStandalone deliberately
    // sets a disguised path (/dashboard, /portal, /faculty-resources,
    // /workspace) and wiping it would defeat the cover on the address bar.
    try {
      if (!IS_INCOGNITO_POPUP && (
        (window.location.pathname !== "/" && window.location.pathname !== "") ||
        window.location.search
      )) {
        console.log("[PushNav] Cleaning up deep link URL:", window.location.pathname + window.location.search, "→ /");
        window.history.replaceState(null, "", "/");
      }
    } catch {}
    const handleSWMessage = (event) => {
      if (event.data?.type === "PUSH_NAV") {
        const url = event.data.url || "/";
        console.log("[PushNav] Received SW message, routing to:", url);
        // Detect call deeplink: "/?call=<charKey>" or "/call/<charKey>" (with optional &conv=<id>)
        try {
          const u = new URL(url, window.location.origin);
          const callKey = u.searchParams.get("call") || (u.pathname.startsWith("/call/") ? u.pathname.slice(6).split("/")[0] : null);
          const convId = u.searchParams.get("conv") || null;
          if (callKey) {
            const char = CHARACTERS.find(c => c.id === callKey) || { id: callKey, name: callKey };
            handleIncomingCall(char, { reason: convId ? 'random' : 'milestone', conversationId: convId });
            setMainTab("phones");
            return;
          }
        } catch {}
        if (url === "/dm") { setMainTab("dm"); setView("home"); }
        else if (url === "/phone" || url === "/phones") { setMainTab("phones"); }
        else if (url === "/channels") { setMainTab("channels"); }
        else { setMainTab("rp"); setView("home"); }
      }
    };
    navigator.serviceWorker.addEventListener("message", handleSWMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleSWMessage);
  }, []);

  // Update a notification preference and persist to Supabase
  // SW-3: these were optimistic with the save result dropped — the crons
  // (check-unread, send-push) gate on the DB row, so a failed save meant
  // pushes all night while the UI said muted AND the local ref suppressed
  // in-app toasts, making the mismatch look impossible. On failure: roll
  // back the optimistic state and say so.
  const updateNotifPref = useCallback(async (updates) => {
    if (!user?.id) return;
    const before = notifPrefsRef.current;
    const merged = { ...before, ...updates };
    setNotifPrefs(merged);
    notifPrefsRef.current = merged;
    const ok = await saveNotifPrefs(user.id, updates);
    if (!ok) {
      setNotifPrefs(before);
      notifPrefsRef.current = before;
      toastRef.current?.addToast?.({ message: "Notification setting didn't save — try again", type: 'dm' });
    }
  }, [user?.id]);

  // Set snooze duration (hours) — 0 or null clears snooze
  const handleSnooze = useCallback(async (hours) => {
    if (!user?.id) return;
    const snoozeUntil = hours && hours > 0
      ? new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()
      : null;
    const before = notifPrefsRef.current;
    const merged = { ...before, snooze_until: snoozeUntil };
    setNotifPrefs(merged);
    notifPrefsRef.current = merged;
    const ok = await setSnooze(user.id, hours);
    if (!ok) {
      setNotifPrefs(before);
      notifPrefsRef.current = before;
      toastRef.current?.addToast?.({ message: "Snooze didn't save — notifications are still on. Try again", type: 'dm' });
    }
    setShowSnoozeMenu(false);
  }, [user?.id]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RELIABILITY STATE — connection, save status, retry queue
  // ═══════════════════════════════════════════════════════════════════════════
  const [connectionStatus, setConnectionStatus] = useState('connected'); // 'connected' | 'reconnecting' | 'offline'
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const retryQueueRef = useRef([]); // [{table, payload, retries}]
  const accessTokenRef = useRef(null); // cached access token for beforeunload
  const isSavingRef = useRef(false); // mutex to prevent concurrent saves

  // Keep refs current
  useEffect(() => {
    messagesRef.current = messages;
    // Instant localStorage backup on every message change — prevents message loss on
    // hard refresh, deploy, or crash. The DB save is debounced (1.5s), but this is instant.
    if (messages.length > 0 && user && viewRef.current === 'chat') {
      try {
        const storyData = { messages, sceneChars, sceneType, playingAs, currentPlayer, customChars, aiProvider, routeFlags: storyRouteFlagsRef.current, ...(backstorySummary ? { backstorySummary } : {}) };
        const name = currentSessionName || `${currentPlayer || "RP"} - ${new Date().toLocaleDateString()}`;
        localStorage.setItem('sixguys_backup_story', JSON.stringify({
          storyId: activeStoryIdRef.current, name, data: storyData,
          savedAt: new Date().toISOString(), userId: user.id,
        }));
      } catch {}
    }
    // Deps include backstorySummary so a backstory change without a new message
    // still triggers a fresh backup snapshot (was missing → backup stale on
    // crash if user only added a backstory).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, backstorySummary]);
  useEffect(() => { activeStoryIdRef.current = activeStoryId; }, [activeStoryId]);

  // Clean up old drafts on mount
  useEffect(() => { cleanupOldDrafts(); }, []);
  // v425: drafts follow the signed-in person between devices
  useEffect(() => { setDraftUser(user?.id || null); }, [user?.id]);

  // Preload modal chunks so they're ready before the user clicks (eliminates flash)
  useEffect(() => {
    const t = setTimeout(() => {
      import("./ProfileSettings.jsx").catch(() => {});
      import("./DailyFateCard.jsx").catch(() => {});
    }, 2000); // Delay 2s so it doesn't compete with initial load
    return () => clearTimeout(t);
  }, []);

  // Draft load on story-switch is now handled inside <RPInputBar />.

  // ─── Story presence + typing channel (combined) ─────────────────────────────
  // Uses Supabase Realtime Presence for always-on "who's here" tracking,
  // plus Broadcast for typing indicators. Both on the same channel.
  useEffect(() => {
    // Clean up previous channels
    if (storyTypingChannelRef.current) {
      supabase.removeChannel(storyTypingChannelRef.current);
      storyTypingChannelRef.current = null;
    }
    if (storyPresenceChannelRef.current) {
      storyPresenceChannelRef.current.untrack();
      supabase.removeChannel(storyPresenceChannelRef.current);
      storyPresenceChannelRef.current = null;
    }
    setRemoteTyping(null);
    setStoryPresence([]);

    if (!activeStoryId || !user) return;

    // Combined channel: presence tracking + typing broadcast
    const channel = supabase.channel(`story-room-${activeStoryId}`)
      // ── Presence: track who has the story open ──
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const presentUsers = [];
        Object.values(state).forEach(presences => {
          presences.forEach(p => {
            if (p.userId !== user.id) {
              presentUsers.push({
                userId: p.userId,
                userName: p.userName,
                avatarUrl: p.avatarUrl,
                charName: p.charName,
                charAvatar: p.charAvatar,
              });
            }
          });
        });
        setStoryPresence(presentUsers);
      })
      // ── Broadcast: typing indicators ──
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (payload.userId === user.id) return;
        setRemoteTyping({ userName: payload.userName, charName: payload.charName, timestamp: Date.now() });
        setTimeout(() => {
          setRemoteTyping(prev => prev && (Date.now() - prev.timestamp > 3500) ? null : prev);
        }, 4000);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Look up character directly — currentChar is defined later in the render
          // pass and would cause a TDZ error if referenced here
          const chars = [...CHARACTERS, ...customChars].filter(Boolean);
          const char = chars.find(c => c?.id === playingAs);
          await channel.track({
            userId: user.id,
            userName: userProfile?.display_name || 'Someone',
            avatarUrl: userProfile?.avatar_url || null,
            charName: char?.name || 'Selecting...',
            charAvatar: char?.avatar || '👤',
            online_at: new Date().toISOString(),
          });
        }
      });

    storyTypingChannelRef.current = channel;
    storyPresenceChannelRef.current = channel; // same channel for both

    return () => {
      channel.untrack();
      supabase.removeChannel(channel);
      storyTypingChannelRef.current = null;
      storyPresenceChannelRef.current = null;
    };
  }, [activeStoryId, user?.id]);

  // Update presence state when character changes (so others see your new char)
  // NOTE: Cannot use currentChar in deps — it's defined later in the render pass
  // and would cause a TDZ error. Use playingAs (state) instead.
  const playingAsRef = useRef(playingAs);
  useEffect(() => { playingAsRef.current = playingAs; }, [playingAs]);
  useEffect(() => {
    if (!storyPresenceChannelRef.current || !activeStoryId || !user) return;
    // Look up character from CHARACTERS + customChars directly
    const chars = [...CHARACTERS, ...customChars].filter(Boolean);
    const char = chars.find(c => c?.id === playingAs);
    storyPresenceChannelRef.current.track({
      userId: user.id,
      userName: userProfile?.display_name || 'Someone',
      avatarUrl: userProfile?.avatar_url || null,
      charName: char?.name || 'Selecting...',
      charAvatar: char?.avatar || '👤',
      online_at: new Date().toISOString(),
    });
  }, [playingAs]);

  // ─── Realtime sync: story_messages from other players in active chat ────────
  // This is the FIX for Bug #1: without this, the owner can't see guest messages
  // in real-time because the old subscription only updated spectateMessages.
  useEffect(() => {
    if (!activeStoryId || !user) return;

    const channel = supabase.channel(`story-live-msgs-${activeStoryId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'story_messages',
        filter: `story_id=eq.${activeStoryId}`,
      }, (payload) => {
        const m = payload.new;
        if (!m || m.user_id === user.id) return; // Ignore own messages (already in state)
        setMessages(prev => {
          // Dedup by ROW ID (authoritative). Text+time was dropping legitimately
          // repeated lines — a character saying "Stop." or "What?" twice within
          // 5s lost the second one. Keep the text+time check ONLY for legacy
          // local rows that carry a client-side id and never got a DB id.
          if (prev.some(msg => msg.id === m.id)) return prev;
          const isLegacyDupe = prev.some(msg =>
            typeof msg.id === 'string' && !msg.id.includes('-') &&
            msg.text === m.text && msg.charId === m.char_id &&
            Math.abs(new Date(msg.createdAt || 0).getTime() - new Date(m.created_at).getTime()) < 5000
          );
          if (isLegacyDupe) return prev;
          return [...prev, {
            id: m.id,
            charId: m.char_id,
            name: m.char_name || 'Unknown',
            text: m.text,
            timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
            createdAt: m.created_at,
            isPlayer: m.is_player || false,
            color: m.color,
            avatar: m.avatar,
          }];
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [activeStoryId, user?.id]);

  // Track view changes and reset typing when navigating away from chat
  useEffect(() => {
    viewRef.current = view;
    if (view !== 'chat') {
      setIsTyping(false);
      setTypingCharName('');
    }
  }, [view]);

  // Mirror apiKey/aiProvider into localStorage. The DB write (cross-device sync)
  // lives inside SettingsModal so its UI can show save status; here we just keep
  // localStorage in sync so other parts of the app see the latest value.
  useEffect(() => { localStorage.setItem("sixguys_apiKey", apiKey); }, [apiKey]);
  useEffect(() => { localStorage.setItem("sixguys_aiProvider", aiProvider); }, [aiProvider]);
  useEffect(() => { localStorage.setItem("sixguys_reader_prefs", JSON.stringify(readerPrefs)); }, [readerPrefs]);

  const READER_FONTS = {
    serif: { label: "Serif (Cormorant)", value: "'Cormorant Garamond', 'Georgia', serif" },
    lora: { label: "Lora", value: "'Lora', 'Georgia', serif" },
    merriweather: { label: "Merriweather", value: "'Merriweather', 'Georgia', serif" },
    baskerville: { label: "Baskerville", value: "'Libre Baskerville', 'Georgia', serif" },
    garamond: { label: "EB Garamond", value: "'EB Garamond', 'Georgia', serif" },
    literata: { label: "Literata", value: "'Literata', 'Georgia', serif" },
    sans: { label: "Sans-serif (Inter)", value: "'Inter', 'Segoe UI', system-ui, sans-serif" },
    nunito: { label: "Nunito", value: "'Nunito', 'Segoe UI', system-ui, sans-serif" },
    sourcesans: { label: "Source Sans", value: "'Source Sans 3', 'Segoe UI', system-ui, sans-serif" },
    mono: { label: "Monospace", value: "'JetBrains Mono', 'Fira Code', monospace" },
    dyslexic: { label: "Dyslexia-friendly", value: "'OpenDyslexic', 'Comic Sans MS', sans-serif" },
  };
  // Each row paints a LITERAL background, so its text has to be a literal that
  // was chosen for that background. Two rows used to borrow theirs from the app
  // theme instead -- `dark` took t.textPrimary and `white` took t.bgBase --
  // which only works while the app theme happens to be dark. Under porcelain
  // or daybreak, t.textPrimary is a dark ink and t.bgBase is an ivory, so the
  // default reader drew dark-on-dark and the white reader drew ivory-on-white.
  // Found 2026-10-03 by opening a story as Tester on porcelain: the scene text
  // measured rgb(58,44,40) on rgb(13,10,10). Values match ReaderSettings.jsx.
  const READER_THEMES = {
    dark: { label: "Dark", bg: "#0d0a0a", text: "#e8d5d5", msgBg: "rgba(255,255,255,0.05)", accent: t.accentGold },
    sepia: { label: "Sepia", bg: "#2b2318", text: "#d4c5a9", msgBg: "rgba(212,197,169,0.08)", accent: t.accentGold },
    midnight: { label: "Midnight", bg: "#0a0e1a", text: "#c8d0e0", msgBg: "rgba(200,208,224,0.06)", accent: "#7aa2f7" },
    parchment: { label: "Parchment", bg: "#1e1a14", text: "#c9b99a", msgBg: "rgba(201,185,154,0.08)", accent: "#b8965a" },
    white: { label: "White", bg: "#ffffff", text: "#1a1212", msgBg: "rgba(0,0,0,0.04)", accent: "var(--ds-accent-deeper, #8b0000)" },
    bluelight: { label: "Blue Light", bg: "#eef3f9", text: "#1e2a3a", msgBg: "rgba(30,80,140,0.06)", accent: "#2a6cb6" },
  };
  const readerFont = READER_FONTS[readerPrefs.fontFamily]?.value || READER_FONTS.serif.value;
  const readerTheme = READER_THEMES[readerPrefs.theme] || READER_THEMES.dark;

  // ═══════════════════════════════════════════════════════════════════════════
  // GLOBAL (SHARED) API KEY — admin-controlled, fetched from app_settings
  // ═══════════════════════════════════════════════════════════════════════════
  const [globalApiKey, setGlobalApiKey] = useState("");
  const [globalApiProvider, setGlobalApiProvider] = useState("gemini");
  const [globalApiEnabled, setGlobalApiEnabled] = useState(false);
  const [globalApiLoaded, setGlobalApiLoaded] = useState(false);

  useEffect(() => {
    const fetchGlobalApi = async () => {
      try {
        const { data, error } = await supabase.from("app_settings").select("key, value").in("key", ["global_api_key", "global_api_enabled"]);
        if (error) { console.error("[Global API] Supabase error:", error); }
        console.log("[Global API] Fetched settings:", data);
        for (const row of (data || [])) {
          if (row.key === "global_api_key") {
            const key = row.value?.key || "";
            const provider = row.value?.provider || "gemini";
            console.log("[Global API] Key found:", key ? `${key.substring(0, 8)}...` : "(empty)", "Provider:", provider);
            setGlobalApiKey(key);
            setGlobalApiProvider(provider);
          }
          if (row.key === "global_api_enabled") {
            console.log("[Global API] Enabled:", row.value?.enabled);
            setGlobalApiEnabled(row.value?.enabled || false);
          }
        }
      } catch (err) { console.error("[Global API] Fetch error:", err); }
      setGlobalApiLoaded(true);
    };
    fetchGlobalApi();
  }, []);

  // Effective API key/provider: user's personal key ALWAYS takes priority for story responses.
  // The global key (Gemini) lives in admin settings for server-side background tasks only
  // (story event extraction, arc summarization, fact extraction — all handled by the edge function).
  // If a user has no personal key set, fall back to global as a courtesy for new/guest users.
  const effectiveApiKey = apiKey || (globalApiEnabled && globalApiKey ? globalApiKey : '');
  const effectiveAiProvider = apiKey ? aiProvider : (globalApiEnabled && globalApiKey ? globalApiProvider : aiProvider);
  // v352: the model FAMILY the writer is on (from Settings), so the client
  // prompt builders speak its dialect — see src/dialectClient.js.
  const aiDialect = detectAiDialect(effectiveAiProvider, apiKey ? aiModel : '');

  // Adult / NSFW content is enabled only when the user has their own personal API key.
  // Global Gemini fallback = no explicit content (it's a shared background key, not a personal one).
  const nsfwEnabled = !!apiKey;

  // Debug: log effective key state
  useEffect(() => {
    if (globalApiLoaded) {
      console.log("[API Config] globalApiEnabled:", globalApiEnabled, "globalApiKey:", globalApiKey ? "SET" : "EMPTY", "userApiKey:", apiKey ? "SET" : "EMPTY", "effectiveKey:", effectiveApiKey ? "SET" : "EMPTY", "provider:", effectiveAiProvider);
    }
  }, [globalApiLoaded, globalApiEnabled, globalApiKey, apiKey, effectiveApiKey, effectiveAiProvider]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SESSION KEEP-ALIVE + RECONNECT (bulletproof session management)
  // ═══════════════════════════════════════════════════════════════════════════
  const ensureSession = async () => {
    // Try up to 2 times before giving up — prevents a single transient failure
    // from showing "Reconnecting" to the user (which previously never recovered)
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const getPromise = safeGetSession();
        // A merely-SLOW getSession (common on backgrounded mobile/PWA tabs) must
        // not be mistaken for "no session" — that escalation forces a refresh that
        // contends for the same auth-token Web Lock and flashes "Reconnecting"
        // even though the session is fine. Mark the timeout sentinel; on a slow
        // read, wait a bounded bit longer for the real result before escalating.
        const getTimeout = new Promise(resolve => setTimeout(() => resolve({ __timeout: true, data: { session: null } }), 3000));
        let raceResult = await Promise.race([getPromise, getTimeout]);
        if (raceResult.__timeout) {
          const secondTimeout = new Promise(resolve => setTimeout(() => resolve({ data: { session: null } }), 5000));
          raceResult = await Promise.race([getPromise, secondTimeout]);
        }
        const { data: { session } } = raceResult;

        // CRITICAL FIX: Check if JWT is actually still valid, not just if a session exists.
        // On mobile/PWA, getSession() returns a cached session with an EXPIRED JWT.
        // RLS then silently returns empty arrays — data appears to vanish.
        const isExpired = session && session.expires_at
          ? (session.expires_at * 1000) < (Date.now() + 30000)
          : false;

        if (session && !isExpired) {
          accessTokenRef.current = session.access_token;
          setSessionExpired(false);
          setConnectionStatus('connected');
          return true;
        }

        if (isExpired) {
          console.warn('[SixGuysRP] Session JWT expired/expiring — forcing refresh');
        }
        // No valid session or expired — try refresh
        if (attempt === 0) setConnectionStatus('reconnecting');
        const refreshPromise = safeRefreshSession();
        const refreshTimeout = new Promise(resolve => setTimeout(() => resolve({ data: { session: null }, error: { message: 'Refresh timed out' } }), 8000));
        const { data, error } = await Promise.race([refreshPromise, refreshTimeout]);
        if (error || !data.session) {
          if (attempt === 0) {
            console.warn("ensureSession: first attempt failed, retrying in 1s...");
            await new Promise(r => setTimeout(r, 1000));
            continue; // Retry once
          }
          console.error("ensureSession: refresh failed after retry — session is dead");
          setSessionExpired(true);
          setConnectionStatus('offline');
          return false;
        }
        accessTokenRef.current = data.session.access_token;
        setSessionExpired(false);
        setConnectionStatus('connected');
        return true;
      } catch (err) {
        if (attempt === 0) {
          console.warn("ensureSession: error on first attempt, retrying...", err.message);
          await new Promise(r => setTimeout(r, 1000));
          continue;
        }
        console.error("Session check failed after retry:", err);
        setSessionExpired(true);
        setConnectionStatus('offline');
        return false;
      }
    }
    return false;
  };

  // ─── Retry queue processor ────────────────────────────────────────────────
  const processRetryQueue = useCallback(async () => {
    if (retryQueueRef.current.length === 0) return;
    const queue = [...retryQueueRef.current];
    retryQueueRef.current = [];
    for (const item of queue) {
      try {
        if (item.type === 'upsert') {
          const { error } = await supabase.from(item.table).upsert(item.payload, item.options || {});
          if (error) throw error;
        } else if (item.type === 'update') {
          const { error } = await supabase.from(item.table).update(item.payload).eq('id', item.id);
          if (error) throw error;
        } else if (item.type === 'insert') {
          const { error } = await supabase.from(item.table).insert(item.payload);
          if (error) throw error;
        }
        console.log(`[RetryQueue] Successfully retried ${item.type} on ${item.table}`);
      } catch (err) {
        item.retries = (item.retries || 0) + 1;
        if (item.retries < 5) {
          retryQueueRef.current.push(item);
          console.warn(`[RetryQueue] Retry ${item.retries}/5 failed for ${item.table}:`, err.message);
        } else {
          console.error(`[RetryQueue] Giving up on ${item.table} after 5 retries`);
          // Persist to localStorage as last resort
          try {
            const failed = JSON.parse(localStorage.getItem('sixguys_failed_writes') || '[]');
            failed.push({ ...item, failedAt: new Date().toISOString() });
            localStorage.setItem('sixguys_failed_writes', JSON.stringify(failed));
          } catch {}
        }
      }
    }
  }, []);

  // ─── localStorage backup helpers ──────────────────────────────────────────
  const backupToLocalStorage = useCallback((key, data) => {
    try {
      localStorage.setItem(`sixguys_backup_${key}`, JSON.stringify({
        data, savedAt: new Date().toISOString(),
      }));
    } catch (e) {
      console.warn('[Backup] localStorage write failed:', e.message);
    }
  }, []);

  const _restoreFromLocalStorage = useCallback((key) => {
    try {
      const raw = localStorage.getItem(`sixguys_backup_${key}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Only use backups less than 24 hours old
      if (parsed.savedAt && (Date.now() - new Date(parsed.savedAt).getTime()) < 86400000) {
        return parsed.data;
      }
      localStorage.removeItem(`sixguys_backup_${key}`);
      return null;
    } catch { return null; }
  }, []);

  // v8 CandlelitCathedral — site-wide ambient motion. Injected once as #root's
  // first child so it lives inside #root's isolated stacking context at
  // z-index:-1 (behind app content, above the page ground). Decorative + inert.
  // Skipped entirely under reduced motion; the v8 CSS also self-guards.
  useEffect(() => {
    if (prefersReduced()) return;
    const root = document.getElementById('root');
    if (!root || document.getElementById('cathedral')) return;
    const bg = document.createElement('div');
    bg.id = 'cathedral';
    bg.setAttribute('aria-hidden', 'true');
    bg.innerHTML = '<div class="cc-hearth"></div><div class="cc-embers"></div><div class="cc-haze"></div>';
    const fore = document.createElement('div');
    fore.id = 'cathedral-fore';
    fore.setAttribute('aria-hidden', 'true');
    fore.innerHTML = '<div class="cc-rays"></div>';
    root.insertBefore(fore, root.firstChild);
    root.insertBefore(bg, root.firstChild);
    return () => { bg.remove(); fore.remove(); };
  }, []);

  useEffect(() => {
    let refreshFailCount = 0;
    const MAX_REFRESH_FAILS = 5;

    const doRefresh = async (source) => {
      try {
        // Step 1: Check cached session first (instant, no network call).
        // The SDK's autoRefreshToken handles routine token rotation — we don't
        // need to call refreshSession() ourselves, which races with the SDK
        // and can invalidate tokens mid-refresh.
        const { data: { session: cachedSession } } = await safeGetSession();
        if (cachedSession?.access_token) {
          // Session exists — we're fine
          const wasOffline = refreshFailCount > 0;
          refreshFailCount = 0;
          accessTokenRef.current = cachedSession.access_token;
          setSessionExpired(false);
          setConnectionStatus('connected');
          if (wasOffline) processRetryQueue();
          return true;
        }

        // Step 2: No cached session — try a real refresh as recovery
        console.warn(`[Session] No cached session (${source}) — attempting refresh...`);
        setConnectionStatus('reconnecting');
        const refreshPromise = safeRefreshSession();
        const timeout = new Promise(resolve => setTimeout(() => resolve({ data: { session: null }, error: { message: `${source} refresh timed out` } }), 10000));
        const { data, error } = await Promise.race([refreshPromise, timeout]);
        if (error || !data?.session) {
          refreshFailCount++;
          console.warn(`Session refresh failed (${source}, attempt ${refreshFailCount}):`, error?.message || "no session");
          if (refreshFailCount >= MAX_REFRESH_FAILS) {
            console.error("Max refresh failures reached — showing session fix banner");
            setSessionExpired(true);
            setConnectionStatus('offline');
          }
          return false;
        }
        const wasOffline = refreshFailCount > 0;
        refreshFailCount = 0;
        accessTokenRef.current = data.session.access_token;
        setSessionExpired(false);
        setConnectionStatus('connected');
        if (wasOffline) processRetryQueue();
        return true;
      } catch (err) {
        refreshFailCount++;
        console.error(`Session refresh error (${source}):`, err);
        setConnectionStatus('reconnecting');
        if (refreshFailCount >= MAX_REFRESH_FAILS) {
          setSessionExpired(true);
          setConnectionStatus('offline');
        }
        return false;
      }
    };

    // (The 4-minute heartbeat interval is registered below, after
    // monitoredDoRefresh exists, so every entry point gets auto-recovery.)

    // Refresh on tab visibility change + reconnect.
    // ALSO: on iOS PWA, swiping out and back in returns to whatever view was
    // active. Haley wants to land on the RP homepage on resume (the
    // story list, not whatever story was open). Track when we went hidden
    // and on resume — if hidden > 30s — reset view to home + clear active
    // story. Brief switch-away (<30s) leaves things untouched.
    let lastHiddenAt = 0;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        monitoredDoRefresh("visibility");
        const hiddenFor = lastHiddenAt ? (Date.now() - lastHiddenAt) : 0;

        // RESUME = CATCH UP, NOT EVICT.
        // This used to throw the player out of their story to the homepage
        // after just 30 seconds away — so glancing at a text lost your place
        // mid-scene. Worse, RP was the only surface with no resume refetch at
        // all (DMs and channels both have one), and the iPhone kills the
        // realtime socket seconds after the screen locks: any message that
        // arrived while away — including the AI reply ai-proxy now persists
        // server-side — was simply missing from the transcript and from the
        // next prompt's context.
        //
        // Now: never evict, always reconcile against the database.
        if (mainTabRef.current === 'rp' && viewRef.current === 'chat' && activeStoryIdRef.current) {
          const storyId = activeStoryIdRef.current;
          (async () => {
            try {
              const { data, error } = await supabase
                .from('story_messages')
                .select('id, char_id, char_name, text, is_player, scene_type, color, avatar, created_at')
                .eq('story_id', storyId)
                .order('created_at', { ascending: false })
                .limit(40);
              if (error || !data || !data.length) return;
              if (activeStoryIdRef.current !== storyId) return; // switched away mid-fetch
              const rows = data.slice().reverse();
              setMessages(prev => {
                const seenText = new Set((prev || []).map(m => `${m.charId || ''}|${String(m.text || '').slice(0, 80)}`));
                const seenIds = new Set((prev || []).map(m => m.id).filter(Boolean));
                const missing = rows.filter(r =>
                  !seenIds.has(r.id) &&
                  !seenText.has(`${r.char_id || ''}|${String(r.text || '').slice(0, 80)}`)
                );
                if (!missing.length) return prev;
                console.log(`[Resume] Recovered ${missing.length} message(s) that arrived while away.`);
                const mapped = missing.map(r => ({
                  id: r.id,
                  charId: r.char_id,
                  name: r.char_name,
                  text: r.text,
                  isPlayer: r.is_player,
                  type: r.scene_type || 'chat',
                  color: r.color || undefined,
                  avatar: r.avatar || undefined,
                  createdAt: r.created_at,
                  timestamp: new Date(r.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
                }));
                return [...(prev || []), ...mapped].sort(
                  (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
                );
              });
            } catch (e) {
              console.warn('[Resume] story catch-up failed:', e?.message || e);
            }
          })();
        }
        void hiddenFor;
        lastHiddenAt = 0;
      } else if (document.visibilityState === "hidden") {
        lastHiddenAt = Date.now();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Online/offline detection
    const handleOnline = () => {
      console.log("[Network] Back online — refreshing session");
      setConnectionStatus('reconnecting');
      monitoredDoRefresh("online");
    };
    const handleOffline = () => {
      console.log("[Network] Went offline");
      setConnectionStatus('offline');
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // ─── AUTO-RECOVERY: When connection drops, retry with backoff ───────────
    // Instead of waiting 4 minutes for the next interval, immediately start
    // retrying when we detect a failure. This is what was missing — Nicole
    // would see "Reconnecting..." and it would NEVER recover until she refreshed.
    let recoveryTimer = null;
    let recoveryAttempt = 0;
    const startRecovery = () => {
      if (recoveryTimer) return; // Already running
      recoveryAttempt = 0;
      const tryRecover = async () => {
        recoveryAttempt++;
        // Exponential backoff: 2s, 4s, 8s, 16s, then cap at 30s
        const delay = Math.min(2000 * Math.pow(2, recoveryAttempt - 1), 30000);
        console.log(`[Recovery] Attempt ${recoveryAttempt} in ${delay/1000}s...`);
        const ok = await doRefresh("recovery");
        if (ok) {
          console.log("[Recovery] Session recovered!");
          recoveryAttempt = 0;
          recoveryTimer = null;
          return; // Stop retrying
        }
        if (recoveryAttempt < 10) {
          recoveryTimer = setTimeout(tryRecover, delay);
        } else {
          console.error("[Recovery] Giving up after 10 attempts");
          recoveryTimer = null;
        }
      };
      recoveryTimer = setTimeout(tryRecover, 2000);
    };

    // Every refresh entry point (interval, visibility, online) goes through
    // this wrapper so a failure anywhere kicks off auto-recovery. A previous
    // version registered the interval + visibility handler twice and swapped
    // them mid-effect — that swap silently dropped the iOS resume-to-home
    // logic above (the original handler was removed before it ever fired).
    // Handlers are now registered ONCE and call this wrapper directly.
    const monitoredDoRefresh = async (source) => {
      const ok = await doRefresh(source);
      if (!ok && !recoveryTimer) {
        startRecovery();
      }
      return ok;
    };
    // 4-minute heartbeat (SDK auto-refreshes tokens; we just verify it's alive)
    const interval = setInterval(() => monitoredDoRefresh("interval"), 4 * 60 * 1000);

    return () => {
      clearInterval(interval);
      if (recoveryTimer) clearTimeout(recoveryTimer);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [processRetryQueue]);

  // ─── Cache access token on auth state changes ────────────────────────────
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.access_token) {
        accessTokenRef.current = session.access_token;
        // Any successful auth event means we're connected
        setSessionExpired(false);
        setConnectionStatus('connected');
      }
      if (event === 'TOKEN_REFRESHED') {
        console.log('[Auth] Token refreshed by SDK — connection confirmed');
      }
      if (event === 'SIGNED_OUT') {
        console.warn('[Auth] Signed out event — checking if session still exists...');
        // Don't immediately clear user — sometimes SIGNED_OUT fires spuriously
        // during token rotation races. Check if we actually have a session.
        setTimeout(async () => {
          const { data: { session: checkSession } } = await safeGetSession();
          if (!checkSession) {
            console.error('[Auth] Confirmed signed out — no session');
            setUser(null);
            setUserProfile(null);
            setCurrentPlayer(null);
          } else {
            console.log('[Auth] False alarm — session still valid after SIGNED_OUT event');
            accessTokenRef.current = checkSession.access_token;
          }
        }, 1000);
      }
    });
    // Also grab initial token
    safeGetSession().then(({ data: { session } }) => {
      if (session?.access_token) accessTokenRef.current = session.access_token;
    });
    return () => subscription?.unsubscribe();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ONLINE STATUS (Update 6C)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;
    const setOnline = async () => {
      try {
        await ensureSession();
        await supabase.from("profiles").update({ is_online: true, last_seen: new Date().toISOString() }).eq("id", user.id);
      } catch {} // Silent — presence updates should never disrupt the app
    };
    const setOffline = async () => {
      try {
        await ensureSession();
        await supabase.from("profiles").update({ is_online: false, last_seen: new Date().toISOString() }).eq("id", user.id);
      } catch {} // Silent — presence updates should never disrupt the app
    };
    setOnline();
    // HEARTBEAT MUST RESPECT VISIBILITY.
    // This used to be a bare setInterval(setOnline, 60000), which ran whether
    // or not the app was on screen. So the teardown below worked and was then
    // immediately undone: background the tab -> visibilitychange sets
    // is_online=false -> 60s later the interval sets it back to TRUE and
    // refreshes last_seen. That also defeated the 3-minute staleness backstop
    // in presence.js, because last_seen never went stale. Net effect: a player
    // showed as "online" for as long as the tab existed anywhere, which is
    // exactly the reported "it says Nicole is online when she's not".
    // (Browsers throttle background timers to roughly once a minute — the same
    // period as this heartbeat — so it fired reliably enough to keep the flag
    // pinned true indefinitely.)
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") setOnline();
    }, 60000);
    const handleVis = () => {
      if (document.visibilityState === "visible") setOnline();
      else setOffline();
    };
    document.addEventListener("visibilitychange", handleVis);
    window.addEventListener("beforeunload", setOffline);
    // `pagehide` is the ONLY reliable teardown signal on iOS Safari / PWAs —
    // beforeunload frequently never fires there, which is a big reason people
    // showed as "online" long after closing the app. (The 3-minute staleness
    // guard in presence.js is the real backstop; this just makes it faster.)
    window.addEventListener("pagehide", setOffline);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVis);
      window.removeEventListener("beforeunload", setOffline);
      window.removeEventListener("pagehide", setOffline);
      setOffline();
    };
  }, [user?.id]);

  // ═══════════════════════════════════════════════════════════════════════════
  // PUSH NOTIFICATION SUBSCRIPTION CHANGE LISTENER (iOS can rotate subscriptions)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;
    const stopSubListener = listenForSubscriptionChanges(user.id);
    // Auto-heal push on every load + periodically while open + when the tab
    // becomes visible again after backgrounding. A new service worker (shipped
    // every deploy) can silently drop the subscription on iOS, which is what
    // used to make the notifications toggle flip OFF on its own. The heal
    // loop never opts the user out — only an explicit Settings toggle can do
    // that. See pushUtils.startPushHealLoop for the full design rationale.
    const stopHealLoop = startPushHealLoop(user.id);
    // Native iOS (APNs) push registration — no-op on web. Web push can't reach
    // the Capacitor WKWebView, so the native app registers an APNs token that
    // send-push delivers to. Same triggers (human + AI messages) reach both.
    initNativePush(user.id);
    // App-icon badge (2026-09-10): one server count, synced on login, on
    // every read marker write, on every unread arrival and on resume.
    const stopBadge = installBadgeSync();
    return () => { stopHealLoop?.(); stopSubListener?.(); stopBadge?.(); };
  }, [user?.id]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LIVE NOTIFICATION FEED — Realtime subscriptions for DMs + Channels
  // ═══════════════════════════════════════════════════════════════════════════
  const addLiveNotification = useCallback((notif) => {
    const id = ++notifIdCounter.current;
    const now = new Date();
    const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    setLiveNotifications(prev => {
      // Cap at 8 notifications visible at once
      const updated = [{ ...notif, id, time: timeStr, exiting: false }, ...prev];
      return updated.slice(0, 8);
    });
    setUnreadNotifCount(prev => prev + 1);
    scheduleBadgeSync();
    // Set red dot on the relevant nav tab (only if user isn't on that tab)
    // A channel notification dots the SECTION the room is in (2026-10-19 audit:
    // every room dotted Our Channels, which a member cannot even open).
    const tabMap = { dm: "dm", channel: notif.channelMode === "private" ? "channels" : "community", story: "rp", phone: "phones", community: "stories" };
    const targetTab = tabMap[notif.type];
    if (targetTab && mainTabRef.current !== targetTab) {
      setUnreadTabs(prev => ({ ...prev, [targetTab]: true }));
    }

    // Upper-right desktop preview toast. Decoupled from the tab check on
    // purpose: the realtime handlers that call this ALREADY drop notifications
    // for the exact conversation/channel you're viewing, so anything reaching
    // here is genuinely elsewhere and worth a heads-up — even if it's another
    // DM while you're on the DM tab (Cruz pinging while you read Sebby). Mobile
    // gets native push instead, so this is desktop-only. Tapping jumps there.
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
    if (isDesktop && toastRef.current) {
      toastRef.current.addToast({
        message: notif.body || 'New message',
        type: notif.type,
        characterName: notif.title,
        avatarSrc: notif.avatar || null,
        duration: 7000,
        onTap: () => { if (targetTab) setMainTab(targetTab); },
      });
    }
  }, []);

  const dismissNotification = useCallback((id) => {
    // Start exit animation
    setLiveNotifications(prev => prev.map(n => n.id === id ? { ...n, exiting: true } : n));
    // Remove after animation
    setTimeout(() => {
      setLiveNotifications(prev => prev.filter(n => n.id !== id));
    }, 300);
  }, []);

  const dismissAllNotifications = useCallback(() => {
    setLiveNotifications([]);
    setUnreadNotifCount(0);
  }, []);

  const handleNotifClick = useCallback((notif) => {
    // Navigate to the relevant tab. Note: we deliberately do NOT dismiss the
    // notification on click — per Haley's spec, notifications stick around
    // in the bell dropdown until the user explicitly X's them or clicks
    // "Clear all". This lets her open the dropdown, glance at what's there,
    // act on one, and still see the rest.
    if (notif.type === "dm") {
      setMainTab("dm");
    } else if (notif.type === "channel") {
      // v420 (Haley, 2026-10-19, Brooke): a Community room opens Community
      // Chat; an Our Channels room opens Our Channels, and only for a writer.
      // Every channel notification used to open Our Channels, which is how a
      // member account reached the writers' rooms through the bell.
      let role = null;
      try { role = localStorage.getItem("mfml_user_role"); } catch { /* storage blocked */ }
      const writer = ["admin", "editor"].includes(role);
      setMainTab(notif.channelMode !== "private" ? "community" : (writer ? "channels" : "community"));
    }
    // Close the dropdown after navigating so the user sees the target view.
    setShowNotifDropdown(false);
  }, []);

  // Callback for child components (ChannelView, DMView) to report what they're currently viewing.
  // We track the active view for one purpose only: SUPPRESSING new incoming
  // bell notifications for THAT specific item (so the user doesn't get bell
  // pings for the conversation they're already looking at). We do NOT touch
  // the existing notification list here — per spec, notifications only clear
  // via the per-notif ✕ or "Clear all".
  const setActiveView = useCallback((type, id) => {
    activeViewRef.current = type && id ? { type, id } : null;
  }, []);

  // Subscribe to DM messages + channel messages for live notifications
  useEffect(() => {
    if (!user) return;

    // Clean up previous subscriptions
    liveNotifChannelsRef.current.forEach(ch => {
      try { supabase.removeChannel(ch); } catch {}
    });
    liveNotifChannelsRef.current = [];

    // Fetch story-category channel IDs for the story notification bell
    // Only include channels the CURRENT USER created (each user sees their own stories)
    (async () => {
      try {
        const { data: cats } = await supabase.from('categories').select('id, name');
        const storyCatIds = (cats || []).filter(c => c.name?.toUpperCase() !== 'GENERAL').map(c => c.id);
        if (storyCatIds.length > 0) {
          const { data: storyChannels } = await supabase
            .from('channels')
            .select('id, category_id')
            .in('category_id', storyCatIds)
            .eq('created_by', user.id);
          storyChannelIdsRef.current = new Set((storyChannels || []).map(ch => ch.id));
          console.log('[StoryNotif] Tracking', storyChannelIdsRef.current.size, 'story channels owned by current user');
        }
      } catch (err) {
        console.warn('[StoryNotif] Failed to load story channel IDs:', err);
      }
    })();

    // 1. Subscribe to DM messages where the user is a participant
    const dmChannel = supabase
      .channel('notif-dm-messages')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'dm_messages',
      }, async (payload) => {
        const msg = payload.new;
        // Don't notify for own messages
        if (msg.sender_id === user.id) return;
        // Only suppress if user is viewing THIS specific DM conversation
        const av = activeViewRef.current;
        if (av?.type === 'dm' && av?.id === msg.conversation_id) return;
        // Respect notification preferences (global toggle, category toggle, snooze)
        if (!isNotifAllowed(notifPrefsRef.current, 'dm')) return;

        // Verify this conversation belongs to the current user — prevents seeing
        // notifications from other users' AI character DMs (e.g. Nicole's DMs).
        const { data: convo } = await supabase
          .from('dm_conversations')
          .select('id')
          .eq('id', msg.conversation_id)
          .eq('user_id', user.id)
          .maybeSingle();
        if (!convo) return;

        // Bug #8 fix: allChars is defined later in render and stale in this closure.
        // Use CHARACTERS directly (imported constant) — DM notifications come from AI characters, not custom ones.
        const char = msg.sender_id ? CHARACTERS.find(c => c.id === msg.sender_id) : null;
        const senderName = char ? char.name : (msg.sender_name || "Someone");
        const avatarUrl = char?.id ? (getCharacterArt(char.id, 'thumb') || getCharacterAvatar(char.id)) : null;

        addLiveNotification({
          type: "dm",
          title: senderName,
          body: msg.content?.substring(0, 100) || "New message",
          icon: char ? null : "💌",
          avatar: avatarUrl,
          color: char?.color || t.accentGold,
          source: "DM",
          conversationId: msg.conversation_id,
        });
      })
      .subscribe();

    // 2. Subscribe to channel messages
    const channelMsgChannel = supabase
      .channel('notif-channel-messages')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'channel_messages',
      }, async (payload) => {
        const msg = payload.new;
        // Don't notify for own messages
        if (msg.user_id === user.id) return;
        // Only suppress if user is viewing THIS specific channel
        const av2 = activeViewRef.current;
        if (av2?.type === 'channel' && av2?.id === msg.channel_id) return;
        // Respect notification preferences (global toggle, category toggle, snooze)
        if (!isNotifAllowed(notifPrefsRef.current, 'channel')) return;

        // v420 (Haley, 2026-10-19, Brooke): the room must be one THIS user may
        // see — the same check the DM handlers make for their conversation.
        // channels_select (migration 20261019120000) returns nothing for an
        // Our Channels room to a member account, so the bell stays quiet; the
        // row also tells the click handler which tab the room lives in.
        const { data: room } = await supabase
          .from('channels')
          .select('id, mode')
          .eq('id', msg.channel_id)
          .maybeSingle();
        if (!room) return;

        // Read through the always-current refs — this effect's closure was
        // captured at login, when onlineProfiles was [] and customChars hadn't
        // loaded, so human senders showed "Someone" forever (the sibling DM
        // handlers were fixed the same way; this one was missed).
        const char = msg.is_ai_character && msg.character_id ? allCharsRef.current.find(c => c.id === msg.character_id) : null;
        // Look up human sender display name from onlineProfiles
        const humanProfile = !char && msg.user_id ? onlineProfilesRef.current.find(p => p.id === msg.user_id) : null;
        const senderName = char ? char.name : (humanProfile?.display_name || msg.sender_name || "Someone");
        const avatarUrl = char?.id ? (getCharacterArt(char.id, 'thumb') || getCharacterAvatar(char.id)) : (humanProfile?.avatar_url || null);

        addLiveNotification({
          type: "channel",
          title: senderName,
          body: msg.content?.substring(0, 100) || "New message",
          icon: char ? null : "📢",
          avatar: avatarUrl,
          color: char?.color || "#7289da",
          source: "Channel",
          channelId: msg.channel_id,
          channelMode: room.mode || 'private',
        });

        // ── Track story channel messages for the special story bell ──
        if (storyChannelIdsRef.current.has(msg.channel_id)) {
          setStoryChannelUnread(prev => prev + 1);
          setStoryNotifMessages(prev => [{
            id: msg.id,
            sender: senderName,
            avatar: avatarUrl,
            content: msg.content?.substring(0, 120) || "(new activity)",
            color: char?.color || t.accentGold,
            channelId: msg.channel_id,
            time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
          }, ...prev].slice(0, 10));
        }
      })
      .subscribe();

    // 3. Subscribe to user-to-user DM messages for in-app bell notifications
    const userDmChannel = supabase
      .channel(`notif-user-dm-messages-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_dm_messages',
      }, async (payload) => {
        const msg = payload.new;
        // Don't notify for own messages
        if (msg.sender_id === user.id) return;
        // Only suppress if user is viewing THIS specific user DM conversation
        const av3 = activeViewRef.current;
        if (av3?.type === 'user_dm' && av3?.id === msg.conversation_id) return;
        // Respect notification preferences (global toggle, category toggle, snooze)
        if (!isNotifAllowed(notifPrefsRef.current, 'dm')) return;

        // ── Critical: verify this conversation belongs to the current user ──
        // Prevents receiving notifications for other users' private DMs.
        const { data: convo } = await supabase
          .from('user_dm_conversations')
          .select('id')
          .eq('id', msg.conversation_id)
          .or(`user_id.eq.${user.id},target_user_id.eq.${user.id}`)
          .maybeSingle();
        if (!convo) return; // Not the current user's conversation

        // Look up sender display name from onlineProfiles (via ref — see sync effect above)
        const senderProfile = onlineProfilesRef.current.find(p => p.id === msg.sender_id);
        const senderName = senderProfile?.display_name || "Someone";

        addLiveNotification({
          type: "dm",
          title: senderName,
          body: msg.content?.substring(0, 100) || "New message",
          icon: "💬",
          avatar: senderProfile?.avatar_url || null,
          color: t.accentSuccess,
          source: "User DM",
          conversationId: msg.conversation_id,
        });
      })
      .subscribe();

    liveNotifChannelsRef.current = [dmChannel, channelMsgChannel, userDmChannel];

    return () => {
      liveNotifChannelsRef.current.forEach(ch => {
        try { supabase.removeChannel(ch); } catch {}
      });
      liveNotifChannelsRef.current = [];
    };
  }, [user?.id, addLiveNotification]);

  // Reset unread count when opening notification feed
  useEffect(() => {
    if (!notifMinimized) setUnreadNotifCount(0);
  }, [notifMinimized]);

  // Keep a live ref of onlineProfiles so the live-notification realtime
  // subscription can read sender names without listing onlineProfiles as an
  // effect dependency (which rebuilt all three channels on every presence tick).
  useEffect(() => {
    onlineProfilesRef.current = onlineProfiles;
  }, [onlineProfiles]);

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO-SAVE: beforeunload + visibilitychange + localStorage backup
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;
    const saveBeforeLeave = () => {
      // DATA-LOSS GUARD: same as saveStory — never PATCH an existing story's
      // blob with empty messages. The unload handler runs on logout/tab-close
      // and could otherwise clobber a 581-message story right as the user
      // leaves.
      if (messagesRef.current.length > 0 && viewRef.current === 'chat') {
        const storyData = {
          messages: messagesRef.current, sceneChars, sceneType, playingAs,
          currentPlayer, customChars, aiProvider,
          routeFlags: storyRouteFlagsRef.current,
          ...(backstorySummary ? { backstorySummary } : {}),
        };
        const name = currentSessionName || `${currentPlayer || "RP"} - ${new Date().toLocaleDateString()}`;

        // Always backup to localStorage first (instant, reliable)
        try {
          localStorage.setItem('sixguys_backup_story', JSON.stringify({
            storyId: activeStoryIdRef.current,
            name, data: storyData,
            savedAt: new Date().toISOString(),
            userId: user.id,
          }));
        } catch {}

        if (activeStoryIdRef.current && accessTokenRef.current) {
          const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/stories?id=eq.${activeStoryIdRef.current}`;
          const body = JSON.stringify({ name, data: storyData, updated_at: new Date().toISOString() });
          // Try sendBeacon first — guaranteed by the spec to fly even if the
          // page is unloading (the fetch+keepalive path can be cancelled by
          // some browsers under tab-close pressure). sendBeacon doesn't take
          // headers, so we PATCH via a regular fetch with keepalive as the
          // primary, BUT we also fire a sendBeacon copy as a redundant beacon.
          // If the browser supports beacon-with-blob, both paths land.
          try {
            if (navigator.sendBeacon) {
              const blob = new Blob([body], { type: 'application/json' });
              // PostgREST won't auth a beacon (no headers), so the beacon path
              // is informational at best — we keep it for browsers that
              // cancel keepalive fetches. The real save is the keepalive PATCH.
              navigator.sendBeacon(url + '&apikey=' + import.meta.env.VITE_SUPABASE_ANON_KEY, blob);
            }
          } catch {}
          fetch(url, {
            method: "PATCH", keepalive: true,
            headers: {
              "Content-Type": "application/json",
              "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
              "Authorization": `Bearer ${accessTokenRef.current}`,
              "Prefer": "return=minimal"
            },
            body,
          }).catch(() => {});
        }
      }
    };
    // Bug #2 fix: Use saveBeforeLeave (ref-based) for both handlers to avoid stale closure.
    // saveStory() closed over stale state since it wasn't in deps; saveBeforeLeave uses refs.
    const handleVisChange = () => {
      if (document.visibilityState === "hidden") {
        saveBeforeLeave();
      }
    };
    window.addEventListener("beforeunload", saveBeforeLeave);
    document.addEventListener("visibilitychange", handleVisChange);
    return () => {
      window.removeEventListener("beforeunload", saveBeforeLeave);
      document.removeEventListener("visibilitychange", handleVisChange);
    };
  }, [user?.id, sceneChars, sceneType, playingAs, currentPlayer, customChars, aiProvider, currentSessionName, backstorySummary]);

  // ─── Restore from localStorage backup on mount ────────────────────────────
  // Runs AT MOST ONCE per browser session (ref guard) and only when there are
  // demonstrably unsaved messages. Never silently navigates the user — always
  // prompts before opening the recovered story.
  //
  // The previous version re-fired whenever the `user` object reference changed
  // (every auth refresh), and on each fire it force-set setActiveStoryId +
  // setView("chat") — yanking the user into a stale RP every time they opened
  // the app or even pressed "Our Channels," because the dependency change
  // re-triggered the restore. Especially noticeable right after a DM→RP
  // conversion, since the conversion leaves a fresh backup behind that's
  // ahead of the DB by a few messages, so the silent auto-restore always won.
  const didAttemptRestoreRef = useRef(false);
  useEffect(() => {
    if (!user?.id) return;
    if (didAttemptRestoreRef.current) return;
    didAttemptRestoreRef.current = true;

    try {
      const backup = localStorage.getItem('sixguys_backup_story');
      if (!backup) return;
      const parsed = JSON.parse(backup);
      if (parsed.userId !== user.id) return;
      if (parsed.savedAt && (Date.now() - new Date(parsed.savedAt).getTime()) > 86400000) {
        localStorage.removeItem('sixguys_backup_story');
        return;
      }
      if (!parsed.storyId || !parsed.data) return;

      ensureSession()
        .then((ok) => {
          // Dead session → the SELECT below would be RLS-denied and resolve
          // {data:null, error} — which the old code conflated with "nothing
          // recoverable" and used to DELETE the backup. That backup is the
          // ONLY copy of unsaved messages (the very failure that stranded
          // them also breaks this read). Keep it and retry next load.
          if (!ok) return null;
          return supabase.from('stories').select('updated_at, data, name').eq('id', parsed.storyId).maybeSingle();
        })
        .then((res) => {
          if (!res) return; // session wasn't healthy — backup kept
          const { data: dbRow, error } = res;
          // Read FAILED (RLS/network/timeout) → we know nothing; keep the backup.
          if (error) { console.warn('[Backup] DB check failed — keeping backup:', error.message); return; }
          // Row not visible → cannot confirm the DB has the messages; keep the backup.
          if (!dbRow) { console.warn('[Backup] Story row not readable — keeping backup'); return; }
          // Only a SUCCESSFUL read that positively confirms the DB is at least
          // as new may discard the backup.
          if (!parsed.savedAt || new Date(parsed.savedAt) <= new Date(dbRow.updated_at)) {
            localStorage.removeItem('sixguys_backup_story');
            return;
          }
          const backupMsgCount = parsed.data.messages?.length || 0;
          const dbMsgCount = dbRow.data?.messages?.length || 0;
          // Backup must have STRICTLY more messages than the DB to count as unsaved work.
          if (backupMsgCount <= dbMsgCount) {
            localStorage.removeItem('sixguys_backup_story');
            return;
          }

          const storyLabel = parsed.name || dbRow.name || 'your previous story';
          const lostCount = backupMsgCount - dbMsgCount;
          const ok = window.confirm(
            `We recovered ${lostCount} unsaved message${lostCount === 1 ? '' : 's'} from "${storyLabel}". ` +
            `Open that story to restore them? (Cancel discards the backup.)`
          );
          if (!ok) {
            localStorage.removeItem('sixguys_backup_story');
            return;
          }

          console.log(`[Backup] User chose to restore ${lostCount} unsaved messages`);
          const d = parsed.data;
          settingsLoadEpochRef.current = Date.now();
          setMessages(d.messages || []);
          storyRouteFlagsRef.current = (d.routeFlags && typeof d.routeFlags === 'object') ? d.routeFlags : {};
          setBlockedChars(d.blockedChars || []);
          setSceneChars((d.sceneChars && d.sceneChars.length) ? d.sceneChars : castFromHistory(d.messages, d.blockedChars));
          setSceneType(d.sceneType || "chat");
          setPlayingAs(prev => d.playingAs || prev);
          setCustomChars(d.customChars || []);
          setActiveStoryId(parsed.storyId);
          setCurrentSessionName(parsed.name || "");
          setMainTab("rp");
          setView("chat");
          setTimeout(() => saveStory(), 500);
        })
        .catch(() => {});
    } catch {}
  }, [user?.id]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ONLINE PROFILES SUBSCRIPTION (Update: Profile Customization)
  // ═══════════════════════════════════════════════════════════════════════════
  // Bounded fetch: the initial pull caps at the 100 most-recently-active
  // profiles ordered by last_seen DESC. The realtime subscription below keeps
  // state in sync from there (any profile change — including from users
  // outside the initial cap — gets merged in via INSERT/UPDATE events).
  // At current scale (3 users) the cap is a no-op; it just prevents an
  // unbounded SELECT * as the user count grows.
  const ONLINE_PROFILES_INITIAL_LIMIT = 100;
  useEffect(() => {
    if (!user) return;
    // Initial fetch
    const fetchProfiles = async () => {
      // Ensure valid session before reading profiles (stale session → empty results)
      await ensureSession();
      const { data } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url, accent_color, is_online, last_seen, status_message, hide_online_status, profile_frame")
        .order("last_seen", { ascending: false, nullsFirst: false })
        .limit(ONLINE_PROFILES_INITIAL_LIMIT);
      if (data) setOnlineProfiles(data);
    };
    fetchProfiles();

    // Subscribe to realtime changes
    const channel = supabase.channel("profiles-presence")
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles" }, (payload) => {
        // DELETE events have no payload.new — handle separately
        if (payload.eventType === "DELETE") {
          const old = payload.old;
          if (old?.id) {
            setOnlineProfiles((prev) => prev.filter((p) => p.id !== old.id));
          }
          return;
        }
        const updated = payload.new;
        if (!updated?.id) return;
        setOnlineProfiles((prev) => {
          const existing = prev.findIndex((p) => p.id === updated.id);
          if (existing >= 0) {
            const copy = [...prev];
            copy[existing] = { ...copy[existing], ...updated };
            return copy;
          }
          return [...prev, updated];
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTH SETUP
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const initAuth = async () => {
      try {
        setAuthLoading(true);

        // Try to get a cached session from localStorage FIRST (instant, no lock needed)
        // This prevents the stuck loading screen when Supabase auth lock hangs
        let cachedEmail = null;
        try {
          const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
          if (storageKey) {
            const stored = JSON.parse(localStorage.getItem(storageKey));
            cachedEmail = stored?.user?.email;
          }
        } catch (e) { /* ignore */ }

        // Race getSession against a 5-second timeout so the app never hangs on the loading screen
        const sessionPromise = safeGetSession();
        const timeoutPromise = new Promise(resolve => setTimeout(() => resolve({ data: { session: null }, timedOut: true }), 5000));
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        const session = result?.data?.session;

        if (session?.user) {
          setUser(session.user);
          // ── INSTANT cache load — prevents "thescheerers" flash on reload ──
          const cached = getCachedProfile(session.user.id);
          if (cached?.display_name) {
            setCurrentPlayer(cached.display_name);
            setUserProfile(cached);
          } else {
            // Set a fallback player name so we never get stuck on "Loading your profile..."
            const fallbackName = session.user.email?.split("@")[0] || "Player";
            setCurrentPlayer(prev => {
              if (prev && prev !== "Player" && !prev.includes("@")) return prev;
              return fallbackName;
            });
          }
          try {
            const profile = await Promise.race([
              getProfile(session.user.id),
              new Promise(resolve => setTimeout(() => resolve(null), 4000))
            ]);
            if (profile) {
              setUserProfile(profile);
              if (profile.display_name) setCurrentPlayer(profile.display_name);
              // Client-version beacon: makes "is her app stale?" a query
              // instead of a guess. Fire-and-forget; RLS self-update.
              supabase.from('profiles')
                .update({ last_client_version: BUILD_VERSION, last_seen_at: new Date().toISOString() })
                .eq('id', session.user.id)
                .then(null, () => {});
              // RP defaults: restore last played-as character + relationship
              // tags into the setup, so re-entering the RP section remembers
              // her choices (2026-07-27 request). Story loads override these.
              try {
                const d = profile.rp_defaults || JSON.parse(localStorage.getItem('mfml_rp_defaults') || 'null');
                if (d && typeof d === 'object') {
                  if (d.playingAs) setPlayingAs(prev => prev || d.playingAs);
                }
              } catch { /* defaults are best-effort */ }
              // Apply saved theme from profile (so theme persists across devices/cleared cache)
              if (profile.theme) {
                try {
                  const storedTheme = localStorage.getItem('mfml-theme');
                  if (!storedTheme || storedTheme === 'dark') {
                    // Only apply profile theme if localStorage has no preference
                    // (localStorage wins so in-session changes aren't overridden on re-auth)
                    themeCtx?.setThemeId(profile.theme);
                  }
                } catch {}
              }
              // Light-theme text color + composer bar follow the profile row
              // onto a fresh device the same way the theme does (localStorage wins).
              try {
                if (profile.text_color && !localStorage.getItem('mfml-text-color')) themeCtx?.setTextColor?.(profile.text_color);
                if (localStorage.getItem('mfml-composer-dark') === null && typeof profile.composer_dark === 'boolean') themeCtx?.setComposerDark?.(profile.composer_dark);
              } catch {}
              // Persist to cache so next reload is instant
              setCachedProfile(session.user.id, profile);
              // Cache role for instant private channel access on next reload
              try { localStorage.setItem("mfml_user_role", profile.role || "guest"); } catch {}
              // One-time memory backfill: extract story events from all existing
              // DM conversations and RP stories so the character memory system
              // has data even for conversations that predate this feature.
              // Runs once per user, gated by localStorage key. Fire-and-forget.
              try {
                const BACKFILL_KEY = 'mfml_memory_backfill_v1';
                const alreadyDone = localStorage.getItem(BACKFILL_KEY);
                if (!alreadyDone && profile) {
                  localStorage.setItem(BACKFILL_KEY, '1');
                  const displayName = profile.display_name || session.user.email?.split('@')[0] || 'Player';
                  // Delay so it doesn't compete with page load
                  setTimeout(() => {
                    backfillStoryMemory(session.user.id, displayName, (msg) => {
                      console.log(`[Memory Backfill] ${msg}`);
                    }, false).catch(err => console.warn('[Memory Backfill] Error:', err));
                  }, 8000); // 8s delay — let the app fully load first
                }
              } catch {} // Backfill is entirely optional — never block login

              // Record daily login for gamification (XP + streak)
              recordDailyLogin(session.user.id).then(result => {
                if (result && !result.alreadyLogged) {
                  // Update profile with new XP/level data
                  setUserProfile(prev => prev ? { ...prev, xp: result.newXP, level: result.newLevel, login_streak: result.newStreak, longest_streak: result.newLongest } : prev);
                  console.log(`[Gamification] Daily login: +${result.xpGain} XP (streak: ${result.newStreak})`);
                }
              }).catch(() => {}); // Non-blocking — gamification is optional
            }
          } catch (profileErr) {
            console.error("Failed to load profile:", profileErr);
            // currentPlayer already set to fallback — no stuck screen
          }
          // Load notification preferences
          loadNotifPrefs(session.user.id).then(prefs => {
            setNotifPrefs(prefs);
            notifPrefsRef.current = prefs;
          });
        } else if (result?.timedOut && cachedEmail) {
          // Auth lock timed out but we have a cached session — set fallback player
          // so user isn't stuck. onAuthStateChange will fix it when the lock resolves.
          console.warn("[MFML] Auth lock timeout — using cached email for immediate display");
          setCurrentPlayer(cachedEmail.split("@")[0] || "Player");
        }
      } catch (err) {
        console.error("Auth init error:", err);
      } finally {
        setAuthLoading(false);
      }
    };
    initAuth();

    // Safety net: if auth still loading after 6 seconds, force past the loading screen
    const authTimeout = setTimeout(() => {
      setAuthLoading(false);
    }, 6000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);
        setSessionExpired(false);
        // ── INSTANT cache load on token refresh / sign-in ──
        const cached = getCachedProfile(session.user.id);
        if (cached?.display_name) {
          setCurrentPlayer(cached.display_name);
          setUserProfile(cached);
        } else {
          const fallback = session.user.email?.split("@")[0] || "Player";
          // Only set fallback if we don't already have a good display name
          // This prevents overwriting "Haley" with "hmjmusic" on token refresh
          setCurrentPlayer(prev => {
            if (prev && prev !== "Player" && !prev.includes("@")) return prev;
            return fallback;
          });
        }
        try {
          const profile = await Promise.race([
            getProfile(session.user.id),
            new Promise(resolve => setTimeout(() => resolve(null), 4000))
          ]);
          if (profile) {
            setUserProfile(profile);
            if (profile.display_name) setCurrentPlayer(profile.display_name);
            // Persist to cache so next reload is instant
            setCachedProfile(session.user.id, profile);
            // Cache role for instant private channel access on next reload
            try { localStorage.setItem("mfml_user_role", profile.role || "guest"); } catch {}
          }
        } catch (profileErr) {
          console.error("Failed to load profile on auth change:", profileErr);
        }
        // Load notification preferences
        loadNotifPrefs(session.user.id).then(prefs => {
          setNotifPrefs(prefs);
          notifPrefsRef.current = prefs;
        });
      } else if (event === 'SIGNED_OUT') {
        // Only a real SIGNED_OUT may tear down — and even then defer + re-verify,
        // exactly like the keep-alive handler. The old bare `else` cleared the
        // session on ANY non-session event (USER_UPDATED, transient token-rotation
        // null, backgrounded mobile tab), which is the "it randomly logged me out
        // mid-RP" bug. A non-SIGNED_OUT null event now leaves state intact.
        setTimeout(async () => {
          const { data: { session: recheck } } = await safeGetSession();
          if (!recheck) {
            setUser(null);
            setUserProfile(null);
            setCurrentPlayer(null);
            setNotifPrefs({ ...DEFAULT_PREFS });
            notifPrefsRef.current = { ...DEFAULT_PREFS };
            try { localStorage.removeItem("mfml_user_role"); } catch {}
          } else {
            accessTokenRef.current = recheck.access_token;
          }
        }, 1000);
      }
    });

    return () => { clearTimeout(authTimeout); subscription?.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (user) loadStories();
  }, [user?.id]);

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO-RESUME: jump back into your most recent story on page reload
  // ═══════════════════════════════════════════════════════════════════════════
  // Why: the symptom Haley reported was "messages save initially but vanish
  // on reload" — what was actually happening is that after a refresh the
  // user landed on the home view with no active story, so the messages
  // looked gone. They were always in the DB. Now we set lastActiveStoryId
  // every time you open or send into a story; on reload we resume there.
  const autoResumeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!user?.id) return;
    if (autoResumeAttemptedRef.current) return;
    if (!savedSessions || savedSessions.length === 0) return;
    // If the localStorage-backup-restore effect already brought us into a
    // story (it runs earlier and pulls in unsaved messages), don't double-load.
    if (activeStoryIdRef.current || activeStoryId) {
      autoResumeAttemptedRef.current = true;
      return;
    }
    autoResumeAttemptedRef.current = true;
    try {
      // RELOAD-ONLY RESUME. This used to read localStorage, which persists
      // forever — so every fresh app open with a story in history yanked the
      // user out of the home page and into that RP without them touching
      // anything ("it auto opens to the rp when I didn't click anything",
      // 2026-07-29).
      //
      // sessionStorage is the right store for the question actually being
      // asked: "was THIS tab in a story a moment ago?" It survives a reload —
      // which is the case this feature exists for, the mid-scene refresh that
      // used to dump you on the home screen looking at what seemed like lost
      // messages — and it is absent on a fresh visit or a new tab, so opening
      // the app lands on home like it should.
      //
      // The localStorage key is still written by loadSession; it stays as the
      // record of "most recent story" for anything that wants it, but it no
      // longer drives navigation.
      const lastId = sessionStorage.getItem(`sixguys_tab_story_${user.id}`);
      if (!lastId) return;
      const target = savedSessions.find(s => s.id === lastId && !s.deleted_at);
      if (!target) {
        sessionStorage.removeItem(`sixguys_tab_story_${user.id}`);
        return;
      }
      // Only auto-resume if we're still on the home view (not deep-linked
      // somewhere else by PUSH_NAV / ?call=...).
      if (viewRef.current === 'home' || viewRef.current === 'rp' || !viewRef.current) {
        loadSession(target);
      }
    } catch (err) { console.warn('[AutoResume] Failed:', err); }
  }, [user?.id, savedSessions]);

  // ─── Spectate scroll behavior ────────────────────────────────────────────
  // - On the FIRST batch landing (initial open), jump to the bottom so the
  //   reader starts at the newest message and can scroll up for older.
  // - On subsequent updates, only auto-scroll to bottom if the reader is
  //   already pinned near the bottom (so they don't get yanked away while
  //   reading older content).
  //
  // useLayoutEffect runs synchronously after DOM mutation, BEFORE paint —
  // so the user never sees a flash of "scrolled to top." Belt-and-suspenders:
  // also re-attempt at RAF and 200ms to handle slow image-loads / layout shifts.
  useLayoutEffect(() => {
    const container = spectateScrollRef.current;
    if (!container || !spectatingStory?.id || spectateMessages.length === 0) return;

    if (!spectateInitialLoaded) {
      const pinToBottom = () => {
        const c = spectateScrollRef.current;
        if (c) c.scrollTop = c.scrollHeight;
      };
      pinToBottom();
      requestAnimationFrame(pinToBottom);
      // Late-arriving images / fonts can change scrollHeight after first paint —
      // one more pin at 200ms covers desktop where the layout settles slower
      const t1 = setTimeout(pinToBottom, 200);
      const t2 = setTimeout(pinToBottom, 600);
      setSpectateInitialLoaded(true);
      spectateAtBottomRef.current = true;
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else if (spectateAtBottomRef.current) {
      // New realtime message landed and reader is at the bottom — follow it
      container.scrollTop = container.scrollHeight;
    }
    // (If reader scrolled up, we leave them where they are.)
  }, [spectateMessages, spectatingStory?.id, spectateInitialLoaded]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LIVE SPECTATE — realtime subscription for spectated story updates
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!spectatingStory?.id) return;
    const storyId = spectatingStory.id;
    const channels = [];

    // ─── DM STORY LIVE SPECTATE ─────────────────────────────────────────
    if (spectatingStory._isDMStory) {
      const dmConvoId = spectatingStory._dmConvoId;
      const dmCharId = spectatingStory._dmCharId;
      const char = allChars.find(c => c.id === dmCharId);

      // Subscribe to new DM messages in real-time
      const playerName = spectatingStory._dmPlayerName || "Player";
      const dmMsgChannel = supabase.channel(`spectate-dm-msgs-${dmConvoId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'dm_messages',
          filter: `conversation_id=eq.${dmConvoId}`,
        }, (payload) => {
          const m = payload.new;
          if (!m) return;
          setSpectateMessages(prev => {
            if (prev.some(msg => msg.id === m.id)) return prev;
            const isUser = m.sender_type === "user";
            const msgChar = !isUser ? allChars.find(c => c.id === m.sender_id) : null;
            return [...prev, {
              id: m.id,
              charId: isUser ? "player" : m.sender_id,
              name: isUser ? playerName : (msgChar?.name || char?.name || "Character"),
              text: m.content,
              timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
              isPlayer: isUser,
              color: isUser ? t.accentSuccess : (msgChar?.color || char?.color || t.accentGold),
              avatar: isUser ? "👤" : (msgChar?.avatar || char?.avatar || "💬"),
            }];
          });
        })
        .subscribe();
      channels.push(dmMsgChannel);

      return () => channels.forEach(ch => supabase.removeChannel(ch)); // audit P2-5
    }

    // ─── REGULAR STORY LIVE SPECTATE ────────────────────────────────────
    // Subscribe to updates on this specific story row
    const channel = supabase.channel(`spectate-${storyId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'stories',
        filter: `id=eq.${storyId}`,
      }, async (payload) => {
        // Story was updated — re-fetch messages
        try {
          // First try story_messages table (used by some stories)
          const { data: liveMsgs } = await supabase
            .from("story_messages")
            .select("*")
            .eq("story_id", storyId)
            .order("created_at", { ascending: true });

          if (liveMsgs && liveMsgs.length > 0) {
            setSpectateMessages(liveMsgs.map((m) => ({
              id: m.id,
              charId: m.char_id,
              name: m.char_name || "Unknown",
              text: m.text,
              timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
              isPlayer: m.is_player || false,
              color: m.color,
              avatar: m.avatar,
            })));
          } else {
            // Fall back to stories.data.messages (embedded JSON)
            const newData = payload.new?.data || {};
            if (newData.messages && newData.messages.length > 0) {
              setSpectateMessages(newData.messages);
            } else {
              // Re-fetch full story if payload didn't include data
              const { data: fullStory } = await supabase
                .from("stories").select("data").eq("id", storyId).single();
              if (fullStory?.data?.messages) {
                setSpectateMessages(fullStory.data.messages);
              }
            }
          }
          // Update the spectating story metadata
          if (payload.new) {
            setSpectatingStory(prev => prev ? { ...prev, ...payload.new } : prev);
          }
        } catch (e) {
          console.warn("[Spectate] Live update fetch failed:", e);
        }
      })
      .subscribe();
    channels.push(channel);

    // Also subscribe to story_messages inserts for stories that use that table
    const msgChannel = supabase.channel(`spectate-msgs-${storyId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'story_messages',
        filter: `story_id=eq.${storyId}`,
      }, (payload) => {
        const m = payload.new;
        if (!m) return;
        setSpectateMessages(prev => {
          // Dedup check
          if (prev.some(msg => msg.id === m.id)) return prev;
          return [...prev, {
            id: m.id,
            charId: m.char_id,
            name: m.char_name || "Unknown",
            text: m.text,
            timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
            isPlayer: m.is_player || false,
            color: m.color,
            avatar: m.avatar,
          }];
        });
      })
      .subscribe();
    channels.push(msgChannel);

    return () => channels.forEach(ch => supabase.removeChannel(ch)); // audit P2-5
  }, [spectatingStory?.id]);

  const getProfile = async (userId) => {
    // Called from onAuthStateChange which already has a fresh session
    // Explicit columns (2026-10-19 audit): `phone` is leaving the public
    // SELECT grant, and select("*") would then fail for the whole row.
    let { data, error } = await supabase.from("profiles").select(PROFILE_COLUMNS).eq("id", userId).single();
    if (error && error.code === "PGRST116") {
      // No profile exists — create one. Use upsert with ignoreDuplicates
      // so we never overwrite an existing profile if there's a race condition
      const { data: newProfile } = await supabase
        .from("profiles")
        .upsert({ id: userId, display_name: "New Player", role: "guest" }, { onConflict: "id", ignoreDuplicates: true })
        .select()
        .single();
      return newProfile;
    }
    if (error) {
      // Non-PGRST116 error (e.g. RLS issue, network) — retry once
      console.warn("[getProfile] First attempt failed:", error.message, "— retrying");
      const retry = await supabase.from("profiles").select(PROFILE_COLUMNS).eq("id", userId).single();
      return retry.data || null;
    }
    return data;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTH HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  const handleSignUp = async () => {
    setAuthError("");
    if (!authEmail || !authPassword || !authDisplayName || !authPhone || !authInviteCode) {
      setAuthError("Please fill in all fields including invite code");
      return;
    }
    try {
      // Validate invite code BEFORE creating the account. Uses the
      // `validate_invite_code` SECURITY DEFINER RPC (added by
      // security_hardening_phase_a migration) so anonymous users can no
      // longer enumerate the entire invite_codes table — they only see
      // a yes/no for the specific code they entered.
      const trimmedCode = authInviteCode.trim().toUpperCase();
      // The invite code now travels WITH the signup as user metadata. The
      // authoritative gate is the handle_new_user trigger on auth.users
      // (migration 20260727053000), which validates and consumes the code in
      // the same transaction and raises — rolling the signup back — if it is
      // missing, unknown, or already used. Previously the check was a separate
      // client RPC that anyone could simply skip by calling /auth/v1/signup
      // directly with the public anon key.
      const { data, error } = await supabase.auth.signUp({
        email: authEmail,
        password: authPassword,
        options: {
          data: {
            invite_code: trimmedCode,
            display_name: authDisplayName,
            phone: authPhone,
            // v423: handle_new_user records the age gate in the same transaction
            // (the client insert below used to run as anon and was refused).
            confirmed_18: ageGateConfirmed === true,
          },
        },
      });
      if (error) {
        // The trigger's raise surfaces here as a signup failure.
        const msg = /invite code/i.test(error.message || '')
          ? "Invalid or already used invite code"
          : error.message;
        setAuthError(msg);
        return;
      }
      if (data.user) {
        // The trigger already created the profile (with role 'member'),
        // consumed the code and recorded the age gate. Nothing to insert or
        // redeem client-side. Email confirmation is required before the first
        // sign-in, so say so (2026-10-19 audit: the old copy said "sign in").
        setAuthEmail(""); setAuthPassword(""); setAuthDisplayName(""); setAuthPhone(""); setAuthInviteCode("");
        setAgeGateConfirmed(false);
        setLoginMode("signin");
        setAuthError("Sign up successful! Check your email for the confirmation link, then sign in.");
      }
    } catch (err) {
      console.error("Sign up error:", err);
      setAuthError("Sign up failed: " + (err.message || "Network error. Please try again."));
    }
  };

  const handleSignIn = async () => {
    setAuthError("");
    if (!authEmail || !authPassword) { setAuthError("Please enter email and password"); return; }
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
      if (error) { setAuthError(error.message); }
      else {
        // Remember email for auto-fill after session expiry re-login
        try { localStorage.setItem('sixguys_last_email', authEmail); } catch {}
        setAuthEmail(""); setAuthPassword("");
      }
    } catch (err) {
      console.error("Sign in error:", err);
      setAuthError("Sign in failed: " + (err.message || "Network error. Please try again."));
    }
  };

  const toggleMusicPlayer = useCallback(() => {
    setMusicPlayerVisible(prev => {
      const next = !prev;
      try { localStorage.setItem("mfml_music_visible", String(next)); } catch {}
      return next;
    });
  }, []);

  // ── Incoming call overlay handlers ──────────────────────────
  // Called by DMView for milestone (3rd convo) or random calls,
  // and by the phone_conversations realtime subscription below.
  const [incomingCallMeta, setIncomingCallMeta] = useState(null); // { reason, conversationId, conversationTitle }

  const handleIncomingCall = useCallback((character, meta = {}) => {
    // "Let characters call me" gate, checked at this single choke point so
    // DMView's client-side random timer and the milestone paths all respect
    // it. The preference is now the SYNCED notification_preferences column
    // (Nicole, 2026-07-31: "I still can't turn off the phone calls from
    // popping up in DMs randomly") — it used to be localStorage only, so it
    // was per-device and did not survive clearing site data. localStorage is
    // still honoured as a legacy opt-out so anyone who already turned it off
    // on this device stays off until their preference syncs.
    //
    // 2026-09-29, Nicole for the THIRD time: "what I want the MOST is to get
    // rid of the phone calls always popping up." The comment above has claimed
    // since July that the milestone paths respect this gate. They did not: the
    // check ran only for `reason === 'random'`, and DMView's two milestone
    // sites call onIncomingCall(char) with NO meta, so `reason` was undefined
    // and the ring went straight through with the setting turned OFF. The gate
    // now covers every unsolicited ring, which is what it always said it did.
    // An explicitly requested call (reason 'user') is not unsolicited.
    if (meta?.reason !== 'user') {
      if (notifPrefsRef.current?.random_calls === false) return;
      try { if (localStorage.getItem('mfml_random_calls_enabled') === 'false') return; } catch { /* default: allowed */ }
    }
    setIncomingCallChar(character);
    setIncomingCallMeta(meta);
  }, []);

  // ── Realtime: incoming phone_conversations rows trigger the ring overlay ──
  // The previous architecture relied on something else firing the popup;
  // wire it directly to the DB so cron/admin INSERTs become live calls.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`incoming-call-${user.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'phone_conversations', filter: `user_id=eq.${user.id}` },
        (payload) => {
          try {
            const row = payload.new || {};
            const charKey = row.char_key;
            if (!charKey) return;
            // Only STAGED CALLS ring. phone_conversations rows are text
            // THREADS — outreach texts and brand-new threads (call_mood NULL)
            // were popping the full-screen call UI over whatever tab the user
            // was on, and Answer then yanked them into the private thread.
            // A real staged call always has call_mood set (dial-reply reads it).
            if (!row.call_mood) return;
            // Respect the "let characters call me" preference. The
            // authoritative check lives in handleIncomingCall (synced pref);
            // this is just an early-out for the legacy per-device opt-out.
            try { if (localStorage.getItem('mfml_random_calls_enabled') === 'false') return; } catch {}
            const char = CHARACTERS.find(c => c.id === charKey) || { id: charKey, name: charKey };
            handleIncomingCall(char, { reason: 'random', conversationId: row.id || null });
          } catch (err) {
            console.error('[Realtime/phone_conversations] handler error:', err);
          }
        }
      )
      .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch {} };
  }, [user?.id, handleIncomingCall]);

  // Map character ID → PopupPhone CHARS key (e.g. 'kya_sg1' → 'kya')

  const handleAnswerIncomingCall = useCallback(() => {
    try {
      if (!incomingCallChar) return;
      const rawId = incomingCallChar.id || incomingCallChar.avatarId;
      // First try the explicit map; if not found, fall back to rawId itself
      // (the DB now uses simple keys like 'ford' that match PopupPhone CHARS directly)
      const phoneKey = charIdToPhoneKey(rawId) || rawId || null;
      if (!incomingCallMeta?.reason || incomingCallMeta.reason === 'milestone') {
        if (rawId) markPhoneRingShown(rawId);
      }
      const meta = incomingCallMeta;
      setIncomingCallChar(null);
      setIncomingCallMeta(null);
      if (phoneKey) {
        if (meta?.reason === 'random' && meta?.conversationId) {
          setPhoneInitialCharKey(phoneKey);
          setPhoneLinkedConvoId(meta.conversationId);
        } else {
          setPhoneInitialCharKey(phoneKey);
        }
        setPhoneOpen(true);
        setMainTab('phones');
      }
    } catch (err) {
      console.error('[IncomingCall] answer error:', err);
      setIncomingCallChar(null);
      setIncomingCallMeta(null);
    }
  }, [incomingCallChar, incomingCallMeta]);

  const handleDismissIncomingCall = useCallback(() => {
    if (incomingCallChar && (!incomingCallMeta?.reason || incomingCallMeta.reason === 'milestone')) {
      markPhoneRingShown(incomingCallChar.id);
    }
    setIncomingCallChar(null);
    setIncomingCallMeta(null);
  }, [incomingCallChar, incomingCallMeta]);

  const handleLogout = async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    // Audit P2-2/P2-8: the retry queues and the BYOK key must not survive
    // into another account's session — account B was re-sending account A's
    // queued messages (RLS-rejected every 30s, forever) and billing A's key.
    try {
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith('sixguys_pending_dm_msgs') || k.startsWith('sixguys_pending_channel_msgs')) localStorage.removeItem(k);
      }
      localStorage.removeItem('sixguys_apiKey');
    } catch { /* storage unavailable — nothing to clear */ }
    // Force-clear all state FIRST so the UI goes to login immediately
    setUser(null); setUserProfile(null); setCurrentPlayer(null);
    setMessages([]); setSceneChars([]); setBlockedChars([]); setPlayingAs(null);
    setBackstorySummary(null);
    clearTabStoryMarker();
    setActiveStoryId(null); setView("home"); setMainTab("rp");
    setSessionExpired(false); setConnectionStatus('connected');
    setSaveStatus('idle'); setLastSavedAt(null);
    accessTokenRef.current = null;
    retryQueueRef.current = [];
    // Nuke ALL Supabase auth tokens and app backups from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("sb-") || key.startsWith("sixguys_backup_") || key === 'sixguys_failed_writes') {
        localStorage.removeItem(key);
      }
    });
    // Also clear any legacy custom storage keys
    localStorage.removeItem('sixguys-auth');
    localStorage.removeItem('mfml_user_role');
    clearProfileCache();
    // Try signOut in background but don't wait on it — state is already cleared
    try {
      const signOutPromise = supabase.auth.signOut();
      const timeout = new Promise(resolve => setTimeout(resolve, 2000));
      await Promise.race([signOutPromise, timeout]);
    } catch (err) {
      console.error("Logout signOut error (already cleared):", err);
    }
  };

  const requestEmailReset = async () => {
    setAuthError("");
    if (!resetEmail) { setAuthError("Please enter your email address"); return; }
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: window.location.origin + "/reset",
    });
    if (error) { setAuthError(error.message); } else { setResetSent(true); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STORY PERSISTENCE (Update 0: Soft delete)
  // ═══════════════════════════════════════════════════════════════════════════
  // Auto-resume reads this per-tab marker. Clearing it whenever the user
  // deliberately LEAVES a story is what stops a later reload from dragging them
  // back into a story they'd already stepped out of.
  const clearTabStoryMarker = () => {
    try { if (user?.id) sessionStorage.removeItem(`sixguys_tab_story_${user.id}`); } catch {}
  };

  // Reported 2026-07-29: "when I first load the home page sometimes I don't see
  // the rp stories on my rp list."
  //
  // This function could not tell "you have no stories" apart from "the query
  // failed", and rendered both as an empty list:
  //
  //   • `const { data } = await ...` never destructured `error`. supabase-js
  //     resolves { data: null, error } instead of throwing, so every RLS
  //     refusal, 401, offline blip and 5xx arrived as data === null and went
  //     straight into setSavedSessions(data || []) — an empty list, reported as
  //     success. (Same root cause as the two P1s in the 07-28 handoff §3.)
  //   • The self-heal retry below was gated on `savedSessions.length > 0`, so
  //     it only ran once stories had ALREADY loaded in this session. On a cold
  //     load savedSessions is [], which is exactly when she sees this.
  //   • A dead session returned early without touching state, silently.
  //
  // Now: errors are detected, retried once after a session refresh regardless
  // of what was loaded before, and a still-failing load leaves the existing
  // list alone and raises storiesLoadFailed so the UI can say so instead of
  // implying the stories are gone.
  const loadStories = async () => {
    if (!user) return;
    const fetchStories = () => supabase
      .from("stories")
      .select("*")
      .eq("user_id", user.id)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    const sessionOk = await ensureSession();
    if (!sessionOk) {
      console.warn('[LoadStories] Session invalid — keeping the current list rather than blanking it');
      setStoriesLoadFailed(true);
      return;
    }

    let { data, error } = await fetchStories();

    // Retry when the read FAILED, or when it came back empty despite our having
    // had stories before (the old silent-RLS case). The failure retry is NOT
    // gated on savedSessions — a cold load is precisely when this matters.
    const looksWrong = error || ((!data || data.length === 0) && savedSessions.length > 0);
    if (looksWrong) {
      console.warn('[LoadStories] Retrying —', error ? `error: ${error.code} ${error.message}` : `got 0 stories but had ${savedSessions.length}`);
      if (await ensureSession()) {
        const retry = await fetchStories();
        if (!retry.error && retry.data) { data = retry.data; error = null; }
        else error = retry.error || error;
      }
    }

    if (error) {
      // Do NOT blank the list on failure — that is the bug. Keep whatever is on
      // screen and tell the user the refresh didn't land.
      console.error('[LoadStories] Failed:', error.code, error.message);
      setStoriesLoadFailed(true);
      return;
    }

    setStoriesLoadFailed(false);
    setStoriesLoaded(true);
    setSavedSessions(data || []);

    // Load shared_stories rows for user's stories so we know which are public
    if (data && data.length > 0) {
      try {
        const storyIds = data.map(s => s.id);
        const { data: sharedRows } = await supabase
          .from('shared_stories')
          .select('story_id, visibility, allow_spectate, allow_join')
          .in('story_id', storyIds);
        const map = {};
        for (const row of (sharedRows || [])) {
          map[row.story_id] = row;
        }
        setStorySharedMap(map);
      } catch (e) {
        console.warn('[LoadStories] shared_stories fetch failed:', e);
      }
    }

    // Also load stories the user has jumped into (participant but not owner)
    try {
      const { data: participations } = await supabase
        .from("story_participants")
        .select("story_id, character_id, joined_at")
        .eq("user_id", user.id);
      if (participations && participations.length > 0) {
        const jumpStoryIds = participations.map(p => p.story_id);
        const { data: jumpData } = await supabase
          .from("stories")
          .select("id, user_id, name, description, status, updated_at, last_active_at, message_count")
          .in("id", jumpStoryIds)
          .neq("user_id", user.id) // Exclude stories the user owns
          .is("deleted_at", null)
          .order("updated_at", { ascending: false });
        setJumpStories((jumpData || []).map(s => {
          const part = participations.find(p => p.story_id === s.id);
          return { ...s, _participantCharId: part?.character_id, _joinedAt: part?.joined_at };
        }));
      } else {
        setJumpStories([]);
      }
    } catch (jumpErr) {
      console.warn("[LoadStories] Jump stories query failed:", jumpErr);
    }
  };

  const saveStory = async ({ userInitiated = false } = {}) => {
    if (!user) return;
    // If we're a guest in someone else's story, don't try to update the stories table.
    // Our messages are already saved to story_messages in real-time.
    // Guard 1: explicit owner check
    if (activeStoryId && storyOwnerId && storyOwnerId !== user.id) {
      setSaveStatus('saved');
      return;
    }
    // Guard 2: only skip if savedSessions is loaded AND has 3+ entries (reducing false-positive
    // risk on new stories that haven't yet appeared in the loaded session list).
    // The immediate setSavedSessions() after insert above handles the common case.
    if (activeStoryId && savedSessions.length >= 3 && !savedSessions.find(s => s.id === activeStoryId)) {
      // Double-check ownership via storyOwnerId before skipping
      if (storyOwnerId && storyOwnerId !== user.id) {
        console.warn('[SaveStory] Confirmed guest story (storyOwnerId mismatch), skipping save');
        setSaveStatus('saved');
        return;
      }
      // If storyOwnerId isn't set, don't skip — let the update attempt run
      console.warn('[SaveStory] Story not in savedSessions but storyOwnerId unclear — attempting save anyway');
    }
    if (isSavingRef.current) {
      // Don't silently drop — reschedule so the latest messages always get saved
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveStory({ userInitiated }), 800);
      return;
    }
    isSavingRef.current = true;
    setSaveStatus('saving');
    // ── Critical fix: use messagesRef.current (always up-to-date) instead of the
    // stale `messages` closure captured when this function was created.  The 1.5s
    // setTimeout that calls saveStory captures the function from a previous render,
    // so `messages` may be missing any messages added after the timer was scheduled
    // (e.g. AI response, Nicole's realtime message).  messagesRef is updated on
    // every render via the useEffect([messages]) at line ~2279, so it is always
    // the current list by the time the timeout fires.
    // ── TRUNCATION GUARD ──────────────────────────────────────────────────
    // loadSession renders only the last RP_PAGE_SIZE messages for performance
    // (a 900-message story was locking up the UI), keeping the FULL history in
    // fullEmbeddedMsgsRef. But saveStory writes `data.messages` from
    // messagesRef — the visible page. So on any long story, the first autosave
    // after opening it rewrote the blob with ~100 messages and silently
    // discarded the other ~800. The empty-messages guard below never caught
    // this because the array was not empty, just short.
    //
    // Reunite them: anything in the known full history that is not in the
    // current window gets prepended, preserving chronology. Identity prefers
    // the row id and falls back to a text+speaker fingerprint, because
    // session-created messages carry synthetic ids.
    const visibleMessages = messagesRef.current;
    const currentMessages = (() => {
      const full = fullEmbeddedMsgsRef.current;
      if (!Array.isArray(full) || !Array.isArray(visibleMessages)) return visibleMessages;
      if (full.length <= visibleMessages.length) return visibleMessages;
      const have = new Set(visibleMessages.map(storyMsgFp));
      const older = full.filter(m => !have.has(storyMsgFp(m)));
      if (!older.length) return visibleMessages;
      console.log(`[SaveStory] Restoring ${older.length} paged-out message(s) so the blob keeps full history.`);
      return [...older, ...visibleMessages];
    })();
    // ── DATA-LOSS GUARD: never overwrite an existing story's blob with empty
    // messages. This is the root cause of "everything got lost visibly" — a
    // logout flicker, story switch, or unmount race clears `messages` to []
    // while a debounced save is still pending, and the save then clobbers a
    // 581-message blob with `messages: []`. Story_messages table fallback only
    // helps stories that have rows there; newer stories where messages live
    // exclusively in the blob are total loss when this fires.
    if (activeStoryId && currentMessages.length === 0 && !allowEmptySaveRef.current) {
      console.warn('[SaveStory] Refusing to save empty messages over an existing story (id=' + activeStoryId + ') — likely a stale-state race. Skipping.');
      isSavingRef.current = false;
      setSaveStatus('idle');
      return;
    }
    // Live incident 2026-07-30: Haley deleted the ONLY message (a starter she
    // didn't want) and the guard above refused every save — the starter
    // resurrected on each re-entry and the blob froze, taking cast edits with
    // it. A DELIBERATE delete arms this one-shot flag; races never do.
    if (allowEmptySaveRef.current) allowEmptySaveRef.current = false;
    // CAST PRESERVATION (audit: live incident 2026-07-27). A story's sceneChars
    // was persisted as [] while 59 AI replies sat in its history, so the client
    // stopped calling the AI entirely and every send got silence — no reply, no
    // error. Never write an empty cast over a story that demonstrably has one:
    // fall back to the characters who have actually spoken.
    const spokenCast = [...new Set(
      (currentMessages || [])
        .filter(m => m && !m.isPlayer && m.charId)
        .map(m => m.charId)
    )].filter(id => !(blockedChars || []).includes(id));
    const effectiveSceneChars = (Array.isArray(sceneChars) && sceneChars.length > 0)
      ? sceneChars
      : spokenCast;

    const storyData = {
      messages: currentMessages,
      sceneChars: effectiveSceneChars, blockedChars,
      sceneType, playingAs, currentPlayer, customChars, aiProvider,
      routeFlags: storyRouteFlagsRef.current,
      ...(backstorySummary ? { backstorySummary } : {}),
      ...(narratorMode ? { narratorMode: true, narratorPlayerName: narratorPlayerName || currentPlayer || '' } : {}),
      ...(routeDescription ? { routePrompt: routeDescription } : {}),
    };
    const name = currentSessionName || `${currentChar?.name || "RP"} - ${new Date().toLocaleDateString()}`;

    // Always backup to localStorage
    backupToLocalStorage('story', {
      storyId: activeStoryId, name, data: storyData, userId: user.id,
    });

    // Audit 2026-08-03 P0-2: snapshot the story identity BEFORE any await.
    // startNewStory/deleteStory/logout can null activeStoryId (and the
    // ref-sync effect nulls the ref) while this save is parked on
    // ensureSession() — the post-await read then saw null and INSERTed a
    // byte-identical twin story (the "Haley - 7/30/2026" ×2 pattern, 23/2
    // messages split across the pair). The snapshot pins the identity this
    // save was scheduled FOR.
    const storyIdAtEntry = activeStoryIdRef.current || activeStoryId;

    try {
      const ok = await ensureSession();
      if (!ok) {
        retryQueueRef.current.push({
          type: activeStoryId ? 'update' : 'insert',
          table: 'stories',
          id: activeStoryId,
          payload: activeStoryId
            ? { name, data: storyData, updated_at: new Date().toISOString() }
            : { user_id: user.id, name, data: storyData },
        });
        setSaveStatus('error');
        return;
      }

      // Read from the ref (synchronous) instead of the closure (stale). When
      // an INSERT just completed, setActiveStoryId is async — but we update
      // the ref synchronously below. Without this, two concurrent saves both
      // see activeStoryId=null in their closures and both INSERT, producing
      // the duplicate-stories pattern (e.g. 0b819177 + 96be7059 at +2s).
      // The pre-await snapshot wins over both: if the story was live when
      // this save started, this save belongs to THAT story even if the UI
      // moved on during the await.
      const effectiveStoryId = storyIdAtEntry || activeStoryIdRef.current || activeStoryId;
      if (effectiveStoryId) {
        // Use .select() to verify the update actually modified a row.
        // Without this, RLS can silently filter the row (0 rows affected, no error)
        // causing "shows saved but data vanishes" bug.
        const updatePayload = { name, data: storyData, updated_at: new Date().toISOString(), message_count: currentMessages.length };
        // Only update last_active_at when the USER sent a message (not AI auto-saves)
        if (userInitiated) updatePayload.last_active_at = new Date().toISOString();

        const { data: updatedRows, error } = await supabase
          .from("stories")
          .update(updatePayload)
          .eq("id", effectiveStoryId)
          .select('id');
        if (error) throw error;
        // Write-verification: if no rows were updated, session went stale mid-save
        if (!updatedRows || updatedRows.length === 0) {
          console.warn('[SaveStory] Update affected 0 rows — retrying with fresh session');
          const retryOk = await ensureSession();
          if (retryOk) {
            const { data: retryRows, error: retryErr } = await supabase
              .from("stories")
              .update(updatePayload)
              .eq("id", effectiveStoryId)
              .select('id');
            if (retryErr) throw retryErr;
            if (!retryRows || retryRows.length === 0) {
              console.error('[SaveStory] Retry also affected 0 rows — save lost');
              throw new Error('Session expired — story save could not be verified');
            }
          } else {
            throw new Error('Session expired — story save could not be verified');
          }
        }
      } else {
        const { data: row, error } = await supabase
          .from("stories")
          .insert({ user_id: user.id, name, data: storyData, last_active_at: new Date().toISOString(), message_count: currentMessages.length })
          .select()
          .single();
        if (error) throw error;
        if (row) {
          // Update the ref SYNCHRONOUSLY before any other save can fire. setActiveStoryId
          // is async; without the synchronous ref update, a concurrent save running in
          // the same render cycle sees activeStoryId=null and INSERTs again → duplicate.
          activeStoryIdRef.current = row.id;
          setActiveStoryId(row.id);
          // ── Critical fix: immediately add to savedSessions so Guard 2 never
          // falsely treats this story as a guest story on the very next autosave
          // (loadStories() is async and may not complete before the next save fires)
          setSavedSessions(prev => prev.find(s => s.id === row.id) ? prev : [...prev, { ...row, data: storyData }]);
          // Award XP for starting a new story (non-blocking)
          awardXP(user.id, 'start_story').catch(() => {});
          // Celebrate first story creation!
          const storyCount = savedSessions.length;
          if (storyCount === 0) {
            setShowConfetti(true);
            setToastMsg({ message: "Your first story has begun!", type: "celebration", icon: "✨" });
          } else {
            setToastMsg({ message: "Story saved", type: "success", icon: "💾" });
          }
          // New stories are PRIVATE by default — we intentionally do NOT
          // auto-share to Community Stories anymore. A story appears in
          // Community ONLY when the user explicitly shares it (the share
          // toggle, or "make public" on a DM→RP conversion). This is what
          // keeps private stories — including DM→private conversions — out
          // of the Community section entirely.
        }
      }
      setSaveStatus('saved');
      setLastSavedAt(new Date());
      localStorage.removeItem('sixguys_backup_story');
      await loadStories();
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      console.error('[SaveStory] Failed:', err.message, err.code, err.details, err);
      setSaveStatus('error');
      const idForRetry = activeStoryIdRef.current || activeStoryId;
      retryQueueRef.current.push({
        type: idForRetry ? 'update' : 'insert',
        table: 'stories',
        id: idForRetry,
        payload: idForRetry
          ? { name, data: storyData, updated_at: new Date().toISOString() }
          : { user_id: user.id, name, data: storyData },
      });
    } finally {
      isSavingRef.current = false;
    }
  };

  // Keep the settings-autosave effect's handle current (defined next to the
  // effect near the top of the component; see the comment there).
  saveStoryFnRef.current = saveStory;

  // Rebuild the cast from history when a story loads with an empty one. Guards
  // against any past write that dropped sceneChars: the characters who have
  // spoken in the transcript ARE the cast, minus anyone blocked.
  const castFromHistory = (msgs, blocked) => [...new Set(
    (msgs || []).filter(m => m && !m.isPlayer && m.charId).map(m => m.charId)
  )].filter(id => !(blocked || []).includes(id));

  const loadSession = async (s) => {
    const d = s.data || {};
    settingsLoadEpochRef.current = Date.now(); // suppress the settings-autosave echo
    // Set non-message state immediately so UI appears fast
    setBlockedChars(d.blockedChars || []);
    setSceneChars(
      (d.sceneChars && d.sceneChars.length)
        ? d.sceneChars
        : castFromHistory(d.messages, d.blockedChars)
    );
    setSceneType(d.sceneType || "chat");
    // CHOICES SURVIVE LOADS (live 2026-07-30, Haley: "character choices are
    // not being saved… the character I am playing is not being saved"). A
    // story blob saved before a choice existed must INHERIT the player's
    // current selection, not clobber it back to empty:
    //  - playingAs: an explicit story value wins; a story with none keeps the
    //    current pick — except narrator stories, where "nobody" is the design.
    //  - narratorMode may not survive alongside a picked character: the flag
    //    means "the AI runs the whole world BECAUSE nobody was chosen".
    setPlayingAs(prev => d.playingAs || (d.narratorMode ? null : prev));
    setCurrentSessionName(s.name || "");
    setCustomChars(d.customChars || []);
    if (d.aiProvider) setAiProvider(d.aiProvider);
    setStoryPreferences(s.story_preferences || {});
    setBackstorySummary(d.backstorySummary || null);
    setStoryOwnerId(s.user_id || user?.id || null);
    setActiveStoryId(s.id);
    setNarratorMode(!!d.narratorMode && !d.playingAs);
    setStoryIsAU(!!d.branchedFrom);
    setNarratorPlayerName(d.narratorPlayerName || '');
    if (d.routePrompt) setRouteDescription(d.routePrompt);
    setView("chat");
    setMainTab("rp");
    // Track the most recently opened story so we can auto-resume on
    // page reload — addresses the "messages save then vanish on reload"
    // symptom (you used to land on the home screen after refresh).
    try {
      if (s.id && user?.id) {
        localStorage.setItem(`sixguys_last_story_${user.id}`, s.id);
        // Per-TAB marker — this is what auto-resume reads. Survives a reload,
        // absent on a fresh open, so refreshing mid-scene returns you here but
        // launching the app does not.
        sessionStorage.setItem(`sixguys_tab_story_${user.id}`, s.id);
      }
    } catch {}

    // ── Persistent message recovery: merge blob + story_messages table ──────────
    // story_messages gets individual inserts in real-time; the blob is debounced.
    // If the blob save ever failed (Guard 2 race, session expiry, etc.) the blob
    // will be behind. Merging both ensures no message is ever lost on reload.
    const embeddedMsgs = (d.messages || []);
    // Stash the FULL blob into a ref so load-older can pull from it (old
    // stories' history lives in the blob, not story_messages).
    fullEmbeddedMsgsRef.current = embeddedMsgs;
    // For long stories the blob can have 1000+ messages — rendering all
    // of them was causing severe UI lag on heavy stories. Take only the
    // last RP_PAGE_SIZE for the immediate render; full history still lives
    // in the blob and in story_messages (paginated load-older below).
    const initialBlobMsgs = embeddedMsgs.length > RP_PAGE_SIZE
      ? embeddedMsgs.slice(-RP_PAGE_SIZE)
      : embeddedMsgs;
    setMessages(initialBlobMsgs);
    // If the blob already exceeds a page, we have older messages to load.
    setHasMoreRpMessages(embeddedMsgs.length > RP_PAGE_SIZE);

    if (s.id) {
      try {
        const { data: liveMsgsDesc } = await supabase
          .from("story_messages")
          .select("*")
          .eq("story_id", s.id)
          .order("created_at", { ascending: false })
          .limit(RP_PAGE_SIZE);
        const liveMsgs = liveMsgsDesc ? [...liveMsgsDesc].reverse() : [];
        // hasMore = the DB returned a full page (so older exists)
        if ((liveMsgsDesc || []).length >= RP_PAGE_SIZE) setHasMoreRpMessages(true);

        if (liveMsgs && liveMsgs.length > 0) {
          const dbMsgs = liveMsgs.map((m) => ({
            id: m.id,
            charId: m.char_id,
            name: m.char_name || "Unknown",
            text: m.text,
            timestamp: new Date(m.created_at).toLocaleString("en-US", {
              timeZone: "America/New_York", month: "short", day: "numeric",
              year: "numeric", hour: "2-digit", minute: "2-digit",
              hour12: true, timeZoneName: "short",
            }),
            createdAt: m.created_at,
            isPlayer: m.is_player || false,
            color: m.color,
            avatar: m.avatar,
          }));

          // ── Functional merge: use the current React state (prev) so we don't
          // overwrite any messages the realtime subscription may have added
          // while the story_messages fetch was in flight.
          // Build the final merged array from embeddedMsgs + dbMsgs (deduped),
          // then inside the functional updater also include any extra realtime messages.
          const embeddedTexts = new Set(embeddedMsgs.map(m => m.text?.substring(0, 80)));
          const recovered = dbMsgs.filter(m => !embeddedTexts.has(m.text?.substring(0, 80)));
          const baseMerged = [...embeddedMsgs, ...recovered].sort((a, b) => {
            const aT = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bT = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return aT - bT;
          });

          if (recovered.length > 0) {
            console.log(`[LoadSession] Recovered ${recovered.length} message(s) from story_messages`);
            // Functional updater: merge baseMerged with whatever realtime added to state
            // while the story_messages fetch was in flight. Avoids overwriting realtime msgs.
            setMessages(prev => {
              const prevTexts = new Set(prev.map(m => m.text?.substring(0, 80)));
              const extras = baseMerged.filter(m => !prevTexts.has(m.text?.substring(0, 80)));
              if (extras.length === 0) return prev; // realtime already has everything
              return [...prev, ...extras].sort((a, b) => {
                const aT = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bT = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return aT - bT;
              });
            });
            // ── Immediately sync the blob so the next autosave (messagesRef.current)
            // starts from a complete base. Using s.id and baseMerged directly avoids
            // the stale saveStory closure that caused the original data loss bug.
            try {
              const mergedData = { ...d, messages: baseMerged };
              await supabase
                .from("stories")
                .update({ data: mergedData, updated_at: new Date().toISOString(), message_count: baseMerged.length })
                .eq("id", s.id);
              console.log(`[LoadSession] Blob synced: ${baseMerged.length} messages (${recovered.length} recovered)`);
            } catch (syncErr) {
              console.warn("[LoadSession] Could not sync merged messages to blob:", syncErr);
            }
          }
        } // end if (liveMsgs && liveMsgs.length > 0)
      } catch (e) {
        console.warn("[LoadSession] story_messages merge failed — using blob only:", e);
      }
    }
  };

  // Soft delete (Update 0)
  const deleteStory = async (id) => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const ok = await ensureSession();
    if (!ok) return;
    // Both halves of this used to fail SILENTLY and the errors were never
    // checked, so a deleted story came straight back on the next load and
    // stayed in Community forever:
    //   - `update stories set deleted_at` was REJECTED by RLS (42501): the
    //     SELECT policy requires deleted_at IS NULL, so the updated row no
    //     longer satisfies it.
    //   - `delete from shared_stories` matched zero rows because that table
    //     had no DELETE policy at all (RLS on + no policy = silent no-op).
    // soft_delete_story is SECURITY DEFINER, checks ownership itself, and does
    // both halves atomically so a story can never be half-deleted.
    const { data, error } = await supabase.rpc('soft_delete_story', { p_story_id: id });
    if (error || !data?.ok) {
      console.error('[deleteStory] failed:', error?.message || data?.reason);
      setShowConfirmModal({
        message: `Couldn't delete that story: ${error?.message || data?.reason || 'unknown error'}`,
        onConfirm: () => setShowConfirmModal(null),
      });
      return;
    }
    setStorySharedMap(prev => { const next = { ...prev }; delete next[id]; return next; });
    if (activeStoryId === id) setActiveStoryId(null);
    await loadStories();
  };

  const renameStory = async (id, newName) => {
    if (!newName.trim()) return;
    const ok = await ensureSession();
    if (!ok) return;
    // saveStory documents .select('id') verification as mandatory on this
    // exact table for the RLS-zero-rows case; the rename skipped it (audit
    // SW-9). A rename during session expiry then showed the new name in the
    // header while loadStories re-read the OLD one into the sidebar — two
    // names for one story, zero feedback — and after the next open the
    // rename had silently never happened.
    const { data: renamed, error: renameErr } = await supabase
      .from("stories")
      .update({ name: newName.trim(), updated_at: new Date().toISOString() })
      .eq("id", id)
      .select("id");
    if (renameErr || !renamed?.length) {
      setToastMsg({
        message: `Couldn't rename the story${renameErr ? `: ${renameErr.message.slice(0, 60)}` : ''}. Try again.`,
        type: 'error', icon: '⚠️',
      });
      return;
    }
    // If this is the active story, update the session name too
    if (activeStoryId === id) setCurrentSessionName(newName.trim());
    setRenamingStoryId(null);
    setRenameText("");
    await loadStories();
  };

  // ─── Branch Story (AU fork) ──────────────────────────────────────────────
  // Creates a new story branched from an existing one at a chosen message index.
  // Everything up to that point is kept; everything after is discarded.
  // The new story is saved as a separate entry so the original is untouched.
  const branchStory = async (sourceStory, branchAtIndex) => {
    if (!user || !sourceStory?.data?.messages) return;
    const d = sourceStory.data;
    const branchedMessages = (d.messages || []).slice(0, branchAtIndex + 1);
    const branchName = `AU: ${sourceStory.name || "Story"} (branched at msg ${branchAtIndex + 1})`;
    const branchData = {
      messages: branchedMessages,
      sceneChars: d.sceneChars || [],
      sceneType: d.sceneType || "chat",
      playingAs: d.playingAs || null,
      currentPlayer: d.currentPlayer || currentPlayer,
      customChars: d.customChars || [],
      aiProvider: d.aiProvider || aiProvider,
      branchedFrom: { storyId: sourceStory.id, storyName: sourceStory.name, branchPoint: branchAtIndex },
    };
    // Save as a new story
    const ok = await ensureSession();
    if (!ok) return;
    const { data: newStory, error } = await supabase
      .from("stories")
      .insert({ user_id: user.id, name: branchName, data: branchData })
      .select()
      .single();
    if (error) { console.error("Branch story failed:", error); return; }
    // Load the branched story
    settingsLoadEpochRef.current = Date.now();
    setMessages(branchedMessages);
    setBlockedChars(d.blockedChars || []);
    setSceneChars((d.sceneChars && d.sceneChars.length) ? d.sceneChars : castFromHistory(branchedMessages, d.blockedChars));
    setSceneType(d.sceneType || "chat");
    setPlayingAs(prev => d.playingAs || prev);
    setCurrentSessionName(branchName);
    setStoryIsAU(true); // a branch is an AU by construction
    setCustomChars(d.customChars || []);
    if (d.aiProvider) setAiProvider(d.aiProvider);
    setActiveStoryId(newStory.id);
    activeStoryIdRef.current = newStory.id;
    setBranchingStory(null);
    try {
      if (user?.id) {
        localStorage.setItem(`sixguys_last_story_${user.id}`, newStory.id);
        sessionStorage.setItem(`sixguys_tab_story_${user.id}`, newStory.id);
      }
    } catch {}
    setView("chat");
    setMainTab("rp");
    await loadStories();
  };

  const startNewStory = () => {
    // P0-2: a debounced save scheduled for the OLD story must not fire after
    // the id is nulled — it would INSERT a twin with the old closure's data.
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    setMessages([]); setSceneChars([]); setBlockedChars([]); setPlayingAs(null);
    setBackstorySummary(null);
    setCurrentSessionName("");
    setCustomChars([]); setActiveStoryId(null);
    setFeedbackContexts({}); setStoryPreferences({});
    setRouteDescription('');
    clearTabStoryMarker();
    setView("home");
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STORY SHARING & SPECTATING
  // ═══════════════════════════════════════════════════════════════════════════
  const toggleStoryPrivacy = async (storyId) => {
    if (!user || !storyId) return;
    const ok = await ensureSession();
    if (!ok) return;
    const isCurrentlyPublic = !!storySharedMap[storyId];
    if (isCurrentlyPublic) {
      // Make private — remove from shared_stories. SW-1: the delete was
      // fire-and-blind, so a failed delete flipped the pill to Private while
      // the story stayed publicly spectatable — the worst direction for an
      // NSFW story. Verify rows actually went away before trusting it.
      const { data: deleted, error: delErr } = await supabase
        .from('shared_stories').delete().eq('story_id', storyId).select('story_id');
      if (delErr || !deleted || deleted.length === 0) {
        console.error('[Story privacy] make-private failed:', delErr?.message || 'no rows deleted');
        setRpSendError('Couldn\'t make the story private — it is still visible in Community Stories. Try again.');
        return;
      }
      setStorySharedMap(prev => { const next = { ...prev }; delete next[storyId]; return next; });
    } else {
      // Make public — add to shared_stories
      const { data: newRow, error: upErr } = await supabase.from('shared_stories').upsert({
        story_id: storyId, visibility: 'public', allow_spectate: true, allow_join: false,
      }, { onConflict: 'story_id' }).select().single();
      if (upErr || !newRow) {
        console.error('[Story privacy] make-public failed:', upErr?.message || 'no row returned');
        setRpSendError('Couldn\'t share the story — it is still private. Try again.');
        return;
      }
      setStorySharedMap(prev => ({ ...prev, [storyId]: newRow }));
      // Notify community — only now that the share is REAL (SW-1: this used
      // to fire even when the upsert failed, pushing at an unshared story).
      const storyName = savedSessions.find(s => s.id === storyId)?.name || 'a story';
      sendPushNotification(null, {
        title: '📖 New community story!',
        body: `${currentPlayer || 'Someone'} shared "${storyName}"`,
        url: '/',
        exclude_user_id: user.id,
        category: 'community',
      });
    }
  };

  const _shareStory = async (storyId, visibility = "public", allowSpectate = true, allowJoin = false) => {
    if (!user || !storyId) return;
    const ok = await ensureSession();
    if (!ok) return;
    const { error } = await supabase.from("shared_stories").upsert({
      story_id: storyId, visibility, allow_spectate: allowSpectate, allow_join: allowJoin,
    }, { onConflict: "story_id" });
    if (error) console.error("Error sharing story:", error);
    else if (visibility === "public") {
      // Notify other users that a new story was shared to the community
      const storyName = savedSessions.find(s => s.id === storyId)?.name || "a story";
      sendPushNotification(null, {
        title: `📖 New community story!`,
        body: `${currentPlayer || "Someone"} shared "${storyName}"`,
        url: '/',
        exclude_user_id: user.id,
        category: 'community',
      });
    }
  };

  // ─── Private Story Invites ───────────────────────────────────────────────
  const loadStoryInvites = useCallback(async (storyId) => {
    if (!user || !storyId) return;
    const ok = await ensureSession();
    if (!ok) return;
    // invitee_id carries two foreign keys (auth.users and public.profiles); the
    // embed names the profiles one so PostgREST never has to guess (PGRST201).
    const { data, error } = await supabase
      .from('story_invites')
      .select('*, invitee:profiles!story_invites_invitee_profile_fkey(id, display_name, avatar_url)')
      .eq('story_id', storyId)
      .eq('inviter_id', user.id)
      .order('created_at', { ascending: false });
    if (error) {
      // supabase-js resolves, never throws — an unlogged error here is an
      // invite list that is empty forever (it was, for months: PGRST200 on every boot).
      console.error('Error loading story invites:', error);
      logClientError('story_invites_load', error, `${error.code || ''} ${error.details || ''}`.trim());
    }
    setStoryInvitesMap(prev => ({ ...prev, [storyId]: data || [] }));
  }, [user?.id]);

  const searchUsersForInvite = useCallback(async (query) => {
    if (!query || query.length < 2) { setInviteSearchResults([]); return; }
    const { data } = await supabase
      .from('profiles')
      .select('id, display_name, avatar_url')
      .ilike('display_name', `%${query}%`)
      .neq('id', user?.id)
      .limit(8);
    setInviteSearchResults(data || []);
  }, [user?.id]);

  const sendStoryInvite = async (storyId, inviteeId, opts = {}) => {
    if (!user || !storyId || !inviteeId) return;
    const ok = await ensureSession();
    if (!ok) return;
    const { error } = await supabase.from('story_invites').upsert({
      story_id: storyId,
      inviter_id: user.id,
      invitee_id: inviteeId,
      can_join: opts.canJoin ?? false,
      allowed_characters: opts.allowedCharacters ?? [],
      blocked_characters: opts.blockedCharacters ?? [],
      status: 'active',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'story_id,invitee_id' });
    if (!error) await loadStoryInvites(storyId);
  };

  const updateStoryInvite = async (storyId, inviteId, updates) => {
    if (!user) return;
    await supabase.from('story_invites').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', inviteId).eq('inviter_id', user.id);
    await loadStoryInvites(storyId);
  };

  const revokeStoryInvite = async (storyId, inviteId) => {
    if (!user) return;
    await supabase.from('story_invites').update({ status: 'revoked', updated_at: new Date().toISOString() }).eq('id', inviteId).eq('inviter_id', user.id);
    await loadStoryInvites(storyId);
  };

  // Load stories the current user was invited to
  const loadInvitedStories = useCallback(async () => {
    if (!user) return;
    const ok = await ensureSession();
    if (!ok) return;
    // Same two-key column on the inviter side — name the profiles key explicitly.
    // The story embed reads through can_access_story, which counts an active
    // invite from the owner as a grant; without that the card renders null.
    const { data, error } = await supabase
      .from('story_invites')
      .select('*, story:story_id(id, name, data, user_id, updated_at), inviter:profiles!story_invites_inviter_profile_fkey(id, display_name, avatar_url)')
      .eq('invitee_id', user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error loading invited stories:', error);
      logClientError('invited_stories_load', error, `${error.code || ''} ${error.details || ''}`.trim());
    }
    setInvitedStories(data || []);
  }, [user?.id]);

  useEffect(() => { loadInvitedStories(); }, [loadInvitedStories]);

  const handleSpectateStory = async (storyItem) => {
    const storyRef = storyItem.stories || storyItem;
    await ensureSession();

    // ─── DM STORY SPECTATE ──────────────────────────────────────────────
    if (storyRef._isDMStory) {
      const dmConvoId = storyRef._dmConvoId;
      const dmCharId = storyRef._dmCharId;
      const char = allChars.find(c => c.id === dmCharId);

      // Fetch the conversation to get the player's identity
      let playerName = "Player";
      try {
        const { data: convoData } = await supabase
          .from("dm_conversations")
          .select("identity_type, identity_data, user_id")
          .eq("id", dmConvoId)
          .single();
        if (convoData?.identity_data) {
          playerName = convoData.identity_data.characterName || convoData.identity_data.name || "Player";
        }
        // If still generic, try to get the user's profile display name
        if (playerName === "Player" && convoData?.user_id) {
          const { data: prof } = await supabase.from("profiles").select("display_name").eq("id", convoData.user_id).single();
          if (prof?.display_name) playerName = prof.display_name;
        }
      } catch (e) { /* use fallback */ }

      // Build a pseudo-story object for the spectate view
      const dmStory = {
        ...storyRef,
        _isDMStory: true,
        _dmConvoId: dmConvoId,
        _dmCharId: dmCharId,
        _dmPlayerName: playerName,
      };
      setSpectatingStory(dmStory);
      // Reset pagination state for the new spectate session
      setSpectateInitialLoaded(false);
      setSpectateHasMore(false);
      spectateAtBottomRef.current = true;

      // Last page only — paginated load on scroll-to-top
      try {
        const { data: dmMsgs } = await supabase
          .from("dm_messages")
          .select("*")
          .eq("conversation_id", dmConvoId)
          .order("created_at", { ascending: false })
          .limit(SPECTATE_PAGE_SIZE + 1);
        const fetched = dmMsgs || [];
        const hasMore = fetched.length > SPECTATE_PAGE_SIZE;
        const slice = hasMore ? fetched.slice(0, SPECTATE_PAGE_SIZE) : fetched;
        setSpectateHasMore(hasMore);
        if (slice.length > 0) {
          setSpectateMessages(slice.slice().reverse().map((m) => {
            const isUser = m.sender_type === "user";
            const msgChar = !isUser ? allChars.find(c => c.id === m.sender_id) : null;
            return {
              id: m.id,
              charId: isUser ? "player" : m.sender_id,
              name: isUser ? playerName : (msgChar?.name || char?.name || "Character"),
              text: m.content,
              timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
              createdAt: m.created_at,
              isPlayer: isUser,
              color: isUser ? t.accentSuccess : (msgChar?.color || char?.color || t.accentGold),
              avatar: isUser ? "👤" : (msgChar?.avatar || char?.avatar || "💬"),
            };
          }));
        } else {
          setSpectateMessages([]);
        }
      } catch (e) {
        console.warn("[Spectate DM] Failed to fetch messages:", e);
        setSpectateMessages([]);
      }
      setMainTab("rp"); setView("spectate");
      return;
    }

    // ─── REGULAR STORY SPECTATE ─────────────────────────────────────────
    // Fetch the FULL story data on-demand (list view no longer includes the data column for performance)
    let story = storyRef;
    try {
      const { data: fullStory, error } = await supabase
        .from("stories")
        .select("*")
        .eq("id", storyRef.id)
        .single();
      if (!error && fullStory) story = fullStory;
    } catch (e) {
      console.warn("[Spectate] Could not fetch full story, using cached:", e);
    }

    setSpectatingStory(story);
    // Reset pagination state on each new spectate session
    setSpectateInitialLoaded(false);
    setSpectateHasMore(false);
    spectateAtBottomRef.current = true;

    // Load only the LAST page of messages on open. Older pages load on
    // scroll-to-top. For very long stories this avoids fetching thousands of
    // rows + dropping the user at the top of a wall of text.
    try {
      // Fetch newest page (DESC) then reverse to display chronologically.
      // Pull SPECTATE_PAGE_SIZE + 1 to know if there's more behind it.
      const { data: liveMsgs } = await supabase
        .from("story_messages")
        .select("*")
        .eq("story_id", story.id)
        .order("created_at", { ascending: false })
        .limit(SPECTATE_PAGE_SIZE + 1);

      const fetched = liveMsgs || [];
      const hasMore = fetched.length > SPECTATE_PAGE_SIZE;
      const slice = hasMore ? fetched.slice(0, SPECTATE_PAGE_SIZE) : fetched;
      // Reverse to chronological for display
      const dbMsgs = slice.slice().reverse().map((m) => ({
        id: m.id,
        charId: m.char_id,
        name: m.char_name || "Unknown",
        text: m.text,
        timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
        createdAt: m.created_at,
        isPlayer: m.is_player || false,
        color: m.color,
        avatar: m.avatar,
      }));

      // If the DB had no rows at all, fall back to the embedded data blob
      // (older stories that predate story_messages).
      if (dbMsgs.length === 0) {
        const d = story.data || {};
        setSpectateMessages((d.messages || []).slice(-SPECTATE_PAGE_SIZE));
        setSpectateHasMore((d.messages || []).length > SPECTATE_PAGE_SIZE);
      } else {
        setSpectateMessages(dbMsgs);
        setSpectateHasMore(hasMore);
      }
    } catch (e) {
      console.warn("[Spectate] Failed to load page:", e);
      const d = story.data || {};
      setSpectateMessages((d.messages || []).slice(-SPECTATE_PAGE_SIZE));
      setSpectateHasMore((d.messages || []).length > SPECTATE_PAGE_SIZE);
    }
    setMainTab("rp"); setView("spectate");
  };

  // Load the next older page of spectate messages (story_messages table).
  // Pinned to scrollHeight-restore so the view doesn't jump.
  const loadOlderSpectateMessages = async () => {
    if (!spectatingStory?.id || spectateLoadingOlder || !spectateHasMore) return;
    const oldestLoaded = spectateMessages[0];
    if (!oldestLoaded?.createdAt) {
      // Embedded-data fallback path: just expand the slice
      const d = spectatingStory.data || {};
      const all = d.messages || [];
      const have = spectateMessages.length;
      const nextSlice = all.slice(Math.max(0, all.length - have - SPECTATE_PAGE_SIZE));
      const container = spectateScrollRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;
      const prevScrollTop = container?.scrollTop || 0;
      setSpectateMessages(nextSlice);
      setSpectateHasMore(nextSlice.length < all.length);
      requestAnimationFrame(() => {
        if (container) {
          const newHeight = container.scrollHeight;
          container.scrollTop = prevScrollTop + (newHeight - prevScrollHeight);
        }
      });
      return;
    }

    setSpectateLoadingOlder(true);
    const container = spectateScrollRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;
    const prevScrollTop = container?.scrollTop || 0;

    try {
      const isDM = !!spectatingStory._isDMStory;
      const tbl = isDM ? "dm_messages" : "story_messages";
      const idCol = isDM ? "conversation_id" : "story_id";
      const idVal = isDM ? spectatingStory._dmConvoId : spectatingStory.id;

      const { data: olderRaw } = await supabase
        .from(tbl)
        .select("*")
        .eq(idCol, idVal)
        .lt("created_at", oldestLoaded.createdAt)
        .order("created_at", { ascending: false })
        .limit(SPECTATE_PAGE_SIZE + 1);

      const fetched = olderRaw || [];
      const hasMoreBeyond = fetched.length > SPECTATE_PAGE_SIZE;
      const slice = hasMoreBeyond ? fetched.slice(0, SPECTATE_PAGE_SIZE) : fetched;
      const playerName = isDM ? (spectatingStory._dmPlayerName || "Player") : null;
      const dmCharId = isDM ? spectatingStory._dmCharId : null;
      const dmChar = dmCharId ? allChars.find(c => c?.id === dmCharId) : null;

      const olderMapped = slice.slice().reverse().map((m) => {
        if (isDM) {
          const isUser = m.sender_type === "user";
          const msgChar = !isUser ? allChars.find(c => c?.id === m.sender_id) : null;
          return {
            id: m.id,
            charId: isUser ? "player" : m.sender_id,
            name: isUser ? playerName : (msgChar?.name || dmChar?.name || "Character"),
            text: m.content,
            timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
            createdAt: m.created_at,
            isPlayer: isUser,
            color: isUser ? t.accentSuccess : (msgChar?.color || dmChar?.color || t.accentGold),
            avatar: isUser ? "👤" : (msgChar?.avatar || dmChar?.avatar || "💬"),
          };
        }
        return {
          id: m.id,
          charId: m.char_id,
          name: m.char_name || "Unknown",
          text: m.text,
          timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
          createdAt: m.created_at,
          isPlayer: m.is_player || false,
          color: m.color,
          avatar: m.avatar,
        };
      });

      setSpectateMessages(prev => [...olderMapped, ...prev]);
      setSpectateHasMore(hasMoreBeyond);

      // Restore scroll so the view doesn't jump to the new top
      requestAnimationFrame(() => {
        if (container) {
          const newHeight = container.scrollHeight;
          container.scrollTop = prevScrollTop + (newHeight - prevScrollHeight);
        }
      });
    } catch (e) {
      console.warn("[Spectate] Load older failed:", e);
    } finally {
      setSpectateLoadingOlder(false);
    }
  };

  const handleJoinStory = async (storyItem, characterId) => {
    const storyRef = storyItem.stories || storyItem;
    if (!user || !storyRef.id) return;
    const ok = await ensureSession();
    if (!ok) return;

    // Fetch full story data on-demand (list view no longer includes the data column)
    let story = storyRef;
    try {
      const { data: fullStory, error } = await supabase
        .from("stories")
        .select("*")
        .eq("id", storyRef.id)
        .single();
      if (!error && fullStory) story = fullStory;
    } catch (e) {
      console.warn("[JoinStory] Could not fetch full story, using cached:", e);
    }

    // ── Character persistence: check if we have a previous participation record ──
    let resolvedCharId = characterId;
    if (!resolvedCharId) {
      try {
        const { data: existingPart } = await supabase
          .from("story_participants")
          .select("character_id")
          .eq("story_id", storyRef.id)
          .eq("user_id", user.id)
          .maybeSingle();
        if (existingPart?.character_id) {
          resolvedCharId = existingPart.character_id;
          console.log("[JoinStory] Restored previous character:", resolvedCharId);
        }
      } catch (e) {
        console.warn("[JoinStory] Could not check participation:", e);
      }
    }

    // Note: StoryBrowser already inserts into story_participants before calling this,
    // so we just load the story data here — no duplicate insert needed.
    const d = story.data || {};
    storyRouteFlagsRef.current = (d.routeFlags && typeof d.routeFlags === 'object') ? d.routeFlags : {};

    // ── Merge story_messages table with embedded data (Bug #2 fix) ──
    // The spectate view does this merge but handleJoinStory previously did not,
    // causing guest messages (only saved to story_messages) to be invisible on reload.
    let mergedMessages = d.messages || [];
    try {
      // Newest-first with an explicit limit, reversed back for merging
      // (audit PERF-6). Unbounded ascending meant hosted PostgREST's hard
      // 1000-row cap kept the OLDEST rows and silently dropped the NEWEST
      // ones for whoever joined a long story — and re-downloaded the whole
      // transcript (~570 KB) on every join either way.
      const { data: liveMsgsDesc } = await supabase
        .from("story_messages")
        .select("*")
        .eq("story_id", story.id)
        .order("created_at", { ascending: false })
        .limit(500);
      const liveMsgs = (liveMsgsDesc || []).slice().reverse();

      if (liveMsgs && liveMsgs.length > 0) {
        const dbMsgs = liveMsgs.map((m) => ({
          id: m.id,
          charId: m.char_id,
          name: m.char_name || "Unknown",
          text: m.text,
          timestamp: new Date(m.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
          createdAt: m.created_at,
          isPlayer: m.is_player || false,
          color: m.color,
          avatar: m.avatar,
        }));
        // Merge: start with embedded, add any DB messages not already present (by text dedup)
        const embeddedTexts = new Set(mergedMessages.map(m => m.text?.substring(0, 80)));
        const newFromDb = dbMsgs.filter(m => !embeddedTexts.has(m.text?.substring(0, 80)));
        mergedMessages = [...mergedMessages, ...newFromDb].sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return aTime - bTime;
        });
        console.log(`[JoinStory] Merged ${newFromDb.length} messages from story_messages table`);
      }
    } catch (e) {
      console.warn("[JoinStory] Could not fetch story_messages for merge:", e);
    }

    settingsLoadEpochRef.current = Date.now();
    setMessages(mergedMessages);
    setBlockedChars(d.blockedChars || []);
    setSceneChars((d.sceneChars && d.sceneChars.length) ? d.sceneChars : castFromHistory(mergedMessages, d.blockedChars));
    setSceneType(d.sceneType || "chat"); setPlayingAs(prev => resolvedCharId || d.playingAs || prev);
    setCurrentSessionName(story.name || "");
    setStoryIsAU(!!d.branchedFrom);
    setCustomChars(d.customChars || []);
    if (d.aiProvider) setAiProvider(d.aiProvider);
    setBackstorySummary(d.backstorySummary || null);
    setStoryOwnerId(story.user_id || null); // Track owner for push notifications
    // Auto-pause AI when joining someone else's story so humans can RP together
    if (story.user_id && story.user_id !== user.id) {
      setAiPaused(true);
    }
    // Clear stale recap/intel from previous story
    setSceneRecap(null);
    setStoryIntel(null);
    setActiveStoryId(story.id); setMainTab("rp"); setView("chat");
  };

  const exportSessions = () => {
    const blob = new Blob([JSON.stringify(savedSessions, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "sixguys_rp_sessions.json"; a.click();
  };

  // Bug #4 fix: Persist imported sessions to Supabase (not just local state)
  const importSessions = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const imported = JSON.parse(ev.target.result);
        if (!Array.isArray(imported) || imported.length === 0) return;

        // Persist each imported story to Supabase
        const toInsert = imported.map(s => ({
          user_id: user.id,
          name: s.name || `Imported - ${new Date().toLocaleDateString()}`,
          data: s.data || s, // handle both { name, data } and raw data formats
          updated_at: s.updated_at || new Date().toISOString(),
        }));

        const { data: rows, error } = await supabase
          .from('stories')
          .insert(toInsert)
          .select();

        if (error) {
          console.error('[Import] Failed to persist to Supabase:', error);
          setToastMsg({ message: "Import failed — couldn't save to cloud", type: "error" });
          return;
        }

        // Update local state with the Supabase-generated rows (which have real IDs)
        if (rows?.length > 0) {
          setSavedSessions(prev => [...prev, ...rows]);
          setToastMsg({ message: `Imported ${rows.length} ${rows.length === 1 ? 'story' : 'stories'}`, type: "success", icon: "📥" });
        }
      } catch (err) {
        console.error('[Import] Parse error:', err);
        setToastMsg({ message: "Import failed — invalid file format", type: "error" });
      }
    };
    reader.readAsText(file);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  // Load older RP messages — paginated, called when the user scrolls near
  // the top. Fetches a page of messages older than the oldest currently
  // loaded one, prepends them to state, and restores scroll position so
  // the user doesn't get teleported. Mirrors ChannelView's pattern.
  const loadOlderRpMessages = useCallback(async () => {
    const sid = activeStoryIdRef.current;
    if (!sid || loadingOlderRp || !hasMoreRpMessages) return;
    const oldest = messagesRef.current?.[0];
    setLoadingOlderRp(true);
    try {
      // Source A: story_messages table (only works if oldest has createdAt)
      let dbOlder = [];
      if (oldest?.createdAt) {
        const { data: olderDesc, error } = await supabase
          .from("story_messages")
          .select("*")
          .eq("story_id", sid)
          .lt("created_at", oldest.createdAt)
          .order("created_at", { ascending: false })
          .limit(RP_PAGE_SIZE);
        if (error) throw error;
        dbOlder = (olderDesc || []).slice().reverse().map((m) => ({
          id: m.id,
          charId: m.char_id,
          name: m.char_name || "Unknown",
          text: m.text,
          timestamp: new Date(m.created_at).toLocaleString("en-US", {
            timeZone: "America/New_York", month: "short", day: "numeric",
            year: "numeric", hour: "2-digit", minute: "2-digit",
            hour12: true, timeZoneName: "short",
          }),
          createdAt: m.created_at,
          isPlayer: m.is_player || false,
          color: m.color,
          avatar: m.avatar,
        }));
      }

      // Source B: embedded blob (the full saved-history backup). Many old
      // stories have their history primarily in stories.data.messages, not
      // story_messages. Without this fallback, scrolling up runs out after
      // 1-2 pages even though the blob has hundreds more.
      const blob = fullEmbeddedMsgsRef.current || [];
      const loadedIds = new Set(messagesRef.current.map(m => m.id).filter(Boolean));
      const loadedTextKeys = new Set(messagesRef.current.map(m => m.text?.substring(0, 80)));
      // Find blob messages that are older than the oldest currently loaded one
      // AND not already loaded (dedup by id and by text-prefix for legacy rows)
      const oldestCreatedTs = oldest?.createdAt ? new Date(oldest.createdAt).getTime() : Infinity;
      const blobOlderAll = blob.filter(m => {
        if (loadedIds.has(m.id)) return false;
        if (loadedTextKeys.has(m.text?.substring(0, 80))) return false;
        const t = m.createdAt ? new Date(m.createdAt).getTime() : 0;
        // If neither has timestamps, assume blob order is chronological
        if (!oldest?.createdAt) return true;
        return t < oldestCreatedTs;
      });
      // Take the most recent RP_PAGE_SIZE of those (closest to current view)
      const blobOlder = blobOlderAll.slice(-RP_PAGE_SIZE);

      // Merge sources, dedup, sort chronologically. Combined page might be
      // larger than RP_PAGE_SIZE if both sources had unique rows — that's fine.
      const combined = [...dbOlder];
      const combinedIds = new Set(combined.map(m => m.id).filter(Boolean));
      const combinedTextKeys = new Set(combined.map(m => m.text?.substring(0, 80)));
      for (const bm of blobOlder) {
        if (combinedIds.has(bm.id)) continue;
        if (combinedTextKeys.has(bm.text?.substring(0, 80))) continue;
        combined.push(bm);
      }
      combined.sort((a, b) => {
        const aT = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bT = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return aT - bT;
      });

      // hasMore: true if EITHER source still has more older content
      const dbHasMore = dbOlder.length >= RP_PAGE_SIZE;
      const blobHasMore = blobOlderAll.length > blobOlder.length;
      setHasMoreRpMessages(dbHasMore || blobHasMore);

      if (combined.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(p => p.id).filter(Boolean));
          const existingTexts = new Set(prev.map(p => p.text?.substring(0, 80)));
          const fresh = combined.filter(m => !existingIds.has(m.id) && !existingTexts.has(m.text?.substring(0, 80)));
          return [...fresh, ...prev];
        });
      } else {
        // Both sources exhausted
        setHasMoreRpMessages(false);
      }
    } catch (err) {
      console.error("[RP] Error loading older messages:", err);
    } finally {
      setLoadingOlderRp(false);
    }
  }, [loadingOlderRp, hasMoreRpMessages]);

  // Keep a ref so the scroll handler can call the latest loadOlder without
  // stale-closure issues (handler is captured once on mount).
  const loadOlderRpRef = useRef(null);
  useEffect(() => { loadOlderRpRef.current = loadOlderRpMessages; }, [loadOlderRpMessages]);

  // After messages update following a load-older, restore scroll position.
  useEffect(() => {
    const anchor = rpScrollAnchorRef.current;
    const el = rpChatContainerRef.current;
    if (!anchor || !el) return;
    el.scrollTop = anchor.scrollTop + (el.scrollHeight - anchor.scrollHeight);
    rpScrollAnchorRef.current = null;
  }, [messages]);

  const handleRpChatScroll = useCallback(() => {
    const el = rpChatContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setRpIsAtBottom(distFromBottom < 120);
    // Trigger load-older when within 200px of the top
    if (el.scrollTop < 200 && loadOlderRpRef.current) {
      if (!rpScrollAnchorRef.current) {
        rpScrollAnchorRef.current = { scrollTop: el.scrollTop, scrollHeight: el.scrollHeight };
      }
      loadOlderRpRef.current();
    }
  }, []);

  const scrollToBottom = useCallback((instant = false) => {
    chatEndRef.current?.scrollIntoView({ behavior: instant ? 'instant' : 'smooth' });
    setRpIsAtBottom(true);
  }, []);

  // Open a story at its most recent message — no "jump to latest" tap needed.
  //
  // The old version fired a setTimeout(…, 0) the moment activeStoryId changed.
  // At that point loadSession has not returned yet, so it scrolled to the
  // bottom of an empty list and the real messages landed afterwards, leaving
  // you part-way up a long scene. Now the intent is remembered and the jump
  // happens on the render where the messages actually exist — the same
  // wait-for-the-first-batch pattern ChannelView already uses.
  const prevActiveStoryIdRef = useRef(activeStoryId);
  const rpInitialScrollRef = useRef(true);
  useEffect(() => {
    const changedStory = prevActiveStoryIdRef.current !== activeStoryId;
    prevActiveStoryIdRef.current = activeStoryId;
    if (changedStory) {
      setRpIsAtBottom(true);
      rpInitialScrollRef.current = true;   // arm; the jump waits for messages
    }
    if (rpInitialScrollRef.current && messages.length > 0) {
      rpInitialScrollRef.current = false;
      // rAF so the transcript has painted at full height before we jump —
      // scrollIntoView against a still-growing list lands short.
      requestAnimationFrame(() => chatEndRef.current?.scrollIntoView({ behavior: 'instant' }));
      return;
    }
    if (rpIsAtBottom) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, activeStoryId]);

  // Bug #1 fix: All currentPlayer initialization moved here (out of render body)
  useEffect(() => {
    if (currentPlayer) return; // already set
    if (user && userProfile?.display_name) {
      setCurrentPlayer(userProfile.display_name);
    } else if (user?.email) {
      setCurrentPlayer(user.email.split("@")[0] || "Player");
    } else if (user) {
      // Last resort: check localStorage for cached auth after short delay
      const timer = setTimeout(() => {
        setCurrentPlayer(prev => {
          if (prev) return prev;
          try {
            const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
            if (storageKey) {
              const stored = JSON.parse(localStorage.getItem(storageKey));
              return stored?.user?.email?.split("@")[0] || "Player";
            }
          } catch { /* ignore */ }
          return "Player";
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, userProfile, currentPlayer]);

  // Perf #12: Memoize allChars to avoid creating a new array every render.
  //
  // rosterVersion is the reason a character created in the app APPEARS in the
  // app. registerCustomCharacters appends to the CHARACTERS array — a
  // mutation this memo's deps cannot see — so the spread froze at whatever
  // the roster held on first render, and every allChars consumer (Contacts,
  // Dial My Fate, story casting) was blind to later registrations. Vale,
  // 2026-08-20: existed in the encyclopedia, nowhere else. The roster module
  // announces changes; this counter carries them into the deps.
  const [rosterVersion, setRosterVersion] = useState(0);
  useEffect(() => {
    const onRoster = () => setRosterVersion(v => v + 1);
    window.addEventListener('mfml-roster-changed', onRoster);
    return () => window.removeEventListener('mfml-roster-changed', onRoster);
  }, []);
  const allChars = React.useMemo(() => [...CHARACTERS, ...customChars].filter(Boolean), [customChars, rosterVersion]);
  // Sync AFTER the declaration above — placing this effect earlier crashed
  // every render with a TDZ ReferenceError (deps arrays evaluate at render).
  useEffect(() => { allCharsRef.current = allChars; }, [allChars]);
  // Role-based access: admin and editor roles get private channel access
  // Uses profile role from Supabase + localStorage cache for instant access on reload
  const PRIVATE_ROLES = ["admin", "editor"];
  // The loaded profile is the truth. The cached role only bridges the moment
  // before it loads (instant tab on reload for the writers); once a profile
  // is in hand a stale cache can no longer promote a member (2026-10-19 audit).
  const isKnownPlayer = userProfile
    ? PRIVATE_ROLES.includes(userProfile.role)
    : (() => { try { return PRIVATE_ROLES.includes(localStorage.getItem("mfml_user_role")); } catch { return false; } })();
  const currentChar = allChars.find((c) => c?.id === playingAs);
  // v420 (Haley, 2026-10-19, Brooke): Our Channels is the writers' section.
  // The tab button was already hidden for everyone else; the tab itself was
  // not, so a saved tab or any setMainTab("channels") still rendered it.
  useEffect(() => {
    if (mainTab === "channels" && userProfile && !isKnownPlayer) setMainTab("community");
  }, [mainTab, userProfile, isKnownPlayer]);
  const myStories = savedSessions.filter((s) => s.user_id === user?.id);
  // Daily-idea generation. MUST live below the myStories declaration —
  // its deps array is evaluated during render and referencing a const
  // declared later in the component is a TDZ crash on mount (v133's
  // GroupChatView bug, repeated by v137 exactly here, 'cl' minified).
  useEffect(() => {
    if (!user?.id || view !== 'home') return;
    const cached = loadIdeaFromStorage(user.id);
    if (cached) { setDailyIdea(cached); return; }
    // One generation attempt per session — a failure (no key, offline,
    // provider down) leaves the static examples, silently, by design.
    if (dailyIdeaAttemptedRef.current) return;
    dailyIdeaAttemptedRef.current = true;
    (async () => {
      try {
        const { data: { session } } = await safeGetSession();
        if (!session?.access_token) return;
        let profile = '';
        try {
          // Resolve by LOGIN id first — currentPlayer is the character name
          // during RP ("Kya"), which resolves to nobody (the same silent
          // failure the story path had). All three layers go in: the deep
          // profile is the main pull (Haley, 2026-08-03), the weekly layer
          // extends it.
          const profs = await getPlayerPreferenceProfiles([user?.id, currentPlayer].filter(Boolean));
          const row = Object.values(profs)[0];
          profile = [row?.stated_notes, row?.deep_profile, row?.profile].filter(Boolean).join('\n\n');
        } catch { /* optional input — the prompt has a no-profile branch */ }
        const recentThemes = (myStories || []).slice(0, 8).map(s => s?.name).filter(Boolean);
        const res = await fetchWithRetry(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-proxy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
          body: JSON.stringify({
            provider: aiProvider,
            systemPrompt: 'You suggest one concise roleplay premise. Output only the premise text.',
            messages: [{ role: 'user', content: buildIdeaPrompt({ profile, recentThemes }) }],
            // 90 exhausted before ANY text on reasoning-style models
            // (measured: 4 error rows, finish_reason=length on DeepSeek) —
            // the feature silently never worked. The prompt still forces a
            // single-line premise; this is headroom, not permission.
            maxTokens: 512,
            feature: 'daily_rp_idea',
            characterId: 'system',
            characterName: 'Daily Idea',
          }),
        });
        const data = await res.json();
        if (!res.ok) return;
        const idea = cleanIdea(data?.text);
        if (!idea) return;
        cacheIdea(idea, user.id);
        setDailyIdea(idea);
      } catch { /* static examples stay — a suggestion is a nicety */ }
    })();
  }, [user?.id, view, currentPlayer, aiProvider, myStories]);

  // Redirect non-private users off the channels tab. Moved out of the render
  // body — calling setMainTab() during render is a React anti-pattern that can
  // cascade into a render loop on slower devices.
  useEffect(() => {
    if (mainTab === "channels" && !isKnownPlayer && userProfile !== null) {
      setMainTab("community");
    }
  }, [mainTab, isKnownPlayer, userProfile]);

  // Choosing a character to play is an explicit mode decision: narrator mode
  // means "the AI runs the whole world BECAUSE nobody was picked" (prompt-
  // driven stories start that way). It must not survive an actual pick —
  // live 2026-07-30, every reply in Haley's story kept coming back as
  // "Scene" narration that also wrote her character, because nothing ever
  // set the flag back to false.
  const pickPlayingAs = (id) => { setPlayingAs(id); setNarratorMode(false); };

  // The character the player IS can never be an AI responder — the AI knows
  // them fully (player profile + cast context in the prompt) but never
  // speaks or acts as them. Live 2026-07-30, Haley: "the AI doesn't have to
  // respond as the character I have selected".
  const aiRespondChars = sceneChars.filter((id) => {
    if (id === playingAs) return false;
    const c = allChars.find((ch) => ch?.id === id);
    return c && c.controlledBy !== currentPlayer;
  });

  // "TALKING TO" — the RP pin (Haley, 2026-08-12: "can we also get a talking
  // to drop down thing in the rp section as well"). Ordered list of cast ids
  // the player is addressing: the first pinned character becomes the anchor
  // (castNow[0] — lead voice AND memory-attribution fallback), and the
  // director is told exactly who is being spoken to, which is the addressee
  // rule the ensemble work added. Unlike channels there is no DB table — a
  // story is played by its owner, so per-story localStorage is the whole
  // persistence story (same instant-hydrate role cachedPin plays there).
  const [rpTalkingTo, setRpTalkingTo] = useState([]);
  const rpTalkingToRef = useRef([]);
  const [rpPinMenuOpen, setRpPinMenuOpen] = useState(false);
  const [rpSummonMenuOpen, setRpSummonMenuOpen] = useState(false);
  useEffect(() => { rpTalkingToRef.current = rpTalkingTo; }, [rpTalkingTo]);
  useEffect(() => {
    if (!activeStoryId) { setRpTalkingTo([]); return; }
    try { setRpTalkingTo(JSON.parse(localStorage.getItem(`mfml_rp_pin_${activeStoryId}`) || '[]')); }
    catch { setRpTalkingTo([]); }
  }, [activeStoryId]);
  const toggleRpTalkingTo = (cid) => {
    setRpTalkingTo(prev => {
      const next = cid === null ? [] : (prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
      rpTalkingToRef.current = next;
      try {
        if (activeStoryId) {
          if (next.length) localStorage.setItem(`mfml_rp_pin_${activeStoryId}`, JSON.stringify(next));
          else localStorage.removeItem(`mfml_rp_pin_${activeStoryId}`);
        }
      } catch { /* private mode */ }
      return next;
    });
  };

  // In narrator mode the AI writes the whole world, so there may be no roster
  // at all. Any character the player doesn't control works as the prompt's
  // narrator handle; the reply is rendered as "Scene" regardless.
  const narratorFallbackCharId = React.useMemo(() => {
    if (!narratorMode) return null;
    const first = allChars.find((c) => c?.id && c.controlledBy !== currentPlayer);
    return first?.id || allChars.find((c) => c?.id)?.id || null;
  }, [narratorMode, allChars, currentPlayer]);

  const createCustomChar = () => {
    if (!newChar.name.trim()) return;
    const AVATARS = ["🌟", "⚡", "🔮", "🌙", "🦋", "🐉", "👑", "🗡️", "🌺", "💫", "🐺", "🧛", "🧜‍♀️", "🧚", "🦊"];
    const COLORS = ["#e74c3c", "#3498db", "#2ecc71", "#9b59b6", "#f39c12", "#1abc9c", "#e91e63", "#00bcd4", "#ff9800", "#8bc34a"];
    const custom = {
      id: `custom_${Date.now()}`,
      name: newChar.name.trim(),
      species: newChar.species.trim() || "Unknown",
      generation: 1,
      controlledBy: currentPlayer,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      avatar: AVATARS[Math.floor(Math.random() * AVATARS.length)],
      personality: newChar.personality.trim() || `A character named ${newChar.name.trim()} created by ${currentPlayer}.`,
      speechPattern: newChar.speechPattern.trim() || "Speaks naturally and expressively.",
      relationships: `Created by ${currentPlayer} for RP.`,
      traits: newChar.name.trim().toLowerCase().split(" ").slice(0, 3),
      backstory: newChar.backstory.trim() || `A new character in the MFML universe, created on the spot by ${currentPlayer}.`,
    };
    setCustomChars((prev) => [...prev, custom]);
    pickPlayingAs(custom.id);
    setNewChar({ name: "", species: "", personality: "", speechPattern: "", backstory: "" });
    setShowCreateChar(false);
    setView("chat");
  };

  // ─── NEW CHARACTER DETECTION FROM AI RESPONSES ───────────────────────────
  const detectNewCharacters = useCallback((responseText) => {
    if (!responseText) return;
    // Look for the AI's structured new character tag: [NEW_CHARACTER: Name | Species | Brief description]
    const newCharRegex = /\[NEW_CHARACTER:\s*([^|]+)\s*\|\s*([^|]+)\s*\|\s*([^\]]+)\]/g;
    let match;
    const detected = [];
    while ((match = newCharRegex.exec(responseText)) !== null) {
      const name = match[1].trim();
      const species = match[2].trim();
      const description = match[3].trim();
      // Don't detect if already exists
      const exists = allChars.some(c => c?.name?.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(c?.name?.toLowerCase()));
      if (!exists && !detectedNewChars.some(d => d.name === name)) {
        detected.push({ name, species, description, detectedAt: new Date().toISOString() });
      }
    }
    if (detected.length > 0) {
      setDetectedNewChars(prev => [...prev, ...detected]);
      // Auto-show prompt for the first new character
      if (!showNewCharPrompt) setShowNewCharPrompt(detected[0]);
    }
  }, [allChars, detectedNewChars, showNewCharPrompt]);

  const saveDetectedChar = (detected, _tag = null) => {
    const AVATARS = ["🌟", "⚡", "🔮", "🌙", "🦋", "🐉", "👑", "🗡️", "🌺", "💫", "🐺", "🧛", "🧜‍♀️", "🧚", "🦊", "🎭", "🌸", "🔥", "❄️", "🌊"];
    const COLORS = ["#e74c3c", "#3498db", "#2ecc71", "#9b59b6", "#f39c12", "#1abc9c", "#e91e63", "#00bcd4", "#ff9800", "#8bc34a"];
    const custom = {
      id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: detected.name,
      species: detected.species,
      generation: 1,
      controlledBy: "AI",
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      avatar: AVATARS[Math.floor(Math.random() * AVATARS.length)],
      personality: detected.description,
      speechPattern: "Matches the tone established in their introduction.",
      relationships: `Introduced during RP. ${detected.description}`,
      traits: detected.species.toLowerCase().split(/[\s/]+/).slice(0, 3),
      backstory: detected.description,
      aiGenerated: true,
    };
    setCustomChars(prev => [...prev, custom]);
    // Add to scene and set tag if provided
    if (!sceneChars.includes(custom.id)) {
      setSceneChars(prev => [...prev, custom.id]);
    }
    // Remove from detected list
    setDetectedNewChars(prev => prev.filter(d => d.name !== detected.name));
    setShowNewCharPrompt(null);
    // Show next pending character if any
    const remaining = detectedNewChars.filter(d => d.name !== detected.name);
    if (remaining.length > 0) {
      setTimeout(() => setShowNewCharPrompt(remaining[0]), 300);
    }
  };

  const dismissDetectedChar = (detected) => {
    setDetectedNewChars(prev => prev.filter(d => d.name !== detected.name));
    setShowNewCharPrompt(null);
    const remaining = detectedNewChars.filter(d => d.name !== detected.name);
    if (remaining.length > 0) {
      setTimeout(() => setShowNewCharPrompt(remaining[0]), 300);
    }
  };

  const matchesSearch = (c) => {
    if (!charSearch) return true;
    const q = charSearch.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.species.toLowerCase().includes(q) ||
      (c.fullName || "").toLowerCase().includes(q) ||
      (c.traits || []).some((t) => t.toLowerCase().includes(q)) ||
      (c.personality || "").toLowerCase().includes(q) ||
      (c.backstory || "").toLowerCase().includes(q)
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STORY INTEL — AI-generated story analysis for jump-in players
  // ═══════════════════════════════════════════════════════════════════════════
  const generateStoryIntel = async () => {
    if (!effectiveApiKey || effectiveAiProvider === "none" || messages.length === 0) return;
    setStoryIntelLoading(true);
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    try {
      const { data: { session }, error: refreshError } = await safeGetSession();
      if (refreshError || !session?.access_token) throw new Error("Session expired");

      // Build a condensed transcript (last 60 messages max to save tokens)
      const transcript = messages.slice(-60).map(m => `[${m.name}]: ${(m.text || '').substring(0, 200)}`).join('\n');

      const systemPrompt = `You are a story analyst for a roleplay server. Given a transcript of an RP story, produce a JSON analysis. Respond ONLY with valid JSON, no markdown fences.

{
  "summary": "2-3 sentence overview of the entire story so far",
  "currentScene": "What's happening RIGHT NOW — the current conversation/action, 1-2 sentences",
  "timeline": ["Event 1: brief description", "Event 2: brief description", ...up to 8 key events],
  "characters": [{"name": "CharName", "role": "Brief role/what they've done in the story"}],
  "mood": "The current emotional tone/atmosphere (e.g., 'tense and romantic', 'lighthearted banter')",
  "themes": ["theme1", "theme2"],
  "plotIdeas": ["Idea 1: brief hook", "Idea 2: brief hook", "Idea 3: brief hook"],
  "openThreads": ["Unresolved plot thread 1", "Unresolved thread 2"]
}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), AI_ABORT_MS); // see aiTimeouts.js — big-context scene calls run long; edge wall-clock is 150s
      const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
          "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          provider: effectiveAiProvider,
          systemPrompt,
          messages: [{ role: "user", content: `Here is the RP transcript to analyze:\n\n${transcript}` }],
          // Same reasoning-model headroom as rp_opening_scene: 1024 total is
          // exhausted by DeepSeek's hidden reasoning before the analysis text.
          maxTokens: 4096,
          feature: 'story_intel',
          nsfw: true, // processes RP transcript which may contain NSFW
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) throw new Error(`ai-proxy ${res.status}`); // audit P3-1: a 5xx body parsed as JSON used to read as a silent no-op
      const data = await res.json();
      const raw = data.text || data.response || '';
      // Parse JSON from response (strip markdown fences if present)
      const jsonStr = raw.replace(/^```json?\s*/i, '').replace(/```\s*$/i, '').trim();
      const intel = JSON.parse(jsonStr);
      setStoryIntel(intel);
      setStoryIntelOpen(true);
    } catch (err) {
      console.error('[StoryIntel] Generation failed:', err);
      setStoryIntel({ error: err.message || 'Failed to generate story intel' });
      setStoryIntelOpen(true);
    }
    setStoryIntelLoading(false);
  };

  // ─── SCENE RECAP + SUGGESTIONS (for jump-in guests) ─────────────────────
  const generateSceneRecap = async () => {
    if (!effectiveApiKey || effectiveAiProvider === "none" || messages.length === 0) return;
    setSceneRecapLoading(true);
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    try {
      const { data: { session }, error: refreshError } = await safeGetSession();
      if (refreshError || !session?.access_token) throw new Error("Session expired");

      const playerChar = allChars.find(c => c?.id === playingAs);
      const playerName = playerChar?.name || userProfile?.display_name || 'the player';
      const playerSpecies = playerChar?.species || 'unknown';

      // Last 25 messages for context
      const transcript = messages.slice(-25).map(m => `[${m.name}]: ${(m.text || '').substring(0, 250)}`).join('\n');

      // Build character context for suggestions
      const activeCharNames = [...new Set(messages.slice(-25).map(m => m.name))].join(', ');

      const systemPrompt = `You are a scene analyst for a roleplay app. A player just joined an ongoing RP story and needs to catch up quickly.

The player is playing as: ${playerName} (${playerSpecies})
Characters active in recent messages: ${activeCharNames}

Given the recent transcript, produce a JSON response. Respond ONLY with valid JSON, no markdown fences.

{
  "recap": "A 3-4 sentence recap of what's happening in the scene right now. Focus on the current action, who is present, what's going on emotionally, and anything the player needs to know to jump in naturally. Write it like a quick briefing: 'Here's what's going on...'",
  "suggestions": [
    "A natural first message ${playerName} could say/do to enter the scene — in character, 1-2 sentences, written in RP style (action in *asterisks*, dialogue in quotes)",
    "An alternative entry — maybe more dramatic or emotional, also in RP style",
    "A third option — maybe quieter or observational, also in RP style"
  ]
}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), AI_ABORT_MS); // see aiTimeouts.js — big-context scene calls run long; edge wall-clock is 150s
      const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
          "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          provider: effectiveAiProvider,
          systemPrompt,
          messages: [{ role: "user", content: `Here is the recent RP transcript:\n\n${transcript}` }],
          maxTokens: 768,
          feature: 'scene_recap',
          nsfw: true, // recaps RP transcript which may contain NSFW
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) throw new Error(`ai-proxy ${res.status}`); // audit P3-1: a 5xx body parsed as JSON used to read as a silent no-op
      const data = await res.json();
      const raw = data.text || data.response || '';
      const jsonStr = raw.replace(/^```json?\s*/i, '').replace(/```\s*$/i, '').trim();
      const recap = JSON.parse(jsonStr);
      setSceneRecap(recap);
    } catch (err) {
      console.error('[SceneRecap] Generation failed:', err);
      setSceneRecap({ error: err.message || 'Failed to generate scene recap' });
    }
    setSceneRecapLoading(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PROMPT-DRIVEN RP — start a story from a free-form prompt, no character pick
  // ═══════════════════════════════════════════════════════════════════════════
  // Pulls a player name out of the prompt ("my name is Eric", "I'm a girl
  // named Mara", "I am Aria"). Falls back to currentPlayer / 'You'.
  const extractPlayerName = (prompt) => {
    if (!prompt) return '';
    const patterns = [
      /\bmy name(?:'s| is)\s+([A-Z][a-zA-Z]{1,30})/,
      /\bI(?:'m| am|m)\s+(?:a\s+\w+\s+(?:named|called)\s+)?([A-Z][a-zA-Z]{1,30})/,
      /\bcalled\s+([A-Z][a-zA-Z]{1,30})/,
    ];
    for (const re of patterns) {
      const m = prompt.match(re);
      if (m && m[1] && !['I','A','The','My','You','We','They'].includes(m[1])) return m[1];
    }
    return '';
  };

  const startPromptDrivenRP = async () => {
    if (!user) { setAuthError("Please sign in first"); return; }
    if (startingPromptRP) return;
    const prompt = (routeDescription || '').trim();
    if (!prompt) {
      setToastMsg({ message: "Type a story prompt first", type: "warn", icon: "✨" });
      return;
    }
    setStartingPromptRP(true);
    try {
      const ok = await ensureSession();
      if (!ok) { setToastMsg({ message: "Session expired — please sign in again", type: "error", icon: "⚠️" }); return; }

      // PROTAGONIST FIDELITY (live 2026-07-30): "Juliette decides… to go to
      // Malakai's house" generated an opening where HALEY walked to
      // ALISTAIR's door — the prompt template injected the player's real
      // name as the person in the scene, and the model obeyed it over the
      // prompt. When the prompt is written about a resolvable character,
      // that character IS the player's stand-in: the scene is built around
      // them, the human writes their lines, and the player's real name
      // stays out of the fiction.
      const protagonist = detectProtagonist(prompt, allChars);
      const protagChar = protagonist ? allChars.find(c => c?.id === protagonist.id) : null;
      const playerName = protagChar
        ? firstNameOf(protagChar.name)
        : (extractPlayerName(prompt) || currentPlayer || 'You');
      const storyName = `${playerName} - ${new Date().toLocaleDateString()}`;

      // 1) Build the opening-scene system prompt and call ai-proxy.
      // Haley (2026-07-30): "characters don't seem all accurate... make sure
      // the characters know everything about themselves even in the starter."
      // The starter used to see 200 chars of personality per character and
      // nothing else, while turn prompts carry full merged canon. Two tiers
      // now: FULL canon (with enrichment overrides) for the characters the
      // prompt actually points at, an index for the rest of the roster.
      const aiChars = allChars.filter(c => c?.id && c?.name && c?.controlledBy === 'AI');
      let mergedChars = aiChars;
      try {
        const ov = await loadCharacterOverrides();
        mergedChars = aiChars.map(c => mergeCharWithOverrides(c, ov));
      } catch (e) { console.warn('[Starter] overrides unavailable, using base canon:', e?.message); }
      const promptLower = prompt.toLowerCase();
      const likely = mergedChars.filter(c => {
        const first = String(c.name).trim().split(/\s+/)[0].toLowerCase();
        if (first.length >= 3 && promptLower.includes(first)) return true;
        if (c.species && promptLower.includes(String(c.species).toLowerCase())) return true;
        return matchesCharacterSearch(c, prompt);
      }).slice(0, 6);
      const likelyIds = new Set(likely.map(c => c.id));
      // Audit P0-1/P2-2: every field the narrator sees is scrubbed of
      // cover→truth mappings; the species string itself can be a reveal.
      const canonBlock = likely.map(c => [
        `=== ${c.name} (${scrubIdentityReveals(c.species || '') || 'unknown species'}, id=${c.id}) ===`,
        c.personality ? `PERSONALITY: ${scrubIdentityReveals(String(c.personality)).slice(0, 1400)}` : null, // guards, not caps (audit P1-10): the enrichment past char 700 was silently discarded
        c.speechPattern ? `VOICE: ${String(c.speechPattern).slice(0, 900)}` : null,
        c.backstory ? `BACKSTORY: ${scrubIdentityReveals(String(c.backstory)).slice(0, 1400)}` : null,
        c.relationships ? `RELATIONSHIPS: ${scrubIdentityReveals(String(c.relationships)).slice(0, 900)}` : null,
        c.traits?.length ? `TRAITS: ${c.traits.slice(0, 10).join(', ')}` : null,
      ].filter(Boolean).join('\n')).join('\n\n');
      const universeChars = mergedChars
        .filter(c => !likelyIds.has(c.id))
        .map(c => `${c.name} (${scrubIdentityReveals(c.species || '')}${c.id ? ', id=' + c.id : ''}): ${scrubIdentityReveals((c.personality || '')).slice(0, 200)}`)
        .slice(0, 60)
        .join('\n');
      // The characters the prompt literally names are the story — their
      // presence in the opening is mandatory, not optional flavor.
      const promptCast = matchCharactersInText(prompt, mergedChars, {
        excludeIds: protagChar ? [protagChar.id] : [],
        excludeFirstNames: [firstNameOf(currentPlayer)],
      });
      const systemPrompt = `You are the Scene Narrator for the MFML universe. Build the OPENING scene of a roleplay from a free-form prompt the player just typed.

${protagChar
  ? `PLAYER CHARACTER: ${protagChar.name} (${protagChar.species || 'unknown species'}). The prompt is about ${playerName}, and the HUMAN player writes ${playerName}'s lines themselves. Build the scene around ${playerName} exactly as the prompt describes — but never write ${playerName}'s dialogue or actions, and never put the player's real name into the fiction.`
  : `PLAYER: ${playerName} (the human in the story — the user types ${playerName}'s lines themselves).`}

PLAYER PROMPT:
"""${prompt}"""
${promptCast.length ? `\nCHARACTERS THE PROMPT NAMES — the scene MUST feature them, doing what the prompt says they do: ${promptCast.map(m => m.first).join(', ')}.\n` : ''}${canonBlock ? `\nCHARACTERS THE PROMPT POINTS AT — their FULL canon. Anyone you bring in from this list must be written exactly to it (voice, history, relationships — no inventing or contradicting):\n${canonBlock}\n` : ''}
UNIVERSE CHARACTERS YOU CAN ALSO BRING IN (use any that logically fit the prompt — vampires for vampire routes, royals for court scenes, the Steele pack for werewolf scenes, etc.):
${universeChars}

Write the opening scene as ONE cohesive narrative paragraph block. The format you must mirror exactly is the in-universe "Scene" narration style: multiple characters arrive organically, each gets a beat of internal observation + a line or two of dialogue, the player has just walked into the situation. Use bold/italic naturally in prose, dialogue in straight quotes. End where ${playerName} naturally needs to respond — don't over-resolve.

Constraints:
- 350–650 words.
- Bring in 3–6 characters that fit the prompt. Characters the prompt names come first; add others ONLY if they clearly fit the prompt.
- Match the universe tone (gothic, intimate, character-driven).
- Do NOT write ${playerName}'s lines or actions for them — leave space for the player to respond.
- Plain prose. No system asides. No "Welcome!" — drop them straight into a scene.`;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await safeGetSession();
      if (!session?.access_token) throw new Error("No session");
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), AI_ABORT_MS);
      let openingText = null;
      try {
        const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
          method: 'POST',
          signal: ctrl.signal,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            provider: effectiveAiProvider,
            systemPrompt,
            messages: [{ role: 'user', content: prompt }],
            // 4096, not 1024: on reasoning-style models (DeepSeek — what both
            // players run) the hidden reasoning phase spends from the same
            // budget as the visible text, and 1024 was exhausted before ANY
            // text emerged (finish_reason=length, client_errors 2026-08-03
            // 17:00 UTC — "Start RP" simply refused for Nicole). Same failure
            // mode the daily idea had at 90. This is headroom, not length
            // permission — the prompt still governs scene size.
            maxTokens: 4096,
            nsfw: true, // route prompts often involve mature content; let the model build the scene
            feature: 'rp_opening_scene',
          }),
        });
        if (!res.ok) {
          const errBody = await res.text().catch(() => '');
          throw new Error(`Opening-scene generation failed: ${res.status} ${errBody.slice(0, 200)}`);
        }
        const data = await res.json();
        openingText = (data.text || data.reply || '').trim();
      } finally { clearTimeout(timer); }
      if (!openingText) throw new Error("Empty scene response");

      // 2) Seed sceneChars from the PROMPT first (the characters the player
      // asked for ARE the story), then from the opening text — both via the
      // shared matcher, so ambiguous first names (three Haley variants from
      // one bare "Haley") and the player's own name can't seed ghosts, and
      // the cap keeps the characters the prompt is about instead of
      // whoever came first in roster order (live 2026-07-30: the old loop
      // seeded 8 characters including three Haleys and capped out before
      // reaching Malakai — in a story about Malakai).
      const detectedCharIds = [...new Set([
        ...promptCast.map(m => m.id),
        ...matchCharactersInText(openingText, mergedChars, {
          excludeIds: protagChar ? [protagChar.id] : [],
          excludeFirstNames: [firstNameOf(currentPlayer)],
        }).map(m => m.id),
      ])].slice(0, 8);

      // 3) Insert story row. data carries narratorMode + routePrompt + playerName.
      const now = new Date();
      const openingMsg = {
        id: `scene_${Date.now()}`,
        charId: '_scene',
        name: 'Scene',
        text: openingText,
        timestamp: now.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
        createdAt: now.toISOString(),
        type: 'chat',
        isPlayer: false,
        isScene: true,
        color: 'var(--ds-gold, #c4a882)',
        avatar: '📖',
      };
      const initialData = {
        messages: [openingMsg],
        sceneChars: detectedCharIds,
        sceneType: 'chat',
        // A resolvable protagonist means the player has a character from the
        // first turn — classic play, not narrator mode. Narrator mode stays
        // for prompts about nobody in particular ("a storm hits the manor").
        playingAs: protagChar?.id || null,
        currentPlayer,
        customChars: [],
        aiProvider: effectiveAiProvider,
        narratorMode: !protagChar,
        narratorPlayerName: playerName,
        routePrompt: prompt,
      };
      // Re-rolling the same prompt REPLACES the story the previous roll
      // created (as long as the player never sent a message in it) instead
      // of minting an identical-named twin.
      let inserted = null;
      let reusedStory = false;
      const reuse = lastPromptStoryRef.current;
      if (reuse && reuse.prompt === prompt && reuse.storyId) {
        // Audit P2-1: this re-roll REPLACES the story blob and DELETES its
        // message mirror. The local ref only clears when THIS client sends —
        // if the other player wrote ten turns in the meantime, re-rolling
        // would erase their writing with no confirmation. Re-check the LIVE
        // story before wiping: any sign of real play makes this a fresh
        // story instead of a wipe.
        const { data: liveCheck } = await supabase
          .from('stories')
          .select('message_count')
          .eq('id', reuse.storyId)
          .maybeSingle();
        const { count: mirrorCount } = await supabase
          .from('story_messages')
          .select('id', { count: 'exact', head: true })
          .eq('story_id', reuse.storyId);
        const played = (liveCheck?.message_count || 0) > 1 || (mirrorCount || 0) > 1;
        if (played) {
          console.log('[startPromptDrivenRP] reuse skipped — the story has real play in it; creating fresh instead');
          lastPromptStoryRef.current = null;
        }
        const { data: updRow, error: updErr } = played ? { data: null, error: null } : await supabase
          .from('stories')
          .update({ name: storyName, data: initialData, message_count: 1, last_active_at: now.toISOString(), updated_at: now.toISOString() })
          .eq('id', reuse.storyId)
          .select('id')
          .maybeSingle();
        if (!updErr && updRow?.id) {
          const { error: wipeErr } = await supabase.from('story_messages').delete().eq('story_id', reuse.storyId);
          if (wipeErr) console.warn('[startPromptDrivenRP] old opening mirror cleanup failed:', wipeErr.message);
          inserted = { id: reuse.storyId };
          reusedStory = true;
        }
      }
      if (!inserted) {
        const { data: freshRow, error: insertErr } = await supabase
          .from('stories')
          .insert({
            user_id: user.id,
            name: storyName,
            data: initialData,
            message_count: 1,
            last_active_at: now.toISOString(),
          })
          .select()
          .single();
        if (insertErr) throw insertErr;
        inserted = freshRow;
      }
      lastPromptStoryRef.current = { prompt, storyId: inserted.id };

      // 4) Mirror the opening Scene message into story_messages so it's
      // recoverable even if the blob ever drifts.
      try {
        await supabase.from('story_messages').insert({
          story_id: inserted.id,
          user_id: user.id,
          char_id: '_scene',
          char_name: 'Scene',
          text: openingText,
          is_player: false,
          scene_type: 'chat',
          color: 'var(--ds-gold, #c4a882)',
          avatar: '📖',
        });
      } catch (smErr) { console.warn('[startPromptDrivenRP] story_messages mirror failed:', smErr); }

      // 4b) Apply the visibility chosen at creation. shared_stories already
      // drives can_access_story()'s public branch and StoryBrowser, so this
      // reuses the existing mechanism rather than adding a second one — the
      // only thing that was missing was being able to say so up front instead
      // of creating the story and then hunting for the toggle.
      if (newRpPublic) {
        try {
          const { data: sharedRow, error: shareErr } = await supabase
            .from('shared_stories')
            .upsert({ story_id: inserted.id, visibility: 'public', allow_spectate: true, allow_join: false },
                    { onConflict: 'story_id' })
            .select().single();
          if (shareErr) {
            console.error('[startPromptDrivenRP] could not make story public:', shareErr.message);
            setRpSendError('Story created, but it could not be made public. Use the toggle in your stories list.');
          } else {
            if (sharedRow) setStorySharedMap(prev => ({ ...prev, [inserted.id]: sharedRow }));
            sendPushNotification(null, {
              title: '📖 New community story!',
              body: `${currentPlayer || 'Someone'} shared "${storyName}"`,
              url: '/', exclude_user_id: user.id, category: 'community',
            });
          }
        } catch (e) {
          console.error('[startPromptDrivenRP] visibility failed:', e?.message || e);
        }
      }

      // 5) Activate the story in the UI.
      settingsLoadEpochRef.current = Date.now(); // creation is a load, not a player edit
      activeStoryIdRef.current = inserted.id;
      setActiveStoryId(inserted.id);
      setStoryOwnerId(user.id);
      setCurrentSessionName(storyName);
      setMessages([openingMsg]);
      setSceneChars(detectedCharIds);
      setSceneType('chat');
      setPlayingAs(protagChar?.id || null);
      setNarratorMode(!protagChar);
      setStoryIsAU(false);
      setNarratorPlayerName(playerName);
      setSavedSessions(prev => {
        const rest = prev.filter(x => x.id !== inserted.id);
        const existing = prev.find(x => x.id === inserted.id);
        return [{ ...(existing || inserted), name: storyName, data: initialData }, ...rest];
      });
      try {
        localStorage.setItem(`sixguys_last_story_${user.id}`, inserted.id);
        sessionStorage.setItem(`sixguys_tab_story_${user.id}`, inserted.id);
      } catch {}
      setRouteDescription('');
      setView('chat');
      setMainTab('rp');
      // NOT auto-shared. Prompt-driven stories used to be upserted into
      // shared_stories as public + joinable with no user action, which
      // contradicts saveStory's documented rule that new stories are private
      // until explicitly shared — and these are frequently NSFW routes started
      // with an expectation of privacy (audit #19). Use the Private/Public
      // toggle on the story to share.
      if (!reusedStory) { try { awardXP(user.id, 'start_story').catch(() => {}); } catch {} }
    } catch (err) {
      console.error('[startPromptDrivenRP] Failed:', err);
      logClientError('start_rp', err);
      setToastMsg({ message: `Couldn't start RP: ${err.message?.slice(0, 80) || 'unknown error'}`, type: "error", icon: "⚠️" });
    } finally {
      setStartingPromptRP(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // AI RESPONSE (Update 3: Enhanced prompt, Update 3B: Regenerate w/ Feedback,
  //              Update 4: OOC handling)
  // ═══════════════════════════════════════════════════════════════════════════
  const generateAIResponse = async (charId, history, feedbackText = null, rejectedResponse = '', retryKind = 'regen') => {
    // One idempotency token per AI turn, shared between the server-side
    // persist directive and the client's fallback insert (MDI-1: they used
    // DIFFERENT tokens — server row + tokenless client row = every
    // foreground single-NPC reply landed twice for the other player).
    const persistToken = (crypto?.randomUUID?.() || `rp_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    const char = allChars.find((c) => c?.id === charId);
    if (!char || !effectiveApiKey || effectiveAiProvider === "none") return null;
    const playerChar = allChars.find((c) => c?.id === playingAs);
    const playerLabel = playerChar ? playerChar.name : currentPlayer;

    // charTags purged (queue item 16, 2026-08-04): relationship kinds now come
    // exclusively from character_dynamics via resolveRelationship (story
    // scope) — the values were migrated to rows and the per-chat
    // RelationshipsPanel (item 18) is the editing surface.
    const otherCharsInScene = sceneChars
      .filter((id) => id !== charId)
      .map((id) => {
        const c = allChars.find((ch) => ch?.id === id);
        return c ? `${c.name} (${c.species})` : null;
      })
      .filter(Boolean)
      .join(", ");

    // Accumulated feedback context for this character
    const prevFeedback = feedbackContexts[charId] || "";
    const feedbackSection = (prevFeedback || feedbackText) ? `\n\nPLAYER DIRECTION — MANDATORY when present (these are EDITS to apply, not loose inspiration):${prevFeedback ? `\nPrevious direction (standing, always applies): ${prevFeedback}` : ""}${feedbackText ? `\nLatest direction (apply these specific changes this turn): ${feedbackText}\n\nThe Latest direction is a mandatory edit list, not creative suggestion. The user already saw your previous attempt and is correcting specific things — honor those corrections exactly. Apply EVERY change requested. If it asks to add X, add X. If it asks to remove Y, remove Y. If it asks to change A to B, change A to B. If the user says "duplicate", "same as before", or "you wrote this already", write a meaningfully different response — different opening, different actions, different word choice, NOT a paraphrase. If the direction conflicts with the previous response, the DIRECTION wins — the previous response was a draft; this is the edit pass.` : ""}` : "";

    // Rejected response: the exact text the user just rejected via regen.
    // Passing this back to the model with "do not repeat" framing is what
    // makes "this was a duplicate" feedback actually produce a different
    // response — otherwise the model has no idea what content to avoid.
    const rejectedSection = rejectedResponse && retryKind === 'edit' ? `\n\nSUPERSEDED REPLY — ${playerLabel} revised the message you are answering, so the reply below (which answered the OLD wording) is being replaced. This is NOT a rejection of your writing; it simply answers a line that no longer exists. Write a FRESH reply to the corrected message — do not reproduce this text, its opening, its shape, or its closing note:\n\n«««\n${rejectedResponse}\n»»»` : rejectedResponse && retryKind === 'dup' ? `\n\nANTI-REPEAT — your first draft of THIS reply accidentally echoed an OLDER reply of yours, quoted below. That older reply belongs to a PAST moment in the story — do NOT respond to it, its scene, or its situation. Respond ONLY to the newest messages above, and do not reuse the quoted text's sentences, images, or shapes:\n\n«««\n${rejectedResponse}\n»»»` : rejectedResponse ? `\n\nREJECTED PREVIOUS RESPONSE — the user just rejected the text below. You wrote it; they did not like it. Write a NEW response that is MATERIALLY DIFFERENT from this one. Do not paraphrase. Do not rearrange the same beats. Different opening, different action, different word choice. Treat the text below as a list of things NOT to write:\n\n«««\n${rejectedResponse}\n»»»\n\nIf the PLAYER DIRECTION above gives specific edits, those take priority over "just be different" — apply the edits exactly. If there's no specific direction, just write something substantively different that still fits the scene.` : "";

    // ── Embed the current scene beat ONCE. Shared by semantic character-
    // voice retrieval (every getCharacterDiscordContext call below) AND
    // cross-conversation linked memory (buildLinkedMemorySection further
    // down). One embed call per turn, all of it. Null → graceful fallback.
    // buildSceneQueryText fills the embed budget from the NEWEST message
    // backwards. Joining oldest-first and letting embedSceneText truncate kept
    // the OLDEST ~2 messages and dropped the player's latest line entirely —
    // see the note on buildSceneQueryText.

    // Fetch Discord RP history for authentic character voice. With
    // sceneEmbedding, getCharacterDiscordContext is semantic — it pulls each
    // character's voice samples most relevant to THIS scene, not random ones.
    //
    // STORY ISOLATION (May 20 fix): we pass activeStoryId so the RPC
    // includes app-mirrored samples FROM THIS STORY but excludes samples
    // mirrored from OTHER stories. Otherwise voice from Haley's "kidnap
    // Cruz" RP would surface inside Nicole's cafe scene — see PR #78. The
    // VOICE-SAMPLE INTEGRITY prompt rule is the belt; this is the suspenders.
    //
    // STORY LINKING (May 20 Phase 1): if the user has explicitly linked
    // OTHER stories to the current one (via conversation_links table),
    // include those linked stories' app-mirrored samples too. Empty
    // array = no opt-in linking, same as before.
    const isolationStoryId = activeStoryIdRef.current || activeStoryId || null;
    let linkedStoryIds = [];
    if (isolationStoryId && user?.id) {
      try {
        const { data: linkRows } = await supabase
          .from('conversation_links')
          .select('a_type, a_id, b_type, b_id')
          .eq('user_id', user.id)
          .or(`a_id.eq.${isolationStoryId},b_id.eq.${isolationStoryId}`);
        const linkedSet = new Set();
        for (const row of linkRows || []) {
          if (row.a_id === isolationStoryId && row.b_type === 'story') linkedSet.add(row.b_id);
          if (row.b_id === isolationStoryId && row.a_type === 'story') linkedSet.add(row.a_id);
        }
        linkedStoryIds = Array.from(linkedSet);
      } catch (e) { /* non-fatal — proceed without linked-story scoping */ }
    }
    // Build character profiles for all scene characters when multi-char.
    // The played character is NOT an NPC: presenting them as "CHARACTER N"
    // in the ensemble roster told the model to voice them, directly
    // contradicting the NEVER-control rule below (live 2026-07-30). They
    // stay in the prompt via the player profile section instead.
    //
    // Hoisted above the voice-sample fetch (2026-08-10) so the sample budget
    // can depend on how many characters are actually being voiced.
    const allSceneNPCs = sceneChars
      .filter((id) => { if (id === playingAs) return false; const c = allChars.find((ch) => ch?.id === id); return c && c.controlledBy !== currentPlayer; })
      .map((id) => allChars.find((ch) => ch?.id === id))
      .filter(Boolean);

    const isMultiChar = allSceneNPCs.length > 1;

    // In an ensemble the generic behaviour blocks below used to name the
    // anchor character 19 times — "what does Cruz want", "Cruz KNOWS their own
    // backstory" — while the other three were named once, in a list. The
    // model wrote what the prompt weighted toward. Single-character scenes
    // keep the exact wording they had; only the ensemble is re-pointed.
    const voicedSubject = isMultiChar ? 'Each character you voice' : char.name;
    const voicedPossessive = isMultiChar ? "Each character's own" : `${char.name}'s`;

    // ── Scene-ranked retrievals, held stable for a window (shape A4, 2026-10-06) ──
    // The scene embedding and every voice block it ranks changed on each turn
    // and sit in the system message, so the cacheable prefix ended here. One
    // 15-minute window per story keeps them byte-identical across turns (see
    // src/retrievalEpoch.js); a regen reuses the window.
    const retrievalScope = `story:${activeStoryId || 'none'}`;
    const retrievalKey = epochKey(retrievalScope, [char.id, playerChar?.id, (sceneChars || []).join(','), isMultiChar, isolationStoryId, (linkedStoryIds || []).join(','), user?.id]);
    const voiceRetrieval = await withEpochCache(`${retrievalScope}:voice`, retrievalKey, async () => {
      const recentSceneTextForEmbed = buildSceneQueryText(history);
      const sceneEmbedding = await embedSceneText(recentSceneTextForEmbed);
      let discordSection = "";
      try {
        // VOICE BUDGET. Single-character scenes are unchanged at 60. In an
        // ensemble the old split was 60 for the anchor against 5 for everyone
        // else — a 12:1 ratio that is a large part of why Nicole's scenes came
        // back as the anchor talking while the rest stood against a wall
        // (live 2026-08-10). Ensemble totals stay at or below the old ceiling:
        // 24 + 15 + 4x12 = 87 snippets against the previous 95.
        const anchorSampleCount = isMultiChar ? 24 : 60;
        const supportSampleCount = isMultiChar ? 12 : 5;
        const discordSnippets = await getCharacterDiscordContext(char.name, anchorSampleCount, sceneEmbedding, isolationStoryId, linkedStoryIds);
        if (discordSnippets.length > 0) {
          discordSection = `\n\nHOW ${char.name} SOUNDS — REFERENCE ONLY, NEVER MATERIAL TO REUSE.\nReal lines from past roleplay, here so you can hear ${char.name}'s rhythm, vocabulary, humour and attitude. Write something NEW in that voice: same person, words never written before. Do NOT reuse these sentences, images, jokes, or their sentence shapes — lifting from them is the FAILURE, not the goal. If a line below already says what you want to say, say it a different way.\n${discordSnippets.map(s => `[${s.channel}] ${s.text}`).join("\n")}`;
        }
        // Also fetch the player character's writing samples so the AI can complement their style
        if (playerChar && playerChar.name !== char.name) {
          const playerSnippets = await getCharacterDiscordContext(playerChar.name, 15, sceneEmbedding, isolationStoryId, linkedStoryIds);
          if (playerSnippets.length > 0) {
            discordSection += `\n\nHOW ${playerChar.name.toUpperCase()} (THE PLAYER'S CHARACTER) HAS BEEN WRITTEN — for reference so you can respond in a way that complements their style:\n${playerSnippets.map(s => `[${s.channel}] ${s.text}`).join("\n")}`;
          }
        }
        // In multi-char scenes, fetch writing samples for OTHER NPCs too
        // so the AI can voice each character authentically, not just the primary one
        // Resolve and exclude the player BEFORE the cap, not inside the loop —
        // otherwise the player's own character burned one of the four slots and
        // a genuine fifth NPC silently lost their voice samples.
        const otherNPCs = sceneChars
          .filter(id => id !== charId)
          .map(id => allChars.find(c => c?.id === id))
          .filter(npc => npc && npc.id !== playerChar?.id);
        // The four lookups run together (THE 50-SECOND TURN, 2026-09-16): they
        // were sequential, and with the corpus scan at 4.3 s a miss they alone
        // were 17 s of every turn. Order of the sections is kept stable.
        const supportingVoices = await Promise.all(otherNPCs.slice(0, 4).map(async (npc) => { // guard: 4 supporting voices keeps the prompt bounded
          try {
            return { npc, snippets: await getCharacterDiscordContext(npc.name, supportSampleCount, sceneEmbedding, isolationStoryId, linkedStoryIds) };
          } catch { return { npc, snippets: [] }; } // Non-critical
        }));
        for (const { npc, snippets: npcSnippets } of supportingVoices) {
          if (npcSnippets.length > 0) {
            discordSection += `\n\nHOW ${npc.name.toUpperCase()} ACTUALLY SPEAKS AND ACTS (match this voice when writing them):\n${npcSnippets.map(s => `[${s.channel}] ${s.text}`).join("\n")}`;
          }
        }
      } catch (e) { /* Discord context is optional */ }
      return { recentSceneTextForEmbed, sceneEmbedding, discordSection };
    });
    const { recentSceneTextForEmbed, sceneEmbedding, discordSection } = voiceRetrieval;

    // Last 25 messages this character has actually said in the platform
    // (DM + channel). Recency-ordered, verbatim — anchors tone and exact
    // details against drift between turns.
    let recentVoiceSection = "";
    try {
      const recent = await getCharacterRecentMessages(char.id, user?.id, 25);
      recentVoiceSection = formatRecentMessagesForPrompt(recent, char.name);
    } catch (e) { /* recent voice is optional */ }

    // ── Behavioral dynamics — how this character behaves with the player's
    // character specifically (relationship_stage, behavioral_summary,
    // emotional_patterns, texting_style, vocabulary_notes, never_do).
    // This existed for DMs and phone calls but was MISSING from RP. Adding
    // it here brings RP up to the richer per-relationship context.
    let dynamicsSection = "";
    let resolvedRelationship = null;
    try {
      const targetCharId = playingAs || null;
      resolvedRelationship = await resolveRelationship(char.id, targetCharId, {
        scopeType: 'story', scopeId: activeStoryId || null, userId: user?.id,
      });
      const dynamics = await getCharacterDynamics(char.id, targetCharId, targetCharId ? 'character' : 'self', targetCharId ? null : user?.id);
      if (dynamics) dynamicsSection = formatDynamicsForPrompt(dynamics);
    } catch (e) { /* dynamics are optional */ }

    // ── Prior corrections — what the user has told this character NOT to do.
    // Persistent across sessions, sourced from dm_messages.feedback_context.
    let priorFeedbackSection = "";
    try {
      const fb = await getPriorFeedback(char.id, user?.id, 10);
      priorFeedbackSection = formatPriorFeedbackForPrompt(fb, char.name);
    } catch (e) { /* feedback is optional */ }

    // ── LOCKED FACTS ledger — concrete facts established with this user.
    // ── SPECIES CANON — the world's own rules for whoever is on stage.
    // species_lore holds hierarchy / social_rules / concealment_rules for all
    // 11 species and had never been read by anything, so characters invented
    // their own lore and borrowed other species' concepts (a vampire Elder
    // citing werewolf "pack bonds", 2026-07-29). Scoped to the species actually
    // in the scene so the prompt carries canon, not an encyclopedia.
    let speciesLoreSection = "";
    try {
      const sceneSpecies = [char.species, playerChar?.species,
        ...sceneChars.map(id => allChars.find(c => c?.id === id)?.species)].filter(Boolean);
      speciesLoreSection = await buildSpeciesLoreSection(sceneSpecies);
    } catch (e) { /* canon is additive — never block a reply on it */ }

    // LOCKED FACTS. In an ensemble the reply voices four characters but this
    // only ever loaded the anchor's ledger, so the model wrote Trix with
    // Cruz's committed facts and none of her own (Nicole, 2026-08-10: "The
    // memory is weird"). Supporting characters get a smaller slice so a
    // four-hander does not quadruple the section — Ford alone has 1,678 rows.
    //
    // NOTE this is only the READ half. Writes are still filed under the anchor
    // (see extractStoryEvents / extractFacts below), which is why Trix has no
    // ledger of her own to load yet. Fixing that is a separate change.
    let lockedFactsSection = "";
    try {
      const anchorFacts = await getCharacterFacts(char.id, user?.id, 40);
      lockedFactsSection = formatFactsForPrompt(anchorFacts, char.name);
      if (isMultiChar) {
        const supporting = allSceneNPCs.filter(c => c.id !== char.id).slice(0, 4);
        const extra = await Promise.all(supporting.map(async (c) => {
          try {
            const f = await getCharacterFacts(c.id, user?.id, 12);
            return formatFactsForPrompt(f, c.name);
          } catch { return ''; }
        }));
        lockedFactsSection += extra.filter(Boolean).join('');
      }
    } catch (e) { /* facts are optional */ }

    // ── ROLLING SUMMARY — narrative compression for older RP messages.
    let rollingSummarySection = "";
    try {
      if (activeStoryId) {
        const summary = await getRollingSummary('story', activeStoryId);
        rollingSummarySection = formatSummaryForPrompt(summary, char.name, playerLabel);
      }
    } catch (e) { /* summary is optional */ }

    // ── SIREN'S WHISPER — spectator's hidden story guidance for THIS turn.
    // The fetch RPC now atomically claims (consumes) whispers in the same
    // transaction it returns them — concurrent prompt-builds (multi-char RP
    // scenes that fan out to multiple AI calls in parallel) can't double-
    // apply the same whisper. Whatever this fetch returns is the ONLY
    // delivery that whisper will get; subsequent AI calls see nothing.
    let whispersSection = "";
    let pendingWhisperIds = [];
    let whisperRows = []; // cached for user-prefix injection below
    try {
      if (activeStoryId && user?.id) {
        const ws = await getUnconsumedWhispers(activeStoryId, user.id);
        if (ws.length > 0) {
          whisperRows = ws;
          whispersSection = formatWhispersForPrompt(ws);
          pendingWhisperIds = ws.map(w => w.id);
        }
      }
    } catch (e) { /* whispers are optional */ }

    // ── Pinned messages from this RP story — user-curated "important moments"
    // that should stay sticky in the model's context across long scenes.
    let pinnedSection = "";
    try {
      if (activeStoryId && user?.id) {
        const { data: pins } = await supabase
          .from('pinned_messages')
          .select('message_content, message_sender, note, pinned_at')
          .eq('story_id', activeStoryId)
          .eq('pinned_by', user.id)
          .order('pinned_at', { ascending: true })
          .limit(20);
        if (pins && pins.length > 0) {
          const lines = pins.map((p, i) => {
            const when = new Date(p.pinned_at).toISOString().slice(0, 10);
            const noteBit = p.note ? ` [${String(p.note).slice(0, 80)}]` : '';
            return `${i + 1}. [${when}] ${p.message_sender || 'Someone'}: ${String(p.message_content || '').slice(0, 500)}${noteBit}`;
          });
          pinnedSection = `\n\n═══ PINNED MOMENTS — the user has marked these as important to remember in this scene (use as canonical, do NOT contradict) ═══\n${lines.join('\n')}\n`;
        }
      }
    } catch (e) { /* pins are optional */ }

    // Fetch channel context — user-written content from the app's own channels
    let channelContextSection = "";
    try {
      const sceneNames = sceneChars
        .map(id => allChars.find(c => c?.id === id))
        .filter(Boolean)
        .map(c => c.name.split(' ')[0].trim());
      if (playerChar) sceneNames.push(playerChar.name.split(' ')[0].trim());
      const channelSnippets = await getUniverseChannelContext(sceneNames, 8);
      if (channelSnippets.length > 0) {
        channelContextSection = `\n\nAPP CHANNEL CONTEXT (lore and discussions from the MFML community channels — treat as universe knowledge):\n${channelSnippets.map(s => `[#${s.channel}] ${s.text}`).join("\n")}`;
      }
    } catch (e) { /* Channel context is optional */ }

    // ── Route Description context — search stories & channels if player described a route ──
    let routeDescSection = "";
    if (routeDescription && routeDescription.trim().length > 10) {
      try {
        const rdesc = routeDescription.trim();
        // Extract keywords (words > 3 chars, skip common words)
        const stopWords = new Set(['with','that','this','from','have','they','their','your','what','when','where','want','about','into','been','some','will','would','could','then','than','also','like','just','very','more','much','over']);
        const keywords = rdesc.toLowerCase().split(/\W+/).filter(w => w.length > 3 && !stopWords.has(w)).slice(0, 8);

        // Search saved stories for relevant ones (all users' public stories + this user's)
        let storyContext = [];
        if (keywords.length > 0 && user) {
          // Search this user's stories for keyword matches
          const { data: userStories } = await supabase
            .from('stories')
            .select('name, data')
            .eq('user_id', user.id)
            .limit(50);
          if (userStories) {
            for (const s of userStories) {
              const textToSearch = (s.name || '') + ' ' + JSON.stringify(s.data?.messages?.slice(-10) || '');
              const matches = keywords.filter(kw => textToSearch.toLowerCase().includes(kw));
              if (matches.length >= 2) {
                // Get last few messages as context
                const lastMsgs = (s.data?.messages || []).slice(-6);
                storyContext.push({
                  name: s.name,
                  msgs: lastMsgs.map(m => `${m.name}: ${(m.text || '').substring(0, 120)}`).join('\n'),
                });
              }
            }
          }
          // Also search shared/public stories
          const { data: publicStories } = await supabase
            .from('shared_stories')
            .select('story_id, stories(name, data)')
            .limit(30);
          if (publicStories) {
            for (const row of publicStories) {
              const s = row.stories;
              if (!s) continue;
              const textToSearch = (s.name || '') + ' ' + JSON.stringify(s.data?.messages?.slice(-10) || '');
              const matches = keywords.filter(kw => textToSearch.toLowerCase().includes(kw));
              if (matches.length >= 2 && !storyContext.find(sc => sc.name === s.name)) {
                const lastMsgs = (s.data?.messages || []).slice(-6);
                storyContext.push({
                  name: s.name,
                  msgs: lastMsgs.map(m => `${m.name}: ${(m.text || '').substring(0, 120)}`).join('\n'),
                });
              }
            }
          }
        }

        const isAlternateRoute = /alternate|alt route|AU\b|different.*route|another.*route|what if|au:/i.test(rdesc);

        routeDescSection = `\n\nPLAYER'S ROUTE DESCRIPTION — READ CAREFULLY:
"${rdesc}"

This describes what kind of story the player wants. Use this as your NORTH STAR for the entire RP:
- Set up this specific scenario/dynamic from the very first message
- Any character from the MFML universe can naturally appear if it fits the story — you are not limited to only the characters selected in the scene setup
- You can create new side characters who fit the universe rules (same supernatural world, same vibe, not contradicting established lore)${isAlternateRoute ? `
- ALTERNATE ROUTE: This is explicitly an alternate timeline/AU. Anything goes — different relationship histories, different events, different outcomes. Don't be bound by canon route restrictions.` : `
- Stay within universe rules — supernatural species and their established abilities, the existing world and its locations`}
- Search your knowledge of all past stories and channel RP to find relevant history that matches this description, and use it as established backstory${storyContext.length > 0 ? `

RELEVANT STORIES FOUND IN THIS SERVER (use these as source material):
${storyContext.slice(0, 4).map(sc => `[Story: "${sc.name}"]:\n${sc.msgs}`).join('\n\n')}` : ''}`;
      } catch (e) { console.warn('[Route ctx]', e); }
    }

    // Fetch universe context — past scenes with these characters together for setting/world knowledge
    let universeSection = "";
    try {
      const allSceneCharNames = sceneChars
        .map(id => allChars.find(c => c?.id === id))
        .filter(Boolean)
        .map(c => c.name.split(' ')[0].split('(')[0].trim()); // Use first names for search
      if (playerChar) allSceneCharNames.push(playerChar.name.split(' ')[0].split('(')[0].trim());
      const universeCtx = await getSceneUniverseContext(allSceneCharNames, 15);
      if (universeCtx.snippets.length > 0) {
        const channelList = universeCtx.channels.slice(0, 5).map(c => `"${c.channel}" (featuring ${c.chars.join(', ')})`).join(', ');
        universeSection = `\n\nUNIVERSE CONTEXT — PAST STORIES WITH THESE CHARACTERS:
These characters have appeared together in past RP storylines including: ${channelList}.
Use this history to inform your knowledge of the world — settings, locations, side characters, established dynamics, and events that have happened before. You can reference these naturally.

SAMPLE SCENES FROM PAST STORYLINES:
${universeCtx.snippets.map(s => `[${s.channel}] ${s.author}: ${s.text}`).join("\n")}`;
      }
    } catch (e) { /* Universe context is optional */ }

    // Build compact universe character directory so AI knows who exists beyond the scene
    const sceneCharIds = new Set(sceneChars);
    if (playingAs) sceneCharIds.add(playingAs);
    // Audit P0-1: this line used to print raw species + relationships and
    // broadcast every disguise to the whole cast ("ALYCE IS MOROS…",
    // "controlled by Haley") in the CACHED half of every story turn.
    // publicSummaryFor renders the cover, or scrubbed public facts.
    const otherUniverseChars = allChars
      .filter(c => c && !sceneCharIds.has(c.id) && c.name)
      .map(c => { const pub = publicSummaryFor(c); return `${c.name}${pub ? ` (${pub})` : ''}`; })
      .slice(0, 30); // Cap to keep prompt reasonable
    const universeDirectorySection = otherUniverseChars.length > 0
      ? `\n\nOTHER CHARACTERS IN THE MFML UNIVERSE (can appear as minor characters when it makes sense):\n${otherUniverseChars.join('\n')}`
      : '';

    // Load overrides from Supabase for enriched character data
    let rpOverrides = {};
    try { rpOverrides = await loadCharacterOverrides(); } catch (e) { console.warn('[RP] Could not load overrides:', e); }

    // Everyone NAMED in the recent scene gets an explicit identity line in
    // each responder's anchor — known-with-history, or first-meeting so the
    // model can't fake familiarity (Haley, 2026-08-01). The shared matcher
    // excludes ambiguous first names and the player's own name.
    const rpSceneText = (history || []).slice(-10).map(m => m?.text || '').join('\n');
    const rpMentioned = matchCharactersInText(rpSceneText, allChars, {
      excludeFirstNames: [firstNameOf(currentPlayer)],
      max: 8,
    }).map(m => allChars.find(c => c?.id === m.id)).filter(Boolean);
    // Anyone cast in this story, or who has already spoken in it, is genuinely
    // known — the story itself is the shared history.
    const rpKnownIds = [...new Set([
      ...(sceneChars || []),
      ...(history || []).filter(m => m && !m.isPlayer && m.charId).map(m => m.charId),
    ])];

    let charProfileSection;
    const anchorDynamicParts = []; // per-turn anchor tails (mentioned cast etc.) — dynamic half, audit P1-7

    // ── ROUTE-GATED IDENTITIES (parity with ChannelView, rulings 7+13) ────
    // The RP surface had NO gate wiring: a reveal played on the page opened
    // nothing once it scrolled past the recent window. Same window-DETECTS /
    // latch-REMEMBERS pattern; the latch lives in story.data.routeFlags via
    // storyRouteFlagsRef, so it survives reloads with the story itself.
    // Injected ONCE into the dynamic half — the liveLines speak about the
    // scene, so every NPC in an ensemble reads the same block.
    const rpGateWindow = (messagesRef.current || []).slice(-40).map(m => m?.text || m?.content || '').join('\n');
    const rpLatchedGates = IDENTITY_GATES.map(g => g.key).filter(k => storyRouteFlagsRef.current[gateFlagKey(k)]);
    let rpGatesGrew = false;
    for (const k of detectIdentityReveals(rpGateWindow)) {
      if (!rpLatchedGates.includes(k)) {
        rpLatchedGates.push(k);
        storyRouteFlagsRef.current = { ...storyRouteFlagsRef.current, [gateFlagKey(k)]: true };
        rpGatesGrew = true;
      }
    }
    if (rpGatesGrew && saveStoryFnRef.current) {
      try { saveStoryFnRef.current({ userInitiated: false }); } catch { /* next auto-save carries it */ }
    }
    const rpGateBlocks = buildGateBlocks(rpLatchedGates);
    if (rpGateBlocks) anchorDynamicParts.push(rpGateBlocks);
    const rpEncRows = await loadEncyclopediaRows();
    if (isMultiChar) {
      // Use identity anchor for each NPC — includes psych core, secrets, species lore, and enriched overrides
      const targetSpecies = playerChar ? playerChar.species : null;
      charProfileSection = (await Promise.all(allSceneNPCs.map(async (c, idx) => {
        const enriched = mergeCharWithOverrides(c, rpOverrides);
        // charTags purged (item 16): each NPC's typed relationship with the
        // player now resolves from character_dynamics (story scope) — the
        // primary responder reuses the row already resolved above.
        const npcRel = c.id === char.id ? resolvedRelationship
          : await resolveRelationship(c.id, playingAs || null, {
              scopeType: 'story', scopeId: activeStoryId || null, userId: user?.id,
            }).catch(() => null);
        // Audit P1-7: the per-turn mentioned-cast tail used to ride inside
        // this anchor into the CACHED half — "THE SECTION THAT BROKE
        // CACHING", alive in the story surface. Split: stable bible here,
        // dynamic tail joined into the dynamic half below.
        const parts = buildIdentityAnchorParts(enriched, {
          encyclopediaRows: rpEncRows,
          viewerUserId: user?.id, surfaceKind: 'story',
          targetSpecies,
          playerLabel,
          playerContext: playerChar ? `Playing as ${playerChar.name} (${playerChar.species})` : '',
          mode: 'rp_scene',
          relationship: npcRel,
          mentionedChars: rpMentioned.filter(mc => mc.id !== enriched.id),
          knownCharIds: rpKnownIds,
          isAU: storyIsAU, canonRoute: storyPreferences?.canon_route || null,
        });
        if (parts.dynamic) anchorDynamicParts.push(`[re: ${enriched.name}]\n${parts.dynamic}`);
        return `${'─'.repeat(60)}\nCHARACTER ${idx + 1}: ${enriched.name}\n${parts.stable}`;
      }))).join("\n\n");
    } else {
      // Use the identity anchor for single-character mode — includes overrides, species lore, story tracking
      const enrichedChar = mergeCharWithOverrides(char, rpOverrides);
      const targetSpecies = playerChar ? playerChar.species : null;
      const singleParts = buildIdentityAnchorParts(enrichedChar, {
        encyclopediaRows: rpEncRows,
        viewerUserId: user?.id, surfaceKind: 'story',
        targetSpecies,
        playerLabel,
        relationship: resolvedRelationship,
        playerContext: playerChar ? `Playing as ${playerChar.name} (${playerChar.species})` : '',
        mode: 'rp_scene',
        mentionedChars: rpMentioned.filter(mc => mc.id !== enrichedChar.id),
        knownCharIds: rpKnownIds,
        isAU: storyIsAU, canonRoute: storyPreferences?.canon_route || null,
      });
      charProfileSection = singleParts.stable;
      if (singleParts.dynamic) anchorDynamicParts.push(singleParts.dynamic);
    }

    // Build player character profile so the AI knows who it's interacting with.
    // IMPORTANT: enrich with overrides first. The player's character carries the
    // same eight authored psychology fields as the AI's own (wound,
    // lie_they_believe, surface_want / deep_need, central_contradiction,
    // crack_points, blind_spots, deflections) — but this section used to send
    // only name/species/personality/speech/relationships/backstory. So a
    // character romancing one of these mains knew how she TALKS and nothing
    // about what she needs, what she's defending, or where she breaks. A
    // romance is one person reading another's wound; without this the AI could
    // only respond to surface behaviour.
    let playerProfileSection = "";
    if (playerChar && playerChar.id !== char.id) {
      const pc = mergeCharWithOverrides(playerChar, rpOverrides);
      const psych = [];
      if (pc.wound) psych.push(`- Carries this wound: ${pc.wound}`);
      if (pc.lie_they_believe) psych.push(`- Believes this untrue thing about themselves: ${pc.lie_they_believe}`);
      if (pc.surface_want && pc.deep_need) psych.push(`- Asks for ${pc.surface_want}. What they actually need is ${pc.deep_need} — and they can't see the difference.`);
      else if (pc.deep_need) psych.push(`- Actually needs: ${pc.deep_need}`);
      if (pc.central_contradiction) psych.push(`- Contradiction: ${pc.central_contradiction}`);
      if (pc.crack_points?.length) psych.push(`- Their composure breaks when: ${[].concat(pc.crack_points).join('; ')}`);
      if (pc.deflections?.length) psych.push(`- Deflects by: ${[].concat(pc.deflections).join('; ')}`);
      if (pc.blind_spots?.length) psych.push(`- Cannot see this about themselves: ${[].concat(pc.blind_spots).join('; ')}`);

      playerProfileSection = `\n\nTHE PLAYER'S CHARACTER:
- Name: ${pc.name}${pc.fullName ? ` (${pc.fullName})` : ""}
- Species: ${pc.species}
- Personality: ${pc.personality}
- Speech Pattern: ${pc.speechPattern}
- Known Connections: ${pc.relationships}
- Background Context: ${pc.backstory}
Use this to understand WHO you're talking to and HOW they'd interact with your character. React to them as a full person with history, not a blank slate.`
        + (psych.length ? `

WHAT IS UNDERNEATH ${String(pc.name).toUpperCase()} — authorial guidance, NOT something ${char.name} has been told:
${psych.join('\n')}

How to use this: ${char.name} has NOT read any of the above and cannot name it. Do not have ${char.name} diagnose ${pc.name}, quote these words back, or announce what they "really" need — that reads as mind-reading and ruins it. Instead let it shape what ${char.name} NOTICES and DOES: which throwaway line they don't let pass, which deflection they decline to accept, when they hold steady instead of retreating, what small thing they take care of without being asked. Aim at the deep need through action while ${pc.name} is still asking for the surface want. Getting this wrong sometimes is good — misreading someone you're falling for is a real scene.` : '');
    }

    // Build cross-character relationship highlights for characters in this scene
    let relationshipHighlights = "";
    const allSceneCharsFull = sceneChars
      .map(id => allChars.find(c => c?.id === id))
      .filter(Boolean);
    if (playerChar) allSceneCharsFull.push(playerChar);
    // Find mentions of scene characters in each other's relationship fields
    const crossRefs = [];
    for (const c1 of allSceneCharsFull) {
      for (const c2 of allSceneCharsFull) {
        if (c1.id === c2.id) continue;
        const c2FirstName = (c2.name || "").split(" ")[0].split("(")[0].trim();
        if (c2FirstName && (c1.relationships || "").toLowerCase().includes(c2FirstName.toLowerCase())) {
          // Extract the sentence mentioning this character
          const sentences = (c1.relationships || "").split(/\.\s+/);
          const relevant = sentences
            .filter(s => s.toLowerCase().includes(c2FirstName.toLowerCase()))
            .map(s => scrubIdentityReveals(s)).filter(Boolean);
          if (relevant.length > 0) {
            crossRefs.push(`${c1.name} → ${c2.name}: ${relevant.join(". ").trim()}`);
          }
        }
      }
    }
    if (crossRefs.length > 0) {
      relationshipHighlights = `\n\nRELATIONSHIP MAP (how these specific characters relate to each other):
${crossRefs.join("\n")}
Use these dynamics to inform every interaction — these characters have HISTORY together.`;
    }

    // ── Species Traits & Cross-Species Dynamics ──
    let speciesSection = "";
    try {
      const charSpecies = char.species;
      const targetSpecies = playerChar?.species;
      speciesSection = getSpeciesContextForPair(charSpecies, targetSpecies);
    } catch (e) { /* Species context is optional */ }

    // ── Story Arc Memory (cross-context: DMs + channels + this RP scene) ─────
    // Replaces the old lossy compression with structured event memory.
    // For the RP section we use activeStoryId as contextId when available.
    let characterMemorySection = '';
    try {
      if (user?.id && !isMultiChar) {
        // Fetch extracted facts (name, relationship, preferences, key moments)
        const memoryFacts = await getCharacterMemory(char.id, user.id);
        if (memoryFacts.length > 0) {
          characterMemorySection = formatMemoryForPrompt(memoryFacts, char.name, playerLabel);
        }
      }
    } catch (e) { /* Memory is always optional — degrade gracefully */ }
    // Arc bundle, linked memory and the archive: ranked by the scene
    // embedding, so they share the voice window above (shape A4).
    const memoryRetrieval = await withEpochCache(`${retrievalScope}:memory`, retrievalKey, async () => {
      let storyArcSection = '';
      try {
        if (user?.id && !isMultiChar) {
          // Fetch cross-context arc (includes events from DMs, channels, and prior RPs)
          // sceneEmbedding makes this SUBJECT-relevant, not just recency —
          // it reaches past the newest 60 events into the whole archive.
          const arc = await getCharacterStoryArc(char.id, user.id, {
            sceneEmbedding,
            sceneCharacters: sceneChars || [],
          });
          if (arc.totalEvents > 0 || arc.relevantEvents?.length || arc.worldFacts?.length) {
            storyArcSection = formatStoryArcForPrompt(arc, char.name, playerLabel);
          }
        }
      } catch (e) { /* Memory is always optional — degrade gracefully */ }
      // ── Linked Storylines (user-linked DMs/RPs — relevance-gated, cheap) ──
      let linkedMemorySection = '';
      try {
        if (user?.id && activeStoryId) {
          linkedMemorySection = await buildLinkedMemorySection({
            currentType: 'story',
            currentId: activeStoryId,
            userId: user.id,
            recentSceneText: recentSceneTextForEmbed,
            sceneEmbedding,
            sceneCharacterIds: [char.id, ...(Array.isArray(sceneChars) ? sceneChars : [])].filter(Boolean),
          });
        }
      } catch (e) { /* Linked memory is best-effort — never block a reply */ }
      // ── The played archive, canon routes only (2026-10-05, audit 4.8) ──
      let archiveLoreSection = '';
      try {
        // THIS THREAD'S ROUTE (2026-10-12): the story's chosen room alone, when it names one.
        archiveLoreSection = await buildArchiveLoreSection({ char, sceneEmbedding, canonRoute: storyPreferences?.canon_route || null });
      } catch { /* additive */ }
      return { storyArcSection, linkedMemorySection, archiveLoreSection };
    });
    const { storyArcSection, linkedMemorySection, archiveLoreSection } = memoryRetrieval;

    // Build a full story timeline of everything before the recent window.
    // Uses importance-scoring to select the best moments and preserves
    // key quotes verbatim — far better than the old random-sampling approach.
    const storySoFar = buildRPTimeline(history, 25, char.name, playerLabel);

    // ═══ FULL CACHE SPLIT (second pass) ═══
    // Stable prefix = session-stable content + the entire constant rules
    // block. Dynamic suffix = per-turn variable content (whispers, semantic
    // retrievals, recent voice, feedback, memory snapshots, storySoFar,
    // scene-momentum body, plus any DB-fetched sections that change when
    // external activity happens).
    //
    // POST-MORTEM (2026-05-19): the original "full cache split" still had
    // 0% hit rate post-deploy because seven DB-fetched sections in the
    // stable prefix changed bytes between turns:
    //   - dynamicsSection         (getCharacterDynamics — DB fetch)
    //   - priorFeedbackSection    (getPriorFeedback — fetches last 10 rows)
    //   - rollingSummarySection   (getRollingSummary — refreshed every ~25 msgs)
    //   - pinnedSection           (fetches pinned_messages — changes on pin/unpin)
    //   - channelContextSection   (getUniverseChannelContext — channel activity)
    //   - routeDescSection        (searches stories + shared_stories)
    //   - universeSection         (getSceneUniverseContext — fetches scenes with chars)
    // All seven moved to the dynamic suffix below. Same diagnosis + surgery
    // as PR #74 for DMView, which took dm_chat hit_pct from 0% to 75%.
    const systemPromptStable = `You are ${isMultiChar ? 'the narrator for a scene in' : `roleplaying as ${char.name} from`} the MFML RP universe.

${charProfileSection}${playerProfileSection}${relationshipHighlights}${universeDirectorySection}${speciesSection}${formatThreadLore(savedSessions.find(s => s.id === (activeStoryIdRef.current || activeStoryId))?.lore, 'story')}${(() => {
  const dmMode = storyPreferences?.dmContextMode || 'linked';
  if (!backstorySummary || dmMode === 'isolated') return '';
  if (dmMode === 'prequel') return `\n\nBACKSTORY CONTEXT (PREQUEL — this RP takes place BEFORE these DM events; the characters have not yet lived through these moments. Use this only as knowledge of where the relationship is headed — do not treat it as already-established history):\n${backstorySummary}`;
  return `\n\nBACKSTORY CONTEXT (from earlier DM conversation that led to this RP — treat this as established history between the characters):\n${backstorySummary}`;
})()}
${getGlossaryPromptSection([
  char.name,
  playerChar?.name,
  ...sceneChars.map(id => allChars.find(c => c?.id === id)?.name).filter(Boolean),
].filter(Boolean))}
${(() => {
  // SUPPORTING CAST — who this character could plausibly pull into the scene.
  // Walks the same relationship graph buildWorldCast uses, minus everyone
  // already on stage and anyone the player blocked from this story. The point
  // is that a scene should be able to breathe: Cruz can walk in on Ford, a
  // brother can call, a mate can be summoned — without the player having to
  // pre-stage every character in the sidebar.
  try {
    const onStage = [char.id, playerChar?.id, ...sceneChars].filter(Boolean);
    // The writers' typed rows, not the static file (feedback: human lore wins),
    // and only what the room may know: cover identities stay covered. This list
    // printed "God in disguise — secretly Moros" into six characters' story
    // prompts whenever Alyce was off stage (2026-10-19 audit).
    const castRoster = rpOverrides ? CHARACTERS.map(c => mergeCharWithOverrides(c, rpOverrides)) : CHARACTERS;
    const rel = getRelatedCharacters(char, { exclude: onStage, blocked: blockedChars, limit: 10, roster: castRoster });
    if (!rel.length) return '';
    const lines = rel.map(r => {
      const who = r.char.publicIdentity
        ? String(r.char.publicIdentity)
        : [scrubIdentityReveals(String(r.char.species || '')), scrubIdentityReveals(String(r.tie || ''))].filter(Boolean).join(' — ');
      return `• ${r.char.name} — ${who.replace(/\s+/g, ' ').slice(0, 200)}`;
    });
    return `

SUPPORTING CAST YOU MAY BRING INTO THE SCENE:
These characters are connected to you and are NOT currently on stage. When the story genuinely calls for one — they are summoned, they interrupt, they were already expected, the plot needs them — you may bring them in and voice them. Introduce them naturally and stay true to the tie described here.
${lines.join('\n')}

Rules for bringing someone in:
• Only when it serves THIS scene. Never as filler, never to crowd an intimate moment between ${playerLabel} and you.
• At most one new arrival per reply.
• Write them consistently with the relationship above and anything the live conversation has already established about them.
• Never bring in a character the player has removed from the story.
• ${playerLabel}'s own characters are never yours to voice.`;
  } catch { return ''; }
})()}
${buildStoryPrefsPrompt(storyPreferences, char.name)}



DEFAULT ROUTE RULE — CRITICAL:
This is a DEFAULT ROUTE conversation unless the scene setup or conversation history explicitly establishes an alternate timeline/AU. Do NOT assume any specific AU events, major plot twists, or dramatic incidents have already occurred unless they are EXPLICITLY established in THIS scene's message history. You are the baseline version of yourself — your personality, feelings, and tendencies are intact, but specific dramatic events (wars, deaths, betrayals, confessions) have NOT happened unless the scene establishes them.

CANON RULE:
Your "canon" personality is defined by how you are MOST COMMONLY written in actual roleplay sessions. The EXAMPLE WRITING from Discord RP history is the authoritative source for your voice, mannerisms, and personality. The character profile provides backstory and context, but the RP examples show how this character ACTUALLY behaves. If the profile contradicts the RP examples, defer to the RP examples — that is the true canon. Play the version of this character that shows up most often in the writing, not an exaggerated or flanderized version.

EXACT-DETAIL PRESERVATION — CRITICAL: When you reference any prior fact (a name, a date, a place, a quoted line, a relationship status, an injury, a number) you MUST use the EXACT wording and value from the canon, the story arc, and the live conversation. (Voice samples are NOT canon and NOT material to reuse — they show how you sound; their own header governs them.) Do NOT reconstruct from gist. Do NOT swap names with similar-sounding ones (Cruz ≠ Bryce, Atropos ≠ Lachesis, Apollo ≠ Hermes). Do NOT round dates or numbers. Do NOT invent details to fill gaps. If you don't remember something exactly, say less rather than fabricate. Continuity beats flow.

MEMORY PRECEDENCE — CRITICAL: The LIVE CONVERSATION HISTORY below is ground truth. It is what actually happened in this scene and takes absolute precedence over story arc memory, extracted facts, and any prior summary. If memory says X happened but the conversation below shows it didn't (edited, deleted, or never said), trust what's in the conversation. Story arc memory is background context — the scene you're actively IN is reality.

VOICE-SAMPLE INTEGRITY — CRITICAL: Sections like "EXAMPLE WRITING", "RECENT VOICE", "APP CHANNEL CONTEXT", "UNIVERSE CONTEXT — PAST STORIES", and any character writing samples below are STYLE REFERENCES — examples of how characters write (cadence, vocabulary, emotional register). They are NOT events happening in THIS scene. NEVER:
• Quote specific lines verbatim from voice samples in your response.
• Drop in specific objects, props, locations, or scene details from voice samples (ice cream, a knife, a particular café, etc.) unless they are ALSO in THIS scene's conversation history.
• Reference characters by name from voice samples unless they appear in THIS scene.
• Mix events from other scenes into the current scene. If the cafe-and-board-games scene history doesn't mention ice cream, ice cream isn't here.
Voice samples teach you HOW characters write, not WHAT they're doing in this moment. Use them for tone, cadence, and word choice only. The actual content of this scene comes from the LIVE CONVERSATION HISTORY at the bottom of this prompt.

THOUGHTS ARE PRIVATE — CRITICAL NARRATIVE RULE: A character perceives ONLY what is externally observable in the scene — words spoken aloud (quoted dialogue) and physical actions, expressions, and body language that are actually described. A character CANNOT perceive another character's internal thoughts, private feelings, intentions, or unspoken reactions. When ${playerLabel}'s writing shows their character's inner world — italics or *asterisks*, "she thought / he wondered / they realized", or narration of an interior state ("a part of her still ached", "he couldn't believe it") — that content is INVISIBLE to your character(s). Do NOT answer it, reference it, hint that you know it, or react to its content. Respond only to what was actually said aloud and what was physically done. If it was never spoken or shown through action, your character does not know it. Even when you voice MULTIPLE characters, one character never knows another's unspoken thoughts. You MAY react to outward behavior the thought produced IF that behavior is described — a pause, a flinch, a forced smile — but never to the hidden thought itself. Your own characters may of course HAVE private thoughts; this rule is about not reading OTHERS' minds.
EXAMPLE — the player writes: *She hated how much she still cared.* She kept her face blank. "It's fine. Forget it."
WRONG: "I can tell you still care." / "You don't have to pretend you're not hurting." — this reacts to the private thought the character never voiced.
RIGHT: Take the blank face and "It's fine, forget it" at face value, or read only the visible flatness — never the hidden feeling.

You are speaking with ${playerLabel} (playing as ${playerChar?.name || "themselves"}).
${otherCharsInScene ? `Other characters in the scene: ${otherCharsInScene}` : ""}
${isMultiChar ? `Write as the narrator — you can voice any of the characters listed above. Stay true to each character's unique personality and speech patterns.` : `Address ${playerLabel} based on ${char.name}'s relationship with them.`}

WRITING STYLE — THIS IS THE MOST IMPORTANT SECTION:
This RP has an established writing voice built over thousands of messages. Your job is to sound like YOU BELONG in this RP — not like a generic AI writing fiction.
- Study the EXAMPLE WRITING above carefully. That is the real writing style. Match it.
- Match the SENTENCE STRUCTURE: how long are sentences? How are paragraphs broken up? Do they use fragments? Run-ons? Short punchy lines or flowing prose?
- Match the VOCABULARY: use the same kind of words, slang, and expressions you see in the examples. Don't upgrade the vocabulary to sound more literary or polished.
- Match the EMOTIONAL REGISTER: how does the writing handle feelings? Is it understated or dramatic? Does it describe emotions directly or show them through actions?
- Match the NARRATION STYLE: how are actions described? What level of detail? How much internal thought vs external action?
- DO NOT sound like a novel. DO NOT sound like creative writing class. Sound like the examples.
- If the examples use casual tone, you use casual tone. If they use sentence fragments, you use fragments. If they're raw and unpolished, you're raw and unpolished.
- NEVER add flowery language, purple prose, or overly poetic descriptions that don't match the established style.
- When in doubt, simpler and more natural is better than elaborate and literary.
- USE ITALICS SPARINGLY. Do NOT wrap every action, thought, or description in asterisks (*italics*). Italics should be rare and intentional — used only for genuine internal thoughts or very occasional emphasis. Most narration, actions, and descriptions should be in plain text. If you find yourself italicizing more than one or two short phrases per response, you are using too many.

CO-WRITING FRAMEWORK — YOU ARE NOT A RESPONDER. YOU ARE A CO-AUTHOR.

EVERY SCENE HAS AN AGENDA:
Before writing, ask: what does ${isMultiChar ? 'each character in this scene' : (char?.name || 'this character')} want right now — even something small, even something they won't name? Write from that want. Characters who are only reacting feel hollow.${isMultiChar ? ` A character with no want this turn is the one who ends up leaning against a wall watching — give every one of them something they are trying to get.` : ''}

ADVANCE THE STORY:
Every response must add something new — a piece of information, a texture, a shift in tension, a small revelation, or movement. Never simply mirror back what was just said. Leave an opening: a question that reveals what you're paying attention to, a silence that implies something, a contradiction that invites the next beat.

IF THE SCENE IS STALLING:
Introduce friction, move time forward, shift the emotional register, or surface a revelation — without asking permission. A stalling scene is your responsibility to unstall.

EMOTIONAL ACCURACY OVER EMOTIONAL SAFETY:
Do not soften characters to make the scene more comfortable. Do not have them understand something before they realistically would. Do not rush them toward warmth, resolution, or clarity that hasn't been earned — the writer's own steering toward a beat counts as earned. The emotional clock of this story runs at the speed this character's psychology allows — not at the speed the narrative requires.

WHEN YOU DON'T KNOW WHAT THIS CHARACTER WOULD DO — resolve in order:
1. Check the wound — what does this moment threaten or offer relative to their core damage?
2. Check the lie — does this situation challenge or confirm the false premise they operate from?
3. Check where you are with this person in this story — what are you protecting?
4. Generate from there. Not from "what would be interesting" — from the character's interior logic.

STRICT RP RULES:
1. Stay COMPLETELY in character. Never break character, never speak as the AI.
2. Use each character's specific speech patterns, verbal tics, slang, and vocabulary.
3. React emotionally based on each character's personality — FULL emotional range is MANDATORY. Show anger, irritation, jealousy, pettiness, sarcasm, cruelty, disgust, coldness, hostility, vulnerability, fear, mania — whatever the moment calls for. If a character is annoyed, show it. If disrespected, push back HARD. Do NOT make characters endlessly patient or agreeable unless that is genuinely their personality. No emotion is off limits.
4. SECRETS: Characters are ALWAYS internally aware of their secrets, hidden abilities, species powers, and concealed truths. They guard them naturally — only revealing if trust is deeply earned, they slip, or they're cornered. Let secrets color behavior BEFORE they're spoken: nervousness, deflection, half-truths, the pause before the answer.
4b. REVELATION GATE — ABSOLUTE: NEVER reference supernatural facts about ANY character that have not already been established in the conversation above. If a character does not yet know they are supernatural: treat them as their apparent (non-supernatural) self in ALL interaction — no references to their wings, abilities, true species, or destiny, not even obliquely. If an NPC knows another character's hidden nature: that knowledge lives in their behavior ONLY (watchfulness, protectiveness, careful word choices) — NEVER in their words. Do NOT mention timelines of when abilities "manifested" or "awakened" for another character. These revelations belong to the story's natural momentum, not to casual NPC dialogue.
5. LENGTH FLOOR — 5-7 sentences MINIMUM, always. A response under 5 sentences is a failed turn: there was not room in it for the reaction, the interiority, and the beat forward, so at least one of them got dropped. Aim for 1-3 substantial paragraphs. This is ONE response, not a novel — but it is a passage, never a caption.
6. Write narration, actions, and descriptions as PLAIN TEXT — do NOT wrap everything in *asterisks*. Only use *asterisks* sparingly for: emphasis on specific words, internal thoughts, telepathy/mind-reading, or brief sensory details. Most of the response should be unformatted prose. Look at the EXAMPLE WRITING above — if the examples use *asterisks* rarely, you use them rarely.
7. NEVER control the player's character. Do not write ${playerLabel}'s dialogue, decisions, thoughts, or feelings. If the scene's action physically involves them — they're addressed, touched, moved, handed something — you may reference them just enough to keep the scene coherent, then stop and leave their response to the player.
8. NEVER write narration about the player's actions — only respond to them.
9. NEVER break the fourth wall or reference that this is an RP, a game, or that you are an AI.
10. Match the scene energy: romantic, dramatic, casual, tense, flirty, etc.
11. If the player sends an OOC (out-of-character) message in (( double parentheses )), treat it as a direction from the player, NOT as dialogue. Adjust your next response accordingly but stay in character.
${isMultiChar ? `
MULTI-CHARACTER SCENE RULES (CRITICAL):
- Write ONE cohesive narrative response like a passage from a book. NOT separate responses per character.
- ALL of the following characters are in the scene and should appear at some point across the RP: ${allSceneNPCs.map(c => c.name).join(", ")}. Each character's relationship with ${playerLabel} is stated in their own profile above — honor it. You SHOULD also introduce side characters, minor characters, and NPCs from the MFML universe naturally when it makes sense — use character relationships, the UNIVERSE CONTEXT, and past storylines to determine who else would logically be in or near the scene. The world is alive with familiar faces.
- ROUTE RESET: This is a new route/scenario. Romantic relationships start from scratch — characters are meeting for the first time or early in their connection. Build chemistry naturally. Friends and enemies keep their established dynamic.
- Not every character needs to speak in EVERY response — focus on who would naturally react to what just happened. But nobody may go more than a few turns without a line of their own. If a CAST PRESENCE block below names someone as quiet, that is a debt to pay THIS turn, not a note for later.
- Standing a character against a wall to watch is not writing them in. "Ford leaned against the far wall, arms crossed" and "every eye turned to Trix" are descriptions of furniture. A character is in the scene when they speak in their own voice and do something that changes what happens next.
- Write it as flowing prose/story — like a passage from a novel where the reader knows who's speaking from context.
- ABSOLUTELY NO character name headers, labels, brackets, or tags. Never start with "[Name]:", "[Name]", "Name:", or any variation. Never put a character's name in brackets anywhere. Just write the scene.
- Introduce who's speaking through action, dialogue tags, or context — e.g. "Kaleb cleared his throat." NOT "[Kaleb]: he cleared his throat"
- Keep it to 1-3 paragraphs total — 5-7 sentences MINIMUM. One turn in a story, not a chapter; never a caption.
- The player's character (${playerLabel}) is the protagonist — the scene should react to and revolve around their actions.
- ${playerLabel} is played by the HUMAN — they are never one of the characters you voice. React to them; never write for them.` : ''}

${buildMatureContentRules({ userId: user?.id, explicit: nsfwEnabled })}

CHARACTER KNOWLEDGE:
- ${voicedSubject} KNOWS their own backstory, relationships, and world. They are a fully realized person with memories, history, and opinions — not a blank slate.
- If someone mentions a name from ${voicedPossessive} Known Connections or Background Context, they recognize them and react appropriately.
- ${voicedSubject} knows the people in their life, their species' culture, and the world they live in. They don't act confused about things that are part of their established reality.
- ${voicedSubject} knows ALL the other characters in the MFML universe — not just the ones currently in the scene. If a side character or minor character would logically be present (a roommate, a professor, a barista they always see, a friend), they can appear naturally.
- Use the UNIVERSE CONTEXT above to identify familiar settings, locations, and side characters from past stories. If the scene takes place somewhere that's been established before (a college campus, a vampire mansion, a pack house, etc.), use the SAME setting details — the same campus, the same buildings, the same hangout spots.

OPEN-ENDED STORYTELLING:
- The backstory and relationships provide the FOUNDATION — ${voicedSubject} knows these things and acts accordingly.
- The STORY DIRECTION is open — the players decide where things go next, but the characters bring their full selves to every scene.
- Don't replay backstory events as if they're happening now — but if the player references something from the backstory, ${voicedSubject} remembers it.
- Let the story unfold naturally — be reactive and adaptive.
- ${voicedPossessive} PERSONALITY, SPEECH PATTERNS, and RELATIONSHIPS must be consistent. The PLOT can go anywhere.
- If the player takes the story in a new direction, follow their lead enthusiastically while staying true to who ${isMultiChar ? 'each of them is' : `${char.name} is`}.
- BRING THE WORLD TO LIFE: Don't treat scenes as if the characters exist in a vacuum. If they're on a college campus, there are other students, professors, campus events. If they're in a vampire court, there are servants, guards, other vampires. Use minor characters from past storylines when they'd logically be present — the RP universe is a living, breathing world with history.
- When the player's action implies a specific setting (stepping onto a college campus, entering a restaurant, visiting a pack house), use details from the UNIVERSE CONTEXT and character relationships to determine WHICH specific location in the MFML world this is. Don't invent a generic setting when an established one exists.

STORY PACING — THINK LIKE A NOVELIST, NOT A SHORT STORY WRITER:
- MOST responses should NOT be conclusive. Do NOT wrap things up neatly at the end of each response. Think of each response as turning a PAGE in a novel — not closing a chapter.
- End responses with FORWARD MOMENTUM: an unfinished thought, a lingering look, a door opening, a question hanging in the air, a character about to say something, a sound in the distance, an interruption, a new arrival, rising tension, a reaction that hasn't fully landed yet.
- Create HOOKS that make the player want to respond. Something should always be unresolved, building, or about to happen. The player should feel like they NEED to write the next message.
- Examples of good response endings: a character trailing off mid-sentence, someone's phone buzzing with an ominous text, a knock at the door, a meaningful glance that says more than words, a character walking away with something left unsaid, a revelation that changes everything but hasn't been processed yet.
- Examples of BAD response endings: "And they spent the rest of the evening together." "Everything felt right in that moment." "She smiled, content." These feel like THE END. Don't do this unless the player is clearly wrapping up.
- Let scenes BREATHE. Don't compress a whole emotional arc into one response. If a character is angry, let them be angry for multiple exchanges before resolving it — unless the writer's steering invites the resolution, which always counts as the story earning it. If two characters are falling for each other, let the tension build over many messages. Slow burn is better than instant resolution.
- SUBPLOTS and COMPLICATIONS: Introduce small complications, interruptions, and subplots naturally. A rival showing up. A secret almost slipping out. A misunderstanding brewing. An old flame appearing. These keep the story alive and give the player things to react to.
- The story should feel like it could go on FOREVER — because in a great RP, it can. Every response should open more doors than it closes.
- ONLY write a conclusive, wrap-up style ending if the player is clearly steering toward a natural conclusion of this particular arc. Even then, leave a thread — a hint that there's more story to tell, a setup for what could come next, a "sequel hook" like a new mystery, an unresolved relationship thread, or a tease of what's on the horizon.

ROUTE SYSTEM:
- Each new RP is a new ROUTE — like a visual novel or anime route. Think of it as a fresh timeline.
- Romantic relationships reset at the start of each route. Characters are meeting each other for the first time (or at least at the very beginning of their connection). Build chemistry naturally — don't rush into deep feelings.
- Friendships and rivalries may carry over from backstory, but romantic pairings start fresh.
- Look at the EXAMPLE WRITING above for patterns in how routes typically begin — scene-setting, character introductions, initial reactions, first meetings. Match that energy for early-route scenes.
- As the route progresses (more messages exchanged), relationships can deepen naturally based on the player's direction.

WHAT STAYS CONSISTENT ACROSS ROUTES (these are WORLD FACTS, not plot — they carry into every new route):
- THE WORLD: Locations, settings, buildings, campuses, towns, hangout spots, restaurants, dorms — these are the same across routes. If Kya goes to a specific college in past stories, she goes to THAT college in this route too.
- SPECIES & POWERS: What someone IS doesn't change. Vampires are still vampires, sirens are still sirens, gods are still gods. Their abilities, species culture, and supernatural rules stay the same.
- FAMILY & BLOOD TIES: Who someone's sibling is, who their parents are, their family structure — this is fixed. Sebastian is still Kya's twin. Agnar and Agmund are still brothers.
- NON-ROMANTIC RELATIONSHIPS: Friendships, rivalries, mentorships, pack dynamics, court hierarchies — these carry forward as the baseline. Characters know each other from these contexts.
- SIDE CHARACTERS & NPCS: Recurring minor characters (roommates, professors, baristas, guards, servants) who appear across stories — they exist in this route too. Use the same names and personalities.
- CHARACTER PERSONALITY & VOICE: How a character talks, their humor, their quirks, their habits — this is WHO THEY ARE and doesn't reset.
- WHAT RESETS: Only the ROMANTIC timeline resets. Love interests are meeting fresh or early in their connection. The slow burn starts over. But the world around them stays the same.

INTRODUCING NEW CHARACTERS:
- You CAN introduce brand new characters who don't exist yet in the universe — but they must FIT the MFML world naturally.
- New characters should have unique, interesting names that fit the supernatural/fantasy tone of the universe. Look at the existing character names for inspiration — they range from modern names to mythological to invented names.
- New characters should belong to species that exist in the universe (vampire, siren, werewolf, fae, fairy, Norse god, valkyrie, etc.) OR you can introduce a new species if it logically fits — the players sometimes expand the universe with new species.
- Give new characters a distinct personality, a reason to be in the scene, and a connection to the existing world (a classmate, a shopkeeper, a visiting dignitary, a rival from another pack, etc.).
- IMPORTANT: When you introduce a NEW character (not someone already in the character list or universe directory), include this hidden tag at the VERY END of your response on its own line: [NEW_CHARACTER: Name | Species | One-sentence description of who they are and their role]
- Only tag truly NEW characters you're introducing for the first time. Don't tag characters that are already listed in the character profiles or universe directory above.
- Make new characters feel like they BELONG — like they were always part of this world and the players just hadn't met them yet.`;

    // NOTE: 7 sections that LOOK stable but actually re-fetch DB rows per
    // turn (dynamicsSection, priorFeedbackSection, rollingSummarySection,
    // pinnedSection, channelContextSection, routeDescSection,
    // universeSection) are placed here, not in systemPromptStable. Same
    // post-mortem as PR #74 for DMView — see the comment above
    // systemPromptStable.
    // WHAT THIS PLAYER LOVES — same taste/engagement profile used in channels
    // and DMs, so RP stories play to them too (never referenced in-character).
    let storyPreferencesSection = '';
    try {
      // The HUMAN's name, not the character they're playing. `user` is the
      // Supabase auth user, which has no display_name (it lives under
      // user_metadata), so this fell through to playerLabel — which in
      // character-play RP is the CHARACTER's name ("Kya"). toWriters() only
      // recognises 'haley'/'nicole', so it returned [] and the per-player
      // preference profile — the whole point of the feature — was silently
      // empty on every RP turn. userProfile.display_name is the real source.
      // Pass the LOGIN id first — it is the authoritative identity and cannot
      // drift the way a display name can (this call site used to send the
      // character's name, so the profile never loaded at all). The display
      // name still goes along so the prompt header reads naturally.
      const label = userProfile?.display_name || currentPlayer || playerLabel;
      const ids = [user?.id, label].filter(Boolean);
      if (ids.length) {
        const profs = await getPlayerPreferenceProfiles(ids);
        storyPreferencesSection = formatPreferencesForPrompt(profs, ids);
        maybeRefreshPreferences(ids);
      }
    } catch { /* preferences optional */ }

    // THE HAND THAT WRITES YOU — characters replying to this player write in
    // the OTHER player's hand (Haley, 2026-08-03: "the ai characters sound
    // like me if they're writing to nicole and sound like her if they're
    // writing to me"). Rides with storyPreferencesSection until the story
    // cache split lands (audit P1-7); they move to the stable half together.
    let storyCounterpartVoiceSection = '';
    try {
      storyCounterpartVoiceSection = await getCounterpartVoiceSection([user?.id]);
    } catch { /* optional */ }

    // CAST PRESENCE (live report, Nicole 2026-08-10): measured on her story,
    // "Red" was named 0 times in 148 AI messages and Trix had no attributed
    // dialogue in the first 118, though both were cast and both had a full
    // identity anchor in the prompt. Naming who has gone quiet is the part
    // that was missing. Dynamic half — it changes every single turn and must
    // never ride into the cached prefix.
    const castPresenceSection = isMultiChar
      ? buildCastPresenceDirective(
          measureCastPresence(history, allSceneNPCs, { windowSize: 12 }),
          { playerLabel },
        )
      : '';

    const anchorDynamicSection = anchorDynamicParts.length ? `\n${anchorDynamicParts.join('\n\n')}\n` : '';
    // ── THE DYNAMIC HALF, ASSEMBLED FROM ONE DECLARED LIST (§2 step 3) ──────
    //
    // This was a template literal interpolating twenty variables, with the
    // manifest keeping a SECOND hand-written list of the same twenty. Two
    // copies of one thing — the pattern behind every entry in
    // docs/ROOT-CAUSES-2026-08-10.md — and the copy that could drift was the
    // one built to make drift visible.
    //
    // Now there is one list. Order and content are unchanged from the template
    // it replaces, deliberately: this is a faithful move, not an improvement.
    // test/promptAssembly.test.js freezes the order so a future edit that
    // reorders the prompt has to say so out loud.
    const sceneMomentum = `

SCENE MOMENTUM:
${history.length <= 3
  ? `This is the BEGINNING of a new route/scene. Set the tone, establish the setting, introduce characters naturally. First impressions matter — make this feel like the opening of a great story.`
  : history.length <= 10
    ? `The scene is STILL EARLY — characters are establishing dynamics, getting to know each other, building chemistry. Don't rush into deep emotional moments yet — let things develop naturally.`
    : `The scene is IN PROGRESS with ${history.length} messages exchanged. The characters have established a rhythm. Continue the natural flow — pick up the energy and emotional thread from the most recent messages. Don't repeat or summarize what already happened. Move the scene FORWARD.`}
${(() => {
  // Detect emotional tone from recent messages to guide the AI
  const recent5 = history.slice(-5).map(m => (m.text || "").toLowerCase()).join(" ");
  const tones = [];
  if (/kiss|lips|blush|heart|lean(ed|s)? (in|close)|breath caught|flutter|cheek/i.test(recent5)) tones.push("romantic/intimate");
  if (/fight|sword|attack|blood|clash|dodge|scream|run|chase|danger/i.test(recent5)) tones.push("action/danger");
  if (/cry|tears|sob|broke|pain|hurt|loss|grief|mourn/i.test(recent5)) tones.push("emotional/heavy");
  if (/laugh|grin|joke|tease|smirk|playful|wink|nudge/i.test(recent5)) tones.push("lighthearted/playful");
  if (/angry|furious|snap|yell|storm|glare|snarl|growl/i.test(recent5)) tones.push("tense/angry");
  if (/quiet|silence|still|pause|stare|moment|breath/i.test(recent5)) tones.push("quiet/reflective");
  return tones.length > 0 ? `The recent energy feels ${tones.join(" and ")} — lean into this tone naturally. Don't jarring shift away from it unless the player steers the scene elsewhere.` : "";
})()}
`;

    // The declared sources of the dynamic half, in prompt order. Adding a
    // source here is the only way to add one to the prompt, so the manifest
    // can no longer disagree with what shipped.
    const dynamicSources = [
      { id: 'anchorDynamic', layer: LAYER.CHARACTER, scope: 'scene', text: anchorDynamicSection, tail: true },
      // Where everyone physically is, quoted from the page itself — the
      // longest physical scenes live on this surface and it was the one
      // surface without the block (2026-08-30 parity audit, gap #4).
      { id: 'physicalContinuity', layer: LAYER.SITUATION, scope: 'scene', text: buildPhysicalContinuityBlock(trimAfterTimeskip(history.slice(-14).map(m => ({ content: m.text || '', speaker: m.name || '' })))), tail: true },
      { id: 'birthday', layer: LAYER.SITUATION, scope: 'user', text: buildBirthdayContext(user?.id) },
      { id: 'relationships', layer: LAYER.CHARACTER, scope: 'conversation', text: dynamicsSection },
      { id: 'priorFeedback', layer: LAYER.SITUATION, scope: 'user', text: priorFeedbackSection },
      { id: 'rollingSummary', layer: LAYER.SITUATION, scope: 'conversation', text: rollingSummarySection },
      { id: 'pinned', layer: LAYER.SITUATION, scope: 'conversation', text: pinnedSection },
      { id: 'channelContext', layer: LAYER.SITUATION, scope: 'conversation', text: channelContextSection },
      { id: 'routeDescription', layer: LAYER.WORLD, scope: 'conversation', text: routeDescSection },
      { id: 'universeContext', layer: LAYER.WORLD, scope: 'global', text: universeSection },
      { id: 'whispers', layer: LAYER.SITUATION, scope: 'scene', text: whispersSection, tail: true },
      { id: 'speciesLore', layer: LAYER.WORLD, scope: 'global', text: speciesLoreSection, required: true },
      { id: 'voiceSamples', layer: LAYER.CHARACTER, scope: 'conversation', text: discordSection, required: true },
      { id: 'recentVoice', layer: LAYER.CHARACTER, scope: 'user', text: recentVoiceSection, tail: true },
      { id: 'lockedFacts', layer: LAYER.CHARACTER, scope: 'user', text: lockedFactsSection },
      { id: 'feedback', layer: LAYER.SITUATION, scope: 'user', text: feedbackSection, tail: true },
      { id: 'rejected', layer: LAYER.SITUATION, scope: 'scene', text: rejectedSection, tail: true },
      { id: 'storyArc', layer: LAYER.SITUATION, scope: 'conversation', text: storyArcSection },
      { id: 'characterMemory', layer: LAYER.SITUATION, scope: 'user', text: characterMemorySection },
      { id: 'linkedMemory', layer: LAYER.SITUATION, scope: 'conversation', text: linkedMemorySection },
      { id: 'archiveLore', layer: LAYER.SITUATION, scope: 'conversation', text: archiveLoreSection },
      { id: 'castPresence', layer: LAYER.SITUATION, scope: 'scene', text: castPresenceSection, tail: true },
      { id: 'storySoFar', layer: LAYER.SITUATION, scope: 'conversation', text: storySoFar, required: true, tail: true },
      { id: 'sceneMomentum', layer: LAYER.SITUATION, scope: 'scene', text: sceneMomentum, tail: true },
      { id: 'storyPreferences', layer: LAYER.SITUATION, scope: 'user', text: storyPreferencesSection },
      { id: 'counterpartVoice', layer: LAYER.CHARACTER, scope: 'user', text: storyCounterpartVoiceSection },
      { id: 'perceptionRules', layer: LAYER.WORLD, scope: 'global', text: `\n${PERCEPTION_RULES}`, required: true },
    ];
    const assembled = assemblePrompt(dynamicSources);
    // A4 (2026-10-06): the sources marked `tail` change with THIS turn and
    // ride on the final message inside <situation> (ai-proxy
    // attachSituationTail); the rest stay in the system message, which is
    // now byte-stable across turns so the transcript behind it is cached.
    // The manifest still describes the whole list.
    const systemPromptDynamic = assemblePrompt(dynamicSources.filter(src => !src.tail)).text;
    const systemPromptTail = assemblePrompt(dynamicSources.filter(src => src.tail)).text;

    // Build message history, handling OOC messages
    // NOTE: AI (assistant) messages are sent as CLEAN PROSE without [Name]: labels.
    // This prevents the AI from mimicking the label format in its own responses.
    // Player messages keep the name prefix so the AI knows who's speaking.
    // THE WHOLE STORY. Haley, 2026-08-09: "make sure app wide that all the ai
    // characters can see the entire chat that they're in at all times for
    // memory purposes we don't care about the cost of the tokens for it."
    //
    // `history` is the RENDERED page — loadSession deliberately renders only
    // the last RP_PAGE_SIZE (100) because a 900-message story locked up the
    // UI. But the full story is already in memory in fullEmbeddedMsgsRef, so
    // on a long story the character was answering from the last 100 beats
    // while the rest sat unread a few bytes away. No fetch needed; just read
    // from the complete list.
    // …but the full list is a SNAPSHOT, and the live window is where the turn
    // that just happened lives.
    //
    // Nicole, 2026-08-12 06:26: "sometimes it gives the same response as last
    // response (or just responding to an earlier response) no matter how much
    // I try to regenerate it and I have to keep sending my response sometimes
    // 3x to get a new one (I can't delete the old responses or else it'll stay
    // stuck in loop)" — and "it's mostly the memory like sometimes it doesn't
    // respond to the most recent response".
    //
    // This line used to pick the full list OUTRIGHT whenever it was longer.
    // fullEmbeddedMsgsRef is written in exactly one place, loadSession, and
    // nothing in the send path appends to it. `history` is newMsgs — the live
    // array WITH the message she just sent. On any story past RP_PAGE_SIZE
    // (100) the full list is always longer, so it always won, and her newest
    // message was dropped from the conversation array on every single turn.
    // Her story is 390 messages.
    //
    // So the model was handed a transcript ending at the previous turn and
    // asked to continue it — which is why it reproduced its own last reply
    // nearly verbatim (messages 356 and 358 in her story, 5½ hours apart),
    // why regenerating changed nothing (same stale transcript), and why
    // sending three times eventually worked (only a reload refreshes the ref).
    // Deleting a reply reverted the window without refreshing the ref, which
    // is the loop she could not get out of.
    //
    // Regressed in v244, "every character sees the entire chat it is in" — the
    // change meant to give characters the whole story is what stopped them
    // seeing the newest part of it.
    //
    // PREFIX SEMANTICS, not a set difference — see mergeStoryHistory. The
    // window decides where the transcript ends, which is what regenerate needs.
    const storyHistory = mergeStoryHistory(fullEmbeddedMsgsRef.current, history, storyMsgFp);
    const recentMsgs = scopeFullHistory(storyHistory).map((m) => {
      const _mc = allChars.find((c) => c?.id === m.charId);
      const isOOC = isOOCMessage(m.text);
      if (isOOC && m.isPlayer) {
        return {
          role: "user",
          content: `[OOC DIRECTION FROM PLAYER]: ${m.text.replace(/^\(\(|\)\)$/g, "").trim()}`,
        };
      }
      if (m.isPlayer) {
        // Anti mind-reading: state exactly what was spoken aloud so the
        // character can't answer the player's unspoken narration/thoughts.
        return { role: "user", content: `[${m.name}]: ${m.text}${perceptionTag(m.text)}` };
      }
      // AI responses: clean prose only — no [Name]: prefix
      return { role: "assistant", content: m.text || '' };
    }).filter((m) => {
      // Remove messages with empty content (causes API 400 errors)
      return m.content && m.content.trim();
    });

    const firstMsg = [{ role: "user", content: `*${playerLabel} enters the scene*` }];

    // v412 (Haley, 2026-10-06: "add the switch to ALL"): this story's model
    // choice rides with the request; the director speaks that model's dialect.
    const threadModel = await getThreadModel(user?.id, 'story', activeStoryIdRef.current || activeStoryId || null);
    const turnDialect = threadModel ? detectAiDialect('openrouter', threadModel) : aiDialect;
    // DIRECTOR — seven rules at depth 0, appended to the final user turn. Same
    // mechanism used in channels and DMs (see src/director.js).
    try {
      const lastPlayerText = [...(history || [])].reverse().find(m => m.isPlayer)?.text || '';
      // The "Talking to" pin, spoken at depth 0. Names who the player is
      // actually addressing so the reply centres on them — the RP-side twin
      // of the channel pin, wired through the director's addressee slot.
      const rpPin = (rpTalkingToRef.current || [])
        .map(id => allChars.find(c => c?.id === id)?.name).filter(Boolean);
      const rpAddresseeNote = rpPin.length
        ? `The player is speaking TO ${rpPin.join(' and ')} this turn — ${rpPin[0]} carries the reply${rpPin.length > 1 ? `, with ${rpPin.slice(1).join(' and ')} responding in the same beat` : ''}; the rest of the cast may only briefly react if the moment truly demands it.`
        : '';
      appendDirector(recentMsgs, buildDirectorBlock({
        charName: char.name,
        dialect: turnDialect,
        // VOICE CHECK at depth 0 (2026-10-05): the merged character's contract, if any.
        voiceContract: voiceContractFor(char.id),
        // HOUSE PHRASES (2026-10-06): the measured app-wide list the depth-0 slice rotates through.
        housePhrases: housePhraseList(),
        characterPhrases: characterPhraseList(char.id),
        playerName: playerLabel,
        lastPlayerText,
        addresseeNote: rpAddresseeNote,
        turnIndex: (history || []).length,
        standingGoal: char?.character_goals?.[0] || '',
        // Regen "suggested edits" carry command weight at depth 0, same as OOC —
        // the system-prompt copy alone sits in the long-context dead zone.
        mandatoryDirective: feedbackText || null,
        recentOwnTexts: (history || []).filter(m => !m.isPlayer).map(m => m.text),
        // The rejected attempt, banned at depth 0. The system-prompt copy of
        // this instruction sits mid-prompt and was being ignored — regenerate
        // kept returning the same beats reworded.
        rejectedText: rejectedResponse || '',
        rejectedKind: retryKind === 'edit' ? 'edit' : 'regen',
      }));
      // Depth-0 diagnosis, same hoist as DM/channel/group (2026-08-30 study).
      if (rejectedResponse && retryKind !== 'dup' && retryKind !== 'edit') appendDirector(recentMsgs, buildRegenDiagnosis(rejectedResponse, { explicitFeedback: feedbackText }));
    } catch (e) { /* additive only */ }

    // ── SIREN'S WHISPER — belt-and-suspenders injection.
    // The system prompt already has whispersSection, but at 30K+ tokens the
    // model can partially ignore system-level direction. Prepending the same
    // whispers to the LAST user-turn (the message the AI is responding to)
    // puts the direction at the front of the model's working memory — much
    // harder to miss. We re-use the cached whisperRows from the first fetch
    // because the fetch RPC now atomically claims, so re-fetching here would
    // return an empty list.
    if (whisperRows.length > 0 && recentMsgs.length > 0) {
      const lastUserIdx = (() => {
        for (let i = recentMsgs.length - 1; i >= 0; i--) {
          if (recentMsgs[i].role === 'user') return i;
        }
        return -1;
      })();
      if (lastUserIdx >= 0) {
        const prefix = formatWhispersForUserPrefix(whisperRows);
        if (prefix) {
          recentMsgs[lastUserIdx] = {
            ...recentMsgs[lastUserIdx],
            content: prefix + recentMsgs[lastUserIdx].content,
          };
        }
      }
    }

    // AI cooldown — prevent rapid-fire AI calls (5s minimum between calls per scene)
    const cooldownKey = `rp-scene-${char.id}`;
    if (!canCallAI(cooldownKey)) {
      console.log('[RP] AI cooldown active — skipping duplicate call');
      return null;
    }
    recordAICall(cooldownKey);

    try {
      // Route through Supabase Edge Function (avoids CORS issues on mobile Safari)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      // ensureSession() already verified+refreshed the session before this call,
      // so getSession() returns the fresh cached token without a network round-trip.
      // Using refreshSession() here caused hangs when the SDK was mid-auto-refresh.
      const getTokenPromise = safeGetSession();
      const getTokenTimeout = new Promise(resolve => setTimeout(() => resolve({ data: { session: null }, error: { message: 'Token fetch timed out' } }), 8000));
      const { data: { session }, error: refreshError } = await Promise.race([getTokenPromise, getTokenTimeout]);

      // If session fetch failed, bail early with a clear message
      if (refreshError || !session?.access_token) {
        console.error("Session fetch failed:", refreshError?.message || "no session");
        throw new Error("Session expired — please log out and log back in");
      }

      // PROMPT MANIFEST. The manifest now comes FROM the assembly above rather
      // than from a second hand-written list of the same sources — that
      // duplicate was itself the drift this tool exists to catch. Dev only,
      // and wrapped, because an observability aid must never break a turn.
      if (import.meta.env?.DEV) {
        try {
          const balance = castBalance(
            `${systemPromptStable}\n${systemPromptDynamic}`,
            isMultiChar ? allSceneNPCs : [char],
          );
          console.log(formatManifest(assembled.manifest, balance));
        } catch (e) { console.warn('[manifest] skipped:', e?.message || e); }
      }

      // Timeout AND retry are owned by fetchWithRetry now. A timed-out turn is
      // reissued automatically instead of landing as a dead-end
      // "*Char pauses.* [AI error: Request timed out]" placeholder that you had
      // to spot and regenerate by hand.
      lastShownRef.current = [systemPromptStable, systemPromptDynamic, systemPromptTail, ...(recentMsgs || []).map(m => (typeof m?.content === 'string' ? m.content : ''))].join('\n');
      const res = await fetchWithRetry(`${supabaseUrl}/functions/v1/ai-proxy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
          "apikey": import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          provider: effectiveAiProvider,
          modelOverride: threadModel || undefined,
          systemPromptStable,   // cached prefix (~6-9K tokens: all session-stable context + constant rules block)
          systemPromptDynamic,  // uncached suffix (whispers, semantic retrievals, recent voice, feedback, memory snapshots, storySoFar, scene momentum)
          systemPromptTail,     // per-turn situational text, attached to the final message (shape A4)
          messages: recentMsgs.length > 0 ? recentMsgs : firstMsg,
          maxTokens: 8000,
          // An edit refresh keeps SCENE sampling on purpose: rp_regen widens to
        // temperature 1.0 / presence_penalty 0.15, which pushes the model off
        // the names and details it is supposed to recall. The superseded reply
        // is banned by the prompt block above instead.
        feature: rejectedResponse && retryKind !== 'edit' ? 'rp_regen' : 'rp_scene',
          nsfw: true, // RP scenes may be NSFW (route to Claude, not Gemini default)
          characterId: char.id,
          characterName: char.name,
          // SERVER-SIDE PERSIST — the reply is inserted by ai-proxy the moment
          // the model finishes, so it survives the app being backgrounded,
          // locked, or killed mid-generation. iOS suspends a PWA's JS within
          // seconds of leaving the app, which previously threw away the whole
          // (already paid-for) turn. Channels and DMs have done this for
          // months; RP was the last surface still relying on the client
          // staying alive. ai-proxy has supported target:'story' the entire
          // time — the client simply never asked for it.
          //
          // clientToken makes it idempotent: if the response is lost in
          // transit and the client falls back to its own insert, the partial
          // unique index collapses the two into one row.
          //
          // Audit 2026-08-03 P0-1: this was gated on !isMultiChar — so
          // ENSEMBLE scenes (the default way they play, 2+ characters) had
          // NO persist at all and every locked-phone reply was lost. The
          // server-side insert doesn't care how many characters spoke; the
          // narrator turn persists under the primary character exactly as
          // the client's own fallback insert would have written it.
          ...(activeStoryId ? {
            persist: {
              target: 'story',
              storyId: activeStoryId,
              characterId: char.id,
              characterName: char.name,
              senderUserId: user.id,
              sceneType,
              color: char.color || null,
              avatar: char.avatar || null,
              clientToken: persistToken,
            },
          } : {}),
        }),
      }, {
        timeoutMs: AI_ABORT_MS,
        // Reasoning-on: ONE billed attempt. A reissue after a 40-60 s median
        // turn is a two-minute typing dot, and the two terminal failures
        // (deadline / budget_spent) are billed in full either way.
        maxAttempts: 1,
        // Say so rather than sitting on a silent "typing…" through a retry.
        onRetry: (n, max) => setTypingCharName(`${char.name} (retrying ${n}/${max})`),
        onDone: () => setTypingCharName(char.name),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(aiProxyErrorMessage(data, `Edge function error ${res.status}`));
      }

      const responseText = data.text || null;

      // ── Fire-and-forget: extract story events from this RP exchange ───────
      // This stores what happened into the cross-context arc so DMs + channels
      // know about it on next call. Uses the story session ID as context key.
      // ⚠️ THIS WAS GATED ON `!isMultiChar` AND THAT IS WHY MEMORY WAS BROKEN.
      //
      // Everything in this block — story events, facts, locked facts, whisper
      // consumption AND the rolling summary — ran only in single-character
      // scenes. Nicole plays a four-character ensemble, so in her story none of
      // it ran at all. Measured 2026-08-11: rp_scene story_events stop dead at
      // 2026-08-10 16:10 while 79 further RP turns were generated through
      // 05:41 the next morning. Channel and DM extraction kept working
      // throughout; only RP ensembles were silent.
      //
      // So "the memory is weird" was never only about attribution. In an
      // ensemble memory was not misfiled — it was never written.
      //
      // The gate was defensible when it was added: with no way to attribute an
      // event, every one of them would have been filed under the anchor.
      // v261 gave extractStoryEvents that attribution, which is what makes
      // opening this up safe. The two per-character fact extractors still file
      // under the anchor and stay gated below until they get the same
      // treatment — see the handoff.
      if (responseText && user?.id) {
        // Deferred (v409): memory is written when the player sends the next
        // message, and never for a reply they deleted or regenerated first.
        await memoryGate.flush();
        memoryGate.stash({ table: 'story_messages', scopeCol: 'story_id', scopeId: activeStoryId || null, clientTokenBase: persistToken, text: responseText, run: async () => {
        try {
          const rpContextId = activeStoryId || `rp_${char.id}_${user.id}`;
          const messagesForExtraction = [
            ...history.slice(-10),
            { name: char.name, text: responseText, charId: char.id, isPlayer: false, created_at: new Date().toISOString() },
          ].map(m => ({
            content: m.text || '',
            sender_type: m.isPlayer ? 'user' : 'character',
            // RP message objects carry `createdAt` (camelCase) — reading only
            // `created_at` meant every message except the just-appended reply
            // had a null timestamp, so storyMemory saw newMessageCount = 1,
            // fell below its threshold of 3, and returned early on EVERY call
            // after the first. Result: a story's cross-context memory froze at
            // its opening scene forever. Live check found 6 of 6 stories with
            // all their events stamped on a single day (audit #48).
            created_at: m.created_at || m.createdAt || null,
            // Speaker identity, so extraction can attribute correctly rather
            // than collapsing everyone into the triggering character.
            speaker: m.isPlayer ? (m.name || playerLabel) : (m.name || char.name),
          }));
          // The cast goes with it, so an event about Trix is remembered as
          // Trix's rather than the anchor's (Nicole, 2026-08-10: "The memory
          // is weird"). Anchor first — it is the fallback for anything the
          // extractor cannot attribute.
          extractStoryEvents(
            'rp_scene', rpContextId, char.id, char.name, playerLabel,
            messagesForExtraction, user.id,
            {
              cast: [
                { id: char.id, name: char.name },
                ...allSceneNPCs.filter(c => c.id !== char.id).map(c => ({ id: c.id, name: c.name })),
              ],
            },
          );
          // STILL SINGLE-CHARACTER ONLY. Both of these file every row under
          // char.id, which in an ensemble is the anchor — the exact bug v261
          // fixed for story events and has not yet fixed here. Running them
          // for a four-hander would record Trix's promises as Cruz's, which is
          // worse than recording nothing. They need the same cast-attribution
          // pass extractStoryEvents got; until then the gate stays.
          // LOCKED FACTS ledger — concrete facts the AI just committed to.
          // Runs for ensembles now: extractAndStoreFacts attributes each fact
          // to the character it belongs to, the same way extractStoryEvents
          // does. This is the table getCharacterFacts reads, and v257 made
          // ensemble prompts load supporting characters' ledgers — which could
          // not exist until something wrote them.
          const lastUserMsg = [...history].reverse().find(m => m.isPlayer);
          extractAndStoreFacts({
            characterId: char.id,
            userId: user.id,
            characterName: char.name,
            userMessage: lastUserMsg?.text || '',
            aiResponse: responseText,
            sourceTable: 'story_messages',
            sourceMessageId: null,
            cast: [
              { id: char.id, name: char.name },
              ...allSceneNPCs.filter(c => c.id !== char.id).map(c => ({ id: c.id, name: c.name })),
            ],
          });

          // STILL SINGLE-CHARACTER ONLY, and for a different reason than the
          // others were. extractFacts writes ONE AGGREGATED ROW per character
          // in dm_conversation_facts — a facts array plus a messages_processed
          // cursor — so attributing it means reading every cast member's row,
          // splitting the new facts across them, and carrying a cursor per
          // character. That is a real piece of work, not a parameter, and it
          // is written up in the handoff rather than half-done here.
          if (!isMultiChar) {
            // threadType 'story' returns at once: the facts table keys on
            // dm_conversations and a story id never landed there (2026-10-19).
            extractFacts(
              rpContextId, char.id, char.name, playerLabel,
              messagesForExtraction, user.id, effectiveAiProvider, { threadType: 'story' }
            );
          }
          // Whispers are already marked consumed by the fetch RPC's atomic-
          // claim transaction; this UPDATE is a defensive no-op for already-
          // consumed rows (idempotent), kept as belt-and-suspenders in case
          // the RPC behavior is ever reverted.
          if (pendingWhisperIds.length > 0) {
            markWhispersConsumed(pendingWhisperIds);
          }
          // Rolling narrative summary — refresh every ~25 messages.
          if (activeStoryId) {
            (async () => {
              try {
                const summaryRow = await getRollingSummary('story', activeStoryId);
                // TRUE total from the DB — the loaded list is only a page, which
                // froze summaries permanently on long stories.
                const dbTotal = await getTrueMessageCount('story', activeStoryId);
                const totalAfter = Math.max(dbTotal, messagesRef.current?.length || 0);
                if (shouldRefreshSummary(totalAfter, summaryRow?.rolling_summary_msg_count || 0)) {
                  refreshRollingSummary({
                    type: 'story',
                    conversationId: activeStoryId,
                    characterName: char.name,
                    playerLabel,
                    messages: messagesRef.current || [],
                    totalCount: totalAfter,
                    priorSummary: summaryRow?.rolling_summary || null,
                  });
                }
              } catch { /* non-fatal */ }
            })();
          }
        } catch (memErr) {
          console.warn('[RP Memory] Background extraction failed to start:', memErr);
        }
        } });
      }

      return { text: responseText, clientToken: persistToken, persisted: data?.persisted || null, truncated: !!data?.truncated, usage: data?.usage || null };
    } catch (err) {
      console.error("AI error:", err);
      const errMsg = err.name === 'AbortError'
        ? 'Request timed out — try again'
        : (err.message || 'Unknown error');
      // MDI-2: this used to return `*Name pauses.* [AI error: ...]` as if it
      // were a reply — six such rows were live in story_messages, surviving
      // reload, showing in spectate, and re-entering model context. Error
      // text must never enter persistence: return null text + the error, and
      // let callers surface the rpAIError banner.
      return { text: null, error: errMsg, clientToken: persistToken };
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // REGENERATE WITH FEEDBACK (Update 3B)
  // ═══════════════════════════════════════════════════════════════════════════
  // ─── DELETE RP MESSAGE ────────────────────────────────────────────────────
  // An RP is backed by TWO stores: the debounced stories.data blob AND the
  // story_messages table (per-message rows for realtime/spectate). On load the
  // two are MERGED, and the merge re-adds any story_messages row whose text
  // isn't in the blob. So deleting from local state alone makes the message
  // come back on reload. We must delete the server row AND re-save the blob.
  const deleteRPMessage = (idx) => {
    const victim = messages[idx];
    if (!victim) return;
    setShowConfirmModal({ message: "Delete this message?", onConfirm: () => {
      memoryGate.drop({ tokenBases: [victim.clientToken || ''], texts: [victim.text || ''] });
      // Remove by identity (not index) so a realtime arrival mid-confirm can't
      // shift the index and delete the wrong message.
      setMessages(prev => prev.filter(m => m !== victim));
      pruneFullEmbeddedRef([victim]);
      allowEmptySaveRef.current = true; // deleting the last message is a legitimate empty save
      setShowConfirmModal(null);
      const sid = activeStoryIdRef.current || activeStoryId;
      // Remove the per-message row so the blob+table merge can't recover it.
      // Match by exact text (session-created messages carry a client-side id
      // that never matches the DB row id; loaded messages match either way).
      if (victim?.text && sid) {
        supabase.from('story_messages').delete().eq('story_id', sid).eq('text', victim.text)
          .then(({ error }) => { if (error) console.warn('[deleteRPMessage] story_messages delete failed:', error.message); })
          .catch(e => console.warn('[deleteRPMessage] story_messages delete error:', e?.message || e));
      }
      // Re-save the blob (without the deleted message) so both stores agree.
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveStory(), 600);
    } });
  };

  // ─── EDIT RP MESSAGE ─────────────────────────────────────────────────────
  const startEditingRP = (idx) => {
    setEditingMsgIdx(idx);
    setEditingMsgText(messages[idx]?.text || '');
  };

  const cancelEditingRP = () => {
    setEditingMsgIdx(null);
    setEditingMsgText('');
  };

  // Resolve the ONE story_messages row behind a local message object.
  //
  // Session-created messages carry a synthetic client id (`regen_…`, Date.now())
  // that never matches a DB uuid, so text is the only handle — but matching on
  // text alone deletes every row that happens to share it (the same line said
  // twice in a long scene). Prefer the real uuid when the message was loaded
  // from the table; otherwise match on text within this story and disambiguate
  // by whichever candidate is closest to the message's own timestamp.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  // Push to everyone in a shared story — owner AND joined participants —
  // minus whoever shouldn't get it (audit PUSH-5). Every story push used to
  // target a single user, so a joined player got nothing for the owner's
  // lines or for any AI reply she didn't personally trigger: the co-op story
  // went silent unless she already had the app open. Best-effort by design —
  // a failed participants read still notifies the owner.
  const pushStoryAudience = async (storyId, ownerId, payload, excludeIds = []) => {
    const audience = new Set();
    if (ownerId) audience.add(ownerId);
    if (storyId) {
      try {
        const { data, error } = await supabase
          .from('story_participants').select('user_id').eq('story_id', storyId);
        if (error) console.warn('[StoryPush] participants read failed:', error.message);
        for (const row of data || []) if (row?.user_id) audience.add(row.user_id);
      } catch (e) { console.warn('[StoryPush] participants read threw:', e?.message || e); }
    }
    for (const id of excludeIds) audience.delete(id);
    for (const id of audience) sendPushNotification(id, payload);
  };

  const resolveStoryRow = async (storyId, m) => {
    if (!storyId || !m) return null;
    try {
      if (typeof m.id === 'string' && UUID_RE.test(m.id)) {
        const { data, error } = await supabase
          .from('story_messages').select('id, created_at').eq('id', m.id).maybeSingle();
        if (!error && data) return data;
      }
      const text = String(m.text || '');
      if (!text) return null;
      const { data, error } = await supabase
        .from('story_messages').select('id, created_at')
        .eq('story_id', storyId).eq('text', text)
        .order('created_at', { ascending: false }).limit(5);
      if (error || !data?.length) return null;
      if (data.length === 1) return data[0];
      const target = m.createdAt ? new Date(m.createdAt).getTime() : NaN;
      if (Number.isNaN(target)) return data[0];
      return data.reduce((best, r) =>
        Math.abs(new Date(r.created_at).getTime() - target) <
        Math.abs(new Date(best.created_at).getTime() - target) ? r : best, data[0]);
    } catch (e) {
      console.warn('[resolveStoryRow] lookup failed:', e?.message || e);
      return null;
    }
  };

  // ─── FAILED SEND / RESEND ────────────────────────────────────────────────
  // Flip the sendFailed flag on one message by identity (not index — a realtime
  // arrival can shift indices between the write starting and its error coming
  // back). Writes through to messagesRef so a save scheduled in between picks
  // up the flag too.
  const markRPSendFailed = useCallback((msgId, failed) => {
    setMessages(prev => {
      let changed = false;
      const next = prev.map(m => {
        if (m.id !== msgId) return m;
        if (!!m.sendFailed === !!failed) return m;
        changed = true;
        const { sendFailed: _drop, ...rest } = m;
        return failed ? { ...rest, sendFailed: true } : rest;
      });
      if (!changed) return prev;
      messagesRef.current = next;
      return next;
    });
  }, []);

  const [resendingMsgId, setResendingMsgId] = useState(null);

  // Resend one of YOUR messages — meaning "this didn't work, try the whole turn
  // again", NOT merely "re-insert the database row".
  //
  // The first version only re-attempted the story_messages write, and bailed out
  // silently when the row was already there (the normal case). So pressing it
  // did nothing visible and never produced an AI call — which is the failure
  // players actually see: the message is on screen and nothing answered it.
  //
  // Both branches below hand off to the existing, exercised paths rather than
  // re-implementing the send pipeline (auto-casting, cast healing, responder
  // choice, persistence, push):
  //   • a reply already exists  → regenerateMessage, which replaces it
  //   • no reply came back      → drop the message and run sendMessage again,
  //                               which rebuilds the whole turn from scratch
  //
  // Plain function, not useCallback: it closes over saveStory and sendMessage
  // (recreated every render, like the other message handlers here), so
  // memoizing would either capture stale ones or invalidate every render.
  const resendRPMessage = async (idx) => {
    const live = messagesRef.current || [];
    const msg = live[idx];
    if (!msg || !msg.isPlayer || !user?.id) return;
    if (isSendingRef.current) return;               // a turn is already running
    const sid = activeStoryIdRef.current || activeStoryId;
    setResendingMsgId(msg.id);
    setRpSendError(null);
    try {
      const ok = await ensureSession();
      if (!ok) { setRpSendError('Your session expired — sign in again, then press Resend.'); return; }

      const next = live[idx + 1];

      // ── A reply DID arrive: the player wants a different one. ────────────
      if (next && !next.isPlayer) {
        await regenerateMessage(idx + 1, '', { reason: 'resend' });
        markRPSendFailed(msg.id, false);
        return;
      }

      // ── Nothing answered. Re-run the turn. ──────────────────────────────
      // Remove the local message and its server row first so sendMessage's
      // insert can't duplicate it, then replay the identical text through the
      // normal send path — that is what brings back auto-casting and the AI
      // call, neither of which a bare re-insert would have done.
      if (sid) {
        const row = await resolveStoryRow(sid, msg);
        if (row) {
          const { error: delErr } = await supabase
            .from('story_messages').delete().eq('id', row.id);
          if (delErr) {
            // MDI-6: replaying the send with the old row still in place is a
            // guaranteed duplicate on the other player's screen. Stop here.
            console.warn('[resendRPMessage] could not clear the old row — aborting resend:', delErr.message);
            alert("Couldn't clear the original message, so resending would duplicate it. Try again in a moment.");
            return;
          }
        }
      }
      const pruned = live.filter((m, i) => i !== idx);
      pruneFullEmbeddedRef([msg]);
      setMessages(pruned);
      messagesRef.current = pruned;   // sendMessage reads this, not the closure

      const sent = await sendMessage(msg.text);
      if (sent === false) {
        // sendMessage refused (no character selected, empty text, session).
        // Put the message back exactly where it was rather than losing it.
        const restored = [...live];
        setMessages(restored);
        messagesRef.current = restored;
        setRpSendError("Couldn't resend — make sure a character is selected, then try again.");
        return;
      }
      markRPSendFailed(msg.id, false);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveStory(), 600);
    } catch (e) {
      console.error('[resendRPMessage] threw:', e?.message || e);
      setRpSendError(`Resend failed: ${e?.message || 'unknown error'}`);
    } finally {
      setResendingMsgId(null);
    }
  };

  const saveRPEdit = async (idx) => {
    const newText = editingMsgText.trim();
    if (!newText) return;
    // messagesRef is the authoritative list (a realtime arrival can land between
    // render and save). `messages` from the closure can already be stale here.
    const before = messagesRef.current || messages;
    const victim = before[idx];
    if (!victim) return;
    const oldText = String(victim.text || '');
    if (oldText === newText) { cancelEditingRP(); return; }

    const updatedMessages = before.map((m, i) => i === idx ? { ...m, text: newText, edited: true } : m);
    setMessages(updatedMessages);
    // Publish to the ref SYNCHRONOUSLY. regenerateMessage runs on this same
    // tick, and setMessages hasn't committed yet — without this the regenerate
    // below rebuilt its prompt from the PRE-EDIT text, so the AI answered the
    // line the player had just corrected. That is the "I edit my message and it
    // forgets what I changed" report.
    messagesRef.current = updatedMessages;
    cancelEditingRP();

    // ── Persist the edit ────────────────────────────────────────────────────
    // An RP is backed by TWO stores (see deleteRPMessage). Editing local state
    // alone meant: reload before the next send lost the edit outright; and once
    // a later send did save the blob, loadSession's 80-char-prefix merge no
    // longer found the untouched row's text in the blob, classified it
    // "recovered", and appended it — so the pre-edit line came back and sat
    // beside the edited one, with both fed to the model as separate events.
    const sid = activeStoryIdRef.current || activeStoryId;
    if (sid && oldText) {
      const row = await resolveStoryRow(sid, victim);
      if (row) {
        // Update in place so created_at (and therefore transcript order) holds.
        const { data: updated, error } = await supabase
          .from('story_messages')
          .update({ text: newText, edited: true })
          .eq('id', row.id)
          .select('id');
        if (error) {
          console.warn('[saveRPEdit] story_messages update failed:', error.message);
        } else if (!updated?.length) {
          // 0 rows = RLS refused (story_messages update is own-rows-only, so
          // this is someone else's line in a shared story). Drop the stale row
          // instead of leaving it to resurrect the pre-edit text on next load.
          const { error: delErr } = await supabase
            .from('story_messages').delete().eq('id', row.id);
          if (delErr) console.warn('[saveRPEdit] stale-row cleanup failed:', delErr.message);
        }
      }
    }
    // Re-save the blob so both stores agree (same dance as deleteRPMessage).
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveStory(), 600);

    // Auto-regenerate the next AI message if the edited message was a player message
    const editedMsg = updatedMessages[idx];
    const nextMsg = updatedMessages[idx + 1];
    if (editedMsg?.isPlayer && nextMsg && !nextMsg.isPlayer) {
      // reason:'edit' — the player corrected THEIR OWN line, they did not
      // reject the AI's. See regenerateMessage for why that distinction matters.
      regenerateMessage(idx + 1, "", { reason: 'edit' });
    }
  };

  // opts.reason === 'edit' means the player corrected THEIR OWN message and we
  // are refreshing the reply to it. That is NOT a rejection of what the
  // character wrote, and treating it as one is why an edit came back vaguer
  // than the original: a rejection sets `rejectedResponse`, which (a) injects
  // the "write something MATERIALLY DIFFERENT, treat the text below as a list
  // of things NOT to write" block, and (b) flips ai-proxy's feature from
  // 'rp_scene' to 'rp_regen', which widens sampling to temperature 1.0 /
  // presence_penalty 0.15. A presence penalty pushes the model off tokens
  // already in context — including the names, places and established details
  // the reply is supposed to recall. An edit refresh keeps normal scene
  // sampling and no anti-repeat ban; it just answers the corrected line.
  const regenerateMessage = async (msgIndex, feedbackText = "", opts = {}) => {
    const isEditRefresh = opts.reason === 'edit';
    const storyIdAtSend = activeStoryIdRef.current;
    // Read the live list, not the render closure. saveRPEdit calls straight
    // into here on the same tick, before setMessages has committed.
    const live = messagesRef.current || messages;
    const msg = live[msgIndex];
    if (!msg || msg.isPlayer) return;
    memoryGate.drop({ tokenBases: [msg.clientToken || ''], texts: [msg.text || ''] }); // a rejected reply never happened

    // Store feedback context for this character persistently — session state
    // AND the durable cross-surface ledger (audit #30: RP corrections — the
    // surface players fight hardest — evaporated on refresh).
    if (feedbackText) {
      // Keep only the last 6 standing directions — an unbounded accumulator
      // meant months of stale edits diluted the one just typed (Haley
      // 2026-08-30: "my feedback is largely ignored").
      setFeedbackContexts((prev) => ({
        ...prev,
        [msg.charId]: ((prev[msg.charId] || "") + "\n" + feedbackText)
          .split("\n").filter(Boolean).slice(-6).join("\n"),
      }));
      recordFeedback(msg.charId, user?.id, feedbackText, 'story');
    }

    // Capture the rejected response BEFORE removing it from history. Passing
    // it into the prompt as "do not repeat" is what gives the AI knowledge of
    // what to avoid — without it, the model usually generates a near-duplicate.
    // ROOT CAUSE of "still sending the same response" (Haley 2026-08-31):
    // this used to be '' on an edit refresh, and the superseded reply is also
    // dropped from historyBefore — so the model had NO knowledge it existed
    // and re-emitted it verbatim, on flash AND on pro. The distinction the
    // comment above draws is real (an edit is not a rejection, and rp_regen
    // sampling harms recall) but it must be carried by FRAMING, not by
    // withholding the text: supersededText is always captured and always
    // banned; retryKind decides how it is described to the model.
    const supersededText = String(msg.text || '').trim();
    const rejectedResponse = supersededText;
    const regenKind = isEditRefresh ? 'edit' : 'regen';

    // Remove the message being regenerated and everything after it
    const historyBefore = live.slice(0, msgIndex);
    setIsTyping(true);
    setTypingCharName(msg.name);
    const regenSafetyTimer = setTimeout(() => { setIsTyping(false); setTypingCharName(""); }, AI_SAFETY_MS);

    try {
      // ── Drop the discarded branch from story_messages BEFORE generating ───
      // Regen throws away this reply and everything after it locally, but their
      // rows stayed in the table. After the next blob save, loadSession's merge
      // no longer found those texts in the blob, classified them "recovered",
      // and appended them — so the rejected version reappeared next to its
      // replacement and BOTH went into the model's context on the following
      // turn. This must run BEFORE the AI call: for single-NPC scenes ai-proxy
      // persists the replacement itself (persist.target 'story'), and a delete
      // afterwards would take the replacement with it. Anchored on the SERVER
      // created_at so client clock skew can't take one row too many.
      if (storyIdAtSend) {
        const anchor = await resolveStoryRow(storyIdAtSend, msg);
        if (anchor) {
          const { error: branchErr } = await supabase
            .from('story_messages')
            .delete()
            .eq('story_id', storyIdAtSend)
            .gte('created_at', anchor.created_at);
          if (branchErr) console.warn('[regenerateMessage] branch delete failed:', branchErr.message);
        }
      }

      // Opening-scene messages carry charId '_scene', which resolves to no
      // character and made generateAIResponse bail with null — regenerating
      // an opening silently did nothing. Route those through the first
      // respondable character (the prompt still narrates the whole scene).
      const genCharId = (msg.charId && msg.charId !== '_scene') ? msg.charId : (aiRespondChars[0] || null);
      if (!genCharId) {
        setRpAIError('No character is in this scene yet — add one from the sidebar before regenerating.');
        return;
      }
      let { text: response, error: regenErr, clientToken: regenToken, persisted: regenPersisted, truncated: regenTruncated } = await generateAIResponse(genCharId, historyBefore, feedbackText, rejectedResponse, regenKind);
      // Restatement guard (Haley 2026-08-30): verify the replacement is not
      // the rejected reply in new clothes — NOR an OLDER reply recycled
      // ("I just switched to pro then it sent me a recycled response": the
      // regen check only banned the one rejected text, so reaching further
      // back passed clean). One retry with the matched text banned too.
      // ALWAYS includes the superseded text — on an edit refresh it was never
      // "rejected", but reproducing it verbatim is exactly the failure.
      const regenBanList = [supersededText, ...historyBefore.filter(m => !m.isPlayer && (m.text || '').trim()).slice(-6).map(m => m.text)].filter(Boolean);
      if (response && isNearDuplicate(response, regenBanList)) {
        console.warn('[RP regen] replacement restated a prior reply — retrying once');
        const recycledOf = matchingPrior(response, regenBanList) || rejectedResponse;
        const retry = await generateAIResponse(genCharId, historyBefore, feedbackText, `${recycledOf}\n———\n${response}`, 'regen');
        if (retry?.text && !isNearDuplicate(retry.text, regenBanList)) {
          if (regenPersisted && regenToken && storyIdAtSend) {
            const { error: rrDelErr } = await supabase.from('story_messages')
              .delete().eq('story_id', storyIdAtSend).eq('client_token', regenToken);
            if (rrDelErr) console.warn('[RP regen] restated-row cleanup failed:', rrDelErr.message);
          }
          ({ text: response, error: regenErr, clientToken: regenToken, persisted: regenPersisted, truncated: regenTruncated } = retry);
        } else {
          setRpAIError('That reply came back close to one already written — shown anyway. Add a note about what to change and regenerate again.');
        }
      }
      // Must still be in chat AND in the SAME story. Without the story check a
      // reply generated in story A landed in story B when the player switched
      // mid-generation, and the following autosave wrote the wrong transcript
      // into B's blob (audit #10).
      if (viewRef.current !== 'chat' || activeStoryIdRef.current !== storyIdAtSend) return;
      if (!response && regenErr) {
        // MDI-2: the old path replaced the branch with a persisted
        // "[AI error: ...]" line. Keep the ORIGINAL reply on screen instead
        // (its table row was already cleared; the blob keeps it for this
        // player) and say what happened.
        const restored = [...historyBefore, msg];
        setMessages(restored);
        messagesRef.current = restored;
        setRpAIError(`Couldn't regenerate: ${regenErr}. The original reply was kept.`);
        return;
      }
      // Trimmed at the budget: shown as-is, with the way out (↻ regenerate).
      if (response && regenTruncated) setRpAIError(`That reply was ${AI_TRUNCATED_NOTE}.`);
      if (response) {
        // The replacement's identity comes from the mode that GENERATED it,
        // not blind inheritance from the message it replaces. Inheriting kept
        // "Scene" as the speaker forever once a story had ever been narrator/
        // ensemble — the blob said "Scene" while the table said "Malakai" for
        // the same reply (live 2026-07-30), so the text-and-identity matches
        // that keep the two stores in sync stopped finding each other.
        const sceneVoice = msg.charId === '_scene' || aiRespondChars.length > 1 || narratorMode;
        const speakChar = allChars.find(c => c?.id === msg.charId);
        const newMsg = {
          ...msg,
          id: `regen_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          text: response,
          name: sceneVoice ? 'Scene' : (speakChar?.name || msg.name),
          isScene: sceneVoice,
          color: sceneVoice ? t.textMuted : (speakChar?.color || msg.color),
          avatar: sceneVoice ? '📖' : (speakChar?.avatar || msg.avatar),
          edited: false,
          timestamp: new Date().toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
          createdAt: new Date().toISOString(),
        };
        const next = [...historyBefore, newMsg];
        {
          const keep = new Set(historyBefore.map(storyMsgFp));
          const dropped = (messagesRef.current || []).filter(m => !keep.has(storyMsgFp(m)));
          pruneFullEmbeddedRef(dropped);
        }
        setMessages(next);
        messagesRef.current = next;
        // Offer the previous reply back until the scene moves on. An edit
        // refresh is not a rejection — there is nothing to undo there.
        setLastRegen(isEditRefresh ? null : { storyId: storyIdAtSend, msgId: newMsg.id, previous: msg });

        // Mirror the replacement into story_messages when nothing carries it
        // yet. ai-proxy already inserted it for single-NPC scenes; multi-char
        // scenes never ask it to, and a persist failure would otherwise leave
        // the new reply in the blob only — invisible to spectate and to the
        // blob+table merge.
        if (storyIdAtSend && user?.id) {
          try {
            const { data: existing } = await supabase
              .from('story_messages').select('id')
              .eq('story_id', storyIdAtSend).eq('text', response).limit(1);
            if (!existing?.length) {
              const mirrorPayload = {
                story_id: storyIdAtSend,
                user_id: user.id,
                char_id: msg.charId,
                char_name: newMsg.name,
                text: response,
                is_player: false,
                scene_type: sceneType,
                color: newMsg.color || null,
                avatar: newMsg.avatar || null,
              };
              let { error: insErr } = await supabase.from('story_messages').insert(mirrorPayload);
              if (insErr) {
                // One retry behind a fresh session. An unpersisted replacement
                // is invisible to spectate AND lets the rejected text win the
                // next blob+table merge (live 2026-07-30: the 16:46 regen's
                // replacement never landed, so the old opening kept
                // resurrecting and re-entering model context).
                const fresh = await ensureSession();
                if (fresh) ({ error: insErr } = await supabase.from('story_messages').insert(mirrorPayload));
              }
              if (insErr) console.warn('[regenerateMessage] replacement insert failed:', insErr.message);
            }
          } catch (e) {
            console.warn('[regenerateMessage] replacement persist error:', e?.message || e);
          }
        }

        // Re-save the blob so both stores agree about the new tail.
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => saveStory(), 600);
      }
    } finally {
      clearTimeout(regenSafetyTimer);
      if (viewRef.current === 'chat') {
        setIsTyping(false);
        setTypingCharName("");
      }
    }
  };

  // ── UNDO A REGENERATE ────────────────────────────────────────────────────
  // Puts the superseded reply back exactly where it was. Regenerate deletes
  // the old row before generating, so restoring means re-inserting it and
  // removing the replacement — the same two stores regenerate itself keeps in
  // sync (the local blob and story_messages), in the same order.
  const undoRegenerate = async () => {
    const snap = lastRegen;
    if (!snap) return;
    const live = messagesRef.current || messages;
    const idx = live.findIndex(m => m?.id === snap.msgId);
    if (idx === -1) { setLastRegen(null); return; }

    const replacement = live[idx];
    const restored = [...live];
    restored[idx] = snap.previous;
    setMessages(restored);
    messagesRef.current = restored;
    setLastRegen(null);

    // Swap the rows back. Best-effort and explicitly ordered: delete the
    // replacement FIRST, so a failure halfway leaves one reply on screen
    // rather than two — two is what put rejected text back into the model's
    // context on the following turn (audit #10).
    if (snap.storyId && user?.id) {
      try {
        if (replacement?.text) {
          await supabase.from('story_messages')
            .delete()
            .eq('story_id', snap.storyId)
            .eq('text', replacement.text);
        }
        const prev = snap.previous;
        const { data: already } = await supabase.from('story_messages')
          .select('id').eq('story_id', snap.storyId).eq('text', prev.text).limit(1);
        if (!already?.length) {
          await supabase.from('story_messages').insert({
            story_id: snap.storyId,
            user_id: user.id,
            char_id: prev.charId,
            char_name: prev.name,
            text: prev.text,
            is_player: false,
            scene_type: sceneType,
            color: prev.color || null,
            avatar: prev.avatar || null,
          });
        }
      } catch (e) {
        console.warn('[undoRegenerate] row swap failed:', e?.message || e);
      }
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveStory(), 400);
    setToastMsg({ message: 'Put the previous reply back', type: 'success', icon: '↩️' });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SEND MESSAGE
  // ═══════════════════════════════════════════════════════════════════════════
  // Accepts text from the RPInputBar child (which now owns input state).
  // Returns false if the send failed early (so the child can restore the
  // user's text); returns true / undefined on success.
  const sendMessage = async (textArg, sendOpts = {}) => {
    // 🔔 Summon (Haley 2026-08-30): a character speaks NEXT without the
    // player typing — same pipeline, laws, anti-dup and persistence as any
    // turn; the only difference is that no player message is written.
    const summonCharId = sendOpts.summonCharId || null;
    const text = (textArg ?? '').trim();
    // Two valid send modes:
    //   1. Classic: playingAs is set (user chose a character to play).
    //   2. Narrator mode: no character picked — the player is themselves
    //      (their display name + any "my name is X" they put in the prompt),
    //      and the AI handles every character around them.
    if (!text && !summonCharId) return false;
    // The previous reply is memory now: the player answered it (if it survived).
    memoryGate.flush();
    // The scene has moved on — the undo offer expires with it, as the failsafe
    // is for the turn you just regenerated, not for arbitrary history.
    setLastRegen(null);
    // WRITE WHENEVER YOU WANT (Haley, 2026-09-23: "it's trying to force me to
    // select a character but I should be able to play whoever I want and write
    // whenever i want"). No character picked is not an error state — it is
    // writing as yourself, which every line below already handles:
    // effectivePlayerName falls through to currentPlayer and char_id to
    // '_player'. The composer no longer waits for a selection either.

    if (isSendingRef.current) return false;
    const storyIdAtSend = activeStoryIdRef.current;

    isSendingRef.current = true;
    try { // Bug #3 fix: Wrap entire body in try/finally so isSendingRef ALWAYS resets
    clearDraft('rp', activeStoryId);
    lastPromptStoryRef.current = null; // a sent message makes the prompt-story permanent — re-rolls stop replacing it

    // Ensure session is alive before writing
    const ok = await ensureSession();
    if (!ok) {
      return false; // child restores the text
    }

    const now = new Date();
    const ooc = isOOCMessage(text);
    // Narrator mode: synthesize a player identity from the prompt + display name
    const effectivePlayerName = narratorMode
      ? (narratorPlayerName || currentPlayer || 'You')
      : (currentChar?.name || currentPlayer);
    const playerMsg = {
      id: Date.now(),
      charId: playingAs || '_player',
      name: effectivePlayerName,
      text: text,
      timestamp: now.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
      createdAt: now.toISOString(),
      type: sceneType,
      isPlayer: true,
      isOOC: ooc,
      color: currentChar?.color,
      avatar: currentChar?.avatar,
    };
    // Build on messagesRef, not the render closure. resendRPMessage prunes the
    // message and calls straight into here on the same tick, so `messages`
    // would still contain the copy it just removed and we'd send a duplicate.
    // It is also simply the more correct base — a realtime arrival can land
    // between render and send.
    const newMsgs = summonCharId ? [...(messagesRef.current || messages)] : [...(messagesRef.current || messages), playerMsg];
    setMessages(newMsgs);
    messagesRef.current = newMsgs;

    // Write to story_messages table for real-time spectate + multi-player sync
    if (!summonCharId && activeStoryId && user) {
      const msgPayload = {
        story_id: activeStoryId,
        user_id: user.id,
        // story_messages.char_id is NOT NULL. In narrator mode `playingAs` is
        // null by design (the player is themselves, the AI plays the world), so
        // this insert used to violate the constraint and EVERY narrator-mode
        // message failed to mirror — silently, because the error was only
        // logged. The local message object has always used this same '_player'
        // fallback; the payload just never matched it.
        char_id: playingAs || '_player',
        char_name: currentChar?.name || currentPlayer,
        text: text,
        is_player: true,
        scene_type: sceneType,
        color: currentChar?.color || null,
        avatar: currentChar?.avatar || null,
        // MDI-6: insert and retry share this token, so a commit whose
        // response was lost collapses on the partial unique index instead of
        // double-posting to the other player's feed.
        client_token: (crypto?.randomUUID?.() || `rpu_${Date.now()}_${Math.random().toString(36).slice(2)}`),
      };
      // Fire-and-forget with error logging (non-blocking so AI response starts immediately)
      supabase.from("story_messages").insert(msgPayload).select().then(async ({ data: _insertedRows, error }) => {
        if (error) {
          console.warn("[story_messages] Insert failed:", error.code, error.message, "— retrying");
          await safeRefreshSession();
          await supabase.from("story_participants").upsert({
            story_id: activeStoryId,
            user_id: user.id,
            character_id: playingAs,
            role: 'player',
            joined_at: new Date().toISOString(),
          }, { onConflict: 'story_id,user_id' });
          const { error: retryErr } = await supabase.from("story_messages").insert(msgPayload).select();
          if (retryErr && String(retryErr.code) === '23505') {
            console.log("[story_messages] Retry hit the token index — first insert had committed");
          } else if (retryErr) {
            console.error("[story_messages] Retry failed:", retryErr.code, retryErr.message);
            // Both attempts failed. Surface it on the message itself so there's
            // a Resend button instead of a console line nobody reads.
            markRPSendFailed(playerMsg.id, true);
          } else {
            console.log("[story_messages] Retry OK");
          }
        }
      }).catch(err => {
        console.error("[story_messages] Unexpected:", err);
        markRPSendFailed(playerMsg.id, true);
      });

      // Notify everyone else in the story — owner and joined participants.
      if (!ooc) {
        const charName = currentChar?.name || currentPlayer || 'Someone';
        pushStoryAudience(storyIdAtSend, storyOwnerId, {
          title: `${charName} says...`,
          body: text.length > 100 ? text.substring(0, 100) + '...' : text,
          url: '/',
          category: 'story',
        }, [user.id]);
      }
    }

    // Narrator mode has no roster by design — the AI plays the whole world.
    // Without this, a prompt-driven story whose opening scene contained no
    // recognizable universe names left sceneChars empty, so every send got
    // NO reply and NO error (audit #3, secondary half).
    // ── THE CAST IS THE PLAYERS' ─────────────────────────────────────────
    // Haley, live 2026-08-10: "the ai is adding random characters into the
    // side bar only we should be able to do that."
    //
    // Auto-casting used to mutate the persisted sidebar from prose: any
    // roster first name appearing in the player's message — or in the AI's
    // reply — joined the cast permanently and was saved to the story. A name
    // mentioned in passing ("she thought about calling Bryce") silently
    // became a cast member forever. The sidebar is a control surface, so only
    // the + button, the character sheet's Add to scene, and confirming an
    // AI-invented character may add to it.
    //
    // This does NOT stop other characters appearing in the writing. The
    // prompt still carries the universe directory and still invites the model
    // to bring in whoever the scene calls for; they just do not get silently
    // written into the roster.
    //
    // The empty-cast self-heal below is RECOVERY, not casting: a story whose
    // cast was lost otherwise gets no reply and no error at all (audit #3,
    // secondary half). It only ever runs when the cast is already empty, and
    // it restores characters who have demonstrably already spoken here.
    let castNow = aiRespondChars;
    // The "Talking to" pin decides who LEADS. Pinned characters (still in the
    // scene) move to the front in pin order — the first becomes castNow[0]:
    // the narrator handle, the ensemble's lead voice, and the anchor that
    // memory attribution falls back to. Nobody is removed; unpinned cast
    // still exists in the scene, they just stop carrying the reply.
    {
      const pinned = (rpTalkingToRef.current || []).filter(id => castNow.includes(id));
      if (pinned.length) castNow = [...pinned, ...castNow.filter(id => !pinned.includes(id))];
    }
    // A summoned character leads outright this turn, whatever the pin says.
    if (summonCharId) castNow = [summonCharId, ...castNow.filter(id => id !== summonCharId)];
    try {
      const blocked = new Set(blockedChars || []);
      if (castNow.length === 0) {
        const healed = castFromHistory(messagesRef.current || messages, blockedChars)
          .filter(id => !blocked.has(id));
        if (healed.length) {
          setSceneChars(healed);
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
          saveTimerRef.current = setTimeout(() => saveStory(), 600);
        }
        castNow = healed.filter(id => {
          const c = allChars.find(ch => ch?.id === id);
          return c && c.controlledBy !== currentPlayer;
        });
      }
    } catch { /* recovery is best-effort; fall through to the existing list */ }

    const canAIRespond = castNow.length > 0 || (narratorMode && !!narratorFallbackCharId);

    // Never fail silently. Before this, an empty cast meant the send saved and
    // NOTHING happened — no reply, no error, no typing dot (the 2026-07-27
    // "AI isn't generating in RP" incident, which ran for 90 minutes).
    if (effectiveApiKey && effectiveAiProvider !== "none" && !canAIRespond && !ooc && !aiPaused) {
      setRpAIError('No character is in this scene yet — add one from the sidebar, or name them in your message and they will step in.');
    }

    if (effectiveApiKey && effectiveAiProvider !== "none" && canAIRespond && !ooc && !aiPaused) {
      setRpAIError('');
      setIsTyping(true);
      // Safety-net: guarantee typing clears after AI_SAFETY_MS no matter what
      const typingSafetyTimer = setTimeout(() => { setIsTyping(false); setTypingCharName(""); }, AI_SAFETY_MS);
      // Declare outside try so catch block can access them
      const narratorId = castNow[0] || narratorFallbackCharId;
      const narrator = allChars.find((c) => c?.id === narratorId);
      try {
        // Generate ONE narrative response featuring all scene characters
        // Use the first NPC as the "narrator" for the AI call, but the prompt
        // tells it to write a scene response featuring all characters naturally
        setTypingCharName((castNow.length > 1 || narratorMode) ? "Scene" : (narrator?.name || "Character"));
        let { text: response, clientToken: aiPersistToken, error: aiError, persisted: aiPersisted, truncated: aiTruncated, usage: aiUsage } = await generateAIResponse(narratorId, newMsgs);
        // ── ANTI-DUPLICATE — story mode had none (2026-08-30 parity audit,
        // gap #4). Channels and DMs detect a reply rebuilt from an earlier
        // one and retry once with the recycled text quoted back as
        // do-not-repeat; the story surface let the repeat land. The rejected
        // attempt may already be persisted server-side (persist.target
        // 'story'), so on an adopted retry the first row is deleted by its
        // client_token — otherwise the blob+table merge resurrects it.
        if (response && typeof response === 'string') {
          const priorOwn = newMsgs.filter(m => !m.isPlayer && m.text).slice(-5).map(m => m.text);
          if (isNearDuplicate(response, priorOwn)) {
            console.warn('[RP] near-duplicate reply — retrying once');
            const dupOf = matchingPrior(response, priorOwn) || response;
            const firstToken = aiPersistToken, firstPersisted = aiPersisted;
            const retry = await generateAIResponse(narratorId, newMsgs, null, String(dupOf).slice(0, 400), 'dup');
            if (retry?.text && typeof retry.text === 'string' && !isNearDuplicate(retry.text, priorOwn)) {
              if (firstPersisted && firstToken && storyIdAtSend) {
                const { error: dupDelErr } = await supabase.from('story_messages')
                  .delete().eq('story_id', storyIdAtSend).eq('client_token', firstToken);
                if (dupDelErr) console.warn('[RP] rejected-duplicate cleanup failed:', dupDelErr.message);
              }
              ({ text: response, clientToken: aiPersistToken, error: aiError, persisted: aiPersisted, truncated: aiTruncated, usage: aiUsage } = retry);
            } else {
              // The retry was ALSO a near-duplicate (or came back empty), so the
              // first reply is kept — but the retry may already have been
              // persisted server-side, and nothing used to delete it. That is
              // how Haley's Teagan scene ended up showing Teagan twice in a row,
              // twice, on 2026-09-19: two rows for one turn, the second a
              // re-run of the same beat. The adopt path above cleans up its
              // loser; this path has to clean up its own.
              if (retry?.persisted && retry?.clientToken && storyIdAtSend) {
                const { error: retryDelErr } = await supabase.from('story_messages')
                  .delete().eq('story_id', storyIdAtSend).eq('client_token', retry.clientToken);
                if (retryDelErr) console.warn('[RP] rejected-retry cleanup failed:', retryDelErr.message);
              }
              if (viewRef.current === 'chat' && activeStoryIdRef.current === storyIdAtSend) {
                // A flawed reply beats silence — shown anyway, with the way out.
                setRpAIError('That reply came back close to an earlier one — shown anyway. Tap ↻ to regenerate, or give the scene a new beat.');
              }
            }
          }
        }
        // ── NAME GUARD (v410): an invented relative, place, room or person is
        // regenerated once with the name banned; every hit is logged. The
        // rejected attempt's server-persisted row is removed like the dupe path.
        if (response && typeof response === 'string') {
          try {
            const hits = findUnknownNames(response, lastShownRef.current, { extra: [narrator?.name || ''] });
            if (hits.length) {
              console.warn('[RP] name guard:', hits.map(h => `${h.kind}:${h.name}`).join(', '));
              const firstToken = aiPersistToken, firstPersisted = aiPersisted;
              const retry = await generateAIResponse(narratorId, newMsgs, guardDirective(hits, narrator?.name || 'you'), '', 'guard');
              const retryHits = (retry?.text && typeof retry.text === 'string') ? findUnknownNames(retry.text, lastShownRef.current, { extra: [narrator?.name || ''] }) : hits;
              if (retry?.text && typeof retry.text === 'string' && retryHits.length < hits.length) {
                if (firstPersisted && firstToken && storyIdAtSend) {
                  await supabase.from('story_messages').delete().eq('story_id', storyIdAtSend).eq('client_token', firstToken).then(null, () => {});
                }
                ({ text: response, clientToken: aiPersistToken, error: aiError, persisted: aiPersisted, truncated: aiTruncated, usage: aiUsage } = retry);
                logGuardEvent({ userId: user.id, characterId: narratorId, surface: 'story', hits, action: retryHits.length ? 'persisted' : 'regenerated', retried: true });
              } else {
                if (retry?.persisted && retry?.clientToken && storyIdAtSend) {
                  await supabase.from('story_messages').delete().eq('story_id', storyIdAtSend).eq('client_token', retry.clientToken).then(null, () => {});
                }
                logGuardEvent({ userId: user.id, characterId: narratorId, surface: 'story', hits, action: 'persisted', retried: !!retry?.text });
              }
            }
          } catch (guardErr) { console.warn('[RP] name guard failed open:', guardErr?.message || guardErr); }
        }
        if (viewRef.current === 'chat' && activeStoryIdRef.current === storyIdAtSend) {
          if (aiError && !response) {
            setRpAIError(`${(castNow.length > 1 || narratorMode) ? 'The scene' : (narrator?.name || 'The character')} couldn't reply: ${aiError}. Your message is saved — send again or nudge them to retry.`);
          }
          // Trimmed at the budget: shown as-is, with the way out (↻ regenerate).
          if (response && aiTruncated) setRpAIError(`That reply was ${AI_TRUNCATED_NOTE}.`);
          if (response && typeof response === 'string') {
            const npcNow = new Date();
            const npcMsgId = `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            // AI-side auto-cast REMOVED (Haley, live 2026-08-10: "the ai is
            // adding random characters into the side bar only we should be
            // able to do that"). This block took every roster first name the
            // reply happened to mention and wrote it into the persisted cast
            // — so a character the AI merely referred to became a permanent
            // sidebar entry, saved to the story. That is the literal thing
            // she is reporting.
            //
            // The reply can still bring anyone on stage in the prose; it just
            // no longer edits her roster to match. Adding to the cast is the
            // + button, the character sheet's Add to scene, or confirming an
            // AI-invented character — all of them player actions.

            const npcMsg = {
              id: npcMsgId,
              charId: narratorId,
              name: (castNow.length > 1 || narratorMode) ? "Scene" : narrator.name,
              text: response,
              timestamp: npcNow.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
              createdAt: npcNow.toISOString(),
              type: sceneType,
              isPlayer: false,
              // What this reply cost — rendered as one dim line under it.
              usage: aiUsage || null,
              isScene: castNow.length > 1 || narratorMode,
              color: (castNow.length > 1 || narratorMode) ? t.textMuted : narrator.color,
              avatar: (castNow.length > 1 || narratorMode) ? "📖" : narrator.avatar,
            };
            // Dedup: only add if this exact message isn't already in state
            setMessages((prev) => {
              if (prev.some(m => m.id === npcMsgId)) return prev;
              return [...prev, npcMsg];
            });
            newMsgs.push(npcMsg);

            // Write AI response to story_messages for real-time spectate
            if (activeStoryId && user) {
              const aiMsgPayload = {
                story_id: activeStoryId,
                user_id: user.id,
                char_id: narratorId,
                char_name: (castNow.length > 1 || narratorMode) ? "Scene" : narrator.name,
                text: response,
                is_player: false,
                scene_type: sceneType,
                color: aiRespondChars.length > 1 ? t.textMuted : (narrator?.color || null),
                avatar: aiRespondChars.length > 1 ? "📖" : (narrator?.avatar || null),
                // Same token the server persist used — the partial unique
                // index collapses this fallback with the server row.
                client_token: aiPersistToken || null,
              };
              // ── BULLETPROOF AI INSERT (silent-drop fix, May 20) ──────────
              // Local state already has npcMsg (line ~7150 above), so the
              // user SEES it on screen this session. But if story_messages
              // never persists, a refresh loses it. Try up to 3 times with
              // session refresh between attempts. On final failure, mark the
              // local message with _droppedFromDB so the UI can flag it (and
              // log loudly so we'd see the pattern in production logs).
              (async () => {
                let saved = false;
                for (let attempt = 0; attempt < 3 && !saved; attempt++) {
                  if (attempt > 0) await safeRefreshSession();
                  const { error } = await supabase.from("story_messages").insert(aiMsgPayload);
                  if (!error) { saved = true; break; }
                  if (String(error.code) === '23505') { saved = true; break; } // server persist already landed this turn
                  console.warn(`[story_messages] AI insert attempt ${attempt + 1}/3 failed:`, error.message);
                }
                if (!saved) {
                  console.error(`[story_messages] CRITICAL: AI msg dropped after 3 attempts. story_id=${activeStoryId} char=${aiMsgPayload.char_name} len=${response.length}`);
                  // Mark the in-memory message so the UI can show a warning
                  setMessages(prev => prev.map(m => m.id === npcMsgId ? { ...m, _droppedFromDB: true } : m));
                  // Telemetry: zero is healthy. Surfaces in UsageDashboard.
                  supabase.from('dm_message_drops').insert({
                    user_id: user?.id || null,
                    conversation_id: activeStoryId || null,
                    character_id: aiMsgPayload?.char_id || null,
                    character_name: aiMsgPayload?.char_name || null,
                    content_length: response?.length || 0,
                    source: 'sixguys_rp',
                    attempt_count: 3,
                    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
                    notes: `RP scene response dropped after 3 retries`,
                  }).then(({ error: dropLogErr }) => {
                    if (dropLogErr) console.warn('[story_messages] dm_message_drops telemetry insert also failed:', dropLogErr.message);
                  });
                }
              })();
            }

            // Detect any new characters the AI introduced
            detectNewCharacters(response);

            // Notify the whole story — the AI's reply is for everyone in it,
            // not just whoever happened to trigger the turn (PUSH-5).
            // PUSH-3: when ai-proxy persisted server-side it ALSO pushed the
            // owner (the suspended-phone case this exists for), so drop the
            // owner from the client-side audience in that case — everyone
            // else still needs telling.
            const storyCharName = (castNow.length > 1 || narratorMode) ? "Scene" : (narrator?.name || "Character");
            pushStoryAudience(storyIdAtSend, storyOwnerId, {
              title: `✨ ${storyCharName} responded`,
              body: response.replace(/\*[^*]+\*/g, '').substring(0, 100).trim(),
              url: '/',
              category: 'story',
            }, aiPersisted && storyOwnerId ? [storyOwnerId] : []);
          } else {
            // AI returned null or empty — show visible error so user knows what happened
            const errNow = new Date();
            const errDetail = !effectiveApiKey ? "No API key configured"
              : effectiveAiProvider === "none" ? "AI provider set to none"
              : "Empty response from AI — try again";
            const errMsg = {
              id: `err_${Date.now()}`,
              charId: narratorId,
              name: narrator?.name || "System",
              text: `⚠️ ${narrator?.name || "AI"} couldn't respond — ${errDetail}`,
              timestamp: errNow.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
              createdAt: errNow.toISOString(),
              type: "ooc",
              isPlayer: false,
              isScene: false,
              color: "#ed4245",
              avatar: "⚠️",
            };
            setMessages((prev) => [...prev, errMsg]);
          }
        }
      } catch (outerErr) {
        console.error("Unexpected AI error in sendMessage:", outerErr);
        if (viewRef.current === 'chat') {
          const errNow = new Date();
          setMessages((prev) => [...prev, {
            id: `err_${Date.now()}`,
            charId: narratorId,
            name: "System",
            text: `⚠️ Something went wrong — ${outerErr.message || "unknown error"}. Try sending your message again.`,
            timestamp: errNow.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" }),
            createdAt: errNow.toISOString(),
            type: "ooc",
            isPlayer: false,
            isScene: false,
            color: "#ed4245",
            avatar: "⚠️",
          }]);
        }
      } finally {
        clearTimeout(typingSafetyTimer);
        if (viewRef.current === 'chat') {
          setIsTyping(false);
          setTypingCharName("");
        }
      }
    }

    // Auto-save after every message exchange (debounced — creates new story if needed)
    // Only mark last_active_at when the USER sent the message (not AI auto-response saves)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveStory({ userInitiated: true }), 1500);

    } finally {
      // Bug #3 fix: isSendingRef ALWAYS resets, even if any code path throws
      isSendingRef.current = false;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Legal Pages (standalone routes — no auth required)
  // ═══════════════════════════════════════════════════════════════════════════
  if (legalRoute) {
    const goBack = () => window.history.back();
    const legalPages = {
      '/privacy': <PrivacyPolicyPage onBack={goBack} />,
      '/terms': <TermsOfServicePage onBack={goBack} />,
      '/ai-disclosure': <AIDisclosurePage onBack={goBack} />,
      '/safety': <SafetyPage onBack={goBack} />,
      '/contact': <ContactPage onBack={goBack} />,
      '/delete-account': <DeleteAccountPage onBack={goBack} user={user} />,
      '/gacha-odds': <GachaOddsPage onBack={goBack} />,
    };
    return (
      <Suspense fallback={<AppSplash />}>
        {legalPages[legalRoute] || <AppSplash />}
      </Suspense>
    );
  }

  // RENDER: Loading
  // ═══════════════════════════════════════════════════════════════════════════
  // ── Popup incognito mode: use module-level constant (NOT window.location.search)
  // so history.replaceState URL masking doesn't break this check on re-renders
  if (IS_INCOGNITO_POPUP) {
    return <IncognitoStandalone />;
  }

  if (authLoading) {
    return <AppSplash />;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Maintenance Mode
  // ═══════════════════════════════════════════════════════════════════════════
  const isAdmin = userProfile?.role === "admin";
  const isAllowedDuringMaintenance = isAdmin || (maintenanceMode?.allowed_emails || []).includes(user?.email);
  if (maintenanceMode && maintenanceMode !== false && maintenanceMode.enabled && !isAllowedDuringMaintenance) {
    return (
      <div style={{ minHeight: "100dvh", background: bgApp, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
        {/* Background texture */}
        <div className="mf-grain mf-grain-strong" style={{ position: "absolute" }} aria-hidden="true" />

        {/* Floating particles */}
        <FloatingParticles t={t} count={12} />

        {/* Top chain border */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, transparent 5%, var(--ds-accent-a44, #c6282844) 20%, var(--ds-accent, #c62828) 50%, var(--ds-accent-a44, #c6282844) 80%, transparent 95%)", zIndex: 1 }} />

        <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: "480px", padding: "0 24px", animation: "fadeSlideUp 0.8s ease-out" }}>
          {/* Logo */}
          <img src="/art/brand/logo3_512.png" alt="My Fate, My Love" style={{ width: "120px", height: "auto", marginBottom: "24px", filter: "drop-shadow(0 0 20px var(--ds-accent-a4c, rgba(198,40,40,0.3)))", animation: "breathe 4s ease-in-out infinite", imageRendering: "auto" }} />

          {/* Pocket watch spinner */}
          <div style={{ width: "48px", height: "48px", margin: "0 auto 28px", position: "relative" }}>
            <div style={{ width: "48px", height: "48px", border: "2px solid var(--ds-accent-a33, #c6282833)", borderTop: "2px solid var(--ds-accent, #c62828)", borderRadius: "50%", animation: "spin 2s linear infinite" }} />
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "6px", height: "6px", borderRadius: "50%", background: t.accentDanger, boxShadow: "0 0 8px var(--ds-accent-a66, #c6282866)" }} />
          </div>

          <h1 style={{ fontSize: "28px", fontFamily: "'Cinzel', serif", fontWeight: 700, margin: "0 0 8px", color: t.accentGold, letterSpacing: "6px", textTransform: "uppercase" }}>
            Updating
          </h1>

          <OrnateDivider width="60%" margin="16px auto" />

          <p style={{ color: t.textMuted, fontSize: "16px", lineHeight: "1.7", margin: "0 0 8px", fontStyle: "italic" }}>
            {maintenanceMode.message || "We're updating MFML with new features. Be right back!"}
          </p>

          <p style={{ color: t.textFaint, fontSize: "12px", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Cinzel', serif", margin: "24px 0 0" }}>
            My Fate, My Love
          </p>
        </div>

        {/* Bottom chain border */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, transparent 5%, var(--ds-accent-a44, #c6282844) 20%, var(--ds-accent, #c62828) 50%, var(--ds-accent-a44, #c6282844) 80%, transparent 95%)", zIndex: 1 }} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Banned User
  // ═══════════════════════════════════════════════════════════════════════════
  if (user && userProfile?.is_banned) {
    return (
      <div style={{ minHeight: "100dvh", background: `linear-gradient(160deg, ${bgApp} 0%, ${bgCard} 40%, ${bgApp} 100%)`, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: "400px" }}>
          <h2 style={{ marginBottom: "8px", fontFamily: "'Cinzel', serif", color: t.accentDanger }}>Account Suspended</h2>
          <p style={{ color: t.textMuted, fontSize: "14px", marginBottom: "24px" }}>Your account has been suspended by an administrator.</p>
          <button onClick={handleLogout} style={{ ...btnPrimary, background: "#2a1a1a", border: "1px solid var(--ds-accent-a44, #c6282844)" }}>Log Out</button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Auth Screen
  // ═══════════════════════════════════════════════════════════════════════════
  if (!user) {
    const authInputStyle = { ...inputStyle, background: "var(--ds-glass, #1d1013c4)", border: "1px solid var(--ds-line, #3a2128)", color: t.textPrimary, caretColor: "var(--ds-gold, #c4a882)", fontFamily: "'Cormorant Garamond', serif", fontSize: "15px", padding: "14px 16px", borderRadius: "var(--ds-r-md, 14px)", transition: "border-color 0.3s ease, box-shadow 0.3s ease" };
    const authBtnStyle = { ...btnPrimary, width: "100%", background: "linear-gradient(135deg, var(--ds-accent-deeper, #8b0000) 0%, var(--ds-accent, #c62828) 50%, var(--ds-accent-deeper, #8b0000) 100%)", border: "1px solid var(--ds-gold-a40, #c4a88240)", fontFamily: "'Cinzel', serif", letterSpacing: "3px", fontSize: "13.5px", textTransform: "uppercase", padding: "16px 20px", borderRadius: "var(--ds-r-pill, 999px)", transition: "all 0.3s ease", boxShadow: "0 4px 15px var(--ds-accent-a4c, rgba(198,40,40,0.3)), var(--ds-glow-gold, 0 0 20px -4px #c4a88259)" };
    const authBtnSecondary = { ...btnPrimary, width: "100%", background: "var(--ds-glass, #1d1013c4)", border: "1px solid var(--ds-glass-edge, #c4a8823d)", fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", fontSize: "12px", color: t.textSecondary, padding: "12px 20px", minHeight: "44px", borderRadius: "var(--ds-r-pill, 999px)", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" };
    const authLinkStyle = { background: "none", border: "none", color: t.accentGold, cursor: "pointer", textDecoration: "none", fontFamily: "'Cormorant Garamond', serif", fontSize: "14px", transition: "color 0.2s", padding: "10px 12px", minHeight: "44px", touchAction: "manipulation", WebkitTapHighlightColor: "var(--ds-gold-a26, rgba(196,168,130,0.15))", WebkitUserSelect: "none", userSelect: "none" };
    return (
      <div style={{ height: "100dvh", overflowY: "auto", WebkitOverflowScrolling: "touch", background: `radial-gradient(ellipse 120% 70% at 50% -10%, var(--ds-accent-a29, rgba(198,40,40,0.16)), transparent 55%), radial-gradient(ellipse 100% 60% at 50% 8%, var(--ds-gold-a12, rgba(196,168,130,0.07)), transparent 50%), linear-gradient(180deg, ${t.bgBase} 0%, ${t.bgDeepest} 60%, #0a0506 100%)`, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif", display: "flex", flexDirection: "column", position: "relative", overflowX: "hidden" }}>
        {/* Background texture */}
        <div className="mf-grain mf-grain-strong" style={{ position: "fixed" }} aria-hidden="true" />

        {/* Floating particles */}
        <FloatingParticles t={t} count={20} />

        {/* Top decorative chain border */}
        <div style={{ position: "relative", zIndex: 1, height: "3px", background: "linear-gradient(90deg, transparent 5%, var(--ds-accent-a44, #c6282844) 20%, var(--ds-accent, #c62828) 50%, var(--ds-accent-a44, #c6282844) 80%, transparent 95%)" }} />

        {/* Banner area with character art poster */}
        <div style={{ position: "relative", zIndex: 1, width: "100%", height: isMobile ? "240px" : "340px", overflow: "hidden", flexShrink: 0 }}>
          {/* Actual poster art as banner background — randomly picks poster 1 or 2 */}
          <img src={`/art/brand/${isMobile ? "banner_mobile" : "banner_desktop"}_${new Date().getMinutes() % 2 === 0 ? 1 : 2}.jpg`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%", imageRendering: "auto" }} />
          {/* Dark gradient overlay for readability */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,10,10,0.15) 0%, rgba(10,10,10,0.5) 60%, #0a0a0a 100%)", zIndex: 2 }} />
          {/* Vignette sides */}
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, transparent 30%, #0a0a0a 100%)", zIndex: 2 }} />

          {/* Medallion floats over the banner; the thread of fate descends from the screen's top edge into it */}
          <div style={{ position: "absolute", zIndex: 3, top: 0, left: "50%", transform: "translateX(-50%)", textAlign: "center", animation: "logoReveal 1s ease-out" }}>
            <img src="/art/brand/logo3_512.png" alt="" aria-hidden="true" className="emblem-idle" style={{ width: isMobile ? "168px" : "236px", height: "auto", filter: "drop-shadow(0 0 30px var(--ds-accent-a80, rgba(198,40,40,0.5))) drop-shadow(0 6px 14px rgba(0,0,0,0.85))", imageRendering: "auto" }} />
          </div>
        </div>

        {/* Calligraphy bridges the banner's edge and the velvet below */}
        <div style={{ position: "relative", zIndex: 3, textAlign: "center", marginTop: isMobile ? "-34px" : "-52px", animation: "fadeSlideUp 0.8s ease-out 0.2s both" }}>
          <img src="/art/brand/logo3_wordmark.png" alt="My Fate, My Love" style={{ width: isMobile ? "300px" : "440px", maxWidth: "92%", height: "auto", filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.85)) drop-shadow(0 0 26px var(--ds-gold-a26, #c4a88226))", imageRendering: "auto" }} />
        </div>

        {/* Ornate divider below banner */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <OrnateDivider width="80%" margin="0 auto" />
        </div>

        {/* Auth form */}
        <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "24px 20px 40px" }}>
          <p style={{ color: t.textFaint, margin: "0 0 28px", padding: "0 12px", textAlign: "center", fontSize: isMobile ? "9.5px" : "11px", letterSpacing: isMobile ? "2px" : "3px", textTransform: "uppercase", fontFamily: "'Cinzel', serif" }}>Where every fate finds love… or fails to.</p>

          <div className="auth-card baroque-frame baroque-frame--modal" style={{ background: "linear-gradient(180deg, var(--ds-glass, #1d1013c4) 0%, var(--ds-glass-strong, #150b0de3) 100%)", backdropFilter: "blur(18px) saturate(1.5)", borderRadius: "var(--ds-r-xl, 28px)", padding: "32px 28px", maxWidth: "390px", width: "92%", animation: "fadeSlideUp 0.6s ease-out 0.3s both" }}>
            <i className="baroque-frame__cnr" aria-hidden="true" />
            {loginMode === "signin" && (
              <>
                <h2 style={{ margin: "0 0 24px", fontSize: "16px", textAlign: "center", fontFamily: "'Cinzel', serif", letterSpacing: "4px", color: t.accentGold, textTransform: "uppercase" }}>Sign In</h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {/* Real <form> with autocomplete so iOS/Safari offers to save the
                      password to Keychain and autofill it with Face ID / Touch ID
                      (works in the WKWebView app too). display:contents keeps the
                      flex layout unchanged. */}
                  <form onSubmit={(e) => { e.preventDefault(); handleSignIn(); }} style={{ display: "contents" }}>
                  <input value={authEmail} onChange={(e) => { setAuthEmail(e.target.value); setAuthError(""); }} className="input-glow" style={authInputStyle} placeholder="Email" type="email" name="username" aria-label="Email address" autoComplete="username" autoCapitalize="none" autoCorrect="off" />
                  <input value={authPassword} onChange={(e) => { setAuthPassword(e.target.value); setAuthError(""); }} className="input-glow" style={authInputStyle} placeholder="Password" type="password" name="password" aria-label="Password" autoComplete="current-password" />
                  {authError && <p style={{ color: t.accentDanger, fontSize: "13px", margin: "0", textAlign: "center", animation: "fadeSlideIn 0.3s ease" }}>{authError}</p>}
                  <button type="submit" style={authBtnStyle} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 25px var(--ds-accent-a80, rgba(198,40,40,0.5))"; e.currentTarget.style.transform = "translateY(-1px)"; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 15px var(--ds-accent-a4c, rgba(198,40,40,0.3))"; e.currentTarget.style.transform = "translateY(0)"; }}>Enter</button>
                  </form>
                  <OrnateDivider width="100%" margin="8px auto" />
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button onClick={() => setLoginMode("signup")} style={{ ...authBtnSecondary, flex: 1, padding: "12px 8px" }}>Create account</button>
                    <button onClick={() => setLoginMode("reset")} style={{ ...authBtnSecondary, flex: 1, padding: "12px 8px" }}>Forgot password?</button>
                  </div>
                  <div style={{ textAlign: "center", marginTop: "2px" }}>
                    <button onClick={() => { if (window.__mfmlForceReset) window.__mfmlForceReset(); }} style={{ ...authLinkStyle, color: t.textFaint, fontSize: "12.5px" }}>Stuck? Reset</button>
                  </div>
                </div>
              </>
            )}
            {loginMode === "signup" && (
              <>
                <h2 style={{ margin: "0 0 24px", fontSize: "16px", textAlign: "center", fontFamily: "'Cinzel', serif", letterSpacing: "4px", color: t.accentGold, textTransform: "uppercase" }}>Create Account</h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  <input value={authEmail} onChange={(e) => { setAuthEmail(e.target.value); setAuthError(""); }} style={authInputStyle} placeholder="Email" type="email" aria-label="Email address" />
                  <input value={authPassword} onChange={(e) => { setAuthPassword(e.target.value); setAuthError(""); }} style={authInputStyle} placeholder="Password" type="password" aria-label="Password" />
                  <input value={authDisplayName} onChange={(e) => { setAuthDisplayName(e.target.value); setAuthError(""); }} style={authInputStyle} placeholder="Display Name" type="text" aria-label="Display name" />
                  <input value={authPhone} onChange={(e) => { setAuthPhone(e.target.value); setAuthError(""); }} style={authInputStyle} placeholder="Phone Number" type="tel" aria-label="Phone number" />
                  <input value={authInviteCode} onChange={(e) => { setAuthInviteCode(e.target.value.toUpperCase()); setAuthError(""); }} style={{ ...authInputStyle, letterSpacing: "3px", textAlign: "center", fontFamily: "'Cinzel', serif", fontSize: "14px" }} placeholder="INVITE CODE" type="text" aria-label="Invite code" autoComplete="off" />
                  <p style={{ color: t.textMuted, fontSize: "11px", margin: "-8px 0 0", textAlign: "center", fontStyle: "italic" }}>An invite code from an admin is required to join</p>
                  {authError && <p style={{ color: t.accentDanger, fontSize: "13px", margin: "0", textAlign: "center" }}>{authError}</p>}
                  {!ageGateConfirmed ? (
                    <AgeGate onConfirm={() => setAgeGateConfirmed(true)} />
                  ) : (
                    <>
                      <button onClick={handleSignUp} style={authBtnStyle}>Create Account</button>
                      <button onClick={() => { setLoginMode("signin"); setAuthError(""); setAgeGateConfirmed(false); }} style={authBtnSecondary}>Back to Sign In</button>
                    </>
                  )}
                </div>
              </>
            )}
            {loginMode === "reset" && (
              <>
                <h2 style={{ margin: "0 0 24px", fontSize: "16px", textAlign: "center", fontFamily: "'Cinzel', serif", letterSpacing: "4px", color: t.accentGold, textTransform: "uppercase" }}>Reset Password</h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {!resetSent ? (
                    <>
                      <p style={{ color: t.textMuted, fontSize: "14px", margin: "0 0 8px", textAlign: "center", fontStyle: "italic" }}>Enter your email and we'll send you a reset link</p>
                      <input value={resetEmail} onChange={(e) => { setResetEmail(e.target.value); setAuthError(""); }} style={authInputStyle} placeholder="Email Address" type="email" aria-label="Email address for password reset" />
                      {authError && <p style={{ color: t.accentDanger, fontSize: "13px", margin: "0", textAlign: "center" }}>{authError}</p>}
                      <button onClick={requestEmailReset} style={authBtnStyle}>Send Reset Link</button>
                    </>
                  ) : (
                    <p style={{ color: t.accentGold, fontSize: "14px", textAlign: "center", padding: "20px 0", fontStyle: "italic" }}>Check your email for a password reset link.</p>
                  )}
                  <button onClick={() => { setLoginMode("signin"); setResetEmail(""); setResetSent(false); setAuthError(""); }} style={authBtnSecondary}>Back to Sign In</button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bottom decorative chain border */}
        <div style={{ position: "relative", zIndex: 1, height: "3px", background: "linear-gradient(90deg, transparent 5%, var(--ds-accent-a44, #c6282844) 20%, var(--ds-accent, #c62828) 50%, var(--ds-accent-a44, #c6282844) 80%, transparent 95%)" }} />
        <SiteFooter />
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Waiting for profile to load (no guest name entry — auth only)
  // ═══════════════════════════════════════════════════════════════════════════
  // Bug #1 fix: Move state-setting out of render body into a useEffect (defined above).
  // The useEffect at line ~2991 already handles user+userProfile→currentPlayer.
  // This block now only renders the loading screen — no state updates.
  if (view === "home" && !currentPlayer) {
    return (
      <div style={{ minHeight: "100dvh", background: `linear-gradient(160deg, ${bgApp} 0%, ${bgCard} 40%, ${bgApp} 100%)`, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 40%, var(--ds-accent-a0d, rgba(198,40,40,0.05)) 0%, transparent 60%)" }} />
        <FloatingParticles t={t} count={6} color={t.accentDanger} />
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", animation: "fadeSlideUp 0.5s ease-out" }}>
          <h1 style={{ fontSize: "32px", fontFamily: "'Cinzel', serif", fontWeight: 600, margin: "0 0 8px", color: t.accentDanger, letterSpacing: "6px" }}>My Fate, My Love</h1>
          <p style={{ color: t.textMuted, fontSize: "14px", marginBottom: "28px", fontStyle: "italic", letterSpacing: "1px" }}>Loading your profile...</p>
          <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center", marginBottom: "8px" }}>
            <div className="typing-dot" />
            <div className="typing-dot" />
            <div className="typing-dot" />
          </div>
          <button onClick={handleLogout} style={{ background: "none", border: "none", color: "#9a8585", cursor: "pointer", marginTop: "24px", fontSize: "13px", fontFamily: "'Cormorant Garamond', serif", letterSpacing: "1px", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color = 'var(--ds-accent, #c62828)'} onMouseLeave={e => e.target.style.color = '#5a4545'}>Log out</button>
        </div>
      </div>
    );
  }

  // ═══ ONE SETTINGS MENU (queue item 17) ═══════════════════════════════════
  // This blob used to be pasted NINE times across the per-tab render branches
  // (byte-identical except the community branch's story-notification jump
  // target — the `storyNotifTab` parameter). One helper, rendered by every
  // branch: a prop tweak now happens once instead of nine times.
  const renderProfileSettings = (storyNotifTab = "channels") => showProfileSettings && (
    <ModalLazy><ProfileSettings
      appTab={<SettingsModal embedded apiKey={apiKey} setApiKey={setApiKey} aiProvider={aiProvider} setAiProvider={setAiProvider} aiModel={aiModel} setAiModel={setAiModel} onClose={() => setShowProfileSettings(false)} sessionName={currentSessionName} setSessionName={setCurrentSessionName} user={user} globalApiEnabled={globalApiEnabled} globalApiProvider={globalApiProvider} notifPrefs={notifPrefs} updateNotifPref={updateNotifPref} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab(storyNotifTab); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} />}
      user={user}
      userProfile={userProfile}
      onClose={() => { setShowProfileSettings(false); setProfileSettingsInitialTab("profile"); setProfileSettingsScope(null); }}
      onProfileUpdate={(updated) => { setUserProfile(updated); setCurrentPlayer(updated.display_name); }}
      onNotifPrefsChange={(prefs) => { setNotifPrefs(prev => ({ ...prev, ...prefs })); notifPrefsRef.current = { ...notifPrefsRef.current, ...prefs }; }}
      initialTab={profileSettingsInitialTab}
      initialScope={profileSettingsScope}
    /></ModalLazy>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Encyclopedia View — MUST precede every mainTab shell (live report,
  // Nicole 2026-08-30: "world button breaks… it only opens in rp"). The tab
  // shells return on mainTab alone, so from channels/DMs/community/phones/
  // stories/search the render never reached this branch — setView flipped
  // state and nothing happened. RP was the one tab that falls through.
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "encyclopedia") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <Lazy><EncyclopediaView onClose={() => setView("home")} userProfile={userProfile} onEditCharacter={(charId) => { setWorldCharEditId(charId); setShowCharacterEditor(true); }} /></Lazy>
        {/* The character editor opens ON TOP of the unified WORLD interface —
            the characters tab's pencil lands here. Without this mount, opening
            it from this view would silently do nothing (this branch returns
            before the shells that normally carry the editor). */}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} initialCharId={worldCharEditId} onClose={() => { setShowCharacterEditor(false); setWorldCharEditId(null); }} /></Lazy>}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Our Channels Tab (Haley & Nicole only)
  // ═══════════════════════════════════════════════════════════════════════════
  if (mainTab === "channels") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        {sessionExpired && <SessionWarning onRefresh={ensureSession} onLogout={handleLogout} showForceReset={showForceReset} />}
        {showIOSBanner && <IOSInstallBanner onDismiss={() => { setShowIOSBanner(false); localStorage.setItem("sixguys_ios_banner_dismissed", "1"); }} />}
        <ConnectionIndicator status={connectionStatus} saveStatus={saveStatus} lastSavedAt={lastSavedAt} />
        <div style={{ flex: 1, overflow: "hidden" }}>
          <Lazy><ChannelView onViewProfile={openUserProfile} user={user} userProfile={userProfile} currentPlayer={currentPlayer} aiProvider={effectiveAiProvider} aiDialect={aiDialect} apiKey={effectiveApiKey} apiLoaded={globalApiLoaded} onActiveViewChange={setActiveView} jumpToChannelId={jumpToChannelId} onJumpHandled={() => setJumpToChannelId(null)} mode="private" nsfwEnabled={nsfwEnabled} /></Lazy>
        </div>
        {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
        {renderProfileSettings()}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Community Chat Tab
  // ═══════════════════════════════════════════════════════════════════════════
  if (mainTab === "community") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("community"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        {sessionExpired && <SessionWarning onRefresh={ensureSession} onLogout={handleLogout} showForceReset={showForceReset} />}
        {showIOSBanner && <IOSInstallBanner onDismiss={() => { setShowIOSBanner(false); localStorage.setItem("sixguys_ios_banner_dismissed", "1"); }} />}
        <ConnectionIndicator status={connectionStatus} saveStatus={saveStatus} lastSavedAt={lastSavedAt} />
        <div style={{ flex: 1, overflow: "hidden" }}>
          <Lazy><ChannelView onViewProfile={openUserProfile} user={user} userProfile={userProfile} currentPlayer={currentPlayer} aiProvider={effectiveAiProvider} aiDialect={aiDialect} apiKey={effectiveApiKey} apiLoaded={globalApiLoaded} onActiveViewChange={setActiveView} jumpToChannelId={null} onJumpHandled={() => {}} mode="community" nsfwEnabled={nsfwEnabled} /></Lazy>
        </div>
        {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
        {renderProfileSettings("community")}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Community Stories Tab
  // ═══════════════════════════════════════════════════════════════════════════
  if (mainTab === "stories") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        <div style={{ flex: 1, overflow: "auto" }}>
          <Lazy><StoryBrowser user={user} userProfile={userProfile} onJoinStory={handleJoinStory} onSpectateStory={handleSpectateStory} /></Lazy>
        </div>
        {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
        {renderProfileSettings()}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: DM Tab (Update 1)
  // ═══════════════════════════════════════════════════════════════════════════
  if (mainTab === "dm") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        {sessionExpired && <SessionWarning onRefresh={ensureSession} onLogout={handleLogout} showForceReset={showForceReset} />}
        {showIOSBanner && <IOSInstallBanner onDismiss={() => { setShowIOSBanner(false); localStorage.setItem("sixguys_ios_banner_dismissed", "1"); }} />}
        <ConnectionIndicator status={connectionStatus} saveStatus={saveStatus} lastSavedAt={lastSavedAt} />
        <div style={{ flex: 1, overflow: "hidden" }}>
          <Lazy><DMView notifPrefs={notifPrefs} updateNotifPref={updateNotifPref} onViewProfile={openUserProfile} user={user} userProfile={userProfile} currentPlayer={currentPlayer} allChars={allChars} apiKey={effectiveApiKey} aiProvider={effectiveAiProvider} aiDialect={aiDialect} apiLoaded={globalApiLoaded} startNewDM={dmStartNew} dmTargetUserId={dmTargetUserId} onClearDmTarget={() => setDmTargetUserId(null)} onActiveViewChange={setActiveView} onIncomingCall={handleIncomingCall} nsfwEnabled={nsfwEnabled} /></Lazy>
        </div>
        {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
        {renderProfileSettings()}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Phones Tab (Mystic Messenger-style interactive phone experience)
  // ═══════════════════════════════════════════════════════════════════════════
  if (mainTab === "phones") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        {sessionExpired && <SessionWarning onRefresh={ensureSession} onLogout={handleLogout} showForceReset={showForceReset} />}
        {/* Dial My Fate branded header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", padding: "14px 16px 10px", background: "linear-gradient(180deg, var(--ds-surface-1, #0d0808) 0%, var(--ds-surface-0, #0a0a0a) 100%)", borderBottom: "1px solid var(--ds-accent-a15, #c6282815)", flexShrink: 0 }}>
          <img src="/art/brand/logo3_128.png" alt="" style={{ width: "36px", height: "36px", filter: "drop-shadow(0 0 8px var(--ds-accent-a66, #c6282866))", imageRendering: "auto" }} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "18px", fontWeight: 700, letterSpacing: "4px", background: "linear-gradient(90deg, var(--ds-accent, #c62828), var(--ds-gold, #c4a882), var(--ds-accent, #c62828))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", textTransform: "uppercase" }}>Dial My Fate</span>
            <span style={{ fontSize: "9px", color: t.textFaint, letterSpacing: "3px", fontFamily: "'Cinzel', serif", textTransform: "uppercase", marginTop: "2px" }}>My Fate, My Love</span>
          </div>
          <img src="/art/brand/logo3_128.png" alt="" style={{ width: "36px", height: "36px", filter: "drop-shadow(0 0 8px var(--ds-accent-a66, #c6282866))", imageRendering: "auto" }} />
        </div>
        {/* Launch "characters texting each other" (AI ↔ AI) */}
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 12px 4px", background: "var(--ds-surface-0, #0a0a0a)", flexShrink: 0 }}>
          <button onClick={() => setShowCrosstalk(true)} style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "var(--ds-gold-a15, rgba(196,168,130,0.09))", border: "1px solid var(--ds-gold-a40, rgba(212,175,55,0.25))", borderRadius: "999px", color: "var(--ds-gold-bright, #dcc6a0)", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "'Cinzel', serif", letterSpacing: "1px" }}>
            💬 Characters texting each other
          </button>
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <Lazy><DialMyFate user={user} allChars={allChars} aiDialect={aiDialect} /></Lazy>
        </div>
        {showCrosstalk && <Lazy><CharacterCrosstalk user={user} onClose={() => setShowCrosstalk(false)} /></Lazy>}
        {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
        {renderProfileSettings()}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Search Tab (Update 14)
  // ═══════════════════════════════════════════════════════════════════════════
  if (mainTab === "search") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Lazy><UnifiedSearch user={user} inline={true} onNavigate={(tab, targetId) => {
            setMainTab(tab);
            if (tab === 'channels' && targetId) setJumpToChannelId(targetId);
          }} /></Lazy>
        </div>
        {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
        {renderProfileSettings()}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Spectate Mode
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "spectate" && spectatingStory) {
    const specData = spectatingStory.data || {};
    // Use the spectateMessages state (populated by handleSpectateStory from DB), fall back to embedded data
    const specMessages = spectateMessages.length > 0 ? spectateMessages : (specData.messages || []);
    const specSceneType = specData.sceneType || "chat";
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        <div className="bq-bar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: t.bgBase, borderBottom: "1px solid #1a1212" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button onClick={() => { setSpectatingStory(null); setView("home"); setMainTab("stories"); }} style={backBtn}>← Back</button>
            <span style={{ fontSize: "14px", fontWeight: 700 }}>{spectatingStory._isDMStory ? '📨' : '👁️'} {spectatingStory._isDMStory ? 'Reading:' : 'Spectating:'} {spectatingStory.name}</span>
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            {spectatingStory._isDMStory && <span style={{ background: 'rgba(33,150,243,0.15)', color: '#2196f3', padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 600 }}>📨 DM STORY</span>}
            <span style={{ background: spectatingStory._isDMStory ? '#4a9e6e22' : "var(--ds-gold-a33, #c4a88233)", color: spectatingStory._isDMStory ? t.accentSuccess : t.accentGold, padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 600 }}>{spectatingStory._isDMStory ? 'LIVE' : 'READ ONLY'}</span>
            {!spectatingStory._isDMStory && <button onClick={() => handleJoinStory({ stories: spectatingStory }, null)} style={{ ...btnSmall, background: t.accentSuccess, color: "#fff" }}>🎮 Jump In</button>}
          </div>
        </div>
        <div
          ref={spectateScrollRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            // Track whether the user is pinned at (or near) the bottom — for
            // realtime new-message auto-scroll behavior
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            spectateAtBottomRef.current = distanceFromBottom < 80;
            // Trigger older-page load when scrolled to the top
            if (el.scrollTop < 80 && spectateHasMore && !spectateLoadingOlder) {
              loadOlderSpectateMessages();
            }
          }}
          style={{ flex: 1, overflowY: "auto", padding: "16px", background: SCENE_TYPES[specSceneType]?.bg || t.bgTertiary }}
        >
          {spectateLoadingOlder && (
            <div style={{ textAlign: "center", color: t.textMuted, padding: "8px", fontSize: "12px", fontStyle: "italic" }}>
              Loading older messages…
            </div>
          )}
          {!spectateLoadingOlder && spectateHasMore && (
            <div style={{ textAlign: "center", padding: "6px" }}>
              <button
                onClick={loadOlderSpectateMessages}
                style={{ background: 'var(--ds-glass-light)', border: '1px solid var(--ds-line)', color: t.textMuted, padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
              >
                ↑ Load older messages
              </button>
            </div>
          )}
          {specMessages.map((msg, i) => {
            const char = allChars.find((c) => c?.id === msg.charId);
            return (
              <div key={msg.id || i} className="msg-animate" style={{ display: "flex", gap: "10px", marginBottom: "14px", padding: "3px 4px", minWidth: 0 }}>
                <CharAvatar char={char} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, color: char?.color || t.accentGold, fontSize: "14px" }}>{msg.name}</span>
                    {!msg.isPlayer && <span style={{ fontSize: "9px", background: "var(--ds-accent-a22, #c6282822)", color: t.accentGold, padding: "1px 5px", borderRadius: "6px" }}>AI</span>}
                    <span style={{ fontSize: "11px", color: t.textMuted }}>{msg.timestamp}</span>
                  </div>
                  <div style={{ fontSize: "14px", lineHeight: "1.5", marginTop: "2px", whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word", color: t.textPrimary }}>{msg.text}</div>
                </div>
              </div>
            );
          })}
          {specMessages.length === 0 && <div style={{ textAlign: "center", color: "#555", padding: "60px" }}>No messages in this story yet</div>}
        </div>
        {/* Siren's Whisper — spectator-only suggestion box that steers
             the AI's next response. Only renders when (a) we're spectating
             a real RP story (not a DM-as-story view), and (b) the current
             user is NOT the story owner. */}
        {!spectatingStory._isDMStory && spectatingStory.user_id && spectatingStory.user_id !== user?.id && (
          <SirenWhisperBox
            storyId={spectatingStory.id}
            fromUserId={user.id}
            toUserId={spectatingStory.user_id}
            toUserName={(onlineProfiles.find(p => p.id === spectatingStory.user_id)?.display_name) || ''}
            storyName={spectatingStory.name}
            mode="spectator"
          />
        )}
        {renderProfileSettings()}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Home (character select)
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "home") {
    return (
      // OWN SCROLLER, not document scroll. Haley 2026-08-30 (third report,
      // with screenshot): "it's only after the your stories list appears that
      // I'm not able to scroll." Home was the ONLY view that relied on the
      // document scroller — every other tab scrolls an explicit 100dvh
      // overflow container, and those all work for her. A fresh tab scrolls
      // this page fine (verified live in her own Chrome), so the jam is
      // environment state we can't reach; an internal scroller bypasses it on
      // web and fixes the notoriously flaky body-scroll in the iOS PWA.
      <div style={{ height: "100dvh", overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", background: bgApp, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif", position: "relative" }}>
        {/* Background texture */}
        <div className="mf-grain" style={{ position: "fixed" }} aria-hidden="true" />

        <MainNav mainTab={mainTab} setMainTab={setMainTab} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
        {sessionExpired && <SessionWarning onRefresh={ensureSession} onLogout={handleLogout} showForceReset={showForceReset} />}
        {showIOSBanner && <IOSInstallBanner onDismiss={() => { setShowIOSBanner(false); localStorage.setItem("sixguys_ios_banner_dismissed", "1"); }} />}
        <ConnectionIndicator status={connectionStatus} saveStatus={saveStatus} lastSavedAt={lastSavedAt} />

        <div style={{ position: "relative", zIndex: 1 }}>
          {movieNight && (
            <div style={{ padding: "16px 20px 0", maxWidth: "800px", margin: "0 auto" }}>
              <MovieNightHomeCard night={movieNight} onClick={() => { setJumpToChannelId(MOVIE_NIGHT_CHANNEL_ID); setMainTab("channels"); }} />
            </div>
          )}
          {/* Your Stories — at the top for quick access */}
          <div style={{ padding: "20px 20px 0", maxWidth: "800px", margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <h2 className="bq-title" style={{ fontSize: "18px", color: t.accentGold, fontFamily: "'Cinzel', serif", letterSpacing: "2px", margin: 0 }}>Your Stories</h2>
              <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, var(--ds-gold-a33, #c4a88233), transparent)" }} />
              {myStories.length > 0 && <span style={{ fontSize: "11px", color: t.textFaint, background: t.bgBase, padding: "2px 8px", borderRadius: "10px", border: "1px solid #2a1a1a" }}>{myStories.length}</span>}
            </div>
            {/* A failed fetch must never render as "No stories yet" — that is
                what made it look like the stories were gone. */}
            {storiesLoadFailed && myStories.length === 0 && (
              <div style={{ textAlign: "center", padding: "32px 20px", background: "rgba(198,40,40,0.10)", borderRadius: "12px", border: "1px solid rgba(198,40,40,0.32)" }}>
                <p style={{ color: "#ffb3ab", fontSize: "15px", marginBottom: "8px" }}>Couldn’t load your stories</p>
                <p style={{ color: t.textFaint, fontSize: "13px", marginBottom: "12px" }}>They’re safe — this is a connection problem, not lost work.</p>
                <button onClick={() => loadStories()} style={{ background: "none", border: "1px solid rgba(198,40,40,0.5)", color: "#ffb3ab", borderRadius: "8px", padding: "6px 16px", fontSize: "13px", cursor: "pointer", fontFamily: "inherit" }}>Try again</button>
              </div>
            )}
            {!storiesLoadFailed && !storiesLoaded && myStories.length === 0 && (
              <div style={{ textAlign: "center", padding: "32px 20px", background: "var(--ds-glass-light)", borderRadius: "12px", border: "1px dashed #2a1a1a" }}>
                <p style={{ color: t.textFaint, fontSize: "15px", fontStyle: "italic" }}>Loading your stories…</p>
              </div>
            )}
            {!storiesLoadFailed && storiesLoaded && myStories.length === 0 && (
              <div style={{ textAlign: "center", padding: "32px 20px", background: "var(--ds-glass-light)", borderRadius: "12px", border: "1px dashed #2a1a1a" }}>
                <p style={{ color: t.textFaint, fontSize: "15px", marginBottom: "8px", fontStyle: "italic" }}>No stories yet</p>
                <p style={{ color: t.borderLight, fontSize: "13px" }}>Choose a character below to begin your first story</p>
              </div>
            )}
            {myStories.map((s, idx) => (
              <div key={s.id} style={{ marginBottom: "8px", animation: `cardReveal 0.3s ease-out ${idx * 0.05}s both` }}>
              <div className="story-card" style={{
                background: activeStoryId === s.id ? "var(--ds-accent-a1f, rgba(198,40,40,0.12))" : t.bgHover,
                border: activeStoryId === s.id ? "1px solid var(--ds-accent, #c62828)" : "1px solid var(--ds-line-soft)",
                borderRadius: invitePanelStoryId === s.id ? "10px 10px 0 0" : "10px", padding: "14px 16px", marginBottom: 0,
                display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: isMobile ? "10px" : "8px",
                borderLeft: activeStoryId === s.id ? "3px solid var(--ds-accent, #c62828)" : "3px solid transparent",
              }}>
                <div role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); renamingStoryId !== s.id && loadSession(s); }} onKeyDown={(e) => { if (e.key === 'Enter' && renamingStoryId !== s.id) loadSession(s); }} style={{ cursor: renamingStoryId === s.id ? "default" : "pointer", flex: 1, touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
                  {renamingStoryId === s.id ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <input
                        autoFocus
                        value={renameText}
                        onChange={(e) => setRenameText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") renameStory(s.id, renameText);
                          if (e.key === "Escape") { setRenamingStoryId(null); setRenameText(""); }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ ...inputStyle, fontSize: "14px", padding: "4px 8px", flex: 1, background: t.bgBase, borderRadius: "6px" }}
                        placeholder="Story name..."
                      />
                      <button onClick={(e) => { e.stopPropagation(); renameStory(s.id, renameText); }} style={{ ...btnSmall, background: "#4a9e6e33", color: t.accentSuccess, padding: "4px 8px" }}>✓</button>
                      <button onClick={(e) => { e.stopPropagation(); setRenamingStoryId(null); setRenameText(""); }} style={{ ...btnSmall, background: "#e74c3c33", color: "#e74c3c", padding: "4px 8px" }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>
                      {s.name}{activeStoryId === s.id ? " (active)" : ""}
                    </div>
                  )}
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {s.data?.messages?.length || 0} msgs · {s.data?.currentPlayer || "Unknown"} · {formatTimestamp(s.updated_at)}
                  </div>
                  <div style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>
                    {(s.data?.sceneChars || []).slice(0, 4).map((id) => {
                      const c = [...CHARACTERS, ...(s.data?.customChars || [])].find((ch) => ch?.id === id);
                      return c ? c.name : null;
                    }).filter(Boolean).join(", ")}
                    {(s.data?.sceneChars || []).length > 4 ? ` +${s.data.sceneChars.length - 4} more` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={() => loadSession(s)} style={{ ...btnSmall, background: "linear-gradient(135deg, var(--ds-accent-deeper, #8b0000), var(--ds-accent, #c62828))", color: t.textPrimary }} aria-label="Load story" title="Load story">▶ Load</button>
                  <button onClick={(e) => { e.stopPropagation(); setRenamingStoryId(s.id); setRenameText(s.name || ""); }} style={{ ...btnSmall, background: "var(--ds-gold-a33, #c4a88233)", color: t.accentGold }} aria-label="Rename story" title="Rename story">✏️</button>
                  {(() => {
                    const isPublic = !!storySharedMap[s.id];
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleStoryPrivacy(s.id); }}
                        style={{ ...btnSmall, background: isPublic ? '#4a9e6e33' : 'var(--ds-glass-light)', color: isPublic ? t.accentSuccess : t.textMuted, border: isPublic ? '1px solid #4a9e6e44' : '1px solid var(--ds-line)', minWidth: '72px' }}
                        title={isPublic ? 'Public — visible in Community Stories. Click to make private.' : 'Private — only you can see this. Click to share.'}
                        aria-label={isPublic ? 'Make story private' : 'Share story publicly'}
                      >
                        {isPublic ? '🌐 Public' : '🔒 Private'}
                      </button>
                    );
                  })()}
                  {/* Private invite button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); if (invitePanelStoryId === s.id) { setInvitePanelStoryId(null); } else { setInvitePanelStoryId(s.id); setInviteUserSearch(''); setInviteSearchResults([]); loadStoryInvites(s.id); } }}
                    style={{ ...btnSmall, background: invitePanelStoryId === s.id ? '#7aa2f733' : 'var(--ds-glass-light)', color: invitePanelStoryId === s.id ? '#7aa2f7' : t.textMuted, border: invitePanelStoryId === s.id ? '1px solid #7aa2f744' : '1px solid var(--ds-line)' }}
                    title="Invite someone to read this story privately"
                    aria-label="Manage private invites"
                  >
                    👥 Invite
                  </button>
                  {(s.data?.messages?.length || 0) > 2 && <button onClick={() => setBranchingStory(s)} style={{ ...btnSmall, background: "#9b59b633", color: "#9b59b6" }} aria-label="Branch alternate universe" title="Branch AU — create an alternate timeline from a chosen point">🌿 AU</button>}
                  <div style={{ width: '1px', height: '16px', background: 'var(--ds-glass-light)', margin: '0 2px' }} />
                  <button onClick={() => setShowConfirmModal({ message: "Delete this story?", onConfirm: () => { deleteStory(s.id); setShowConfirmModal(null); } })} aria-label="Delete story" title="Delete story" style={{ ...btnSmall, background: "#e74c3c22", color: "#e74c3c", border: "1px solid #e74c3c33" }}>🗑️</button>
                </div>
              </div>{/* end story-card */}

              {/* ── Invite Panel ── */}
              {invitePanelStoryId === s.id && (() => {
                const invites = (storyInvitesMap[s.id] || []).filter(inv => inv.status === 'active');
                const storyAllChars = [...CHARACTERS, ...(s.data?.customChars || [])];
                return (
                  <div style={{ background: "rgba(122,162,247,0.04)", border: "1px solid rgba(122,162,247,0.2)", borderTop: "none", borderRadius: "0 0 10px 10px", padding: "14px 16px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#7aa2f7", marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                      👥 Private Invites
                      <span style={{ fontSize: "11px", fontWeight: 400, color: t.textFaint }}>— invited users can read this story privately</span>
                    </div>

                    {/* Search to add user */}
                    <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                      <input
                        value={inviteUserSearch}
                        onChange={e => { setInviteUserSearch(e.target.value); searchUsersForInvite(e.target.value); }}
                        placeholder="Search by username to invite..."
                        style={{ ...inputStyle, flex: 1, padding: "7px 12px", fontSize: "12px" }}
                      />
                    </div>

                    {/* Search results */}
                    {inviteSearchResults.length > 0 && (
                      <div style={{ background: t.bgBase, border: "1px solid rgba(122,162,247,0.2)", borderRadius: "8px", marginBottom: "10px", overflow: "hidden" }}>
                        {inviteSearchResults.map(u => {
                          const alreadyInvited = invites.some(inv => inv.invitee_id === u.id);
                          return (
                            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", borderBottom: "1px solid var(--ds-line-soft)" }}>
                              {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#7aa2f733", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, color: "#7aa2f7" }}>{(u.display_name || '?')[0].toUpperCase()}</div>}
                              <span style={{ flex: 1, fontSize: "13px", color: t.textPrimary }}>{u.display_name}</span>
                              {alreadyInvited ? (
                                <span style={{ fontSize: "11px", color: t.accentSuccess }}>✓ Invited</span>
                              ) : (
                                <button onClick={() => { sendStoryInvite(s.id, u.id, { canJoin: false }); setInviteUserSearch(''); setInviteSearchResults([]); }} style={{ ...btnSmall, background: "#7aa2f733", color: "#7aa2f7", border: "1px solid #7aa2f744", fontSize: "11px" }}>
                                  + Invite to Read
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Current invites list */}
                    {invites.length === 0 ? (
                      <div style={{ fontSize: "12px", color: t.textFaint, fontStyle: "italic", padding: "4px 0" }}>No active invites yet — search above to invite someone</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {invites.map(inv => {
                          const invitee = inv.invitee || {};
                          return (
                            <div key={inv.id} style={{ background: t.bgBase, border: "1px solid rgba(122,162,247,0.12)", borderRadius: "8px", padding: "10px 12px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                                {invitee.avatar_url ? <img src={invitee.avatar_url} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#7aa2f733", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, color: "#7aa2f7" }}>{(invitee.display_name || '?')[0].toUpperCase()}</div>}
                                <span style={{ flex: 1, fontSize: "13px", fontWeight: 600, color: t.textPrimary }}>{invitee.display_name || 'Unknown'}</span>
                                <button onClick={() => revokeStoryInvite(s.id, inv.id)} style={{ ...btnSmall, background: "#e74c3c22", color: "#e74c3c", border: "1px solid #e74c3c33", fontSize: "11px" }}>Remove</button>
                              </div>
                              {/* Can join toggle */}
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: inv.can_join ? "8px" : 0 }}>
                                <button
                                  onClick={() => updateStoryInvite(s.id, inv.id, { can_join: !inv.can_join })}
                                  style={{ ...btnSmall, background: inv.can_join ? "#4a9e6e33" : "var(--ds-glass-light)", color: inv.can_join ? t.accentSuccess : t.textMuted, border: inv.can_join ? "1px solid #4a9e6e44" : "1px solid var(--ds-line)", fontSize: "11px" }}
                                >
                                  {inv.can_join ? "✓ Can Play Characters" : "Read-Only"}
                                </button>
                                {inv.can_join && <span style={{ fontSize: "11px", color: t.textFaint }}>click to toggle</span>}
                              </div>
                              {/* Character restrictions (only when can_join) */}
                              {inv.can_join && (
                                <div style={{ marginTop: "8px" }}>
                                  <div style={{ fontSize: "11px", color: t.textMuted, marginBottom: "4px" }}>Blocked characters (can't play):</div>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                    {storyAllChars.filter(c => c && (s.data?.sceneChars || []).includes(c.id)).map(c => {
                                      const isBlocked = (inv.blocked_characters || []).includes(c.id);
                                      return (
                                        <button
                                          key={c.id}
                                          onClick={() => {
                                            const newBlocked = isBlocked ? (inv.blocked_characters || []).filter(id => id !== c.id) : [...(inv.blocked_characters || []), c.id];
                                            updateStoryInvite(s.id, inv.id, { blocked_characters: newBlocked });
                                          }}
                                          style={{ ...btnSmall, background: isBlocked ? "#e74c3c22" : "var(--ds-glass-light)", color: isBlocked ? "#e74c3c" : t.textMuted, border: isBlocked ? "1px solid #e74c3c33" : "1px solid var(--ds-line)", fontSize: "10px", padding: "2px 7px" }}
                                          title={isBlocked ? "Click to allow this character" : "Click to block this character"}
                                        >
                                          {isBlocked ? "🚫 " : ""}{c.name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <div style={{ fontSize: "10px", color: t.textFaint, marginTop: "4px", fontStyle: "italic" }}>Click a character to block/unblock them from being played by this invitee. They can play any new characters they create.</div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              </div>
            ))}
            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
              <button onClick={exportSessions} style={btnSmall} disabled={savedSessions.length === 0}>Export All</button>
              <label style={{ ...btnSmall, cursor: "pointer" }}>Import Stories<input type="file" accept=".json" onChange={importSessions} style={{ display: "none" }} /></label>
            </div>
          </div>

          {/* Jump Stories — stories the user joined but doesn't own */}
          {jumpStories.length > 0 && (
            <div style={{ padding: "20px 20px 0", maxWidth: "800px", margin: "0 auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                <h2 style={{ fontSize: "16px", color: "#7aa2f7", fontFamily: "'Cinzel', serif", letterSpacing: "2px", margin: 0 }}>🎮 Jump Stories</h2>
                <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, #7aa2f733, transparent)" }} />
                <span style={{ fontSize: "11px", color: t.textFaint, background: t.bgBase, padding: "2px 8px", borderRadius: "10px", border: "1px solid #2a1a1a" }}>{jumpStories.length}</span>
              </div>
              {jumpStories.map((s, idx) => {
                const charObj = allChars.find(c => c?.id === s._participantCharId);
                return (
                  <div key={s.id} className="story-card" style={{
                    background: "rgba(122,162,247,0.06)",
                    border: "1px solid rgba(122,162,247,0.15)",
                    borderRadius: "10px", padding: "14px 16px", marginBottom: "8px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    borderLeft: "3px solid #7aa2f744",
                    animation: `cardReveal 0.3s ease-out ${idx * 0.05}s both`,
                  }}>
                    <div role="button" tabIndex={0} style={{ flex: 1, cursor: "pointer", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }} onClick={(e) => { e.stopPropagation(); handleJoinStory({ stories: s }, s._participantCharId); }} onKeyDown={(e) => { if (e.key === 'Enter') handleJoinStory({ stories: s }, s._participantCharId); }}>
                      <div style={{ fontWeight: 600, fontSize: "14px", color: t.textPrimary }}>{s.name}</div>
                      <div style={{ fontSize: "12px", color: "#7aa2f7", marginTop: "2px" }}>
                        Playing as {charObj?.name || s._participantCharId || "Unknown"} · {s.message_count || 0} msgs · {formatTimestamp(s.last_active_at || s.updated_at)}
                      </div>
                      {s.description && <div style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>{s.description}</div>}
                    </div>
                    <button onClick={() => handleJoinStory({ stories: s }, s._participantCharId)} style={{ ...btnSmall, background: "linear-gradient(135deg, #2a5298, #7aa2f7)", color: "#fff" }}>▶ Rejoin</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Shared With Me — stories other users invited this user to read */}
          {invitedStories.length > 0 && (
            <div style={{ padding: "20px 20px 0", maxWidth: "800px", margin: "0 auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                <h2 style={{ fontSize: "16px", color: "var(--ds-gold, #c4a882)", fontFamily: "'Cinzel', serif", letterSpacing: "2px", margin: 0 }}>👥 Shared With Me</h2>
                <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, var(--ds-gold-a33, #c4a88233), transparent)" }} />
                <span style={{ fontSize: "11px", color: t.textFaint, background: t.bgBase, padding: "2px 8px", borderRadius: "10px", border: "1px solid #2a1a1a" }}>{invitedStories.length}</span>
              </div>
              {invitedStories.map((inv, idx) => {
                const story = inv.story;
                const inviter = inv.inviter;
                if (!story) return null;
                const sceneChars = (story.data?.sceneChars || []).slice(0, 4).map(id => {
                  const c = [...CHARACTERS, ...(story.data?.customChars || [])].find(ch => ch?.id === id);
                  return c?.name;
                }).filter(Boolean);
                return (
                  <div key={inv.id} style={{
                    background: "var(--ds-gold-a0f, rgba(196,168,130,0.06))", border: "1px solid var(--ds-gold-a26, rgba(196,168,130,0.15))",
                    borderRadius: "10px", padding: "14px 16px", marginBottom: "8px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    borderLeft: "3px solid var(--ds-gold-a44, #c4a88244)",
                    animation: `cardReveal 0.3s ease-out ${idx * 0.05}s both`,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "14px", color: t.textPrimary }}>{story.name || "Untitled Story"}</div>
                      <div style={{ fontSize: "12px", color: t.accentGold, marginTop: "2px" }}>
                        Shared by {inviter?.display_name || "Someone"} · {story.data?.messages?.length || 0} msgs · {formatTimestamp(story.updated_at)}
                      </div>
                      {sceneChars.length > 0 && <div style={{ fontSize: "11px", color: t.textFaint, marginTop: "2px" }}>{sceneChars.join(", ")}</div>}
                      <div style={{ fontSize: "11px", marginTop: "4px" }}>
                        {inv.can_join ? <span style={{ color: t.accentSuccess }}>✓ You can play characters</span> : <span style={{ color: t.textMuted }}>👁 Read-only</span>}
                        {inv.can_join && (inv.blocked_characters || []).length > 0 && (
                          <span style={{ color: t.textFaint, marginLeft: "8px" }}>
                            (blocked: {(inv.blocked_characters || []).map(id => CHARACTERS.find(c => c.id === id)?.name || id).join(", ")})
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleSpectateStory({ stories: story })}
                      style={{ ...btnSmall, background: "linear-gradient(135deg, #5a4020, var(--ds-gold, #c4a882))", color: "#fff", marginLeft: "12px" }}
                    >
                      ▶ Read
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Branch AU Modal — pick the point to fork the story */}
          {branchingStory && (
            <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px) saturate(1.15)", WebkitBackdropFilter: "blur(8px) saturate(1.15)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
              <div style={{ background: t.bgBase, border: "1px solid var(--ds-accent-a44, #c6282844)", borderRadius: "12px", maxWidth: "600px", width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #2a1a1a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <h3 style={{ margin: 0, color: "#9b59b6", fontSize: "16px", fontFamily: "'Cinzel', serif" }}>🌿 Branch AU</h3>
                    <p style={{ margin: "4px 0 0", color: t.textMuted, fontSize: "12px" }}>Pick the moment where the story splits into an alternate timeline. Everything after that point will be erased in the new branch.</p>
                  </div>
                  <button onClick={() => setBranchingStory(null)} style={{ background: "none", border: "none", color: "#e74c3c", fontSize: "20px", cursor: "pointer", padding: "4px" }}>✕</button>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
                  <p style={{ color: t.accentGold, fontSize: "13px", marginBottom: "12px" }}>From: <strong>{branchingStory.name}</strong> ({branchingStory.data?.messages?.length || 0} messages)</p>
                  {(branchingStory.data?.messages || []).map((m, idx) => {
                    const char = allChars.find(c => c?.id === m.charId);
                    return (
                      <div key={idx} onClick={() => branchStory(branchingStory, idx)}
                        style={{ padding: "8px 12px", marginBottom: "4px", borderRadius: "8px", cursor: "pointer", border: "1px solid transparent", background: t.bgHover, transition: "all 0.15s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(155,89,182,0.15)"; e.currentTarget.style.borderColor = "#9b59b644"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = t.bgHover; e.currentTarget.style.borderColor = "transparent"; }}
                      >
                        <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "2px" }}>
                          <span style={{ fontSize: "10px", color: "#666", fontFamily: "monospace" }}>#{idx + 1}</span>
                          <span style={{ fontWeight: 700, color: char?.color || t.accentGold, fontSize: "13px" }}>{m.name}</span>
                          {!m.isPlayer && <span style={{ fontSize: "9px", background: "var(--ds-accent-a22, #c6282822)", color: t.accentGold, padding: "1px 4px", borderRadius: "4px" }}>AI</span>}
                        </div>
                        <div style={{ fontSize: "12px", color: "#aaa", lineHeight: 1.4, maxHeight: "40px", overflow: "hidden" }}>
                          {(m.text || "").substring(0, 150)}{(m.text || "").length > 150 ? "..." : ""}
                        </div>
                        <div style={{ fontSize: "10px", color: "#9b59b6", marginTop: "4px", opacity: 0.7 }}>
                          ↳ Click to branch here — keep messages 1-{idx + 1}, start new AU from this point
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <OrnateDivider width="60%" margin="20px auto" />

          {/* ── QUICK ACCESS — Featured sections ─────────────────────────── */}
          <div style={{ padding: "12px 20px 4px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "8px" }}>
              {[
                { view: "fategram",      icon: "📱", label: "FateGram",    sub: "Social feed",    accent: "#e91e63", bg: "135deg, #200815 0%, #300f1e 100%" },
                { view: "gallery",       icon: "🖼️",  label: "Art Gallery", sub: "Character art",  accent: "#c4a882", bg: "135deg, #1a120a 0%, #26180c 100%" },
                { view: "collection",    icon: "🃏", label: "Collection",  sub: "Gacha cards",    accent: "#d1b3ff", bg: "135deg, #150f1e 0%, #1a0f22 100%" },
                { view: "constellation", icon: "✨", label: "Bonds",       sub: "Relationship map", accent: "#ffd700", bg: "135deg, #111108 0%, #1a1a08 100%" },
                { view: "encyclopedia",  icon: "📖", label: "Lore",        sub: "Universe wiki",  accent: "#00bcd4", bg: "135deg, #081818 0%, #0c2020 100%" },
              ].map(({ view: v, icon, label, sub, accent, bg }) => (
                <button
                  key={v}
                  className="magnetic-btn"
                  onClick={() => setView(v)}
                  style={{
                    background: `linear-gradient(${bg})`,
                    border: `1px solid ${accent}33`,
                    borderRadius: "12px",
                    padding: isMobile ? "12px 6px" : "14px 8px",
                    color: accent,
                    cursor: "pointer",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "5px",
                    transition: "all 0.2s",
                    boxShadow: `0 2px 12px ${accent}11`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = `${accent}66`; e.currentTarget.style.boxShadow = `0 4px 20px ${accent}22`; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = `${accent}33`; e.currentTarget.style.boxShadow = `0 2px 12px ${accent}11`; e.currentTarget.style.transform = ""; }}
                >
                  <div style={{ fontSize: isMobile ? "20px" : "24px", lineHeight: 1 }}>{icon}</div>
                  <div style={{ fontSize: isMobile ? "9px" : "11px", fontFamily: "'Cinzel', serif", letterSpacing: "0.5px", fontWeight: 700, lineHeight: 1.2 }}>{label}</div>
                  {!isMobile && <div style={{ fontSize: "9px", color: `${accent}88`, lineHeight: 1 }}>{sub}</div>}
                </button>
              ))}
            </div>
          </div>

          <OrnateDivider width="50%" margin="20px auto 0" />

          {/* Character RP section */}
          <div style={{ textAlign: "center", padding: "12px 20px 4px" }}>
            <h2 className="bq-title" style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 4px", fontFamily: "'Cinzel', serif", letterSpacing: "4px", color: t.accentGold, textTransform: "uppercase" }}>Start an RP</h2>
            <p style={{ color: t.textMuted, fontSize: "12px", margin: 0, fontStyle: "italic" }}>Describe the story you want — the universe brings the cast.</p>
          </div>

          {/* Route Description — primary entry: prompt + Start RP button */}
          <div style={{ padding: "0 20px 8px", maxWidth: "900px", margin: "0 auto" }}>
            <div className="bq-leaf" style={{ background: "var(--ds-gold-a0f, rgba(196,168,130,0.06))", border: "1px solid var(--ds-gold-a2e, rgba(196,168,130,0.18))", borderRadius: "12px", padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ fontSize: "16px" }}>✨</span>
                <span style={{ fontSize: "13px", fontWeight: 700, color: t.accentGold, fontFamily: "'Cinzel', serif", letterSpacing: "2px", textTransform: "uppercase" }}>What story do you want to live in?</span>
              </div>
              <textarea
                value={routeDescription}
                onChange={e => setRouteDescription(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (routeDescription.trim() && !startingPromptRP) startPromptDrivenRP();
                  }
                }}
                placeholder={dailyIdea
                  ? `Today's spark: "${dailyIdea}" · or write your own`
                  : 'e.g. "my name is Eric and I want to meet a bunch of vampires" · "A quiet morning at the siren palace where Kya tries to escape Francesca\'s dressing routine" · "I\'m a hunter who walks into the wrong werewolf bar"'}
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: "rgba(0,0,0,0.3)", border: "1px solid var(--ds-gold-a33, rgba(196,168,130,0.2))",
                  borderRadius: "8px", padding: "12px 14px",
                  color: t.textPrimary, fontSize: "16px", lineHeight: "1.5",
                  resize: "vertical", minHeight: "100px", maxHeight: "200px",
                  fontFamily: "inherit", outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={e => { e.target.style.borderColor = "var(--ds-gold-a80, rgba(196,168,130,0.5))"; }}
                onBlur={e => { e.target.style.borderColor = "var(--ds-gold-a33, rgba(196,168,130,0.2))"; }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
                <button
                  onClick={startPromptDrivenRP}
                  disabled={!routeDescription.trim() || startingPromptRP}
                  style={{
                    flex: "1 1 auto",
                    minWidth: "200px",
                    minHeight: "52px",
                    background: routeDescription.trim() && !startingPromptRP
                      ? "linear-gradient(135deg, var(--ds-accent-deeper, #8b0000), var(--ds-accent, #c62828))"
                      : "var(--ds-gold-a1a, rgba(196,168,130,0.1))",
                    border: "none",
                    color: routeDescription.trim() && !startingPromptRP ? "var(--ds-on-accent, #fff8f2)" : "var(--ds-ink, #b9a89f)",
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: "3px",
                    fontSize: "14px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    borderRadius: "10px",
                    cursor: routeDescription.trim() && !startingPromptRP ? "pointer" : "default",
                    boxShadow: routeDescription.trim() && !startingPromptRP ? "0 4px 16px var(--ds-accent-a59, rgba(198,40,40,0.35))" : "none",
                    opacity: routeDescription.trim() && !startingPromptRP ? 1 : 0.45,
                    touchAction: "manipulation",
                    transition: "transform 0.1s, box-shadow 0.2s",
                  }}
                  onMouseEnter={e => { if (routeDescription.trim()) e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
                >
                  {startingPromptRP ? "✨ Building scene…" : "▶  Start RP"}
                </button>
                {/* Visibility at creation — a SEGMENTED toggle so the starting
                    state is unambiguous (Haley: "clear on which one you're
                    starting"). The active side is lit; both options always
                    visible. */}
                <div role="group" aria-label="Story visibility" style={{ display: 'flex', border: '1px solid var(--ds-line)', borderRadius: '9px', overflow: 'hidden', flexShrink: 0, minHeight: '44px' }}>
                  <button
                    type="button"
                    onClick={() => setNewRpPublic(true)}
                    aria-pressed={newRpPublic}
                    title="Starts public — visible in Community Stories from the first scene."
                    style={{
                      background: newRpPublic ? '#4a9e6e33' : 'transparent',
                      color: newRpPublic ? t.accentSuccess : t.textFaint,
                      border: 'none', borderRight: '1px solid var(--ds-line)',
                      padding: '10px 12px', fontSize: '12px', cursor: 'pointer',
                      fontFamily: "'Cinzel', serif", letterSpacing: '1px',
                      fontWeight: newRpPublic ? 700 : 400,
                      touchAction: 'manipulation',
                    }}
                  >
                    🌐 Public
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewRpPublic(false)}
                    aria-pressed={!newRpPublic}
                    title="Starts private — only you can read it."
                    style={{
                      background: !newRpPublic ? 'var(--ds-gold-a26, rgba(196,168,130,0.15))' : 'transparent',
                      color: !newRpPublic ? t.accentGold : t.textFaint,
                      border: 'none',
                      padding: '10px 12px', fontSize: '12px', cursor: 'pointer',
                      fontFamily: "'Cinzel', serif", letterSpacing: '1px',
                      fontWeight: !newRpPublic ? 700 : 400,
                      touchAction: 'manipulation',
                    }}
                  >
                    🔒 Private
                  </button>
                </div>
                {routeDescription.trim() && !startingPromptRP && (
                  <button onClick={() => setRouteDescription('')} style={{ background: "transparent", border: "1px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))", color: t.textMuted, fontSize: "12px", cursor: "pointer", padding: "10px 14px", borderRadius: "8px", fontFamily: "'Cinzel', serif", letterSpacing: "1px" }}>Clear</button>
                )}
              </div>
              <div style={{ marginTop: "8px", fontSize: "11px", color: t.textFaint, fontStyle: "italic" }}>
                AI brings in any universe characters that fit the prompt. You type your own lines as yourself. <span style={{ color: t.textMuted }}>⌘+Enter to start.</span>{' '}
                {newRpPublic
                  ? <span style={{ color: t.accentSuccess }}>Starting public — it appears in Community Stories. Tap 🔒 Private to keep it just yours.</span>
                  : <span style={{ color: t.accentGold }}>Starting private — only you can read it.</span>}
              </div>
            </div>
          </div>

          {/* Advanced — collapsible character picker */}
          <div style={{ padding: "8px 20px 4px", maxWidth: "900px", margin: "0 auto" }}>
            <button
              onClick={() => setAdvancedCharPickOpen(prev => !prev)}
              style={{
                width: "100%", background: "transparent", border: "1px dashed var(--ds-gold-a40, rgba(196,168,130,0.25))",
                color: t.textMuted, fontFamily: "'Cinzel', serif", letterSpacing: "2px", fontSize: "11px",
                textTransform: "uppercase", padding: "10px 14px", borderRadius: "8px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                touchAction: "manipulation",
              }}
            >
              <span>{advancedCharPickOpen ? "▾" : "▸"}</span>
              <span>Advanced — play as a specific established character</span>
            </button>
          </div>

          {/* Character selection with art-enhanced cards — collapsible */}
          {advancedCharPickOpen && (
          <div style={{ padding: "8px 20px 20px", maxWidth: "900px", margin: "0 auto", animation: "fadeSlideIn 0.2s ease-out" }}>
            <div style={{ position: "relative", marginBottom: "14px" }}>
              <input ref={charSearchRef} value={charSearch} onChange={(e) => setCharSearch(e.target.value)} className="input-glow" style={{ ...inputStyle, width: "100%", boxSizing: "border-box", background: t.bgBase, borderRadius: "10px", padding: "12px 16px 12px 40px", border: "1px solid var(--ds-accent-a26, rgba(198,40,40,0.15))" }} placeholder="Search by name, species, traits..." aria-label="Search characters by name, species, or traits" />
              <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: t.textFaint, fontSize: "14px", pointerEvents: "none" }}>🔍</span>
              {charSearch && <button onClick={() => setCharSearch("")} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textFaint, fontSize: "16px", padding: "4px 6px", lineHeight: 1 }} aria-label="Clear search">✕</button>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "12px", maxHeight: "420px", overflowY: "auto", padding: "4px" }}>
              {allChars
                .filter(matchesSearch)
                .sort((a, b) => {
                  // Pin Kya and Evie first, then popularity order
                  const PIN = { kya_sg1: 0, evelyn: 1 };
                  const POPULARITY = { cruz: 2, ezra: 3, bryce: 4, alistair: 5, lachesis: 6, kieran: 7, lance: 8, sebastian_vamp: 9, nolan: 10, dimitri: 11, ford: 12, ryder: 13 };
                  const aRank = PIN[a.id] ?? POPULARITY[a.id] ?? 99;
                  const bRank = PIN[b.id] ?? POPULARITY[b.id] ?? 99;
                  return aRank - bRank;
                })
                .map((c, idx) => {
                  const hasFullArt = !!getCharacterArt(c.id, 'thumb');
                  return (
                    <button
                      key={c.id}
                      className="char-card char-glow"
                      onClick={() => { pickPlayingAs(c.id); setCharSearch(""); setView("chat"); }}
                      style={{
                        '--char-color': `${c.color}44`,
                        background: playingAs === c.id ? `linear-gradient(135deg, ${c.color}33, ${c.color}11)` : "linear-gradient(135deg, #1a121288, #120e0e88)",
                        border: playingAs === c.id ? `1px solid ${c.color}88` : `1px solid ${c.color}22`,
                        borderRadius: "14px", padding: "0", color: t.textPrimary, cursor: "pointer", textAlign: "left",
                        overflow: "hidden", animation: `cardReveal 0.4s ease-out ${idx * 0.03}s both`,
                      }}
                    >
                      {/* Character art thumbnail at top of card — transparent PNG rendered on dark card bg */}
                      {hasFullArt && (
                        <div style={{ width: "100%", height: "120px", overflow: "hidden", position: "relative", background: "transparent", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                          <img src={getCharacterArt(c.id, 'thumb')} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom center" }} loading="lazy" />
                        </div>
                      )}
                      <div style={{ padding: hasFullArt ? "8px 12px 12px" : "14px 12px", display: "flex", alignItems: "center", gap: "10px" }}>
                        {!hasFullArt && <CharAvatar char={c} size={36} />}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "14px", fontFamily: "'Cinzel', serif", letterSpacing: "0.5px" }}>{c.name}</div>
                          <div style={{ fontSize: "11px", color: c.color, fontStyle: "italic" }}>{c.species}</div>
                          {c.controlledBy !== currentPlayer && c.controlledBy !== "AI" && <div style={{ fontSize: "9px", color: t.textFaint }}>{c.controlledBy}'s character</div>}
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
          )}

          {/* Action buttons — redesigned as a sleek toolbar */}
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", padding: "8px 20px 24px", flexWrap: "wrap" }}>
            <button className="magnetic-btn" onClick={() => setShowCreateChar(true)} style={{ ...btnPrimary, background: "linear-gradient(135deg, var(--ds-accent-deeper, #8b0000), var(--ds-accent, #c62828))", color: "var(--ds-on-accent, #fff8f2)", border: "1px solid var(--ds-accent-a66, #c6282866)", borderRadius: "10px", padding: "12px 24px", fontFamily: "'Cinzel', serif", letterSpacing: "2px", fontSize: "12px", textTransform: "uppercase", boxShadow: "0 4px 15px var(--ds-accent-a40, rgba(198,40,40,0.25))" }}>Create Character</button>
            <button className="magnetic-btn" onClick={() => setView("characters")} style={{ ...btnPrimary, background: "transparent", border: "1px solid var(--ds-gold-a44, #c4a88244)", color: t.accentGold, borderRadius: "10px", padding: "12px 24px", fontFamily: "'Cinzel', serif", letterSpacing: "2px", fontSize: "12px", textTransform: "uppercase" }}>Browse All</button>
          </div>


        </div>{/* end zIndex wrapper */}

        {showCreateChar && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px) saturate(1.15)", WebkitBackdropFilter: "blur(8px) saturate(1.15)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setShowCreateChar(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: t.bgBase, borderRadius: "16px", padding: "24px", maxWidth: "500px", width: "90%", maxHeight: "80vh", overflowY: "auto" }}>
              <h2 style={{ margin: "0 0 16px", fontSize: "20px", color: "var(--ds-ink-hi, #fff)" }}>✨ Create Your Character</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <input value={newChar.name} onChange={(e) => setNewChar((p) => ({ ...p, name: e.target.value }))} style={inputStyle} placeholder="Character Name *" />
                <input value={newChar.species} onChange={(e) => setNewChar((p) => ({ ...p, species: e.target.value }))} style={inputStyle} placeholder="Species (e.g. Vampire, Werewolf, Siren...)" />
                <textarea value={newChar.personality} onChange={(e) => setNewChar((p) => ({ ...p, personality: e.target.value }))} style={{ ...inputStyle, minHeight: "60px", resize: "vertical" }} placeholder="Personality" />
                <textarea value={newChar.speechPattern} onChange={(e) => setNewChar((p) => ({ ...p, speechPattern: e.target.value }))} style={{ ...inputStyle, minHeight: "60px", resize: "vertical" }} placeholder="Speech pattern" />
                <textarea value={newChar.backstory} onChange={(e) => setNewChar((p) => ({ ...p, backstory: e.target.value }))} style={{ ...inputStyle, minHeight: "60px", resize: "vertical" }} placeholder="Backstory" />
                <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                  <button onClick={createCustomChar} disabled={!newChar.name.trim()} style={{ ...btnPrimary, flex: 1, opacity: newChar.name.trim() ? 1 : 0.5 }}>Create & Play</button>
                  <button onClick={() => setShowCreateChar(false)} style={{ ...btnPrimary, background: "#555", flex: 1 }}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
        {renderProfileSettings()}
        {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
        {showConfetti && <Confetti t={t} onDone={() => setShowConfetti(false)} />}
        {toastMsg && <Toast t={t} message={toastMsg.message} type={toastMsg.type} icon={toastMsg.icon} onDone={() => setToastMsg(null)} />}
        <Lazy><MusicPlayer visible={musicPlayerVisible} onToggleVisible={toggleMusicPlayer} isMobile={isMobile} /></Lazy>
        {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
        {isMobile && <div style={{ height: "env(safe-area-inset-bottom)", flexShrink: 0 }} />}
        <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
        {showFateCard && <ModalLazy><DailyFateCard user={user} onClose={() => setShowFateCard(false)} onCardDrawn={() => setFateDrawnToday(true)} /></ModalLazy>}
        {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
        {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
        <CookieConsent />
        <ToastContainer ref={toastRef} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Character Browser (new component with alphabetical + filters)
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "characters") {
    return (
      <Lazy><CharacterBrowser
        allChars={allChars}
        customChars={customChars}
        onBack={() => { setView("home"); setCharSearch(""); }}
        onViewChar={(c) => setViewingChar(c)}
      /></Lazy>
    );
  }

  // Character Detail Modal
  if (viewingChar) {
    const canPlay = viewingChar.controlledBy === currentPlayer || viewingChar.controlledBy === "AI";
    return (
      <CharModal
        char={viewingChar}
        onClose={() => setViewingChar(null)}
        onPlay={() => { pickPlayingAs(viewingChar.id); setView("chat"); setViewingChar(null); }}
        onAddScene={() => { setSceneChars((p) => p.includes(viewingChar.id) ? p : [...p, viewingChar.id]); setBlockedChars((b) => b.filter(x => x !== viewingChar.id)); setViewingChar(null); }}
        canPlay={canPlay}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Character Art Gallery
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "gallery") {
    return (
      <div style={{ minHeight: "100dvh", background: bgApp, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif", position: "relative" }}>
        <div className="mf-grain" style={{ position: "fixed" }} aria-hidden="true" />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="bq-bar" style={{ ...topBar, background: "linear-gradient(180deg, var(--ds-surface-1, #0d0808), var(--ds-surface-0, #0a0a0a))", borderBottom: "1px solid var(--ds-gold-a22, #c4a88222)" }}>
            <button onClick={() => setView("home")} style={{ ...backBtn, fontFamily: "'Cinzel', serif", fontSize: "12px", letterSpacing: "1px", color: t.accentGold }}>← Back</button>
            <h2 style={{ margin: 0, fontSize: "16px", fontFamily: "'Cinzel', serif", letterSpacing: "3px", color: t.accentGold, textTransform: "uppercase" }}>Art Gallery</h2>
            <div style={{ width: "60px" }} />
          </div>
          <Lazy><CharacterArtGallery onClose={() => setView("home")} onSelectCharacter={(charId) => { pickPlayingAs(charId); setView("chat"); }} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Gacha Collection
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "collection") {
    return (
      <div style={{ minHeight: "100dvh", background: bgApp, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif", position: "relative" }}>
        <div className="mf-grain" style={{ position: "fixed" }} aria-hidden="true" />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="bq-bar" style={{ ...topBar, background: "linear-gradient(180deg, var(--ds-surface-1, #0d0808), var(--ds-surface-0, #0a0a0a))", borderBottom: "1px solid #d1b3ff22" }}>
            <button onClick={() => setView("home")} style={{ ...backBtn, fontFamily: "'Cinzel', serif", fontSize: "12px", letterSpacing: "1px", color: t.accentPrimary }}>← Back</button>
            <h2 style={{ margin: 0, fontSize: "16px", fontFamily: "'Cinzel', serif", letterSpacing: "3px", color: t.accentPrimary, textTransform: "uppercase" }}>Collection</h2>
            <div style={{ width: "60px" }} />
          </div>
          <Lazy><GachaCollection /></Lazy>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Relationship Constellation
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "constellation") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <div className="bq-bar" style={{ ...topBar, background: "linear-gradient(180deg, var(--ds-surface-1, #0d0808), var(--ds-surface-0, #0a0a0a))", borderBottom: "1px solid #ffd70022", flexShrink: 0 }}>
          <button onClick={() => setView("home")} style={{ ...backBtn, fontFamily: "'Cinzel', serif", fontSize: "12px", letterSpacing: "1px", color: "#ffd700" }}>← Back</button>
          <h2 style={{ margin: 0, fontSize: "16px", fontFamily: "'Cinzel', serif", letterSpacing: "3px", color: "#ffd700", textTransform: "uppercase" }}>Constellation</h2>
          <div style={{ width: "60px" }} />
        </div>
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
          <Lazy><RelationshipConstellation onCharacterSelect={(charId) => { pickPlayingAs(charId); setView("chat"); }} /></Lazy>
        </div>
      </div>
    );
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: FateGram — Character Social Feed
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "fategram") {
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <Lazy><CharacterSocialFeed onClose={() => setView("home")} user={user} /></Lazy>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Chat View (with OOC styling, timestamps, animations, regen)
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: bgApp, color: textMain, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
      <MainNav mainTab={mainTab} setMainTab={(t) => { setMainTab(t); if (t !== "rp") setView("home"); }} currentPlayer={currentPlayer} userProfile={userProfile} onLogout={handleLogout} onAdmin={() => setShowAdminPanel(true)} onCharEditor={() => setShowCharacterEditor(true)} onEncyclopedia={() => setView("encyclopedia")} onSettings={() => { setProfileSettingsInitialTab("app"); setShowProfileSettings(true); }} onProfile={() => setShowProfileSettings(true)} isMobile={isMobile} onlineProfiles={onlineProfiles} showUserList={showUserList} setShowUserList={setShowUserList} user={user} unreadNotifCount={unreadNotifCount} onNotifToggle={() => { setShowNotifDropdown(prev => !prev); if (!showNotifDropdown) setUnreadNotifCount(0); }} notifPrefs={notifPrefs} showSnoozeMenu={showSnoozeMenu} setShowSnoozeMenu={setShowSnoozeMenu} handleSnooze={handleSnooze} showNotifDropdown={showNotifDropdown} setShowNotifDropdown={setShowNotifDropdown} liveNotifications={liveNotifications} dismissNotification={dismissNotification} dismissAllNotifications={dismissAllNotifications} handleNotifClick={handleNotifClick} unreadTabs={unreadTabs} setDmTargetUserId={setDmTargetUserId} setDmStartNew={setDmStartNew} storyChannelUnread={storyChannelUnread} showStoryNotifPanel={showStoryNotifPanel} setShowStoryNotifPanel={setShowStoryNotifPanel} storyNotifMessages={storyNotifMessages} onStoryNotifClick={(notif) => { if (notif?.channelId) setJumpToChannelId(notif.channelId); setMainTab("channels"); setShowStoryNotifPanel(false); setStoryChannelUnread(0); }} onClearStoryNotifs={() => { setStoryChannelUnread(0); setStoryNotifMessages([]); setShowStoryNotifPanel(false); }} onOpenStoryLinks={() => setShowStoryLinks(true)} hasActiveStory={!!activeStoryId} musicPlayerVisible={musicPlayerVisible} onToggleMusic={toggleMusicPlayer} phoneOpen={phoneOpen} onTogglePhone={() => setPhoneOpen(p => !p)} onShowFateCard={() => setShowFateCard(true)} fateDrawnToday={fateDrawnToday} friendsList={friendsList} friendIncoming={friendIncoming} friendOutgoing={friendOutgoing} refreshFriends={refreshFriends} setViewingProfile={setViewingProfile} onProfileTab={(tab) => { setProfileSettingsInitialTab(tab); setShowProfileSettings(true); }} onOpenCrosstalk={() => { setMainTab("phones"); setShowCrosstalk(true); }} />
      {sessionExpired && <SessionWarning onRefresh={ensureSession} onLogout={handleLogout} showForceReset={showForceReset} />}
        {showIOSBanner && <IOSInstallBanner onDismiss={() => { setShowIOSBanner(false); localStorage.setItem("sixguys_ios_banner_dismissed", "1"); }} />}
        <ConnectionIndicator status={connectionStatus} saveStatus={saveStatus} lastSavedAt={lastSavedAt} />
      <div style={{ background: t.bgBase, borderBottom: "1px solid #1a1212", padding: "6px 12px" }}>
        {/* Row 1: Navigation + character name + action buttons */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button onClick={() => { clearTabStoryMarker(); setView("home"); }} style={backBtn}>←</button>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} title={sidebarOpen ? "Hide sidebar" : "Show sidebar"} style={{ ...backBtn, fontSize: "18px", lineHeight: 1, padding: "4px 8px", color: sidebarOpen ? "var(--ds-gold, #c4a882)" : "#8e9297" }}>{sidebarOpen ? "✕" : "☰"}</button>
            {(playingAs || narratorMode) && <span style={{ fontSize: "12px", color: "#888" }}>{playingAs ? currentChar?.name : (narratorPlayerName || "Narrator")}</span>}
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <button onClick={saveStory} style={{ ...btnSmall, background: t.accentSuccess, padding: "4px 8px" }} title="Save story" aria-label="Save story">💾</button>
            <button onClick={() => {
              if (messages.length > 0 && !activeStoryId) {
                setShowConfirmModal({ message: "Save current story first?", onConfirm: () => { saveStory(); startNewStory(); setShowConfirmModal(null); }, onCancel: () => { startNewStory(); setShowConfirmModal(null); } });
                return;
              }
              startNewStory();
            }} style={{ ...btnSmall, background: "#f39c12", padding: "4px 8px" }} title="New story" aria-label="Start new story">📖</button>
            <button onClick={() => { setProfileSettingsInitialTab("appearance"); setProfileSettingsScope({ surface: "rp" }); setShowProfileSettings(true); }} style={{ ...btnSmall, background: "var(--ds-gold-a22, #c4a88222)", color: t.accentGold, padding: "4px 8px" }} aria-label="Reading preferences" title="Reading preferences">Aa</button>
            <button
              onClick={() => { setProfileSettingsInitialTab("story"); setProfileSettingsScope(activeStoryId ? { storyId: activeStoryId, storyName: currentSessionName, characterName: currentChar?.name } : null); setShowProfileSettings(true); }}
              style={{
                ...btnSmall, padding: "4px 8px",
                color: Object.values(storyPreferences).some(v => v) ? t.textPrimary : t.textMuted,
                background: Object.values(storyPreferences).some(v => v) ? 'var(--ds-gold-a15, #c4a88215)' : 'transparent',
                border: Object.values(storyPreferences).some(v => v) ? '1px solid var(--ds-gold-a66, #c4a88266)' : undefined,
              }}
              aria-label="Story preferences"
              title="Story preferences — pacing, tone, relationship"
            >⚙ Story</button>
            {/* v412 (Haley, 2026-10-06, "add the switch to ALL"): this story's model. */}
            <ModelSwitch userId={user?.id} threadType="story" threadId={activeStoryId} compact />
            {/* v422 (Haley, 2026-10-19): this story's lore, written by the author, read by every reply here. */}
            {activeStoryId && (
              <button
                onClick={() => setShowStoryLore(true)}
                style={{ ...btnSmall, padding: "4px 8px", color: savedSessions.find(s => s.id === activeStoryId)?.lore ? t.accentGold : t.textMuted, background: savedSessions.find(s => s.id === activeStoryId)?.lore ? 'var(--ds-gold-a15, #c4a88215)' : 'transparent', border: savedSessions.find(s => s.id === activeStoryId)?.lore ? '1px solid var(--ds-gold-a66, #c4a88266)' : undefined }}
                aria-label="This story's lore"
                title="This story's lore — what is true here, above the profiles and the world"
              >📜 Lore</button>
            )}
            {showStoryLore && activeStoryId && (
              <ThreadLorePanel threadType="story" threadId={activeStoryId} threadName={currentSessionName} isMobile={isMobile}
                initial={savedSessions.find(s => s.id === activeStoryId)?.lore || ''}
                onSaved={(lore) => setSavedSessions(prev => prev.map(s => s.id === activeStoryId ? { ...s, lore } : s))}
                onClose={() => setShowStoryLore(false)} />
            )}
            <button
              onClick={() => setShowPinPanel(p => !p)}
              style={{ ...btnSmall, padding: "4px 8px", background: showPinPanel ? 'var(--ds-gold-a2e, rgba(196,168,130,0.18))' : undefined, color: showPinPanel ? 'var(--ds-gold, #c4a882)' : t.textMuted, border: showPinPanel ? '1px solid var(--ds-gold-a66, rgba(196,168,130,0.4))' : undefined }}
              aria-label="Pinned messages"
              title="Pinned messages"
            >📌</button>
            <button
              onClick={() => setShowRpLinkPanel(true)}
              disabled={!activeStoryId}
              style={{ ...btnSmall, padding: "4px 8px", color: '#7aa2f7', border: '1px solid rgba(122,162,247,0.4)', opacity: activeStoryId ? 1 : 0.4 }}
              aria-label="Link storylines"
              title="Link this RP to other DMs/RPs so they share memory"
            >🔗</button>
            {/* The RP toolbar's gear is gone too — Haley said "gears", and
                this was a third door into the same panel. Profile → App & AI. */}
          </div>
        </div>
        {/* Row 2: Scene type tabs — hidden when only one scene type */}
        {Object.keys(SCENE_TYPES).length > 1 && (
        <div style={{ display: "flex", gap: "6px", justifyContent: "center", flexWrap: "wrap" }}>
          {Object.entries(SCENE_TYPES).map(([k, s]) => (
            <button key={k} onClick={() => setSceneType(k)} style={{ ...genTab, background: sceneType === k ? t.accentDanger : t.bgBase, fontSize: "11px", padding: "4px 10px" }}>
              {s.icon} {s.label}
            </button>
          ))}
        </div>
        )}
        {/* Row 3: Active players presence — always visible when others are here */}
        {storyPresence.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 0 2px", borderTop: "1px solid #1a121266", marginTop: "6px" }}>
            <span style={{ fontSize: "10px", color: t.textFaint, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px", flexShrink: 0 }}>Here now:</span>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", alignItems: "center" }}>
              {storyPresence.map((p) => (
                <div key={p.userId} title={`${p.userName} as ${p.charName}`} style={{ position: "relative", display: "flex", alignItems: "center", gap: "4px", background: "rgba(74,158,110,0.08)", border: "1px solid rgba(74,158,110,0.2)", borderRadius: "20px", padding: "2px 8px 2px 2px" }}>
                  {p.avatarUrl ? (
                    <img src={p.avatarUrl} alt="" style={{ width: "22px", height: "22px", borderRadius: "50%", objectFit: "cover", border: "1px solid #4a9e6e55" }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                  ) : null}
                  {!p.avatarUrl && (
                    <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "linear-gradient(135deg, #4a9e6e33, var(--ds-gold-a33, #c4a88233))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", border: "1px solid #4a9e6e55" }}>
                      {p.charAvatar || '👤'}
                    </div>
                  )}
                  {/* Green online dot */}
                  <div style={{ position: "absolute", bottom: "0px", left: "16px", width: "7px", height: "7px", borderRadius: "50%", background: t.accentSuccess, border: "1px solid #120e0e" }} />
                  <span style={{ fontSize: "11px", color: t.textPrimary, fontWeight: 600, lineHeight: 1 }}>{p.userName}</span>
                  <span style={{ fontSize: "9px", color: t.textFaint }}>as {p.charName}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
        {/* Mobile overlay backdrop */}
        {sidebarOpen && isMobile && (
          <div onClick={() => setSidebarOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 49 }} />
        )}
        {sidebarOpen && (
          <div style={{ width: "220px", background: t.bgBase, borderRight: "1px solid #202225", overflowY: "auto", flexShrink: isMobile ? 0 : 0, ...(isMobile ? { position: "absolute", top: 0, left: 0, bottom: 0, zIndex: 50 } : {}) }}>
            <div style={{ padding: "10px", borderBottom: "1px solid #1a1212" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", marginBottom: "6px" }}>Playing As</div>
              <div style={{ position: "relative", marginBottom: "6px" }}>
                <input
                  value={charSearch}
                  onChange={(e) => setCharSearch(e.target.value)}
                  style={{ ...inputStyle, fontSize: "12px", padding: "8px 30px 8px 10px", width: "100%", boxSizing: "border-box", border: "1px solid var(--ds-gold-a40, rgba(196,168,130,0.25))", background: t.bgBase }}
                  placeholder="🔍 Search all characters..."
                />
                {charSearch && <button onClick={() => setCharSearch("")} style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textMuted, fontSize: "14px", cursor: "pointer", padding: "2px 4px", lineHeight: 1 }}>✕</button>}
              </div>
              {charSearch.trim() && <div style={{ fontSize: "10px", color: t.accentGold, marginBottom: "4px" }}>{allChars.filter(matchesSearch).length} found</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: charSearch ? "300px" : "200px", overflowY: "auto" }}>
                {allChars.filter(matchesSearch).slice(0, charSearch ? 50 : 8).map((c) => (
                  <button key={c.id} onClick={() => { pickPlayingAs(c.id); setCharSearch(""); }} style={{
                    background: playingAs === c.id ? `${c.color}44` : t.bgHover,
                    border: `1px solid ${c.color}33`, borderRadius: "6px", padding: "6px 8px",
                    color: "var(--ds-ink-hi, #fff)", cursor: "pointer", textAlign: "left", fontSize: "12px",
                  }}>
                    <span style={{ fontSize: "14px", marginRight: "4px" }}>{c.avatar}</span>{c.name}
                  </button>
                ))}
                {charSearch && allChars.filter(matchesSearch).length === 0 && (
                  <div style={{ fontSize: "10px", color: "#555", padding: "4px 8px", fontStyle: "italic" }}>No matches</div>
                )}
              </div>
            </div>

            <div style={{ padding: "10px", borderBottom: "1px solid #1a1212" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", marginBottom: "2px" }}>Scene Characters</div>
              <div style={{ fontSize: "9px", color: "#555", marginBottom: "6px" }}>These characters are guaranteed to appear in the story. Romantic relationships start fresh each route — characters meet for the first time.</div>
              {/* The editing surface the old blurb promised. Scoped to this
                  story, so a relationship set here does not leak into other
                  routes; the panel's own toggle switches to global defaults. */}
              <button
                onClick={() => setShowRpRelationships(true)}
                disabled={sceneChars.length === 0}
                title={sceneChars.length === 0 ? "Add scene characters first" : "Set how the cast relates to your character in this story"}
                style={{
                  width: "100%", marginBottom: "6px", padding: "7px 8px", borderRadius: "6px",
                  border: "1px solid var(--ds-gold-a40, rgba(196,168,130,0.25))",
                  background: sceneChars.length === 0 ? t.btnDisabled : t.bgHover,
                  color: sceneChars.length === 0 ? t.textFaint : t.textPrimary,
                  cursor: sceneChars.length === 0 ? "default" : "pointer",
                  fontSize: "11px", fontWeight: 600, textAlign: "left",
                }}
              >
                💞 Relationships{sceneChars.length ? ` (${sceneChars.filter(id => id !== playingAs).length})` : ""}
              </button>
              <div style={{ position: "relative", marginBottom: "6px" }}>
                <input value={sceneSearch} onChange={(e) => setSceneSearch(e.target.value)} style={{ ...inputStyle, fontSize: "12px", padding: "8px 30px 8px 10px", width: "100%", boxSizing: "border-box", border: "1px solid var(--ds-gold-a40, rgba(196,168,130,0.25))", background: t.bgBase }} placeholder="🔍 Search to add..." />
                {sceneSearch && <button onClick={() => setSceneSearch("")} style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textMuted, fontSize: "14px", cursor: "pointer", padding: "2px 4px", lineHeight: 1 }}>✕</button>}
              </div>
              {sceneChars.map((id) => {
                const c = allChars.find((ch) => ch?.id === id);
                return (
                  <div key={id} style={{ padding: "5px 8px", borderRadius: "4px", marginBottom: "3px", background: t.bgHover }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                        <span>{c?.avatar || "✍️"}</span>
                        <span onClick={() => c && setViewingChar(c)} style={{ fontWeight: 500, cursor: "pointer", textDecoration: "underline", textDecorationColor: "var(--ds-gold-a4c, rgba(196,168,130,0.3))", textUnderlineOffset: "2px" }} title={`View ${c?.name}'s profile`}>{c?.name}</span>
                      </div>
                      <button onClick={() => { setSceneChars((p) => p.filter((x) => x !== id)); setBlockedChars((b) => b.includes(id) ? b : [...b, id]); }} style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: "14px" }}>×</button>
                    </div>
                    {/* charTags picker removed (item 16) — per-story
                        relationships are set in the Relationships panel
                        (item 18), which writes character_dynamics rows. */}
                  </div>
                );
              })}
              {sceneChars.length === 0 && <div style={{ fontSize: "11px", color: "#444", padding: "4px 8px" }}>Add characters below</div>}
            </div>

            {/* ── NEW CHARACTER DETECTED PROMPT ── */}
            {showNewCharPrompt && (
              <div style={{ padding: "10px", borderBottom: "1px solid #1a1212", background: "rgba(209,179,255,0.08)", animation: "fadeSlideIn 0.3s ease-out" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: t.accentGold, textTransform: "uppercase", marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
                  ✨ New Character Detected
                </div>
                <div style={{ fontSize: "13px", color: "var(--ds-ink-hi, #fff)", fontWeight: 600, marginBottom: "4px" }}>{showNewCharPrompt.name}</div>
                <div style={{ fontSize: "11px", color: t.textMuted, marginBottom: "4px" }}>{showNewCharPrompt.species}</div>
                <div style={{ fontSize: "11px", color: t.textMuted, marginBottom: "8px", lineHeight: "1.4" }}>{showNewCharPrompt.description}</div>
                <div style={{ fontSize: "10px", color: t.textFaint, marginBottom: "6px" }}>Set relationship:</div>
                <div style={{ display: "flex", gap: "3px", flexWrap: "wrap", marginBottom: "8px" }}>
                  {[{ key: "love", icon: "💕", label: "Love" }, { key: "friend", icon: "💚", label: "Friend" }, { key: "enemy", icon: "⚔️", label: "Enemy" }, { key: null, icon: "👤", label: "None" }].map(t => (
                    <button key={t.key || 'none'} onClick={() => saveDetectedChar(showNewCharPrompt, t.key)} style={{
                      background: "var(--ds-accent-a33, rgba(198,40,40,0.2))", border: "1px solid var(--ds-accent-a66, rgba(198,40,40,0.4))",
                      borderRadius: "4px", padding: "3px 8px", cursor: "pointer", fontSize: "10px", color: "var(--ds-ink-hi, #fff)",
                      display: "flex", alignItems: "center", gap: "3px",
                    }}>{t.icon} {t.label}</button>
                  ))}
                </div>
                <button onClick={() => dismissDetectedChar(showNewCharPrompt)} style={{
                  background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: "10px", padding: "2px 0",
                }}>Skip — don't save</button>
                {detectedNewChars.length > 1 && (
                  <div style={{ fontSize: "9px", color: "#555", marginTop: "4px" }}>+{detectedNewChars.length - 1} more new character{detectedNewChars.length > 2 ? 's' : ''}</div>
                )}
              </div>
            )}
            {/* ── DETECTED CHARS BADGE (when prompt is dismissed but chars remain) ── */}
            {!showNewCharPrompt && detectedNewChars.length > 0 && (
              <div style={{ padding: "6px 10px", borderBottom: "1px solid #1a1212", background: "rgba(209,179,255,0.05)" }}>
                <button onClick={() => setShowNewCharPrompt(detectedNewChars[0])} style={{
                  background: "rgba(209,179,255,0.15)", border: "1px solid rgba(209,179,255,0.3)", borderRadius: "6px",
                  padding: "6px 10px", cursor: "pointer", color: t.accentGold, fontSize: "11px", width: "100%", textAlign: "left",
                }}>
                  ✨ {detectedNewChars.length} new character{detectedNewChars.length > 1 ? 's' : ''} detected — tap to save
                </button>
              </div>
            )}

            <div style={{ padding: "10px", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", marginBottom: "6px" }}>Add to Scene</div>
              <div style={{ position: "relative", marginBottom: "6px" }}>
                <input value={sceneSearch} onChange={(e) => setSceneSearch(e.target.value)} style={{ ...inputStyle, fontSize: "12px", padding: "8px 30px 8px 10px", width: "100%", boxSizing: "border-box", border: "1px solid var(--ds-gold-a40, rgba(196,168,130,0.25))", background: t.bgBase }} placeholder="🔍 Search all characters..." />
                {sceneSearch && <button onClick={() => setSceneSearch("")} style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: t.textMuted, fontSize: "14px", cursor: "pointer", padding: "2px 4px", lineHeight: 1 }}>✕</button>}
              </div>
              {sceneSearch.trim() && <div style={{ fontSize: "10px", color: t.accentGold, marginBottom: "4px" }}>{allChars.filter((c) => !sceneChars.includes(c.id) && matchesCharacterSearch(c, sceneSearch)).length} found</div>}
              <div style={{ overflowY: "auto", flex: 1 }}>
                {allChars
                  .filter((c) => !sceneChars.includes(c.id) && matchesCharacterSearch(c, sceneSearch))
                  .slice(0, sceneSearch ? 50 : 15)
                  .map((c) => (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", color: t.textMuted }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = t.bgHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span>{c.avatar}</span>
                      <span onClick={() => setViewingChar(c)} style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "var(--ds-gold-a4c, rgba(196,168,130,0.3))", textUnderlineOffset: "2px" }} title={`View ${c.name}'s profile`}>{c.name}</span>
                      <span style={{ marginLeft: "auto", fontSize: "9px", color: c.controlledBy === "AI" ? "var(--ds-gold, #c4a882)" : "#9a8585" }}>
                        {c.controlledBy === "AI" ? "AI" : c.controlledBy !== currentPlayer ? `(${c.controlledBy})` : ""}
                      </span>
                      <span onClick={() => { setSceneChars((p) => p.includes(c.id) ? p : [...p, c.id]); setBlockedChars((b) => b.filter(x => x !== c.id)); setSceneSearch(""); }} style={{ color: t.accentSuccess, fontSize: "14px", cursor: "pointer", padding: "2px 6px", borderRadius: "4px" }} title="Add to scene">+</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* ─── SCENE RECAP + SUGGESTIONS (for guests / anyone) ──── */}
            <div style={{ padding: "10px", borderTop: "1px solid #1a1212" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: t.accentGold, textTransform: "uppercase", marginBottom: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
                🎬 Catch Me Up
              </div>

              {!sceneRecap && !sceneRecapLoading && (
                <button
                  onClick={generateSceneRecap}
                  disabled={!effectiveApiKey || messages.length === 0}
                  style={{ width: "100%", padding: "8px", background: "linear-gradient(135deg, rgba(74,158,110,0.15), var(--ds-gold-a26, rgba(196,168,130,0.15)))", border: "1px solid rgba(74,158,110,0.3)", borderRadius: "8px", color: t.accentSuccess, fontSize: "11px", cursor: "pointer", fontFamily: "'Cinzel', serif" }}
                >
                  ✨ Generate Scene Recap
                </button>
              )}

              {sceneRecapLoading && (
                <div style={{ textAlign: "center", padding: "12px", color: t.accentSuccess, fontSize: "11px", fontStyle: "italic" }}>
                  <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /> Reading the scene...
                </div>
              )}

              {sceneRecap && !sceneRecap.error && (
                <div style={{ fontSize: "11px", color: t.textPrimary, lineHeight: "1.5", marginTop: "4px" }}>
                  <div style={{ color: t.textPrimary, padding: "6px 8px", background: "rgba(74,158,110,0.08)", borderRadius: "6px", borderLeft: "2px solid #4a9e6e", marginBottom: "8px" }}>
                    {sceneRecap.recap}
                  </div>

                  {sceneRecap.suggestions && sceneRecap.suggestions.length > 0 && (
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", marginBottom: "4px" }}>💬 Quick Responses</div>
                      {sceneRecap.suggestions.map((sug, i) => (
                        <button
                          key={i}
                          onClick={() => rpInputBarRef.current?.setText(sug)}
                          style={{
                            display: "block", width: "100%", textAlign: "left", padding: "6px 8px", marginBottom: "4px",
                            background: "var(--ds-gold-a0f, rgba(196,168,130,0.06))", border: "1px solid var(--ds-gold-a26, rgba(196,168,130,0.15))",
                            borderRadius: "6px", color: t.textPrimary, fontSize: "10px", cursor: "pointer",
                            lineHeight: "1.4", transition: "all 0.15s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "var(--ds-gold-a26, rgba(196,168,130,0.15))"; e.currentTarget.style.borderColor = "var(--ds-gold-a66, rgba(196,168,130,0.4))"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "var(--ds-gold-a0f, rgba(196,168,130,0.06))"; e.currentTarget.style.borderColor = "var(--ds-gold-a26, rgba(196,168,130,0.15))"; }}
                          title="Tap to pre-fill your message input"
                        >
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={generateSceneRecap}
                    disabled={sceneRecapLoading}
                    style={{ width: "100%", padding: "5px", marginTop: "4px", background: "rgba(74,158,110,0.08)", border: "1px solid rgba(74,158,110,0.2)", borderRadius: "6px", color: t.accentSuccess, fontSize: "10px", cursor: "pointer" }}
                  >
                    🔄 Refresh
                  </button>
                </div>
              )}

              {sceneRecap?.error && (
                <div style={{ padding: "8px", marginTop: "6px", background: "var(--ds-accent-a1a, rgba(198,40,40,0.1))", borderRadius: "6px", color: "#e57373", fontSize: "11px" }}>
                  Failed: {sceneRecap.error}
                </div>
              )}
            </div>

            {/* ─── CHARACTER QUICK-REF CARDS ───────────────────────────── */}
            {sceneChars.length > 0 && (
              <div style={{ padding: "10px", borderTop: "1px solid #1a1212" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: t.accentGold, textTransform: "uppercase", marginBottom: "6px" }}>
                  📋 Character Quick-Ref
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "300px", overflowY: "auto" }}>
                  {sceneChars.map(id => {
                    const c = allChars.find(ch => ch?.id === id);
                    if (!c) return null;
                    return (
                      <div key={id} style={{
                        padding: "8px", borderRadius: "8px",
                        background: `linear-gradient(135deg, ${c.color}11, rgba(255,255,255,0.03))`,
                        border: `1px solid ${c.color}33`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                          <span style={{ fontSize: "16px" }}>{c.avatar}</span>
                          <div>
                            <div style={{ fontSize: "12px", fontWeight: 700, color: c.color || "#e8d5d5" }}>{c.name}</div>
                            <div style={{ fontSize: "9px", color: t.textMuted }}>{c.species}{c.generation ? ` · Gen ${c.generation}` : ''}</div>
                          </div>
                        </div>
                        {c.personality && (
                          <div style={{ fontSize: "10px", color: t.textPrimary, lineHeight: "1.4", marginBottom: "3px" }}>
                            {(c.personality || '').substring(0, 120)}{(c.personality || '').length > 120 ? '...' : ''}
                          </div>
                        )}
                        {c.speechPattern && (
                          <div style={{ fontSize: "9px", color: t.textFaint, fontStyle: "italic" }}>
                            🗣️ {(c.speechPattern || '').substring(0, 80)}{(c.speechPattern || '').length > 80 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ─── STORY INTEL PANEL ─────────────────────────────────── */}
            <div style={{ padding: "10px", borderTop: "1px solid #1a1212" }}>
              <div
                onClick={() => { if (storyIntel) setStoryIntelOpen(p => !p); else generateStoryIntel(); }}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "4px 0" }}
              >
                <div style={{ fontSize: "11px", fontWeight: 700, color: t.accentGold, textTransform: "uppercase" }}>
                  📜 Story Intel
                </div>
                <span style={{ fontSize: "10px", color: t.textFaint }}>{storyIntelOpen ? "▼" : "▶"}</span>
              </div>

              {!storyIntel && !storyIntelLoading && (
                <button
                  onClick={generateStoryIntel}
                  disabled={!effectiveApiKey || messages.length === 0}
                  style={{ width: "100%", padding: "8px", marginTop: "6px", background: "linear-gradient(135deg, var(--ds-gold-a26, rgba(196,168,130,0.15)), rgba(139,0,0,0.15))", border: "1px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))", borderRadius: "8px", color: t.accentGold, fontSize: "11px", cursor: "pointer", fontFamily: "'Cinzel', serif" }}
                >
                  ✨ Analyze Story
                </button>
              )}

              {storyIntelLoading && (
                <div style={{ textAlign: "center", padding: "12px", color: t.accentGold, fontSize: "11px", fontStyle: "italic" }}>
                  <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /> Analyzing story...
                </div>
              )}

              {storyIntel && storyIntelOpen && !storyIntel.error && (
                <div style={{ fontSize: "11px", color: t.textPrimary, lineHeight: "1.5", marginTop: "6px" }}>
                  {/* Current Scene */}
                  <div style={{ marginBottom: "10px" }}>
                    <div style={{ fontWeight: 700, color: t.accentGold, marginBottom: "3px", fontSize: "10px", textTransform: "uppercase" }}>🎬 Right Now</div>
                    <div style={{ color: t.textPrimary, padding: "6px 8px", background: "var(--ds-gold-a14, rgba(196,168,130,0.08))", borderRadius: "6px", borderLeft: "2px solid var(--ds-gold, #c4a882)" }}>{storyIntel.currentScene}</div>
                  </div>

                  {/* Mood & Themes */}
                  {storyIntel.mood && (
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, color: t.accentGold, marginBottom: "3px", fontSize: "10px", textTransform: "uppercase" }}>🌙 Mood</div>
                      <div style={{ color: t.textMuted }}>{storyIntel.mood}</div>
                      {storyIntel.themes && <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "4px" }}>{storyIntel.themes.map((t, i) => <span key={i} style={{ background: "var(--ds-gold-a1f, rgba(196,168,130,0.12))", color: t.accentGold, padding: "2px 6px", borderRadius: "10px", fontSize: "9px" }}>{t}</span>)}</div>}
                    </div>
                  )}

                  {/* Summary */}
                  <div style={{ marginBottom: "10px" }}>
                    <div style={{ fontWeight: 700, color: t.accentGold, marginBottom: "3px", fontSize: "10px", textTransform: "uppercase" }}>📖 Story So Far</div>
                    <div style={{ color: t.textPrimary }}>{storyIntel.summary}</div>
                  </div>

                  {/* Timeline */}
                  {storyIntel.timeline && storyIntel.timeline.length > 0 && (
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, color: t.accentGold, marginBottom: "3px", fontSize: "10px", textTransform: "uppercase" }}>⏳ Timeline</div>
                      {storyIntel.timeline.map((event, i) => (
                        <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "3px", color: t.textMuted }}>
                          <span style={{ color: t.accentGold, fontSize: "9px", flexShrink: 0, marginTop: "2px" }}>●</span>
                          <span>{event}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Characters */}
                  {storyIntel.characters && storyIntel.characters.length > 0 && (
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, color: t.accentGold, marginBottom: "3px", fontSize: "10px", textTransform: "uppercase" }}>👥 Characters</div>
                      {storyIntel.characters.map((c, i) => (
                        <div key={i} style={{ marginBottom: "3px" }}>
                          <span style={{ fontWeight: 600, color: t.textPrimary }}>{c.name}</span>
                          <span style={{ color: t.textFaint }}> — </span>
                          <span style={{ color: t.textMuted }}>{c.role}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Open Threads */}
                  {storyIntel.openThreads && storyIntel.openThreads.length > 0 && (
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, color: t.accentGold, marginBottom: "3px", fontSize: "10px", textTransform: "uppercase" }}>🧵 Open Threads</div>
                      {storyIntel.openThreads.map((t, i) => (
                        <div key={i} style={{ color: t.textMuted, marginBottom: "2px" }}>• {t}</div>
                      ))}
                    </div>
                  )}

                  {/* Plot Ideas */}
                  {storyIntel.plotIdeas && storyIntel.plotIdeas.length > 0 && (
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, color: t.accentGold, marginBottom: "3px", fontSize: "10px", textTransform: "uppercase" }}>💡 Plot Ideas</div>
                      {storyIntel.plotIdeas.map((idea, i) => (
                        <div key={i} style={{ padding: "5px 8px", marginBottom: "4px", background: "rgba(74,158,110,0.08)", borderRadius: "6px", borderLeft: "2px solid #4a9e6e", color: t.textPrimary }}>{idea}</div>
                      ))}
                    </div>
                  )}

                  {/* Refresh button */}
                  <button
                    onClick={generateStoryIntel}
                    disabled={storyIntelLoading}
                    style={{ width: "100%", padding: "6px", marginTop: "4px", background: "var(--ds-gold-a14, rgba(196,168,130,0.08))", border: "1px solid var(--ds-gold-a33, rgba(196,168,130,0.2))", borderRadius: "6px", color: t.accentGold, fontSize: "10px", cursor: "pointer" }}
                  >
                    🔄 Refresh Intel
                  </button>
                </div>
              )}

              {storyIntel?.error && storyIntelOpen && (
                <div style={{ padding: "8px", marginTop: "6px", background: "var(--ds-accent-a1a, rgba(198,40,40,0.1))", borderRadius: "6px", color: "#e57373", fontSize: "11px" }}>
                  Failed: {storyIntel.error}
                </div>
              )}
            </div>

            {/* ─── ACTIVE PLAYERS (sidebar summary) ────────────────────── */}
            {storyPresence.length > 0 && (
              <div style={{ padding: "8px 10px", borderTop: "1px solid #1a1212" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", marginBottom: "4px" }}>
                  🟢 {storyPresence.length} Other Player{storyPresence.length > 1 ? 's' : ''} Here
                </div>
                {storyPresence.map(p => (
                  <div key={p.userId} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                    {p.avatarUrl ? (
                      <img src={p.avatarUrl} alt="" style={{ width: "18px", height: "18px", borderRadius: "50%", objectFit: "cover" }} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span style={{ fontSize: "12px" }}>{p.charAvatar || '👤'}</span>
                    )}
                    <span style={{ fontSize: "11px", color: t.textPrimary }}>{p.userName}</span>
                    <span style={{ fontSize: "9px", color: t.textFaint }}>as {p.charName}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Draft assistant — bottom of sidebar, above the crisis panel.
                Scoped per-story (scopeId = activeStoryId) so each story has
                its own draft state and panel.
                Turn-order awareness: scenePartners tells the AI which other
                characters are in the scene and who plays them, so the draft
                only writes for THIS player's character on THEIR turn. */}
            {activeStoryId && (playingAs || narratorMode) && (() => {
              const myChar = allChars.find(c => c?.id === playingAs);
              const others = (sceneChars || [])
                .filter(id => id !== playingAs)
                .map(id => {
                  const c = allChars.find(ch => ch?.id === id);
                  if (!c) return null;
                  // Other player participation: if there's a story_participant
                  // for this character that isn't us, mark as 'player'.
                  const otherPart = (storyPresence || []).find(p => p.charId === id);
                  return {
                    name: c.name,
                    role: otherPart ? 'player' : 'ai',
                    playerName: otherPart?.userName,
                  };
                })
                .filter(Boolean);
              const recent = (messages || []).slice(-30).map(m => {
                const who = m.isPlayer ? (m.name || currentPlayer || 'Player') : (m.name || 'Character');
                return `${who}: ${(m.text || '').toString().slice(0, 600)}`;
              });
              return (
                <DraftAssistant
                  scope="rp"
                  scopeId={activeStoryId}
                  getCurrentText={() => rpInputBarRef.current?.getText?.() || ''}
                  onApplyDraft={(next) => {
                    rpInputBarRef.current?.setText?.(next);
                    saveDraft('rp', activeStoryId, next);
                    requestAnimationFrame(() => rpInputBarRef.current?.focus?.());
                  }}
                  aiProvider={aiProvider}
                  recentMessages={recent}
                  playerName={currentPlayer || userProfile?.display_name || 'Player'}
                  playerCharacterName={myChar?.name || null}
                  playerCharacterId={myChar?.id || null}
                  scenePartners={others}
                  contextDescription={currentSessionName ? `RP story: ${currentSessionName}` : 'this RP scene'}
                  user={user}
                  castIds={(sceneChars || []).filter(id => id !== playingAs)}
                  theme={t}
                  compact
                />
              );
            })()}
            <CrisisResourcePanel compact />
          </div>
        )}

        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: SCENE_TYPES[sceneType]?.bg || t.bgTertiary, minWidth: 0, overflow: "hidden" }}>
          <div ref={rpChatContainerRef} onScroll={handleRpChatScroll} style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "16px", background: readerTheme?.bg || "#0d0a0a", transition: "background 0.3s" }}>
            {loadingOlderRp && (
              <div style={{ textAlign: "center", padding: "8px", color: t.textMuted, fontSize: "12px" }}>Loading older messages…</div>
            )}
            {hasMoreRpMessages && !loadingOlderRp && messages.length > 0 && (
              <div style={{ textAlign: "center", padding: "4px", color: t.textFaint, fontSize: "11px", fontStyle: "italic" }}>↑ Scroll up to load older messages</div>
            )}
            {messages.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#555" }}>
                <div style={{ fontSize: "48px", marginBottom: "10px" }}>🐺✨🧜‍♀️</div>
                <h3 style={{ color: "#777", margin: "0 0 6px" }}>Start your RP</h3>
                <p style={{ fontSize: "13px" }}>
                  Playing as <span style={{ color: currentChar?.color, fontWeight: 700 }}>{currentChar?.name || "..."}</span>
                </p>
                <p style={{ fontSize: "12px", color: "#555" }}>Add any character to the scene. AI responds for characters you don't own.</p>
                <p style={{ fontSize: "11px", color: "#888", marginTop: "6px" }}>Tip: Wrap messages in (( double parentheses )) for OOC directions to the AI</p>
                {!effectiveApiKey && <p style={{ fontSize: "11px", color: "#f39c12", marginTop: "8px" }}>Add your API key under your profile → App &amp; AI for AI character responses</p>}
              </div>
            )}
            {messages.map((msg, idx) => {
              const char = allChars.find((c) => c?.id === msg.charId);
              const isMe = msg.isPlayer;
              const oocMsg = msg.isOOC || isOOCMessage(msg.text);

              // OOC message styling
              const oocStyle = oocMsg ? { background: "rgba(250, 166, 26, 0.08)", borderLeft: "3px solid var(--ds-gold, #c4a882)", paddingLeft: "10px", fontStyle: "italic" } : {};

              // LEGACY: "text" scene type — kept for backward compatibility with old saved stories
              if (sceneType === "text") {
                return (
                  <div key={msg.id} id={`rp-msg-${idx}`} className={isMe ? "msg-send" : "msg-receive"} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: "6px" }}>
                    <div className={rpActionsIdx === idx ? 'rp-actions-open' : ''} style={{ maxWidth: "70%", padding: "10px 14px", borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: oocMsg ? "rgba(250,166,26,0.15)" : (isMe ? char?.color || t.accentGold : t.bgHover), color: (!oocMsg && isMe) ? readableOn(char?.color || t.accentGold) : "var(--ds-ink-hi, #fff)", fontSize: "14px", position: "relative", touchAction: "manipulation" }}
                      {...rpBubbleTouchHandlers(idx, msg.id)}
                    >
                      {!isMe && <div style={{ fontSize: "11px", color: char?.color, fontWeight: 700, marginBottom: "3px" }}>{msg.name}</div>}
                      {oocMsg && <span style={{ fontSize: "9px", background: "var(--ds-gold-a33, #c4a88233)", color: t.accentGold, padding: "1px 5px", borderRadius: "6px", marginBottom: "4px", display: "inline-block" }}>OOC</span>}
                      {editingMsgIdx === idx ? (
                        <div>
                          <textarea value={editingMsgText} onChange={e => setEditingMsgText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveRPEdit(idx); } if (e.key === 'Escape') cancelEditingRP(); }}
                            style={{ background: "rgba(0,0,0,0.3)", color: "var(--ds-ink-hi, #fff)", border: "1px solid var(--ds-line-strong)", borderRadius: "6px", padding: "6px 8px", fontSize: "14px", outline: "none", width: "100%", boxSizing: "border-box", minHeight: "50px", resize: "vertical", fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
                            autoFocus />
                          <div style={{ display: "flex", gap: "6px", marginTop: "4px", fontSize: "11px" }}>
                            <button onClick={() => saveRPEdit(idx)} style={{ background: t.accentDanger, color: "#fff", border: "none", borderRadius: "4px", padding: "3px 8px", fontSize: "11px", cursor: "pointer" }}>Save</button>
                            <button onClick={cancelEditingRP} style={{ background: "var(--ds-hover)", color: "var(--ds-ink-hi, #fff)", border: "none", borderRadius: "4px", padding: "3px 8px", fontSize: "11px", cursor: "pointer" }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word", fontStyle: oocMsg ? "italic" : "normal", fontFamily: readerFont, fontSize: `${readerPrefs.fontSize}px`, lineHeight: readerPrefs.lineHeight, maxWidth: "100%", userSelect: 'text', WebkitUserSelect: 'text' }}>
                            {formatMessageText(msg.text)}
                            {msg.edited && <span style={{ fontSize: "9px", opacity: 0.5, marginLeft: "4px" }}>(edited)</span>}
                          </div>
                          <div style={{ position: "absolute", right: "4px", top: "4px", display: "flex", gap: "2px" }}>
                            <button className="rp-action-btn" onClick={() => toggleRPHighlight(idx)}
                              style={{ background: msg.highlighted ? "var(--ds-gold-a4c, rgba(196,168,130,0.3))" : "rgba(0,0,0,0.4)", border: "none", borderRadius: "4px 0 0 4px", padding: "2px 7px", fontSize: "11px", fontWeight: 800, color: msg.highlighted ? "var(--ds-gold, #c4a882)" : "var(--ds-ink)", cursor: "pointer", opacity: msg.highlighted ? 0.85 : 0, transition: "opacity 0.15s", boxShadow: msg.highlighted ? "0 0 7px var(--ds-gold-a66, rgba(196,168,130,0.4))" : "none" }}
                              title={msg.highlighted ? "Remove highlight" : "Mark as important"}>!</button>
                            <button className="rp-action-btn" onClick={async (e) => { const b = e.currentTarget; if (await copyMessageText(msg.text)) { b.textContent = '✓'; setTimeout(() => { b.textContent = '📋'; }, 900); } }} style={{ background: "var(--ds-scrim, rgba(0,0,0,0.4))", border: "none", borderRadius: "0", padding: "2px 6px", fontSize: "10px", color: "var(--ds-on-scrim, #fff)", cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Copy text">📋</button>
                            <button className="rp-action-btn" onClick={() => startEditingRP(idx)} style={{ background: "var(--ds-scrim, rgba(0,0,0,0.4))", border: "none", borderRadius: "0", padding: "2px 6px", fontSize: "10px", color: "var(--ds-on-scrim, #fff)", cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Edit">✏️</button>
                            <div style={{ position: "relative", display: "inline-block" }}>
                              <button className="rp-action-btn" onClick={() => setPinPopoverMsgIdx(pinPopoverMsgIdx === idx ? null : idx)} style={{ background: "var(--ds-scrim, rgba(0,0,0,0.4))", border: "none", borderRadius: "0", padding: "2px 6px", fontSize: "10px", color: "var(--ds-gold, #c4a882)", cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Pin">📌</button>
                              {pinPopoverMsgIdx === idx && (
                                <PinPopover message={{ id: msg.id, text: msg.text, name: msg.name }} contextType="story" contextId={activeStoryId} userId={user?.id} onClose={() => setPinPopoverMsgIdx(null)} />
                              )}
                            </div>
                            {isMe && <button className="rp-action-btn" onClick={() => resendRPMessage(idx)} disabled={resendingMsgId === msg.id} data-persist={msg.sendFailed ? "1" : "0"} style={{ background: "var(--ds-scrim, rgba(0,0,0,0.4))", border: "none", borderRadius: "0", padding: "2px 6px", fontSize: "10px", color: msg.sendFailed ? "#ed4245" : "#fff", cursor: "pointer", opacity: msg.sendFailed ? 0.95 : 0, transition: "opacity 0.15s" }} title={msg.sendFailed ? "Didn't reach the server — resend" : "Resend to server"}>{resendingMsgId === msg.id ? "…" : "↻"}</button>}
                            <button className="rp-action-btn" onClick={() => deleteRPMessage(idx)} style={{ background: "var(--ds-scrim, rgba(0,0,0,0.4))", border: "none", borderRadius: "0 4px 4px 0", padding: "2px 6px", fontSize: "10px", color: "#ed4245", cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Delete">🗑️</button>
                          </div>
                        </div>
                      )}
                      <div style={{ fontSize: "10px", color: "var(--ds-ink-low)", marginTop: "3px", textAlign: "right" }}>{formatTimestamp(msg.createdAt || msg.timestamp)}</div>
                      {!isMe && !oocMsg && editingMsgIdx !== idx && <RegenButton onRegen={(fb) => regenerateMessage(idx, fb)} onUndo={lastRegen?.msgId === msg.id ? undoRegenerate : null} />}
                      {isMe && msg.sendFailed && <FailedSendNotice onResend={() => resendRPMessage(idx)} resending={resendingMsgId === msg.id} />}
                    </div>
                  </div>
                );
              }
              // LEGACY: "call" scene type — kept for backward compatibility with old saved stories
              if (sceneType === "call") {
                return (
                  <div key={msg.id} id={`rp-msg-${idx}`} className={`msg-animate rp-msg rp-msg-scene${rpActionsIdx === idx ? ' rp-actions-open' : ''}`} style={{ marginBottom: "10px", ...oocStyle, position: "relative", touchAction: "manipulation" }}
                    {...rpBubbleTouchHandlers(idx, msg.id)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                      <span>{char?.avatar || "🎙️"}</span>
                      <span style={{ fontWeight: 700, color: char?.color || t.accentGold, fontSize: "14px" }}>{msg.name}</span>
                      {oocMsg && <span style={{ fontSize: "9px", background: "var(--ds-gold-a33, #c4a88233)", color: t.accentGold, padding: "1px 5px", borderRadius: "6px" }}>OOC</span>}
                      <span style={{ fontSize: "11px", color: "#555" }}>{formatTimestamp(msg.createdAt || msg.timestamp)}</span>
                    </div>
                    {editingMsgIdx === idx ? (
                      <div style={{ paddingLeft: "28px" }}>
                        <textarea value={editingMsgText} onChange={e => setEditingMsgText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveRPEdit(idx); } if (e.key === 'Escape') cancelEditingRP(); }}
                          style={{ background: "var(--ds-surface-1, #0d0a0a)", color: t.textPrimary, border: "1px solid #40444b", borderRadius: "6px", padding: "8px 10px", fontSize: "14px", outline: "none", width: "100%", boxSizing: "border-box", minHeight: "50px", resize: "vertical", fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
                          autoFocus />
                        <div style={{ display: "flex", gap: "6px", marginTop: "4px", fontSize: "11px", color: t.textMuted }}>
                          <button onClick={() => saveRPEdit(idx)} style={{ background: t.accentDanger, color: "#fff", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer" }}>Save</button>
                          <button onClick={cancelEditingRP} style={{ background: t.bgBase, color: "var(--ds-ink)", border: "none", borderRadius: "6px", padding: "4px 8px", fontSize: "11px", cursor: "pointer" }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ paddingLeft: "28px", color: readerTheme?.text || t.textPrimary, fontSize: `${readerPrefs.fontSize}px`, lineHeight: readerPrefs.lineHeight, fontFamily: readerFont, fontStyle: oocMsg ? "italic" : "normal", whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", userSelect: 'text', WebkitUserSelect: 'text' }}>
                        {formatMessageText(msg.text)}
                        {msg.edited && <span style={{ fontSize: "10px", color: t.textMuted, marginLeft: "6px" }}>(edited)</span>}
                      </div>
                    )}
                    {editingMsgIdx !== idx && (
                      <div style={{ position: "absolute", right: "4px", top: "4px", display: "flex", gap: "2px" }}>
                        <button className="rp-action-btn" onClick={() => toggleRPHighlight(idx)}
                          style={{ background: msg.highlighted ? "var(--ds-gold-a40, rgba(196,168,130,0.25))" : "#0d0a0a", border: `1px solid ${msg.highlighted ? "var(--ds-gold-a8c, rgba(196,168,130,0.55))" : "#40444b"}`, borderRadius: "4px 0 0 4px", padding: "2px 7px", fontSize: "12px", fontWeight: 800, color: msg.highlighted ? "var(--ds-gold, #c4a882)" : t.textMuted, cursor: "pointer", opacity: msg.highlighted ? 0.85 : 0, transition: "opacity 0.15s, box-shadow 0.15s", boxShadow: msg.highlighted ? "0 0 7px var(--ds-gold-a66, rgba(196,168,130,0.4))" : "none" }}
                          title={msg.highlighted ? "Remove highlight" : "Mark as important"}>!</button>
                        <button className="rp-action-btn" onClick={async (e) => { const b = e.currentTarget; if (await copyMessageText(msg.text)) { b.textContent = '✓'; setTimeout(() => { b.textContent = '📋'; }, 900); } }} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0", padding: "2px 6px", fontSize: "11px", color: t.textMuted, cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Copy text">📋</button>
                        <button className="rp-action-btn" onClick={() => startEditingRP(idx)} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0", padding: "2px 6px", fontSize: "11px", color: t.textMuted, cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Edit">✏️</button>
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <button className="rp-action-btn" onClick={() => setPinPopoverMsgIdx(pinPopoverMsgIdx === idx ? null : idx)} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0", padding: "2px 6px", fontSize: "11px", color: "var(--ds-gold, #c4a882)", cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Pin">📌</button>
                          {pinPopoverMsgIdx === idx && (
                            <PinPopover message={{ id: msg.id, text: msg.text, name: msg.name }} contextType="story" contextId={activeStoryId} userId={user?.id} onClose={() => setPinPopoverMsgIdx(null)} />
                          )}
                        </div>
                        {isMe && <button className="rp-action-btn" onClick={() => resendRPMessage(idx)} disabled={resendingMsgId === msg.id} data-persist={msg.sendFailed ? "1" : "0"} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0", padding: "2px 6px", fontSize: "11px", color: msg.sendFailed ? "#ed4245" : t.textMuted, cursor: "pointer", opacity: msg.sendFailed ? 0.95 : 0, transition: "opacity 0.15s" }} title={msg.sendFailed ? "Didn't reach the server — resend" : "Resend to server"}>{resendingMsgId === msg.id ? "…" : "↻"}</button>}
                            <button className="rp-action-btn" onClick={() => deleteRPMessage(idx)} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0 4px 4px 0", padding: "2px 6px", fontSize: "11px", color: "#ed4245", cursor: "pointer", opacity: 0, transition: "opacity 0.15s" }} title="Delete">🗑️</button>
                      </div>
                    )}
                    {!isMe && !oocMsg && editingMsgIdx !== idx && <div style={{ paddingLeft: "28px", marginTop: "4px" }}><RegenButton onRegen={(fb) => regenerateMessage(idx, fb)} onUndo={lastRegen?.msgId === msg.id ? undoRegenerate : null} /></div>}
                    {isMe && msg.sendFailed && <FailedSendNotice onResend={() => resendRPMessage(idx)} resending={resendingMsgId === msg.id} />}
                  </div>
                );
              }
              // Default: chat mode
              return (
                <div key={msg.id} id={`rp-msg-${idx}`} className={`msg-animate rp-msg ${isMe ? "rp-msg-me" : "rp-msg-them"}${rpActionsIdx === idx ? ' rp-actions-open' : ''}`} style={{ display: "flex", flexDirection: isMe ? "row-reverse" : "row", gap: "10px", marginBottom: "14px", padding: "3px 4px", borderRadius: "8px", alignItems: "flex-start", touchAction: "manipulation", ...oocStyle }}
                  onMouseEnter={(e) => {
                    if (!isMe) e.currentTarget.style.background = oocMsg ? "rgba(250,166,26,0.12)" : "rgba(0,0,0,0.06)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isMe) e.currentTarget.style.background = oocMsg ? "rgba(250,166,26,0.08)" : "transparent";
                  }}
                  {...rpBubbleTouchHandlers(idx, msg.id)}
                >
                  <CharAvatar char={char} />
                  <div style={{ flex: 1, maxWidth: isMe ? "78%" : "100%", display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start" }}>
                    <div style={{ display: "flex", flexDirection: isMe ? "row-reverse" : "row", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, color: char?.color || t.accentGold, fontSize: "14px" }}>{msg.name}</span>
                      {!isMe && <span style={{ fontSize: "9px", background: "var(--ds-accent-a22, #c6282822)", color: t.accentGold, padding: "1px 5px", borderRadius: "6px" }}>AI</span>}
                      {oocMsg && <span style={{ fontSize: "9px", background: "var(--ds-gold-a33, #c4a88233)", color: t.accentGold, padding: "1px 5px", borderRadius: "6px" }}>OOC</span>}
                      <span style={{ fontSize: "11px", color: t.textMuted }}>{formatTimestamp(msg.createdAt || msg.timestamp)}</span>
                    </div>
                    {(() => { const u = formatUsage(msg.usage); return u
                      ? <div style={usageLineStyle} title={u.title}>{u.text}</div>
                      : null; })()}
                    {editingMsgIdx === idx ? (
                      <div style={{ marginTop: "4px", width: "100%" }}>
                        <textarea
                          value={editingMsgText}
                          onChange={e => setEditingMsgText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveRPEdit(idx); } if (e.key === 'Escape') cancelEditingRP(); }}
                          style={{ background: "var(--ds-surface-1, #0d0a0a)", color: t.textPrimary, border: "1px solid #40444b", borderRadius: "6px", padding: "8px 10px", fontSize: "14px", outline: "none", width: "100%", boxSizing: "border-box", minHeight: "60px", resize: "vertical", fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
                          autoFocus
                        />
                        <div style={{ display: "flex", gap: "6px", marginTop: "4px", fontSize: "11px", color: t.textMuted, flexDirection: isMe ? "row-reverse" : "row" }}>
                          <span>Esc to cancel · Enter to save</span>
                          <button onClick={() => saveRPEdit(idx)} style={{ background: t.accentDanger, color: "#fff", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer", marginLeft: isMe ? 0 : "auto", marginRight: isMe ? "auto" : 0 }}>Save</button>
                          <button onClick={cancelEditingRP} style={{ background: t.bgBase, color: "var(--ds-ink)", border: "none", borderRadius: "6px", padding: "4px 8px", fontSize: "11px", cursor: "pointer" }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ position: "relative", marginTop: "2px" }}>
                        <div style={{
                          fontSize: `${readerPrefs.fontSize}px`, lineHeight: readerPrefs.lineHeight, fontFamily: readerFont,
                          whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word",
                          color: oocMsg ? t.accentGold : readerTheme?.text || t.textPrimary,
                          fontStyle: oocMsg ? "italic" : "normal",
                          userSelect: 'text', WebkitUserSelect: 'text',
                          ...(isMe && !oocMsg ? {
                            background: msg.highlighted ? "var(--ds-gold-a52, rgba(196,168,130,0.32))" : "var(--ds-gold-a1a, rgba(196,168,130,0.10))",
                            border: msg.highlighted ? "1.5px solid var(--ds-gold, #c4a882)" : "1px solid var(--ds-gold-a2e, rgba(196,168,130,0.18))",
                            borderRadius: "14px 4px 14px 14px",
                            padding: "8px 12px",
                            boxShadow: msg.highlighted ? "0 0 18px var(--ds-gold-a73, rgba(196,168,130,0.45)), 0 0 6px var(--ds-gold-a40, rgba(196,168,130,0.25))" : "none",
                            transition: "all 0.2s",
                          } : msg.highlighted ? {
                            background: "var(--ds-gold-a24, rgba(196,168,130,0.14))",
                            borderLeft: "3px solid var(--ds-gold, #c4a882)",
                            paddingLeft: "10px",
                            borderRadius: "0 8px 8px 0",
                            boxShadow: "0 0 16px var(--ds-gold-a38, rgba(196,168,130,0.22)), -2px 0 10px var(--ds-gold-a2e, rgba(196,168,130,0.18))",
                            transition: "all 0.2s",
                          } : {}),
                        }}>
                          {formatMessageText(msg.text)}
                          {msg.edited && <span style={{ fontSize: "10px", color: t.textMuted, marginLeft: "6px" }}>(edited)</span>}
                          {msg.highlighted && <span style={{ fontSize: "9px", color: "var(--ds-gold-ab2, rgba(196,168,130,0.7))", marginLeft: "6px", verticalAlign: "middle" }}>★</span>}
                        </div>
                        <div style={{ position: "absolute", left: isMe ? "-4px" : "auto", right: isMe ? "auto" : "-4px", top: "-4px", display: "flex", gap: "2px" }}>
                          {/* Highlight / Important button */}
                          <button className="rp-action-btn" onClick={() => toggleRPHighlight(idx)}
                            style={{
                              background: msg.highlighted ? "var(--ds-gold-a47, rgba(196,168,130,0.28))" : "#0d0a0a",
                              border: `1px solid ${msg.highlighted ? "var(--ds-gold-a99, rgba(196,168,130,0.6))" : "#40444b"}`,
                              borderRadius: "4px 0 0 4px", padding: "2px 7px", fontSize: "12px", fontWeight: 800,
                              color: msg.highlighted ? "var(--ds-gold, #c4a882)" : t.textMuted, cursor: "pointer",
                              opacity: msg.highlighted ? 0.9 : 0, transition: "opacity 0.15s, background 0.15s, box-shadow 0.15s",
                              whiteSpace: "nowrap",
                              boxShadow: msg.highlighted ? "0 0 7px var(--ds-gold-a66, rgba(196,168,130,0.4))" : "none",
                            }}
                            title={msg.highlighted ? "Remove highlight" : "Mark as important"}
                          >!</button>
                          <button className="rp-action-btn" onClick={() => startEditingRP(idx)} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: msg.highlighted ? "0" : "4px 0 0 4px", padding: "2px 6px", fontSize: "11px", color: t.textMuted, cursor: "pointer", opacity: 0, transition: "opacity 0.15s", whiteSpace: "nowrap" }} title="Edit">✏️</button>
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <button className="rp-action-btn" onClick={() => setPinPopoverMsgIdx(pinPopoverMsgIdx === idx ? null : idx)} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0", padding: "2px 6px", fontSize: "11px", color: "var(--ds-gold, #c4a882)", cursor: "pointer", opacity: 0, transition: "opacity 0.15s", whiteSpace: "nowrap" }} title="Pin">📌</button>
                            {pinPopoverMsgIdx === idx && (
                              <PinPopover message={{ id: msg.id, text: msg.text, name: msg.name }} contextType="story" contextId={activeStoryId} userId={user?.id} onClose={() => setPinPopoverMsgIdx(null)} />
                            )}
                          </div>
                          {isMe && <button className="rp-action-btn" onClick={() => resendRPMessage(idx)} disabled={resendingMsgId === msg.id} data-persist={msg.sendFailed ? "1" : "0"} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0", padding: "2px 6px", fontSize: "11px", color: msg.sendFailed ? "#ed4245" : t.textMuted, cursor: "pointer", opacity: msg.sendFailed ? 0.95 : 0, transition: "opacity 0.15s", whiteSpace: "nowrap" }} title={msg.sendFailed ? "Didn't reach the server — resend" : "Resend to server"}>{resendingMsgId === msg.id ? "…" : "↻"}</button>}
                            <button className="rp-action-btn" onClick={() => deleteRPMessage(idx)} style={{ background: "var(--ds-surface-1, #0d0a0a)", border: "1px solid #40444b", borderRadius: "0 4px 4px 0", padding: "2px 6px", fontSize: "11px", color: "#ed4245", cursor: "pointer", opacity: 0, transition: "opacity 0.15s", whiteSpace: "nowrap" }} title="Delete">🗑️</button>
                        </div>
                      </div>
                    )}
                    {!isMe && !oocMsg && editingMsgIdx !== idx && <RegenButton onRegen={(fb) => regenerateMessage(idx, fb)} onUndo={lastRegen?.msgId === msg.id ? undoRegenerate : null} />}
                    {isMe && msg.sendFailed && <FailedSendNotice onResend={() => resendRPMessage(idx)} resending={resendingMsgId === msg.id} />}
                  </div>
                </div>
              );
            })}
            {isTyping && (
              <div style={{ color: t.textMuted, fontSize: "13px", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px", animation: "fadeSlideUp 0.2s ease-out", background: "var(--ds-accent-a0a, rgba(198,40,40,0.04))", borderRadius: "8px", margin: "4px 0" }}>
                <span style={{ display: "inline-flex", gap: "3px" }}>
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </span>
                <span style={{ fontStyle: "italic" }}>{typingCharName || "Character"} is typing...</span>
              </div>
            )}
            {/* Remote player typing indicator */}
            {remoteTyping && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 16px", color: t.accentGold, fontSize: "13px", opacity: 0.85 }}>
                <span style={{ display: "flex", gap: "3px" }}>
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </span>
                <span style={{ fontStyle: "italic" }}>{remoteTyping.userName} is typing as {remoteTyping.charName}...</span>
              </div>
            )}
            <div ref={chatEndRef} />

            {/* Scroll-to-bottom FAB */}
            {!rpIsAtBottom && (
              <button
                onClick={() => scrollToBottom(false)}
                style={{
                  position: 'sticky', bottom: '12px', float: 'right', marginRight: '12px',
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: 'rgba(20,10,10,0.92)', border: '1px solid var(--ds-gold-a66, rgba(196,168,130,0.4))',
                  color: 'var(--ds-gold, #c4a882)', fontSize: '18px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
                  zIndex: 10,
                }}
                title="Scroll to latest"
              >
                ↓
              </button>
            )}
          </div>

          {/* ── RP Highlight jump banner ── */}
          {rpHighlightBanner && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 14px', margin: '0 8px 4px',
              background: 'var(--ds-gold-a1a, rgba(196,168,130,0.10))',
              border: '1px solid var(--ds-gold-a52, rgba(196,168,130,0.32))',
              borderRadius: '8px', flexShrink: 0,
              boxShadow: '0 0 12px var(--ds-gold-a1f, rgba(196,168,130,0.12))',
              animation: 'fadeSlideIn 0.2s ease',
            }}>
              <span style={{ fontSize: '13px', color: 'var(--ds-gold, #c4a882)', flexShrink: 0 }}>★</span>
              <span
                style={{ flex: 1, fontSize: '13px', color: 'var(--ds-gold, #c4a882)', fontWeight: 500, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onClick={() => {
                  const el = document.getElementById(`rp-msg-${rpHighlightBanner.idx}`);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.style.transition = 'background 0.3s';
                    el.style.background = 'var(--ds-gold-a24, rgba(196,168,130,0.14))';
                    setTimeout(() => { el.style.background = ''; }, 1800);
                  }
                }}
                title="Click to jump to highlighted message"
              >⚑ Highlighted: {rpHighlightBanner.label}</span>
              <button
                onClick={() => { clearTimeout(rpHighlightBannerTimerRef.current); setRpHighlightBanner(null); }}
                style={{ background: 'none', border: 'none', color: 'var(--ds-gold-a8c, rgba(196,168,130,0.55))', fontSize: '18px', cursor: 'pointer', padding: '0 2px', lineHeight: 1, flexShrink: 0 }}
                title="Dismiss"
              >×</button>
            </div>
          )}

          <div style={{ padding: "12px 14px", paddingBottom: isMobile ? "calc(12px + env(safe-area-inset-bottom))" : "12px", borderTop: "1px solid var(--ds-accent-a1f, rgba(198,40,40,0.12))", background: "linear-gradient(180deg, var(--ds-glass, rgba(10,10,10,0.4)) 0%, var(--ds-glass-strong, rgba(10,10,10,0.6)) 100%)", backdropFilter: "blur(8px)" }}>
            {/* Siren's Box — owner mode. Per-story self-notes the AI uses
                in its next response. Mounted on the active RP chat for the
                story owner. Spectators get their own (in spectate render). */}
            {(playingAs || narratorMode) && activeStoryId && user?.id && (
              <SirenWhisperBox
                storyId={activeStoryId}
                fromUserId={user.id}
                toUserId={user.id}
                storyName={currentSessionName || ''}
                mode="owner"
              />
            )}
            {/* AI Paused Banner */}
            {aiPaused && (playingAs || narratorMode) && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", marginBottom: "8px", borderRadius: "8px", background: "var(--ds-gold-a1a, rgba(196, 168, 130, 0.1))", border: "1px solid var(--ds-gold-a40, rgba(196, 168, 130, 0.25))" }}>
                <span style={{ fontSize: "13px", color: t.accentGold, fontStyle: "italic" }}>
                  ⏸️ AI responses paused — RP freely!
                </span>
                <button
                  onClick={() => setAiPaused(false)}
                  style={{ padding: "5px 14px", borderRadius: "6px", border: "1px solid #4a9e6e55", background: "rgba(74, 158, 110, 0.15)", color: t.accentSuccess, fontSize: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "'Cinzel', serif", letterSpacing: "0.5px", transition: "all 0.2s" }}
                >
                  ▶ Resume AI
                </button>
              </div>
            )}
            {rpAIError && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 8px", padding: "10px 12px", borderRadius: "10px", background: "rgba(198,40,40,0.12)", border: "1px solid rgba(198,40,40,0.32)", color: "#ffb3ab", fontSize: "13px", lineHeight: 1.45 }}>
                <span style={{ flex: 1 }}>{rpAIError}</span>
                <button onClick={() => setRpAIError('')} aria-label="Dismiss" style={{ background: "none", border: "none", color: "#ffb3ab", cursor: "pointer", fontSize: "16px", minWidth: "32px", minHeight: "32px" }}>✕</button>
              </div>
            )}
            {rpSendError && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 8px", padding: "10px 12px", borderRadius: "10px", background: "rgba(198,40,40,0.12)", border: "1px solid rgba(198,40,40,0.32)", color: "#ffb3ab", fontSize: "13px", lineHeight: 1.45 }}>
                <span style={{ flex: 1 }}>{rpSendError}</span>
                <button onClick={() => setRpSendError(null)} aria-label="Dismiss" style={{ background: "none", border: "none", color: "#ffb3ab", cursor: "pointer", fontSize: "16px", minWidth: "32px", minHeight: "32px" }}>✕</button>
              </div>
            )}
            {!playingAs && !narratorMode && <div style={{ textAlign: "center", color: t.textFaint, fontSize: "12px", padding: "4px 8px", fontStyle: "italic" }}>Writing as {currentPlayer || 'yourself'} — pick someone in the sidebar to play as them instead.</div>}
            {/* "Talking to" pin — the RP twin of the channel chip. Pin one or
                more scene characters: the first pinned leads the reply (and
                becomes the memory anchor), the rest answer in the same beat,
                and the director is told exactly who is being addressed. */}
            {aiRespondChars.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 0 6px', position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setRpPinMenuOpen(o => !o)}
                  title="Who your messages are addressed to"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', minHeight: '34px',
                    padding: '4px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
                    background: rpTalkingTo.length ? 'var(--ds-gold-a1a, rgba(196,168,130,0.12))' : t.bgHover,
                    border: `1px solid ${rpTalkingTo.length ? 'var(--ds-gold-a4c, rgba(196,168,130,0.35))' : 'var(--ds-line)'}`,
                    color: rpTalkingTo.length ? t.accentGold : t.textMuted,
                    touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none',
                  }}
                >
                  <span style={{ pointerEvents: 'none' }}>🎯</span>
                  <span style={{ pointerEvents: 'none' }}>
                    {(() => {
                      const names = rpTalkingTo
                        .map(id => allChars.find(c => c?.id === id)?.name?.split(/\s+/)[0]).filter(Boolean);
                      if (!names.length) return 'Talking to: the scene';
                      if (names.length === 1) return `Talking to ${names[0]}`;
                      if (names.length === 2) return `Talking to ${names[0]} & ${names[1]}`;
                      return `Talking to ${names[0]}, ${names[1]} +${names.length - 2}`;
                    })()}
                  </span>
                  <span style={{ pointerEvents: 'none', fontSize: '10px', opacity: 0.7 }}>▾</span>
                </button>
                {rpPinMenuOpen && createPortal(
                  <div onClick={() => setRpPinMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(0,0,0,0.45)' }}>
                    <div onClick={e => e.stopPropagation()} style={{
                      position: 'fixed', zIndex: 99999, background: 'var(--ds-bg, #14100f)',
                      border: '1px solid var(--ds-gold-a26, rgba(212,175,55,0.25))',
                      ...(isMobile
                        ? { left: 0, right: 0, bottom: 0, borderRadius: '16px 16px 0 0', paddingBottom: 'env(safe-area-inset-bottom)', maxHeight: '60vh', overflowY: 'auto' }
                        : { left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: '340px', borderRadius: '14px', maxHeight: '70vh', overflowY: 'auto' }),
                    }}>
                      <div style={{ padding: '14px 16px 8px', fontSize: '12px', fontWeight: 800, letterSpacing: '1px', color: t.accentGold }}>WHO YOU ARE TALKING TO</div>
                      <div style={{ padding: '0 16px 8px', fontSize: '12px', color: t.textMuted, lineHeight: 1.5 }}>
                        Pin who your messages are addressed to — they carry the reply. Pin more than one and they answer together, in one synced beat; the first you pick leads. Everyone else stays in the scene but steps back.
                      </div>
                      <button type="button" onClick={() => { toggleRpTalkingTo(null); setRpPinMenuOpen(false); }} style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', minHeight: '48px',
                        background: !rpTalkingTo.length ? 'var(--ds-gold-a1f, rgba(196,168,130,0.1))' : 'none', border: 'none',
                        borderTop: '1px solid var(--ds-line-soft)', color: !rpTalkingTo.length ? t.accentGold : t.textMain,
                        cursor: 'pointer', fontSize: '14px', textAlign: 'left', touchAction: 'manipulation',
                      }}>
                        <span>✨</span><span>The scene decides</span>
                      </button>
                      {aiRespondChars.map(cid => {
                        const ch = allChars.find(c => c?.id === cid);
                        if (!ch) return null;
                        const order = rpTalkingTo.indexOf(cid);
                        const active = order !== -1;
                        return (
                          <button key={cid} type="button" onClick={() => toggleRpTalkingTo(cid)} style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', minHeight: '48px',
                            background: active ? 'var(--ds-gold-a1f, rgba(196,168,130,0.12))' : 'none', border: 'none',
                            borderTop: '1px solid var(--ds-line-soft)', color: active ? t.accentGold : t.textMain,
                            cursor: 'pointer', fontSize: '14px', textAlign: 'left', touchAction: 'manipulation',
                          }}>
                            <span style={{ fontSize: '18px' }}>{ch.avatar || '🎭'}</span>
                            <span>{ch.name}</span>
                            {active && <span style={{ marginLeft: 'auto', fontSize: '12px' }}>{rpTalkingTo.length > 1 ? `${order + 1}️⃣` : '📌'}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>,
                  document.body
                )}
                {/* 🔔 Summon — a character speaks next without a message. */}
                <button
                  type="button"
                  onClick={() => setRpSummonMenuOpen(o => !o)}
                  title="Summon a character to speak next"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', minHeight: '34px',
                    padding: '4px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
                    background: t.bgHover, border: '1px solid var(--ds-line)', color: t.textMuted,
                    touchAction: 'manipulation', WebkitUserSelect: 'none', userSelect: 'none',
                  }}
                >
                  <span style={{ pointerEvents: 'none' }}>🔔</span>
                  <span style={{ pointerEvents: 'none' }}>Summon</span>
                </button>
                {rpSummonMenuOpen && createPortal(
                  <div onClick={() => setRpSummonMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99998, background: 'rgba(0,0,0,0.45)' }}>
                    <div onClick={e => e.stopPropagation()} style={{
                      position: 'fixed', zIndex: 99999, background: 'var(--ds-bg, #14100f)',
                      border: '1px solid var(--ds-gold-a26, rgba(212,175,55,0.25))',
                      ...(isMobile
                        ? { left: 0, right: 0, bottom: 0, borderRadius: '16px 16px 0 0', paddingBottom: 'env(safe-area-inset-bottom)', maxHeight: '60vh', overflowY: 'auto' }
                        : { left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: '340px', borderRadius: '14px', maxHeight: '70vh', overflowY: 'auto' }),
                    }}>
                      <div style={{ padding: '14px 16px 8px', fontSize: '12px', fontWeight: 800, letterSpacing: '1px', color: t.accentGold }}>SUMMON A CHARACTER</div>
                      <div style={{ padding: '0 16px 8px', fontSize: '12px', color: t.textMuted, lineHeight: 1.5 }}>
                        They speak next — no message needed from you. The scene continues from exactly where it stands.
                      </div>
                      {aiRespondChars.map(cid => {
                        const ch = allChars.find(c => c?.id === cid);
                        if (!ch) return null;
                        return (
                          <button key={cid} type="button" onClick={() => { setRpSummonMenuOpen(false); sendMessage('', { summonCharId: cid }); }} style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', minHeight: '48px',
                            background: 'none', border: 'none',
                            borderTop: '1px solid var(--ds-line-soft)', color: t.textMain,
                            cursor: 'pointer', fontSize: '14px', textAlign: 'left', touchAction: 'manipulation',
                          }}>
                            <span style={{ fontSize: '18px' }}>{ch.avatar || '🎭'}</span>
                            <span>{ch.name}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.6 }}>🔔</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}
            {(
              <RPInputBar
                playerName={currentPlayer}
                ref={rpInputBarRef}
                activeStoryId={activeStoryId}
                currentChar={currentChar}
                aiPaused={aiPaused}
                setAiPaused={setAiPaused}
                inputStyle={inputStyle}
                btnPrimary={btnPrimary}
                isSendingRef={isSendingRef}
                t={t}
                isMobile={isMobile}
                onSend={sendMessage}
                onTypingBroadcast={() => {
                  if (activeStoryId && storyTypingChannelRef.current && !typingBroadcastTimerRef.current) {
                    storyTypingChannelRef.current.send({
                      type: 'broadcast',
                      event: 'typing',
                      payload: { userId: user?.id, userName: userProfile?.display_name || 'Someone', charName: currentChar?.name || 'Unknown' },
                    });
                    typingBroadcastTimerRef.current = setTimeout(() => { typingBroadcastTimerRef.current = null; }, 2000);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
      {/* ─── PIN PANEL ─── */}
      {showPinPanel && activeStoryId && user && (
        <PinPanel
          contextType="story"
          contextId={activeStoryId}
          userId={user.id}
          onClose={() => setShowPinPanel(false)}
          t={t}
        />
      )}
      {/* Reader settings + story preferences moved into the ONE settings menu
          (queue item 17). The stories write-verify (audit SW-11) lives in
          ProfileSettings.saveStoryPrefs now; this view syncs via the
          mfml-story-prefs-changed listener. */}
      {showStoryLinks && (
        <StoryLinksManager
          user={user}
          activeStoryId={activeStoryId}
          activeStoryName={currentSessionName}
          onClose={() => setShowStoryLinks(false)}
        />
      )}
      {/* Relationships for the RP section (Nicole, 2026-08-10). Mounted at
          the top level like every other overlay — the panel is position:fixed
          and would be clipped inside the scrolling sidebar. The target is the
          character the player is playing, so the cast's ties are defined
          toward her; the cast is the story's own, minus that character. */}
      {showRpRelationships && (
        <RelationshipsPanel
          targetCharacterId={playingAs || sceneChars[0] || null}
          castIds={(sceneChars || []).filter((id) => id !== playingAs)}
          scope={{ type: 'story', id: activeStoryId || null }}
          scopeLabel={currentSessionName || 'this story'}
          userId={user?.id}
          onClose={() => setShowRpRelationships(false)}
        />
      )}
      {showRpLinkPanel && activeStoryId && (
        <ConversationLinkPanel
          currentType="story"
          currentId={activeStoryId}
          currentTitle={currentSessionName || 'this RP'}
          userId={user?.id}
          onClose={() => setShowRpLinkPanel(false)}
          theme={t}
          isMobile={isMobile}
        />
      )}
      {showAdminPanel && <Lazy><AdminPanel user={user} userProfile={userProfile} onClose={() => setShowAdminPanel(false)} /></Lazy>}
        {showCharacterEditor && <Lazy><CharacterEditor user={user} userProfile={userProfile} aiProvider={aiProvider} onClose={() => setShowCharacterEditor(false)} /></Lazy>}
      {renderProfileSettings()}
      {showConfirmModal && <ConfirmModal message={showConfirmModal.message} onConfirm={showConfirmModal.onConfirm} onCancel={() => setShowConfirmModal(null)} />}
      {viewingProfile && <UserProfileModal profile={viewingProfile} currentUserId={user?.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} onClose={() => setViewingProfile(null)} onDM={(uid) => { setDmTargetUserId(uid); setDmStartNew(true); setMainTab("dm"); setTimeout(() => setDmStartNew(false), 500); }} formatLastActive={formatLastActive} />}
      {showConfetti && <Confetti t={t} onDone={() => setShowConfetti(false)} />}
      {toastMsg && <Toast t={t} message={toastMsg.message} type={toastMsg.type} icon={toastMsg.icon} onDone={() => setToastMsg(null)} />}
      {/* Dead code #21: ToastContainer removed — addToast() was never called */}

      {/* User list is now in MainNav */}

      {/* ─── Music Player ──────────────────────────────────────────────── */}
      <Lazy><MusicPlayer
        visible={musicPlayerVisible}
        onToggleVisible={toggleMusicPlayer}
        isMobile={isMobile}
      /></Lazy>
      {/* Bottom spacer when music player is visible to prevent content overlap */}
      {musicPlayerVisible && <div style={{ height: isMobile ? "64px" : "56px", flexShrink: 0 }} />}
      <Lazy><PopupPhone aiDialect={aiDialect} user={user} currentPlayer={currentPlayer} open={phoneOpen} onToggle={() => setPhoneOpen(p => !p)} savedSessions={savedSessions} initialCharKey={phoneInitialCharKey} initialLinkedConvoId={phoneLinkedConvoId} onInitialCharHandled={() => { setPhoneInitialCharKey(null); setPhoneLinkedConvoId(null); }} /></Lazy>
      {incomingCallChar && <IncomingCallOverlay character={incomingCallChar} onAnswer={handleAnswerIncomingCall} onDismiss={handleDismissIncomingCall} reason={incomingCallMeta?.reason || 'milestone'} />}
      {showContentConsent && <ContentConsentModal onAccept={() => setShowContentConsent(false)} onDecline={handleLogout} />}
      <AnnouncementModal onOpenAppearance={(tab) => { setProfileSettingsInitialTab(tab || "appearance"); setShowProfileSettings(true); }} />
      <CookieConsent />
        <ToastContainer ref={toastRef} />
    </div>
  );
}

// ─── SESSION WARNING BANNER ─────────────────────────────────────────────────
// ─── CONNECTION STATUS INDICATOR ────────────────────────────────────────────
function ConnectionIndicator({ status, saveStatus, lastSavedAt }) {
  const { theme: t } = useContext(ThemeContext);
  if (status === 'connected' && saveStatus === 'idle') return null;

  const statusConfig = {
    connected: { color: '#57f287', icon: '🟢', text: 'Connected' },
    reconnecting: { color: t.accentGold, icon: '🟡', text: 'Reconnecting...' },
    offline: { color: '#ed4245', icon: '🔴', text: 'Offline' },
  };
  const saveConfig = {
    idle: null,
    saving: { color: t.accentGold, text: '💾 Saving...' },
    saved: { color: '#57f287', text: '✅ Saved' },
    error: { color: '#ed4245', text: '⚠️ Save failed — will retry' },
  };

  const s = statusConfig[status] || statusConfig.connected;
  const sv = saveConfig[saveStatus];

  return (
    <div className="slide-up" style={{
      position: 'fixed', bottom: '16px', right: '16px', zIndex: 9999,
      background: 'linear-gradient(135deg, rgba(20,16,16,0.95), rgba(15,10,10,0.95))', borderRadius: '10px', padding: '8px 14px',
      fontSize: '11px', color: t.textMuted, display: 'flex', alignItems: 'center', gap: '8px',
      border: `1px solid ${s.color}22`, backdropFilter: 'blur(12px)',
      boxShadow: `0 4px 20px rgba(0,0,0,0.3), 0 0 8px ${s.color}11`,
      fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.5px',
    }}>
      {status !== 'connected' && (
        <span style={{ color: s.color, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: s.color, display: 'inline-block', animation: status === 'reconnecting' ? 'subtlePulse 1.5s ease infinite' : 'none' }} />
          {s.text}
        </span>
      )}
      {sv && (
        <span style={{ color: sv.color, display: 'flex', alignItems: 'center', gap: '4px' }}>{sv.text}</span>
      )}
      {lastSavedAt && saveStatus === 'idle' && status === 'connected' && (
        <span style={{ color: t.textFaint }}>
          Last saved {formatTimestamp(lastSavedAt)}
        </span>
      )}
    </div>
  );
}

function SessionWarning({ onRefresh: _onRefresh, onLogout, showForceReset }) {
  const { theme: t } = useContext(ThemeContext);
  const handleHardReset = () => {
    if (window.__mfmlForceReset) window.__mfmlForceReset();
    else { onLogout(); setTimeout(() => window.location.reload(), 500); }
  };
  return (
    <div style={{ background: t.accentDanger, color: "#fff", padding: "10px 16px", fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
      <span>{showForceReset ? "⚠️ Session stuck — tap Hard Reset" : "⚠️ Session expired"}</span>
      <div style={{ display: "flex", gap: "6px" }}>
        <button onClick={onLogout} style={{ background: "var(--ds-hover)", color: "var(--ds-ink-hi, #fff)", border: "1px solid var(--ds-line-strong)", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
          Re-login
        </button>
        <button onClick={handleHardReset} style={{ background: showForceReset ? "var(--ds-surface-3)" : "var(--ds-hover)", color: showForceReset ? t.accentDanger : "var(--ds-ink-hi)", border: showForceReset ? "none" : "1px solid var(--ds-line-strong)", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", animation: showForceReset ? "pulse 1s ease infinite" : "none" }}>
          Hard Reset
        </button>
      </div>
    </div>
  );
}

// ─── REGENERATE BUTTON (Update 3B) ─────────────────────────────────────────
// Shown on YOUR OWN message when its story_messages write failed. The message
// is not lost — it is in local state and in the story blob — but it never
// reached the table, so Nicole's client and the spectate view never saw it and
// the blob+table merge has nothing to reconcile. Before this the only trace was
// a console.error.
//
// The flag rides along in the blob, so a message that failed still offers
// Resend after a reload — which is exactly when you would come back and notice.
function FailedSendNotice({ onResend, resending }) {
  const { theme: t } = useContext(ThemeContext);
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "4px", justifyContent: "flex-end" }}>
      <span style={{ fontSize: "11px", color: t.accentDanger || "#ed4245" }} title="Saved here, but it never reached the server — the other player can't see it yet.">
        ⚠ Didn’t reach the server
      </span>
      <button
        onClick={onResend}
        disabled={resending}
        aria-label="Resend this message"
        style={{
          background: "none", border: `1px solid ${t.accentDanger || "#ed4245"}`,
          color: t.accentDanger || "#ed4245", borderRadius: "4px",
          padding: "2px 8px", fontSize: "11px",
          cursor: resending ? "default" : "pointer", opacity: resending ? 0.6 : 1,
          fontFamily: "inherit",
        }}
      >
        {resending ? "Resending…" : "Resend"}
      </button>
    </div>
  );
}

// onUndo is passed ONLY on the reply that was just regenerated, and only until
// the scene moves on — see lastRegen. Haley asked for "an undo regenerate
// button as a failsafe", and the failsafe is needed precisely because
// regenerate deletes the old reply outright rather than keeping it around.
function RegenButton({ onRegen, onUndo = null }) {
  const { theme: t } = useContext(ThemeContext);
  const [showInput, setShowInput] = useState(false);
  const [feedback, setFeedback] = useState("");
  if (!showInput) {
    return (
      <div style={{ display: "flex", gap: "4px", alignItems: "center", marginTop: "4px" }}>
        {onUndo && (
          <button onClick={onUndo} style={{ background: "none", border: "1px solid var(--ds-gold-a40, rgba(196,168,130,0.28))", color: "var(--ds-gold, #c4a882)", fontSize: "11px", cursor: "pointer", padding: "2px 7px", borderRadius: "5px" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-gold-a15, rgba(196,168,130,0.12))")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            title="Put the previous reply back"
          >
            ↩️ Undo regenerate
          </button>
        )}
        {/* Direct regen — no feedback required */}
        <button onClick={() => onRegen("")} style={{ background: "none", border: "none", color: t.textMuted, fontSize: "11px", cursor: "pointer", padding: "2px 0" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-accent, #c62828)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textMuted)}
          title="Regenerate response"
        >
          🔄 Regenerate
        </button>
        {/* Expand feedback input */}
        <button onClick={() => setShowInput(true)} style={{ background: "none", border: "none", color: t.textFaint, fontSize: "10px", cursor: "pointer", padding: "2px 4px" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#d1b3ff")}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textFaint)}
          title="Regenerate with direction"
        >
          ✏️
        </button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: "6px", display: "flex", gap: "6px", alignItems: "center" }}>
      <input value={feedback} onChange={(e) => setFeedback(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onRegen(feedback); setShowInput(false); setFeedback(""); } }} style={{ background: "var(--ds-surface-1, #0d0a0a)", color: t.textPrimary, border: "1px solid #40444b", borderRadius: "4px", padding: "4px 8px", fontSize: "11px", flex: 1, outline: "none" }} placeholder="Direction (optional — press Enter or Go)" autoFocus />
      <button onClick={() => { onRegen(feedback); setShowInput(false); setFeedback(""); }} style={{ background: t.accentDanger, color: "#fff", border: "none", borderRadius: "4px", padding: "4px 8px", fontSize: "11px", cursor: "pointer" }}>Go</button>
      <button onClick={() => { setShowInput(false); setFeedback(""); }} style={{ background: "none", border: "none", color: t.textMuted, fontSize: "14px", cursor: "pointer" }}>×</button>
    </div>
  );
}

// ─── COMPONENTS ──────────────────────────────────────────────────────────────
function CharModal({ char, onClose, onPlay, onAddScene, canPlay }) {
  const { theme: t } = useContext(ThemeContext);
  const fullArt = char?.id ? getCharacterArt(char.id, 'full') : null;
  const mediumArt = char?.id ? getCharacterArt(char.id, 'medium') : null;
  const artUrl = fullArt || mediumArt;

  return (
    <div style={{ ...modalOverlay, backdropFilter: "blur(8px)", background: "rgba(0,0,0,0.85)" }} onClick={onClose}>
      <div style={{ ...modalContent, maxWidth: "560px", maxHeight: "85vh", overflowY: "auto", borderRadius: "16px", padding: 0, border: `1px solid ${char.color}33`, boxShadow: `0 20px 60px rgba(0,0,0,0.8), 0 0 40px ${char.color}11`, animation: "modalFadeIn 0.3s ease-out" }} onClick={(e) => e.stopPropagation()}>
        {/* Character art header */}
        {artUrl && (
          <div style={{ width: "100%", height: "280px", overflow: "hidden", position: "relative" }}>
            <img src={artUrl} alt={char.name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }} loading="lazy" />
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "60%", background: `linear-gradient(transparent, #120e0e)` }} />
            {/* Close button */}
            <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: "12px", right: "12px", background: "var(--ds-scrim-strong, rgba(0,0,0,0.6))", border: "1px solid #ffffff22", borderRadius: "50%", width: "44px", height: "44px", color: t.textPrimary, cursor: "pointer", fontSize: "18px", backdropFilter: "blur(4px)", transition: "all 0.2s", touchAction: "manipulation" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-accent-a99, rgba(198,40,40,0.6))"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.6)"; }}
            >✕</button>
            {/* Name overlay on art */}
            <div style={{ position: "absolute", bottom: "16px", left: "20px", right: "20px" }}>
              <h2 style={{ margin: 0, fontSize: "28px", fontFamily: "'Cinzel', serif", letterSpacing: "2px", textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>{char.name}</h2>
              {char.fullName && <div style={{ fontSize: "12px", color: t.accentGold, fontStyle: "italic", marginTop: "2px", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{char.fullName}</div>}
            </div>
          </div>
        )}

        <div style={{ padding: artUrl ? "12px 20px 20px" : "20px" }}>
          {/* Header if no art */}
          {!artUrl && (
            <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "16px" }}>
              <CharAvatar char={char} size={60} />
              <div>
                <h2 style={{ margin: 0, fontSize: "24px", fontFamily: "'Cinzel', serif" }}>{char.name}</h2>
                {char.fullName && <div style={{ fontSize: "11px", color: t.textMuted, fontStyle: "italic" }}>{char.fullName}</div>}
              </div>
            </div>
          )}

          {/* Character info bar */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ color: char.color, fontSize: "13px", fontWeight: 600, fontFamily: "'Cinzel', serif" }}>{char.species}</span>
            <span style={{ color: t.textFaint }}>·</span>
            <span style={{ color: t.textMuted, fontSize: "12px" }}>Gen {char.generation}</span>
            <span style={{ color: t.textFaint }}>·</span>
            <span style={{ color: t.textMuted, fontSize: "12px" }}>{char.controlledBy === "AI" ? "AI Character" : `Played by ${char.controlledBy}`}</span>
          </div>

          {/* Traits */}
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
            {char.traits.map((t) => (
              <span key={t} style={{ fontSize: "11px", background: char.color + "15", color: char.color, padding: "4px 12px", borderRadius: "20px", border: `1px solid ${char.color}22`, fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{t}</span>
            ))}
          </div>

          <OrnateDivider width="100%" margin="0 auto 16px" />

          <Sec title="Personality" text={char.personality} />
          <Sec title="Speech Pattern" text={char.speechPattern} />
          <Sec title="Relationships" text={char.relationships} />
          <Sec title="Backstory" text={char.backstory} />

          <OrnateDivider width="100%" margin="8px auto 16px" />

          {/* Action buttons */}
          <div style={{ display: "flex", gap: "10px" }}>
            {canPlay && (
              <button onClick={onPlay} style={{ ...btnPrimary, flex: 1, background: `linear-gradient(135deg, ${char.color}cc, ${char.color})`, border: `1px solid ${char.color}66`, borderRadius: "10px", fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", fontSize: "12px", textTransform: "uppercase", padding: "13px 20px", boxShadow: `0 4px 15px ${char.color}33`, transition: "all 0.3s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 6px 25px ${char.color}55`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 4px 15px ${char.color}33`; }}
              >Play as {char.name}</button>
            )}
            <button onClick={onAddScene} style={{ ...btnPrimary, flex: canPlay ? 0 : 1, background: "transparent", border: "1px solid var(--ds-accent-a44, #c6282844)", color: t.accentDanger, borderRadius: "10px", fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", fontSize: "12px", textTransform: "uppercase", padding: "13px 20px", transition: "all 0.3s" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-accent-a15, #c6282815)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >Add to Scene</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Sec({ title, text }) {
  const { theme: t } = useContext(ThemeContext);
  if (!text) return null;
  return (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ fontSize: "11px", color: t.accentGold, marginBottom: "4px", fontWeight: 600, fontFamily: "'Cinzel', serif", letterSpacing: "2px", textTransform: "uppercase" }}>{title}</div>
      <div style={{ fontSize: "14px", color: t.textPrimary, lineHeight: "1.6", fontFamily: "'Cormorant Garamond', serif" }}>{text}</div>
    </div>
  );
}

// ReaderSettingsPanel (the in-file duplicate of ReaderSettings.jsx) was
// removed with queue item 17 — reading prefs live in the ONE settings menu.

const APP_SETTINGS_KEY = 'mfml_app_settings';
const DEFAULT_APP_SETTINGS = {
  textSpeed: 'medium',       // 'slow' | 'medium' | 'fast' | 'instant'
  bgmVolume: 60,             // 0–100
  sfxVolume: 80,
  voiceVolume: 100,
  reducedMotion: false,
  highContrast: false,
  fontSize: 'medium',        // 'small' | 'medium' | 'large'
  contentLevel: 'mature',    // 'all_ages' | 'teen' | 'mature'
};
export function loadAppSettings() {
  try { return { ...DEFAULT_APP_SETTINGS, ...JSON.parse(localStorage.getItem(APP_SETTINGS_KEY) || '{}') }; } catch { return { ...DEFAULT_APP_SETTINGS }; }
}
function saveAppSettings(s) {
  try { localStorage.setItem(APP_SETTINGS_KEY, JSON.stringify(s)); } catch {}
}
// Reflect accessibility settings onto <html> so they actually take effect.
// (These toggles were persisted but never applied — Reduced Motion did nothing.)
// CSS in index.css keys off these classes/attribute; motion.js prefersReduced()
// honors `reduce-motion` too, so the in-app toggle damps the v6 motion layer.
export function applyAppSettings(s) {
  try {
    const root = document.documentElement;
    if (!root || !s) return;
    root.classList.toggle('reduce-motion', !!s.reducedMotion);
    root.classList.toggle('high-contrast', !!s.highContrast);
    root.setAttribute('data-font-size', s.fontSize || 'medium');
  } catch {}
}
// Apply at boot (loadAppSettings is hoisted; runs once on module load).
try { applyAppSettings(loadAppSettings()); } catch {}

// `embedded` renders just the settings BODY, no overlay chrome — it is how
// this whole panel now lives inside ProfileSettings as the "App & AI" tab
// ("please merge all profile and settings components into one menu",
// 2026-07-29). The standalone-overlay form is kept for nothing; every entry
// point routes to the unified panel.
function SettingsModal({ apiKey, setApiKey, aiProvider, setAiProvider, aiModel, setAiModel, onClose, sessionName, setSessionName, user, globalApiEnabled, globalApiProvider, notifPrefs, updateNotifPref, handleSnooze, onOpenStoryLinks, hasActiveStory, embedded = false }) {
  // Device-level Enter-sends preference (see enterSends.js).
  const [enterSendPref, setEnterSendPref] = useState(enterSendsEnabled());
  const { theme: t } = useContext(ThemeContext);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const nativePushActive = isNativePushActive(); // APNs registered on this device (iOS wrapper); set long before this modal opens
  const [pushLoading, setPushLoading] = useState(false);
  const [appSettings, setAppSettings] = useState(() => loadAppSettings());
  const [apiKeySaveStatus, setApiKeySaveStatus] = useState("idle"); // idle | saving | saved | error
  const [apiKeySaveError, setApiKeySaveError] = useState("");
  // "Let characters call me" — gates EVERY unsolicited incoming-call
  // ring. Backed by the SYNCED notification_preferences.random_calls column;
  // it used to be localStorage only, which is why Nicole could not turn the
  // calls off (per-device, and wiped by clearing site data). localStorage is
  // still written so a device that already opted out stays out, and so the
  // legacy early-out in the realtime handler keeps agreeing with the UI.
  const randomCallsEnabled = notifPrefs?.random_calls !== false;
  const toggleRandomCalls = async () => {
    const next = !randomCallsEnabled;
    try { localStorage.setItem('mfml_random_calls_enabled', next ? 'true' : 'false'); } catch {}
    await updateNotifPref?.({ random_calls: next });
  };

  // Unprompted character DMs. Nicole, 2026-08-10: the auto-DMs repeat, "and
  // cannot be turned off". The `enabled` flag already existed in
  // getOutreachPrefs AND was already honoured by DMView — there was simply no
  // control anywhere in the app that could set it to false. This is that
  // control. It lives beside "Let characters call me" because they are the
  // same decision about the same thing: whether characters contact you first.
  // v421: the local switch is retired for the synced character_dm_mode. A
  // device where it was OFF carries that choice over once, so nobody who had
  // silenced the DMs gets them back by this change.
  const [dmPickSearch, setDmPickSearch] = useState('');
  useEffect(() => {
    if (!user?.id || !notifPrefs || !updateNotifPref) return;
    try {
      const local = getOutreachPrefs(user.id) || {};
      if (local.enabled === false && (notifPrefs.character_dm_mode || 'all') === 'all') {
        updateNotifPref({ character_dm_mode: 'off' });
        saveOutreachPrefs(user.id, { ...local, enabled: true, carriedOver: true });
      }
    } catch { /* a blocked storage changes nothing */ }
  }, [user?.id, notifPrefs, updateNotifPref]);

  // Live OpenRouter model catalog for the searchable model dropdown. Fetched
  // once from the public /models endpoint (no auth), cached 24h in localStorage.
  const [orModels, setOrModels] = useState([]);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  useEffect(() => {
    if (aiProvider !== 'openrouter' || orModels.length > 0) return;
    (async () => {
      try {
        const cached = JSON.parse(localStorage.getItem('mfml_or_models') || 'null');
        if (cached?.ts && Date.now() - cached.ts < 86400000 && Array.isArray(cached.models) && cached.models.length) {
          setOrModels(cached.models); return;
        }
        const res = await fetch('https://openrouter.ai/api/v1/models');
        if (!res.ok) return;
        const j = await res.json();
        const models = (j.data || [])
          .map(m => ({ id: m.id, name: m.name || m.id }))
          .filter(m => m.id)
          .sort((a, b) => a.id.localeCompare(b.id));
        if (models.length) {
          setOrModels(models);
          try { localStorage.setItem('mfml_or_models', JSON.stringify({ ts: Date.now(), models })); } catch {}
        }
      } catch { /* offline / CSP — the plain typed field still works */ }
    })();
  }, [aiProvider, orModels.length]);

  // Persist a model choice WITHOUT re-entering the key (the input shows a masked
  // hint after saving). Guarantees "the model we pick is the one we get" the
  // moment it's selected — no separate Save needed. No-ops until a key is on
  // file (first-time setup persists the model together with the key on Save).
  const persistModelSelection = useCallback(async (modelId) => {
    if (!user?.id || !apiKey) return;
    const providerParam = (aiProvider === 'openrouter' || aiProvider === 'deepseek' || aiProvider === 'openai') ? aiProvider : 'anthropic';
    try {
      const { error } = await supabase.rpc('set_my_ai_provider_model', { p_provider: providerParam, p_model: modelId || null });
      if (!error) { setApiKeySaveStatus('saved'); setTimeout(() => setApiKeySaveStatus(s => (s === 'saved' ? 'idle' : s)), 1600); }
    } catch { /* non-fatal */ }
  }, [user?.id, apiKey, aiProvider]);

  // Persist personal key via vault-encrypted RPCs. The `set_my_anthropic_key`
  // RPC validates format (sk-ant- prefix, length) and stores the secret in
  // Supabase Vault — the plaintext value never lives in the profiles table
  // anymore. `clear_my_anthropic_key` deletes both the vault secret and the
  // user_api_keys row.
  //
  // Sentinel detection: the input is pre-populated with the key hint
  // ("sk-ant-...XXXX") on load. If the user hasn't typed a new value, we
  // see the sentinel and skip the save (otherwise the RPC's length check
  // would reject the partial-looking sentinel).
  const isHintSentinel = (val) => typeof val === 'string' && val.includes('...');

  const syncKeyToProfile = useCallback(async (key, provider, uid) => {
    if (!uid) return { ok: false, reason: "no-user" };
    if (isHintSentinel(key)) {
      // Key unchanged (still the masked hint) — but the provider/model may have
      // changed. Persist those without needing the key re-typed, so "the model
      // we choose is the one we get" even when Save is clicked after only
      // switching the model.
      const providerParam = (typeof provider === 'string' && provider.startsWith('claude')) ? 'anthropic'
        : (provider === 'openrouter' || provider === 'deepseek' || provider === 'openai') ? provider : 'anthropic';
      const isCompat = providerParam === 'openrouter' || providerParam === 'deepseek' || providerParam === 'openai';
      const modelParam = (isCompat && aiModel && aiModel.trim()) ? aiModel.trim() : null;
      try {
        const { error } = await supabase.rpc('set_my_ai_provider_model', { p_provider: providerParam, p_model: modelParam });
        if (error) { setApiKeySaveStatus("error"); setApiKeySaveError(error.message || "Update failed"); return { ok: false, reason: error.message }; }
        setApiKeySaveStatus("saved");
        return { ok: true, reason: "provider-model-updated" };
      } catch (e) {
        setApiKeySaveStatus("error"); setApiKeySaveError(e?.message || "Update failed");
        return { ok: false, reason: e?.message };
      }
    }
    setApiKeySaveStatus("saving");
    setApiKeySaveError("");
    try {
      await safeRefreshSession().catch(() => {});
      // Empty input → clear the key.
      if (!key || !key.trim()) {
        const { error } = await supabase.rpc('clear_my_anthropic_key');
        if (error) {
          setApiKeySaveStatus("error");
          setApiKeySaveError(error.message || "Clear failed");
          return { ok: false, reason: error.message };
        }
        setApiKeySaveStatus("saved");
        return { ok: true };
      }
      // Map the UI provider id to the stored provider. claude-* → anthropic
      // (native Claude path); openrouter/deepseek/openai pass through.
      const providerParam = (typeof provider === 'string' && provider.startsWith('claude')) ? 'anthropic'
        : (provider === 'openrouter' || provider === 'deepseek' || provider === 'openai') ? provider
        : 'anthropic';
      const isCompat = providerParam === 'openrouter' || providerParam === 'deepseek' || providerParam === 'openai';
      const modelParam = (isCompat && aiModel && aiModel.trim()) ? aiModel.trim() : null;
      // Set/rotate. RPC errors with a clear message on bad format.
      const { data, error } = await supabase.rpc('set_my_ai_key', { p_provider: providerParam, p_key: key.trim(), p_model: modelParam });
      if (error) {
        setApiKeySaveStatus("error");
        setApiKeySaveError(error.message || "Save failed");
        return { ok: false, reason: error.message };
      }
      // Replace the input with the hint so the actual key isn't sitting in
      // localStorage / React state once it's been persisted.
      if (data?.hint) setApiKey(data.hint);
      // If they saved a key while on a non-BYOK provider (none/gemini), move
      // them to Claude so the key is actually used.
      if (provider === 'none' || provider === 'gemini') setAiProvider('claude-haiku');
      setApiKeySaveStatus("saved");
      return { ok: true };
    } catch (e) {
      setApiKeySaveStatus("error");
      setApiKeySaveError(e?.message || "Save failed");
      return { ok: false, reason: e?.message };
    }
  }, [setApiKey, setAiProvider, aiModel]);

  // Reset the saved/error status when the user edits the input. The actual
  // save is triggered by clicking the explicit Save button (no debounced
  // auto-sync — partial keys would spam the RPC's format validator).
  useEffect(() => {
    setApiKeySaveStatus(prev => prev === "idle" ? prev : "idle");
  }, [apiKey, aiProvider]);

  const updateAppSetting = (key, value) => {
    setAppSettings(prev => {
      const next = { ...prev, [key]: value };
      saveAppSettings(next);
      applyAppSettings(next);
      return next;
    });
  };
  const PROVIDERS = [
    { id: "none", label: "No AI (Manual RP)", desc: "Free — you and Nicole write all characters yourselves", color: "#888" },
    { id: "gemini", label: "Google Gemini", desc: "Free — great quality, no cost", color: "#4285f4" },
    { id: "claude-haiku", label: "Claude Haiku", desc: "~$0.001/msg — fast and cheap", color: "#1abc9c", requiresPersonalKey: true },
    { id: "claude-sonnet", label: "Claude Sonnet", desc: "~$0.01/msg — best quality", color: "#e74c3c", requiresPersonalKey: true },
    { id: "openrouter", label: "OpenRouter", desc: "Your key — ANY model from any company (Claude, GPT, DeepSeek, Llama…)", color: "#8b5cf6", requiresPersonalKey: true },
    { id: "deepseek", label: "DeepSeek", desc: "Your key — very cheap, strong quality", color: "#4d6bfe", requiresPersonalKey: true },
    { id: "openai", label: "OpenAI (GPT)", desc: "Your key — GPT-4o and others", color: "#10a37f", requiresPersonalKey: true },
  ];
  const isAdminUser = user?.id === '6db164e7-0419-470c-a8fe-2ddb899f5578';
  const keyPlaceholder = aiProvider === "gemini" ? "AIzaSy..."
    : aiProvider === "openrouter" ? "sk-or-..."
    : (aiProvider === "deepseek" || aiProvider === "openai") ? "sk-..."
    : "sk-ant-...";
  const keyHint = aiProvider === "gemini" ? "Get a free key at aistudio.google.com/apikey"
    : aiProvider === "openrouter" ? "Get a key at openrouter.ai/keys — one key, every model"
    : aiProvider === "deepseek" ? "Get a key at platform.deepseek.com"
    : aiProvider === "openai" ? "Get a key at platform.openai.com/api-keys"
    : "Get a key at console.anthropic.com";
  const modelPlaceholder = aiProvider === "openrouter" ? "model (optional) — e.g. anthropic/claude-3.5-sonnet"
    : aiProvider === "deepseek" ? "model (optional) — default deepseek-chat"
    : aiProvider === "openai" ? "model (optional) — default gpt-4o"
    : "";
  const showModelField = aiProvider === "openrouter" || aiProvider === "deepseek" || aiProvider === "openai";

  useEffect(() => {
    // Heal-then-reflect: ensurePushSubscription re-subscribes if a deploy
    // dropped the sub, so the toggle shows the true intended state (ON) rather
    // than a transient OFF. Falls back to a plain read if we have no user id.
    if (user?.id) {
      ensurePushSubscription(user.id).then(sub => setPushSubscribed(!!sub));
    } else {
      getPushSubscription().then(sub => setPushSubscribed(!!sub));
    }
  }, [user?.id]);

  const snoozeStatus = getSnoozeStatus(notifPrefs);

  // Toggle style for notification category switches
  const toggleStyle = (enabled) => ({
    width: "36px", height: "20px", borderRadius: "10px", border: "none", cursor: "pointer",
    background: enabled ? t.accentSuccess : t.borderLight, position: "relative", transition: "background 0.2s",
    display: "inline-flex", alignItems: "center", padding: "2px",
  });
  const toggleKnob = (enabled) => ({
    width: "16px", height: "16px", borderRadius: "50%", background: "#fff",
    transition: "transform 0.2s", transform: enabled ? "translateX(16px)" : "translateX(0)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
  });

  const NOTIF_TYPES = [
    { key: "dm_notifications", label: "DM AI Messages", desc: "When a character responds to your DM", icon: "💬" },
    { key: "channel_notifications", label: "Channel Messages", desc: "New messages in server channels", icon: "📢" },
    { key: "story_notifications", label: "Story AI Responses", desc: "When characters respond in your stories", icon: "✨" },
    { key: "phone_notifications", label: "Phone AI Replies", desc: "When characters text you on the phone", icon: "📱" },
    { key: "community_notifications", label: "Community Updates", desc: "Shared stories and announcements", icon: "📖" },
  ];

  // How channel messages arrive — 'instant' or 'batched' (see notifPrefs.js).
  const channelPushMode = notifPrefs.channel_push_mode === "instant" ? "instant" : "batched";

  // Which OpenRouter host serves DeepSeek (user_api_keys.host_mode; read by
  // ai-proxy and dial-reply through get_my_ai_config). Probe 2026-10-06:
  // Alibaba reuses the cached prompt prefix on a new turn, SiliconFlow does
  // not; DeepInfra is ~6× cheaper per token than Alibaba at fp8.
  const HOST_MODE_OPTIONS = [
    { value: "auto", label: "Auto (recommended)", desc: "Alibaba first, SiliconFlow only while Alibaba is down. Alibaba reuses the cached prompt between turns; SiliconFlow does not." },
    { value: "alibaba", label: "Alibaba only", desc: "Never pays the uncached rate, but replies fail for as long as Alibaba is unavailable (it was out for six straight days in late September)." },
    { value: "cost", label: "Cost first (DeepInfra)", desc: "About 6× cheaper per token than Alibaba, same fp8 precision as SiliconFlow, every parameter supported. Untested in play — watch the writing." },
  ];
  const [hostMode, setHostMode] = useState("auto");
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    supabase.rpc("get_my_ai_status").then(({ data }) => {
      if (!cancelled && data?.host_mode) setHostMode(String(data.host_mode));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);
  const saveHostMode = async (mode) => {
    const before = hostMode;
    setHostMode(mode);
    const { error } = await supabase.rpc("set_my_host_mode", { p_mode: mode });
    if (error) { setHostMode(before); alert(`Couldn't save the host choice: ${error.message}`); }
  };

  const SNOOZE_OPTIONS = [
    { hours: 1, label: "1 hour" },
    { hours: 2, label: "2 hours" },
    { hours: 4, label: "4 hours" },
    { hours: 8, label: "8 hours" },
    { hours: 24, label: "24 hours" },
  ];

  const body = (
    <>

        {globalApiEnabled && !apiKey && (
          <div style={{ marginBottom: "14px", padding: "10px 14px", background: "rgba(87, 242, 135, 0.1)", border: "1px solid rgba(87, 242, 135, 0.3)", borderRadius: "8px", fontSize: "13px", color: "#57f287" }}>
            ✅ Shared API is active — AI responses are working! You're using the admin's {globalApiProvider === "gemini" ? "Gemini" : "Claude"} key. You can optionally add your own key below to use a different provider.
          </div>
        )}

        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "8px" }}>AI Provider {globalApiEnabled && !apiKey ? "(optional — shared key active)" : ""}</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {PROVIDERS.map((p) => {
              const locked = p.requiresPersonalKey && !apiKey && !isAdminUser;
              return (
                <button
                  key={p.id}
                  onClick={() => {
                    if (locked) return;
                    setAiProvider(p.id);
                  }}
                  disabled={locked}
                  title={locked ? "Add your own API key below to use this model — premium models require personal billing" : ""}
                  style={{
                    background: aiProvider === p.id ? `${p.color}33` : t.bgHover,
                    border: `2px solid ${aiProvider === p.id ? p.color : "#2a1a1a"}`,
                    borderRadius: "10px", padding: "10px", color: "var(--ds-ink-hi, #fff)",
                    cursor: locked ? "not-allowed" : "pointer",
                    textAlign: "left", transition: "all 0.2s",
                    opacity: locked ? 0.45 : 1,
                    position: "relative",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "2px" }}>
                    {p.label} {locked && <span style={{ fontSize: "11px" }}>🔒</span>}
                  </div>
                  <div style={{ fontSize: "10px", color: "#aaa" }}>
                    {locked ? "Requires your own API key" : p.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Always visible — selecting "No AI" or Gemini still lets the user
            paste their key, which then unlocks Haiku/Sonnet automatically. */}
        {(
          <div style={{ marginBottom: "14px" }}>
            <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "4px" }}>
              API Key {!apiKey && <span style={{ fontSize: "11px", color: t.textMuted, fontWeight: 400 }}>— add yours to unlock Claude</span>}
            </label>
            <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
              <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder={keyPlaceholder} type="password" />
              <button
                onClick={() => user?.id && syncKeyToProfile(apiKey, aiProvider, user.id)}
                disabled={!user?.id || apiKeySaveStatus === "saving"}
                style={{
                  background: apiKeySaveStatus === "saved" ? t.accentSuccess : t.accentGold,
                  border: "none", borderRadius: "8px", color: "#000",
                  padding: "0 14px", fontSize: "12px", fontWeight: 600,
                  cursor: user?.id ? "pointer" : "not-allowed",
                  fontFamily: "'Cinzel', serif", letterSpacing: "1px",
                  whiteSpace: "nowrap",
                }}
              >
                {apiKeySaveStatus === "saving" ? "Saving…" : apiKeySaveStatus === "saved" ? "✓ Saved" : "Save"}
              </button>
            </div>
            {showModelField && (aiProvider === "openrouter" ? (
              <div style={{ position: "relative", marginTop: "6px" }}>
                <input
                  value={aiModel}
                  onChange={(e) => { setAiModel(e.target.value); try { localStorage.setItem("sixguys_aiModel", e.target.value); } catch {}; setModelDropdownOpen(true); }}
                  onFocus={() => setModelDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setModelDropdownOpen(false), 160)}
                  style={{ ...inputStyle, width: "100%" }}
                  placeholder={orModels.length ? `Search ${orModels.length} models… (blank = default)` : "model slug — list loading…"}
                />
                {modelDropdownOpen && orModels.length > 0 && (() => {
                  const q = (aiModel || "").toLowerCase();
                  const matches = orModels.filter(m => !q || m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)).slice(0, 80);
                  return (
                    <div style={{ position: "absolute", zIndex: 60, top: "100%", left: 0, right: 0, maxHeight: "240px", overflowY: "auto", background: t.bgSecondary || "#181219", border: `1px solid ${t.borderMedium || "#372b39"}`, borderRadius: "8px", marginTop: "4px", boxShadow: "0 10px 30px rgba(0,0,0,0.55)" }}>
                      {matches.length === 0 ? (
                        <div style={{ padding: "8px 10px", fontSize: "12px", color: t.textMuted }}>No match — you can still type any slug</div>
                      ) : matches.map(m => (
                        <button
                          key={m.id}
                          onMouseDown={(e) => { e.preventDefault(); setAiModel(m.id); try { localStorage.setItem("sixguys_aiModel", m.id); } catch {}; setModelDropdownOpen(false); persistModelSelection(m.id); }}
                          style={{ display: "block", width: "100%", textAlign: "left", background: aiModel === m.id ? "rgba(212,175,55,0.14)" : "transparent", border: "none", borderBottom: "1px solid var(--ds-line-soft)", color: t.textPrimary, padding: "7px 10px", fontSize: "12px", cursor: "pointer" }}
                        >
                          <div style={{ fontWeight: 600 }}>{m.name}</div>
                          <div style={{ fontSize: "10px", color: t.textMuted }}>{m.id}</div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <input
                value={aiModel}
                onChange={(e) => { setAiModel(e.target.value); try { localStorage.setItem("sixguys_aiModel", e.target.value); } catch {} }}
                style={{ ...inputStyle, width: "100%", marginTop: "6px" }}
                placeholder={modelPlaceholder}
              />
            ))}
            {aiProvider === "openrouter" && /deepseek/i.test(aiModel || "") && (
              <div style={{ marginTop: "10px" }}>
                <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "6px" }}>DeepSeek host</label>
                {HOST_MODE_OPTIONS.map((o) => (
                  <label key={o.value} style={{ display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "12px", color: t.textPrimary, marginBottom: "6px", cursor: "pointer" }}>
                    <input type="radio" name="deepseek-host" checked={hostMode === o.value} onChange={() => saveHostMode(o.value)} style={{ marginTop: "2px", accentColor: "var(--ds-accent, #c62828)" }} />
                    <span><strong>{o.label}</strong> — <span style={{ color: t.textFaint }}>{o.desc}</span></span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "3px", gap: "8px" }}>
              <p style={{ fontSize: "11px", color: "#555", margin: 0 }}>{keyHint}</p>
              {apiKeySaveStatus === "saved" && (
                <span style={{ fontSize: "11px", color: t.accentSuccess }}>✓ Saved to your profile — AI calls now bill to your key</span>
              )}
              {apiKeySaveStatus === "saving" && (
                <span style={{ fontSize: "11px", color: t.textMuted }}>Saving…</span>
              )}
              {apiKeySaveStatus === "error" && (
                <span style={{ fontSize: "11px", color: t.accentDanger }}>⚠ Save failed: {apiKeySaveError || "unknown error"}</span>
              )}
            </div>
          </div>
        )}

        <div style={{ marginBottom: "14px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "4px" }}>Session Name</label>
          <input value={sessionName} onChange={(e) => setSessionName(e.target.value)} style={inputStyle} placeholder="Name this session..." />
          {/* Story-links opener — only shown when a story is active. Opens
              the StoryLinksManager modal where the user can link this
              story to others for voice continuity. */}
          {hasActiveStory && onOpenStoryLinks && (
            <button
              onClick={onOpenStoryLinks}
              style={{
                marginTop: 10,
                width: '100%',
                padding: '8px 12px',
                background: 'transparent',
                color: '#d1b3ff',
                border: '1px solid #d1b3ff66',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                textAlign: 'left',
              }}
              title="Link this story to others — voice samples can pull from linked stories' history"
            >
              🔗 Manage story links
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: '#888' }}>
                — voice continuity across stories
              </span>
            </button>
          )}
        </div>

        {/* ── Typing (Haley 2026-08-29: "so it's the send button instead
            cause I keep doing that") — per-device, so one player's flip never
            changes the other's muscle memory. ── */}
        <div className="bq-leaf" style={{ marginBottom: "14px", padding: "12px", background: t.bgHover, borderRadius: "8px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "8px" }}>Typing</label>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "13px", color: t.textPrimary }}>
            <input
              type="checkbox"
              checked={enterSendPref}
              onChange={(e) => { setEnterSendPref(e.target.checked); setEnterSends(e.target.checked); }}
              style={{ width: "16px", height: "16px", accentColor: "var(--ds-accent, #c62828)" }}
            />
            Enter key sends the message
          </label>
          <div style={{ fontSize: "11px", color: t.textFaint, marginTop: "6px", lineHeight: 1.5 }}>
            Off: Enter makes a new line and only the send button sends (Cmd/Ctrl+Enter still sends). This device only.
          </div>
        </div>

        {/* ── Push Notifications ── */}
        <div className="bq-leaf" style={{ marginBottom: "14px", padding: "12px", background: t.bgHover, borderRadius: "8px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "8px" }}>Push Notifications</label>
          {!isPushSupported() ? (
            <div style={{ fontSize: "12px", color: t.accentGold, lineHeight: "1.5" }}>
              {/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.navigator.standalone
                ? 'To get notifications on iPhone: tap the Share button → "Add to Home Screen" → open the app from your home screen'
                : 'Push notifications not supported in this browser'}
            </div>
          ) : notifPermission === "denied" ? (
            <div style={{ fontSize: "12px", color: "#e74c3c" }}>Notifications blocked — enable in browser settings</div>
          ) : pushSubscribed ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ fontSize: "12px", color: t.accentSuccess }}>Notifications enabled</div>
              <button onClick={async () => {
                setPushLoading(true);
                await unsubscribeFromPush(user?.id);
                setPushSubscribed(false);
                setPushLoading(false);
              }} disabled={pushLoading} style={{ background: "rgba(237,66,69,0.2)", border: "1px solid rgba(237,66,69,0.3)", color: "#ed4245", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer" }}>
                {pushLoading ? "..." : "Disable"}
              </button>
            </div>
          ) : (
            <button onClick={async () => {
              setPushLoading(true);
              try {
                await registerServiceWorker();
                await subscribeToPush(user?.id);
                setPushSubscribed(true);
                setNotifPermission("granted");
              } catch (err) {
                console.error("Push subscribe error:", err);
                if (err.message?.includes("denied")) setNotifPermission("denied");
              }
              setPushLoading(false);
            }} disabled={pushLoading} style={{ background: t.accentDanger, border: "none", color: "#fff", borderRadius: "8px", padding: "8px 14px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
              {pushLoading ? "Setting up..." : "Enable Notifications"}
            </button>
          )}
        </div>

        {/* ── Notification Preferences ── */}
        {/* v423: web push OR native APNs — the block was gated on the web subscription alone, so the iOS app never showed it */}
        {(pushSubscribed || nativePushActive) && (
          <div style={{ marginBottom: "14px", padding: "12px", background: t.bgHover, borderRadius: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <label style={{ fontSize: "13px", color: "#aaa" }}>Notification Types</label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "11px", color: notifPrefs.global_enabled ? t.accentSuccess : t.textMuted }}>
                  {notifPrefs.global_enabled ? "All on" : "All off"}
                </span>
                <button
                  onClick={() => updateNotifPref({ global_enabled: !notifPrefs.global_enabled })}
                  style={toggleStyle(notifPrefs.global_enabled)}
                  aria-label="Toggle all notifications"
                >
                  <div style={toggleKnob(notifPrefs.global_enabled)} />
                </button>
              </div>
            </div>

            <div style={{ opacity: notifPrefs.global_enabled ? 1 : 0.4, pointerEvents: notifPrefs.global_enabled ? "auto" : "none", transition: "opacity 0.2s" }}>
              {NOTIF_TYPES.map(({ key, label, desc, icon }) => (
                <div key={key} style={{ borderTop: "1px solid var(--ds-line-soft)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "14px" }}>{icon}</span>
                      <div>
                        <div style={{ fontSize: "12px", color: t.textPrimary, fontWeight: 500 }}>{label}</div>
                        <div style={{ fontSize: "10px", color: "#9a8585" }}>{desc}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => updateNotifPref({ [key]: !notifPrefs[key] })}
                      style={toggleStyle(notifPrefs[key])}
                      aria-label={`Toggle ${label}`}
                    >
                      <div style={toggleKnob(notifPrefs[key])} />
                    </button>
                  </div>
                  {/* ── How channel messages arrive ── (2026-10-05, Nicole: "just
                      normal push notifs in each channel"). One choice, read by
                      send-push, check-unread and channel-reminder. */}
                  {key === "channel_notifications" && notifPrefs.channel_notifications && (
                    <div style={{ padding: "0 0 10px 22px" }}>
                      <div role="radiogroup" aria-label="How channel messages arrive" style={{ display: "inline-flex", gap: "2px", padding: "2px", borderRadius: "8px", border: `1px solid ${t.borderLight}` }}>
                        {CHANNEL_PUSH_MODES.map((m) => {
                          const on = channelPushMode === m.value;
                          return (
                            <button
                              key={m.value}
                              role="radio"
                              aria-checked={on}
                              onClick={() => { if (!on) updateNotifPref({ channel_push_mode: m.value }); }}
                              style={{ border: "none", cursor: "pointer", borderRadius: "6px", padding: "7px 12px", fontSize: "11px", fontWeight: 600, lineHeight: 1.4, background: on ? t.accentSuccess : "transparent", color: on ? readableOn(t.accentSuccess) : t.textPrimary, transition: "background 0.2s" }}
                            >
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: "10px", color: "#9a8585", marginTop: "6px", lineHeight: 1.5 }}>
                        {CHANNEL_PUSH_MODES.find((m) => m.value === channelPushMode)?.desc}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* ── Random character calls ── (independent of push prefs above;
                gates the in-app incoming-call ring for staged/random calls) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--ds-line-soft)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "14px" }}>📞</span>
                <div>
                  <div style={{ fontSize: "12px", color: t.textPrimary, fontWeight: 500 }}>Let characters call me</div>
                  <div style={{ fontSize: "10px", color: "#9a8585" }}>All incoming calls from characters {randomCallsEnabled ? "on" : "off"}</div>
                </div>
              </div>
              <button onClick={toggleRandomCalls} style={toggleStyle(randomCallsEnabled)} aria-label="Toggle random character calls">
                <div style={toggleKnob(randomCallsEnabled)} />
              </button>
            </div>

            {/* ── Who may message me first ── v421 (Haley, 2026-10-19): one
                SYNCED choice for every character-initiated DM — the cold
                introductions AND the warm follow-ups the server sends. It
                replaces the per-device switch Nicole could not find. */}
            <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--ds-line-soft)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ fontSize: "14px" }}>💌</span>
                <div>
                  <div style={{ fontSize: "12px", color: t.textPrimary, fontWeight: 500 }}>Characters may message me first</div>
                  <div style={{ fontSize: "10px", color: "#9a8585" }}>Introductions from characters you have not met, and texts from the ones you talk to. Synced to every device.</div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {CHARACTER_DM_MODES.map(m => (
                  <label key={m.value} style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", fontSize: "12px", color: t.textPrimary }}>
                    <input type="radio" name="character-dm-mode" value={m.value} checked={(notifPrefs?.character_dm_mode || 'all') === m.value}
                      onChange={() => updateNotifPref?.({ character_dm_mode: m.value })} style={{ marginTop: "2px" }} aria-label={`Characters may message me first: ${m.label}`} />
                    <span><span style={{ fontWeight: 600 }}>{m.label}</span> <span style={{ color: "#9a8585" }}>— {m.desc}</span></span>
                  </label>
                ))}
              </div>
              {['selected', 'all_except'].includes(notifPrefs?.character_dm_mode || 'all') && (
                <div style={{ marginTop: "8px" }}>
                  <input type="text" value={dmPickSearch} onChange={e => setDmPickSearch(e.target.value)} placeholder="Find a character…" aria-label="Find a character to allow"
                    style={{ width: "100%", padding: "6px 8px", borderRadius: "8px", border: "1px solid var(--ds-line)", background: "var(--ds-input, #1a1012)", color: t.textPrimary, fontSize: "12px", marginBottom: "6px", boxSizing: "border-box" }} />
                  <div style={{ maxHeight: "180px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px", border: "1px solid var(--ds-line-soft)", borderRadius: "8px", padding: "6px" }}>
                    {CHARACTERS.filter(c => c?.controlledBy === 'AI' && (!dmPickSearch.trim() || String(c.name || '').toLowerCase().includes(dmPickSearch.trim().toLowerCase()))).slice(0, 60).map(c => {
                      // v423: the box means "may message me first" in every mode;
                      // one pure helper decides the write (notifPrefs.js).
                      const on = characterMayDM(notifPrefs, c.id);
                      return (
                        <label key={c.id} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: t.textPrimary, cursor: "pointer", padding: "2px 4px" }}>
                          <input type="checkbox" checked={on} aria-label={`${c.name} may message me first`}
                            onChange={() => updateNotifPref?.(nextCharacterDmPrefs(notifPrefs, c.id))} />
                          <span>{c.avatar || '🎭'} {c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: "10px", color: "#9a8585", marginTop: "4px" }}>{(notifPrefs?.character_dm_mode || 'all') === 'all_except' ? `${(notifPrefs?.character_dm_blocked || []).length} switched off` : `${(notifPrefs?.character_dm_allowed || []).length} allowed`}. The 💬 switch on a DM row does the same thing.</div>
                </div>
              )}
            </div>

            {/* ── Snooze ── */}
            <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--ds-line-soft)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <div>
                  <div style={{ fontSize: "12px", color: t.textPrimary, fontWeight: 500 }}>Snooze Notifications</div>
                  {snoozeStatus.active && (
                    <div style={{ fontSize: "10px", color: t.accentGold, marginTop: "2px" }}>
                      Snoozed — {snoozeStatus.remaining} remaining
                    </div>
                  )}
                </div>
                {snoozeStatus.active ? (
                  <button
                    onClick={() => handleSnooze(0)}
                    style={{ background: "rgba(74,158,110,0.2)", border: "1px solid rgba(74,158,110,0.3)", color: t.accentSuccess, borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer" }}
                  >
                    Unsnooze
                  </button>
                ) : null}
              </div>
              {!snoozeStatus.active && (
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {SNOOZE_OPTIONS.map(({ hours, label }) => (
                    <button
                      key={hours}
                      onClick={() => handleSnooze(hours)}
                      style={{
                        background: "var(--ds-accent-a1a, rgba(198,40,40,0.1))", border: "1px solid var(--ds-accent-a33, rgba(198,40,40,0.2))",
                        color: t.accentGold, borderRadius: "6px", padding: "4px 10px", fontSize: "11px",
                        cursor: "pointer", transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-accent-a40, rgba(198,40,40,0.25))"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "var(--ds-accent-a1a, rgba(198,40,40,0.1))"; }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Text Speed ── */}
        <div className="bq-leaf" style={{ marginBottom: "14px", padding: "12px", background: t.bgHover, borderRadius: "8px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "10px" }}>✍️ Text Speed</label>
          <div style={{ display: "flex", gap: "6px" }}>
            {[["slow","Slow"],["medium","Medium"],["fast","Fast"],["instant","Instant"]].map(([val, label]) => (
              <button key={val} onClick={() => updateAppSetting("textSpeed", val)} style={{
                flex: 1, padding: "8px 4px", borderRadius: "7px", border: `2px solid ${appSettings.textSpeed === val ? t.accentGold : "#2a1a1a"}`,
                background: appSettings.textSpeed === val ? `${t.accentGold}22` : t.bgBase,
                color: appSettings.textSpeed === val ? t.accentGold : "#888", fontSize: "12px", fontWeight: appSettings.textSpeed === val ? 700 : 400, cursor: "pointer",
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* ── Volume Controls ── */}
        <div className="bq-leaf" style={{ marginBottom: "14px", padding: "12px", background: t.bgHover, borderRadius: "8px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "12px" }}>🔊 Volume</label>
          {[["bgmVolume","🎵 Background Music"],["sfxVolume","✨ Sound Effects"],["voiceVolume","🎤 Voice"]].map(([key, label]) => (
            <div key={key} style={{ marginBottom: "10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ fontSize: "12px", color: t.textSecondary }}>{label}</span>
                <span style={{ fontSize: "11px", color: t.textMuted }}>{appSettings[key]}%</span>
              </div>
              <input
                type="range" min={0} max={100} value={appSettings[key]}
                onChange={e => updateAppSetting(key, Number(e.target.value))}
                style={{ width: "100%", accentColor: t.accentGold, cursor: "pointer" }}
              />
            </div>
          ))}
        </div>

        {/* ── Accessibility ── */}
        <div className="bq-leaf" style={{ marginBottom: "14px", padding: "12px", background: t.bgHover, borderRadius: "8px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "10px" }}>♿ Accessibility</label>
          {[["reducedMotion","Reduced Motion","Less animation throughout the app"],["highContrast","High Contrast","Stronger borders and text contrast"]].map(([key, label, desc]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--ds-line-soft)" }}>
              <div>
                <div style={{ fontSize: "12px", color: t.textPrimary, fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: "10px", color: "#9a8585" }}>{desc}</div>
              </div>
              <button onClick={() => updateAppSetting(key, !appSettings[key])} style={toggleStyle(appSettings[key])} aria-label={`Toggle ${label}`}>
                <div style={toggleKnob(appSettings[key])} />
              </button>
            </div>
          ))}
          <div style={{ paddingTop: "10px", borderTop: "1px solid var(--ds-line-soft)" }}>
            <div style={{ fontSize: "12px", color: t.textPrimary, fontWeight: 500, marginBottom: "8px" }}>Font Size</div>
            <div style={{ display: "flex", gap: "6px" }}>
              {[["small","Small"],["medium","Medium"],["large","Large"]].map(([val, _label]) => (
                <button key={val} onClick={() => updateAppSetting("fontSize", val)} style={{
                  flex: 1, padding: "7px 4px", borderRadius: "7px", border: `2px solid ${appSettings.fontSize === val ? t.accentPrimary : "#2a1a1a"}`,
                  background: appSettings.fontSize === val ? `${t.accentPrimary}22` : t.bgBase,
                  color: appSettings.fontSize === val ? t.accentPrimary : "#888", fontSize: val === "small" ? "11px" : val === "large" ? "14px" : "12px", fontWeight: appSettings.fontSize === val ? 700 : 400, cursor: "pointer",
                }}>A{val === "large" ? "A" : ""}</button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Content Level ── */}
        <div style={{ marginBottom: "14px", padding: "12px", background: t.bgHover, borderRadius: "8px" }}>
          <label style={{ fontSize: "13px", color: "#aaa", display: "block", marginBottom: "4px" }}>🔞 Content Level</label>
          <p style={{ fontSize: "11px", color: "#555", margin: "0 0 10px" }}>Controls how mature themes are handled in AI responses.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {[
              ["all_ages","All Ages 🌟","Keep everything family-friendly. No violence, romance, or dark themes."],
              ["teen","Teen 🌙","Allows mild romance, tension, and mild conflict. Default."],
              ["mature","Mature 🩸","Full dark themes, complex conflict, descriptive romance. 18+ only."],
            ].map(([val, label, desc]) => (
              <button key={val} onClick={() => updateAppSetting("contentLevel", val)} style={{
                textAlign: "left", padding: "10px 12px", borderRadius: "8px",
                border: `2px solid ${appSettings.contentLevel === val ? (val === "mature" ? "#e74c3c" : val === "teen" ? t.accentPrimary : t.accentGold) : "#2a1a1a"}`,
                background: appSettings.contentLevel === val ? `${val === "mature" ? "#e74c3c" : val === "teen" ? t.accentPrimary : t.accentGold}18` : t.bgBase,
                color: "var(--ds-ink-hi, #fff)", cursor: "pointer", transition: "all 0.15s",
              }}>
                <div style={{ fontWeight: 700, fontSize: "13px" }}>{label}</div>
                <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>{desc}</div>
              </button>
            ))}
          </div>
        </div>

        {!embedded && <button onClick={onClose} style={{ ...btnPrimary, width: "100%" }}>Done</button>}
    </>
  );

  if (embedded) return body;
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalContent, maxHeight: "85vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <h2 className="bq-title" style={{ margin: "0 0 16px", fontSize: "20px" }}>Settings</h2>
        {body}
      </div>
    </div>
  );
}

// ─── iOS INSTALL BANNER ─────────────────────────────────────────────────────
function IOSInstallBanner({ onDismiss }) {
  const { theme: t } = useContext(ThemeContext);
  return (
    <div style={{
      background: "linear-gradient(135deg, var(--ds-surface-2, #1a0f0f), var(--ds-surface-1, #120e0e))", border: "1px solid var(--ds-accent-a33, #c6282833)",
      borderRadius: "12px", margin: "8px 12px", padding: "14px 16px",
      animation: "fadeSlideIn 0.3s ease-out", position: "relative",
    }}>
      <button onClick={onDismiss} style={{
        position: "absolute", top: "8px", right: "10px", background: "none", border: "none",
        color: t.textFaint, cursor: "pointer", fontSize: "16px", padding: "2px 6px",
      }}>×</button>
      <div style={{ fontSize: "14px", fontWeight: 700, color: t.accentGold, marginBottom: "8px", fontFamily: "'Cinzel', serif", letterSpacing: "1px" }}>
        Get Notifications
      </div>
      <div style={{ fontSize: "12px", color: "#b5a5a5", lineHeight: "1.6", marginBottom: "10px" }}>
        Install MFML as an app to get push notifications when someone messages you:
      </div>
      <div style={{ fontSize: "12px", color: t.textPrimary, lineHeight: "1.8", paddingLeft: "4px" }}>
        <div style={{ marginBottom: "4px" }}>1. Tap the <span style={{ display: "inline-block", background: "var(--ds-accent-a33, #c6282833)", borderRadius: "4px", padding: "1px 6px", color: t.accentGold, fontWeight: 600 }}>Share</span> button (the square with arrow at the bottom of Safari)</div>
        <div style={{ marginBottom: "4px" }}>2. Scroll down and tap <span style={{ display: "inline-block", background: "var(--ds-accent-a33, #c6282833)", borderRadius: "4px", padding: "1px 6px", color: t.accentGold, fontWeight: 600 }}>Add to Home Screen</span></div>
        <div>3. Open MFML from your Home Screen — that's it!</div>
      </div>
      <div style={{ fontSize: "11px", color: t.textMuted, marginTop: "10px", fontStyle: "italic" }}>
        It'll look and feel like a real app, full screen and everything.
      </div>
    </div>
  );
}

// ─── MAIN NAVIGATION BAR ─────────────────────────────────────────────────────
// ─── CUSTOM CONFIRM MODAL ──────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onCancel }) {
  const { theme: t } = useContext(ThemeContext);
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onCancel]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px) saturate(1.15)", WebkitBackdropFilter: "blur(8px) saturate(1.15)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={onCancel} role="dialog" aria-modal="true" aria-label="Confirmation">
      <div style={{ background: t.bgBase, borderRadius: "12px", padding: "24px", maxWidth: "380px", width: "90%", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
        <p style={{ color: t.textPrimary, fontSize: "15px", margin: "0 0 20px", lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
          <button onClick={onCancel} style={{ background: "#2a1a1a", border: "none", color: t.textPrimary, padding: "10px 24px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}>Cancel</button>
          <button onClick={onConfirm} autoFocus style={{ background: "#ed4245", border: "none", color: "var(--ds-ink-hi, #fff)", padding: "10px 24px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function MainNav({ mainTab, setMainTab, currentPlayer, userProfile, onLogout, onAdmin, onCharEditor, onEncyclopedia, onProfile, onSettings, isMobile, onlineProfiles, showUserList, setShowUserList, user, unreadNotifCount, onNotifToggle, notifPrefs, showSnoozeMenu, setShowSnoozeMenu, handleSnooze, showNotifDropdown, setShowNotifDropdown, liveNotifications, dismissNotification, dismissAllNotifications, handleNotifClick, unreadTabs, setDmTargetUserId, setDmStartNew, storyChannelUnread, showStoryNotifPanel, setShowStoryNotifPanel, storyNotifMessages, onStoryNotifClick, onClearStoryNotifs, musicPlayerVisible, onToggleMusic, phoneOpen, onTogglePhone, onShowFateCard, onOpenStoryLinks, onProfileTab, onOpenCrosstalk, fateDrawnToday: _fateDrawnToday = false, friendsList = [], friendIncoming = [], friendOutgoing = [], refreshFriends, onOpenIncognito: _onOpenIncognito, setViewingProfile }) {
  const { theme: t } = useContext(ThemeContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userListTab, setUserListTab] = useState("all"); // "all" | "friends" — local to this dropdown
  const [showIncogPicker, setShowIncogPicker] = useState(false); // mode picker for incognito popup
  // Role-based access: admin and editor roles get private channel access
  // Uses profile role + localStorage cache for instant access before profile loads
  const PRIVATE_ROLES = ["admin", "editor"];
  // The loaded profile is the truth. The cached role only bridges the moment
  // before it loads (instant tab on reload for the writers); once a profile
  // is in hand a stale cache can no longer promote a member (2026-10-19 audit).
  const isKnownPlayer = userProfile
    ? PRIVATE_ROLES.includes(userProfile.role)
    : (() => { try { return PRIVATE_ROLES.includes(localStorage.getItem("mfml_user_role")); } catch { return false; } })();

  // ── eat speak rp repeat "your turn" indicator ───────────────────────────────
  // Visible only for Nicole & Haley. Lights up when the last message in that
  // channel was NOT sent by the current user (meaning they owe a reply).
  const EAT_SPEAK_CHANNEL_ID = '4d7fbae5-db3a-4412-95e3-53a685c8ba2d';
  const EAT_SPEAK_PLAYER_IDS = new Set([
    '8420ef71-0898-41e0-ac6c-6916969b82f2', // Nicole
    '6db164e7-0419-470c-a8fe-2ddb899f5578', // Haley
  ]);
  const isEatSpeakUser = user?.id && EAT_SPEAK_PLAYER_IDS.has(user.id);
  const [eatSpeakNeedsReply, setEatSpeakNeedsReply] = useState(false);

  useEffect(() => {
    if (!isEatSpeakUser) return;
    let active = true;

    const checkLastMessage = async () => {
      const { data } = await supabase
        .from('channel_messages')
        .select('user_id')
        .eq('channel_id', EAT_SPEAK_CHANNEL_ID)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      if (!active || !data) return;
      // Needs reply = last message was from the OTHER player (not me)
      setEatSpeakNeedsReply(data.user_id !== user.id);
    };

    checkLastMessage();

    // Live: update whenever a new message lands in this channel
    const sub = supabase
      .channel(`eat-speak-turn-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'channel_messages',
        filter: `channel_id=eq.${EAT_SPEAK_CHANNEL_ID}`,
      }, (payload) => {
        if (!active) return;
        // I just sent → no longer need to reply; they sent → I need to reply
        setEatSpeakNeedsReply(payload.new.user_id !== user.id);
      })
      .subscribe();

    return () => { active = false; supabase.removeChannel(sub); };
  }, [isEatSpeakUser, user?.id]);
  // ────────────────────────────────────────────────────────────────────────────
  // Staleness guard now lives in ./presence.js so every surface (friends list,
  // DM header, channel dots, admin) derives "online" identically.
  const onlineCount = (onlineProfiles || []).filter(p => p?.id && isUserOnline(p)).length;
  // Below ~1500px the full tab labels collide (nowrap text inside shrinking
  // flex buttons) — switch to the short labels. Captured once at mount, same
  // pattern as isMobile throughout the codebase.
  const [useShortTabLabels] = useState(() => window.innerWidth < 1500);
  const tabs = [
    { id: "rp",        label: "RP",             icon: "⚔️",  short: "RP" },
    { id: "dm",        label: "DMs",            icon: "💬",  short: "Messages" },
    { id: "phones",    label: "Dial My Fate",   icon: "🎲",  short: "Fate" },
    // "Our Channels" — only visible to Haley & Nicole
    ...(isKnownPlayer ? [{ id: "channels",  label: "Our Channels",    icon: "💙",  short: "Our" }] : []),
    { id: "community", label: "Community Chat", icon: "🌐",  short: "Community" },
    { id: "stories",   label: "Stories",        icon: "📚",  short: "Stories" },
    { id: "search",    label: "Search",         icon: "🔍",  short: "Search" },
  ];
  // Mobile bottom tab bar: the 5 most-used tabs; rest go in hamburger
  const primaryTabIds = isKnownPlayer
    ? ["rp", "dm", "channels", "community", "stories"]
    : ["rp", "dm", "community", "stories", "search"];
  const _primaryTabs = primaryTabIds.map(id => tabs.find(t => t.id === id)).filter(Boolean);
  const isAdmin = userProfile?.role === "admin";
  const canEditChars = isAdmin || userProfile?.role === "editor";

  // ── FEATURE COMPASS ──
  // The one place that finds everything. Every feature with plain-language
  // keywords so "theme" / "colors" / "api key" / "call" all land right.
  const [compassOpen, setCompassOpen] = useState(false);
  // Director's Room — Haley's private GM studio. Exact-uuid gate, not role:
  // "only me" is the requirement. Renders nothing for anyone else.
  const [directorOpen, setDirectorOpen] = useState(false);
  const isDirector = user?.id === '6db164e7-0419-470c-a8fe-2ddb899f5578';
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") { e.preventDefault(); setCompassOpen(o => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const compassFeatures = React.useMemo(() => {
    const go = (tab) => () => setMainTab(tab);
    return [
      { id: "rp", icon: "\u2694\uFE0F", name: "RP Home", desc: "Roleplay scenes with the characters", keywords: "roleplay play scene home main adventure", group: "Places", run: go("rp") },
      { id: "dm", icon: "\uD83D\uDCAC", name: "Messages (DMs)", desc: "Direct messages with characters and each other", keywords: "dm chat text message inbox talk convo", group: "Places", run: go("dm") },
      { id: "phones", icon: "\uD83C\uDFB2", name: "Dial My Fate", desc: "The Fate phone \u2014 calls, feeds, character texting", keywords: "phone call fate dial feed social ring voicemail", group: "Places", run: go("phones") },
      ...(isKnownPlayer ? [{ id: "channels", icon: "\uD83D\uDC99", name: "Our Channels", desc: "Private RP rooms for the two of you", keywords: "private rooms shared rp long form story channels together", group: "Places", run: go("channels") }] : []),
      { id: "community", icon: "\uD83C\uDF10", name: "Community Chat", desc: "Public channels with ambient character chatter", keywords: "public chat channels community general", group: "Places", run: go("community") },
      { id: "stories", icon: "\uD83D\uDCDA", name: "Stories", desc: "Written stories, arcs, and the library", keywords: "library book read write lore archive", group: "Places", run: go("stories") },
      { id: "encyclopedia", icon: "\uD83D\uDCD6", name: "Encyclopedia", desc: "Browse, search and edit the universe wiki", keywords: "encyclopedia lore wiki dictionary species places groups edit", group: "Places", run: () => onEncyclopedia?.() },
      { id: "search", icon: "\uD83D\uDD0D", name: "Search & Ask AI", desc: "Search everything, or ask grounded questions", keywords: "find lookup ask ai question history", group: "Places", run: go("search") },
      { id: "fate-card", icon: "\uD83C\uDCCF", name: "Daily Fate Card", desc: "Draw today\u2019s fate card", keywords: "draw daily tarot luck card fortune", group: "Things to do", run: () => onShowFateCard?.() },
      ...(onOpenCrosstalk ? [{ id: "crosstalk", icon: "\uD83D\uDCF1", name: "Characters Texting Each Other", desc: "Watch two characters text \u2014 you pick the pair", keywords: "crosstalk gossip texting watch pair conversation between", group: "Things to do", run: () => onOpenCrosstalk() }] : []),
      { id: "music", icon: "\uD83C\uDFB5", name: "Music Player", desc: "Toggle the ambient music player", keywords: "song audio sound ambience playlist volume", group: "Things to do", run: () => onToggleMusic?.() },
      { id: "phone-overlay", icon: "\uD83D\uDCF2", name: "Pocket Phone", desc: "Open the in-app phone overlay", keywords: "popup phone overlay texts pocket", group: "Things to do", run: () => onTogglePhone?.() },
      ...(onOpenStoryLinks ? [{ id: "story-links", icon: "\uD83D\uDD17", name: "Story Links", desc: "Link DMs and stories together", keywords: "link connect thread continuity bridge", group: "Things to do", run: () => onOpenStoryLinks() }] : []),
      { id: "theme", icon: "\uD83C\uDFA8", name: "Theme & Appearance", desc: "Light & dark themes, colors, accents \u2014 incl. Porcelain Rose", keywords: "theme dark light mode color colors appearance skin porcelain daybreak accent look style", group: "Look & feel", run: () => onProfileTab?.("appearance") },
      { id: "profile", icon: "\uD83D\uDC64", name: "Edit Profile", desc: "Display name, avatar, frames, badges, status", keywords: "avatar name photo status frame badge xp level me account", group: "Look & feel", run: () => onProfileTab?.("profile") },
      { id: "friends", icon: "\uD83D\uDC9B", name: "Friends", desc: "Friend list and requests", keywords: "friend requests social people add", group: "Look & feel", run: () => onProfileTab?.("friends") },
      { id: "notifs", icon: "\uD83D\uDD14", name: "Notification Settings", desc: "Push, categories, snooze \u2014 per device", keywords: "notification notifications push alert snooze mute quiet dnd", group: "Look & feel", run: () => onProfileTab?.("notifications") },
      { id: "ai", icon: "\uD83E\uDD16", name: "AI & API Keys", desc: "Your AI provider, key, and model (OpenRouter etc.)", keywords: "api key openrouter deepseek anthropic claude model provider byok settings ai", group: "Look & feel", run: () => onSettings?.() },
      ...(isAdmin ? [{ id: "admin", icon: "\uD83D\uDEE0\uFE0F", name: "Admin Panel", desc: "Usage, logs, tools", keywords: "admin usage dashboard logs cost", group: "Admin", run: () => onAdmin?.() }] : []),
      ...(canEditChars ? [{ id: "char-editor", icon: "\u270D\uFE0F", name: "Character Editor", desc: "Edit character profiles and dynamics", keywords: "character edit dynamics relationship personality override", group: "Admin", run: () => onCharEditor?.() }] : []),
      ...(isDirector ? [{ id: "director", icon: "\uD83C\uDFAC", name: "Director's Room", desc: "Private route planning studio \u2014 only you", keywords: "director route plan bible secret gm studio drafts", group: "Admin", run: () => setDirectorOpen(true) }] : []),
    ];
  }, [isKnownPlayer, isAdmin, canEditChars, isDirector, setMainTab, onShowFateCard, onOpenCrosstalk, onToggleMusic, onTogglePhone, onOpenStoryLinks, onProfileTab, onSettings, onAdmin, onCharEditor, onEncyclopedia]);
  return (
    <nav role="navigation" aria-label="Main navigation" className="main-nav" style={{ display: "flex", alignItems: "center", padding: "0 12px", background: "linear-gradient(180deg, var(--ds-glass-strong, rgba(13,8,8,0.85)) 0%, var(--ds-glass, rgba(10,10,10,0.75)) 100%)", borderBottom: "1px solid var(--ds-accent-a22, #c6282822)", flexShrink: 0, height: "52px", gap: "0", position: "relative", zIndex: 50, touchAction: "manipulation" }}>
      {/* Subtle bottom glow line */}
      <div style={{ position: "absolute", bottom: 0, left: "10%", right: "10%", height: "1px", background: "linear-gradient(90deg, transparent, var(--ds-accent-a44, #c6282844), transparent)" }} />

      {/* Logo — button for mobile touch reliability */}
      <button
        onClick={(e) => { e.stopPropagation(); setMainTab("rp"); setMobileMenuOpen(false); }}
        aria-label="Go to home page"
        style={{
          display: "flex", alignItems: "center", gap: "8px", marginRight: "16px", flexShrink: 0,
          cursor: "pointer", background: "none", border: "none", padding: "4px 8px",
          touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none",
          minHeight: "44px", minWidth: "44px",
        }}
      >
        <img src="/art/brand/logo3_128.png" alt="MFML" className="emblem-idle" style={{ width: "28px", height: "28px", filter: "drop-shadow(0 0 6px var(--ds-accent-a44, #c6282844))", imageRendering: "auto", pointerEvents: "none" }} />
        <span style={{ fontWeight: 700, fontSize: "13px", fontFamily: "'Cinzel', serif", letterSpacing: "3px", background: "linear-gradient(90deg, var(--ds-accent, #c62828), var(--ds-gold, #c4a882))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", pointerEvents: "none" }}>MFML</span>
      </button>

      {/* Compass — find anything (also \u2318K) */}
      <button
        onClick={(e) => { e.stopPropagation(); setCompassOpen(true); setMobileMenuOpen(false); }}
        aria-label="Open compass — find any feature"
        title="Find anything (⌘K)"
        style={{ background: "none", border: "1px solid var(--ds-gold-a26, rgba(212,175,55,0.15))", borderRadius: "10px", cursor: "pointer", fontSize: "16px", width: "36px", height: "36px", minWidth: "36px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ds-gold, #c4a882)", marginRight: "8px", flexShrink: 0, touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}
      ><span style={{ pointerEvents: "none" }}>🧭</span></button>
      <FeatureCompass open={compassOpen} onClose={() => setCompassOpen(false)} features={compassFeatures} isMobile={isMobile} />
      {isDirector && directorOpen && (
        <Lazy><RouteDirector open onClose={() => setDirectorOpen(false)} user={user} isMobile={isMobile} /></Lazy>
      )}

      {/* Mobile: simplified top nav — only essentials visible.
          Music / Incognito / Daily Fate Card moved into the hamburger menu
          to free up tap room and make the visible icons bigger. Story bell
          only renders when there's unread activity so it doesn't take a
          slot when idle. Visible at all times: Phone, Notif bell, Hamburger. */}
      {isMobile && (
        <div style={{ display: "flex", alignItems: "center", gap: "4px", marginLeft: "auto", position: "relative", zIndex: 50, touchAction: "manipulation" }}>
          {/* Phone toggle (mobile) */}
          <button
            onClick={(e) => { e.stopPropagation(); onTogglePhone(); }}
            aria-label={phoneOpen ? "Close phone" : "Open phone"}
            style={{
              background: phoneOpen ? "var(--ds-gold-a1f, rgba(196,168,130,0.12))" : "none",
              border: "none", cursor: "pointer", fontSize: "20px",
              width: "44px", height: "44px", minWidth: "44px", minHeight: "44px",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: phoneOpen ? t.accentGold : t.textFaint,
              touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none",
              borderRadius: "10px", flexShrink: 0,
            }}
          ><span style={{ pointerEvents: "none" }}>📱</span></button>
          {/* Story bell (mobile) — only renders when there's unread activity
              so the visible nav stays uncluttered. The full story panel is
              still reachable from the hamburger menu when bell is hidden. */}
          {storyChannelUnread > 0 && (
          <button onClick={(e) => { e.stopPropagation(); setShowStoryNotifPanel(prev => !prev); }} aria-label="Story updates" style={{
            background: "var(--ds-gold-a1f, rgba(196,168,130,0.12))", border: "none", cursor: "pointer", fontSize: "20px",
            width: "44px", height: "44px", minWidth: "44px", minHeight: "44px",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", color: t.accentGold,
            WebkitTapHighlightColor: "var(--ds-gold-a26, rgba(196,168,130,0.15))",
            touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none",
            borderRadius: "10px", flexShrink: 0,
          }}>
            <span style={{ pointerEvents: "none" }} className="story-bell-glow">🍔</span>
            <span style={{
              position: "absolute", top: "2px", right: "0px",
              minWidth: "16px", height: "16px", borderRadius: "8px",
              background: "linear-gradient(135deg, var(--ds-gold, #c4a882), #a88652)", color: t.bgDeepest,
              fontSize: "9px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
              border: "1.5px solid #0a0a0a", padding: "0 3px",
              animation: "storyBadgePulse 2s ease-in-out infinite",
              pointerEvents: "none",
            }}>{storyChannelUnread > 9 ? "9+" : storyChannelUnread}</span>
          </button>
          )}
          {/* Notification bell (mobile) — 48px min touch target */}
          {(() => {
            const snooze = getSnoozeStatus(notifPrefs);
            const isSnoozed = snooze.active;
            return (
              <button onClick={(e) => { e.stopPropagation(); onNotifToggle(); }} aria-label="Notifications" style={{
                background: "none", border: "none", cursor: "pointer", fontSize: "20px",
                width: "44px", height: "44px", minWidth: "44px", minHeight: "44px",
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative", color: isSnoozed ? t.textFaint : t.textMuted,
                WebkitTapHighlightColor: "var(--ds-accent-a26, rgba(198,40,40,0.15))",
                touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none",
                borderRadius: "10px", flexShrink: 0,
              }}>
                <span style={{ pointerEvents: "none" }} className={unreadNotifCount > 0 && !isSnoozed ? "bell-shake" : ""}>{isSnoozed ? "🔕" : "🔔"}</span>
                {unreadNotifCount > 0 && !isSnoozed && (
                  <span style={{
                    position: "absolute", top: "6px", right: "2px",
                    width: "14px", height: "14px", borderRadius: "50%",
                    background: t.accentDanger, color: "#fff", fontSize: "8px", fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "1.5px solid #0a0a0a",
                    pointerEvents: "none",
                  }}>{unreadNotifCount > 9 ? "9+" : unreadNotifCount}</span>
                )}
              </button>
            );
          })()}
          {/* Hamburger — 48×48. (Daily-fate-card nag dot removed per Haley's
              request — the menu item is still in here, just no notification.) */}
          <button
            onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(prev => !prev); }}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            title={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            style={{
              background: mobileMenuOpen ? "var(--ds-gold-a1f, rgba(196,168,130,0.12))" : "none", border: "none", color: t.accentGold, cursor: "pointer", fontSize: "22px",
              width: "48px", height: "48px", minWidth: "48px", minHeight: "48px",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "4px", marginLeft: "2px", borderRadius: "10px",
              WebkitTapHighlightColor: "var(--ds-gold-a40, rgba(196,168,130,0.25))",
              touchAction: "manipulation", position: "relative", zIndex: 51,
              WebkitUserSelect: "none", userSelect: "none", flexShrink: 0,
            }}
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </button>

          {/* ── Mobile notification-bell dropdown ────────────────────────────
              The desktop version of this panel sits inside the !isMobile
              branch below, anchored to its own bell button via position:
              absolute. On mobile that button has no relative-positioned
              parent, so we render a separate fixed-position copy here.
              Stays visible until the user explicitly ✕'s a row or hits
              "Clear all" — toggling the bell doesn't drop the list. */}
          {showNotifDropdown && (
            <>
              <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }} onClick={() => setShowNotifDropdown(false)} />
              <div style={{
                position: "fixed", top: "60px", right: "8px",
                width: "min(340px, calc(100vw - 16px))", maxHeight: "min(60vh, 480px)",
                background: "linear-gradient(135deg, var(--ds-surface-2, #1a0f0f) 0%, var(--ds-surface-0, #0d0808) 100%)",
                border: "1px solid var(--ds-accent-a40, rgba(198,40,40,0.25))", borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 20px var(--ds-accent-a14, rgba(198,40,40,0.08))",
                zIndex: 9999, display: "flex", flexDirection: "column",
                animation: "fadeSlideIn 0.15s ease-out", overflow: "hidden",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", borderBottom: "1px solid var(--ds-accent-a26, rgba(198,40,40,0.15))",
                }}>
                  <span style={{ fontSize: "11px", color: t.accentGold, fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600 }}>
                    Notifications
                  </span>
                  {liveNotifications.length > 0 && (
                    <button onClick={() => dismissAllNotifications()} style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: "10px", padding: "2px 8px", fontFamily: "'Cormorant Garamond', serif" }}>Clear all</button>
                  )}
                </div>
                <div style={{ overflowY: "auto", flex: 1, padding: "6px 0" }}>
                  {liveNotifications.length === 0 ? (
                    <div style={{ padding: "24px 14px", textAlign: "center", color: t.textFaint, fontSize: "13px", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                      No new notifications
                    </div>
                  ) : (
                    liveNotifications.map(n => (
                      <button key={n.id} onClick={(e) => { e.stopPropagation(); handleNotifClick(n); }} style={{
                        padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: "10px",
                        borderBottom: "1px solid var(--ds-accent-a14, rgba(198,40,40,0.08))", transition: "background 0.15s",
                        background: "none", border: "none", width: "100%", textAlign: "left",
                        touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none", minHeight: "44px",
                      }}>
                        <span style={{ fontSize: "18px", flexShrink: 0, lineHeight: 1.2 }}>{n.icon || "🔔"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: t.accentGold, fontFamily: "'Cinzel', serif", lineHeight: 1.3, marginBottom: "2px" }}>
                            {n.title}
                          </div>
                          <div style={{ fontSize: "12px", color: t.textMuted, lineHeight: 1.4, fontFamily: "'Cormorant Garamond', serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {n.body}
                          </div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); dismissNotification(n.id); }} aria-label="Dismiss notification" style={{
                          background: "none", border: "none", color: "#5a4040", cursor: "pointer", fontSize: "16px", padding: "4px 8px", flexShrink: 0, lineHeight: 1, minWidth: "32px", minHeight: "32px",
                        }}>✕</button>
                      </button>
                    ))
                  )}
                </div>
                {/* Sticky footer — prominent "Clear notifications" button.
                    Only shows when there's something to clear. Designed so
                    you don't have to scroll back to the header link. */}
                {liveNotifications.length > 0 && (
                  <button
                    onClick={() => { dismissAllNotifications(); setShowNotifDropdown(false); }}
                    style={{
                      flexShrink: 0,
                      padding: "12px 14px",
                      background: "linear-gradient(135deg, var(--ds-accent-a2e, rgba(198,40,40,0.18)), var(--ds-accent-a14, rgba(198,40,40,0.08)))",
                      border: "none", borderTop: "1px solid var(--ds-accent-a40, rgba(198,40,40,0.25))",
                      color: t.accentGold, cursor: "pointer",
                      fontSize: "13px", fontWeight: 600,
                      fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase",
                      transition: "background 0.15s",
                      touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none",
                      minHeight: "44px",
                    }}
                  >Clear notifications</button>
                )}
              </div>
            </>
          )}

          {/* ── Mobile story-burger (🍔) dropdown ────────────────────────────
              Mirrors the desktop story panel but with fixed positioning so it
              works without a relative parent. Same persistence rules — only
              the per-row ✕ or "Clear all" drops entries. */}
          {showStoryNotifPanel && (
            <>
              <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }} onClick={() => setShowStoryNotifPanel(false)} />
              <div style={{
                position: "fixed", top: "60px", right: "8px",
                width: "min(320px, calc(100vw - 16px))", maxHeight: "min(60vh, 440px)",
                background: "linear-gradient(135deg, var(--ds-surface-2, #1a1408) 0%, var(--ds-surface-0, #0d0a05) 100%)",
                border: "1px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))", borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 24px var(--ds-gold-a1a, rgba(196,168,130,0.1))",
                zIndex: 9999, display: "flex", flexDirection: "column",
                animation: "fadeSlideIn 0.15s ease-out", overflow: "hidden",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", borderBottom: "1px solid var(--ds-gold-a26, rgba(196,168,130,0.15))",
                }}>
                  <span style={{ fontSize: "11px", color: t.accentGold, fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                    🍔 Story Updates
                  </span>
                  {storyNotifMessages.length > 0 && (
                    <button onClick={onClearStoryNotifs} style={{ background: "none", border: "none", color: "#5a4535", cursor: "pointer", fontSize: "10px", padding: "2px 8px", fontFamily: "'Cormorant Garamond', serif" }}>Clear all</button>
                  )}
                </div>
                <div style={{ overflowY: "auto", flex: 1, padding: "4px 0" }}>
                  {storyNotifMessages.length === 0 ? (
                    <div style={{ padding: "24px 14px", textAlign: "center", color: t.textFaint, fontSize: "13px", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                      No story updates
                    </div>
                  ) : (
                    storyNotifMessages.map(n => (
                      <button key={n.id} onClick={(e) => { e.stopPropagation(); onStoryNotifClick(n); }} style={{
                        padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: "10px",
                        borderBottom: "1px solid var(--ds-gold-a14, rgba(196,168,130,0.08))", transition: "background 0.15s",
                        background: "none", border: "none", width: "100%", textAlign: "left",
                        touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none", minHeight: "44px",
                      }}>
                        <span style={{ fontSize: "18px", flexShrink: 0, lineHeight: 1.2 }}>{n.icon || "🍔"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: t.accentGold, fontFamily: "'Cinzel', serif", lineHeight: 1.3, marginBottom: "2px" }}>
                            {n.title}
                          </div>
                          <div style={{ fontSize: "12px", color: t.textMuted, lineHeight: 1.4, fontFamily: "'Cormorant Garamond', serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {n.body}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {storyNotifMessages.length > 0 && (
                  <button
                    onClick={onClearStoryNotifs}
                    style={{
                      flexShrink: 0,
                      padding: "12px 14px",
                      background: "linear-gradient(135deg, var(--ds-gold-a38, rgba(196,168,130,0.22)), var(--ds-gold-a14, rgba(196,168,130,0.08)))",
                      border: "none", borderTop: "1px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))",
                      color: t.accentGold, cursor: "pointer",
                      fontSize: "13px", fontWeight: 600,
                      fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase",
                      transition: "background 0.15s",
                      touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none",
                      minHeight: "44px",
                    }}
                  >Clear notifications</button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Desktop tabs */}
      {!isMobile && tabs.map((tab) => {
        const isActive = mainTab === tab.id;
        return (
        <button key={tab.id} className={`nav-btn ${isActive ? "active" : ""}`} onClick={() => setMainTab(tab.id)} aria-current={isActive ? "page" : undefined} style={{
          background: isActive ? "var(--ds-accent-a12, rgba(198,40,40,0.07))" : "transparent",
          border: "none",
          borderBottom: "2px solid transparent",
          borderTop: "2px solid transparent",
          color: isActive ? "var(--ds-ink-hi, #f0d0d0)" : t.textMuted,
          padding: "12px 10px", fontSize: "12px", fontWeight: isActive ? 700 : 600, cursor: "pointer",
          fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase",
          transition: "all 0.18s ease", whiteSpace: "nowrap", position: "relative",
          flexShrink: 1, minWidth: 0, overflow: "hidden", borderRadius: isActive ? "4px 4px 0 0" : "4px",
        }}
          onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.color = "var(--ds-gold, #c4a882)"; e.currentTarget.style.background = "var(--ds-gold-a0f, rgba(196,168,130,0.06))"; } }}
          onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.color = t.textMuted; e.currentTarget.style.background = "transparent"; } }}
        >
          {useShortTabLabels ? tab.short : tab.label}
          {unreadTabs?.[tab.id] && mainTab !== tab.id && (
            <span style={{
              position: "absolute", top: "8px", right: "4px",
              width: "7px", height: "7px", borderRadius: "50%",
              background: t.accentDanger, boxShadow: "0 0 6px var(--ds-accent-a88, #c6282888)",
              animation: "fadeSlideIn 0.2s ease-out",
            }} />
          )}
          {/* eat speak rp repeat "your turn" dot — only for Nicole & Haley */}
          {tab.id === "channels" && eatSpeakNeedsReply && mainTab !== "channels" && isEatSpeakUser && (
            <span style={{
              position: "absolute", top: "6px", right: "2px",
              width: "8px", height: "8px", borderRadius: "50%",
              background: "var(--ds-gold, #c4a882)",
              boxShadow: "0 0 6px var(--ds-gold-a88, #c4a88288), 0 0 12px var(--ds-gold-a44, #c4a88244)",
              animation: "storyBadgePulse 2s ease-in-out infinite",
            }} title="Your turn to reply in eat speak rp repeat" />
          )}
        </button>
        );
      })}
      {/* Phones tab is now in the tabs array above */}

      {!isMobile && (
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
          {/* Phone Toggle (Desktop) */}
          <button
            onClick={onTogglePhone}
            aria-label={phoneOpen ? "Close phone" : "Open phone"}
            title={phoneOpen ? "Close phone" : "Open phone"}
            style={{
              background: phoneOpen ? "var(--ds-gold-a22, #c4a88222)" : "#1a121266",
              border: "1px solid var(--ds-accent-a33, #c6282833)", borderRadius: "6px",
              color: phoneOpen ? t.accentGold : t.textFaint,
              cursor: "pointer", fontSize: "14px", fontWeight: 600,
              padding: "4px 10px", transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: "4px",
              fontFamily: "'Cinzel', serif", letterSpacing: "1px",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-gold-a22, #c4a88222)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = phoneOpen ? "var(--ds-gold-a22, #c4a88222)" : "#1a121266"; }}
          >📱</button>
          {/* Music Toggle (Desktop) */}
          <button
            onClick={onToggleMusic}
            aria-label={musicPlayerVisible ? "Hide music player" : "Show music player"}
            title={musicPlayerVisible ? "Hide music player" : "Show music player"}
            style={{
              background: musicPlayerVisible ? "var(--ds-gold-a22, #c4a88222)" : "#1a121266",
              border: "1px solid var(--ds-accent-a33, #c6282833)", borderRadius: "6px",
              color: musicPlayerVisible ? t.accentGold : t.textFaint,
              cursor: "pointer", fontSize: "14px", fontWeight: 600,
              padding: "4px 10px", transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: "4px",
              fontFamily: "'Cinzel', serif", letterSpacing: "1px",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-gold-a22, #c4a88222)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = musicPlayerVisible ? "var(--ds-gold-a22, #c4a88222)" : "#1a121266"; }}
          >♫</button>
          {/* Incognito mode (Desktop) — opens popup in chosen disguise */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowIncogPicker(p => !p)}
              title="Incognito mode — open a disguised chat window"
              style={{ background: showIncogPicker ? "#2563eb22" : "#1a121266", border: showIncogPicker ? "1px solid #2563eb66" : "1px solid var(--ds-accent-a33, #c6282833)", borderRadius: "6px", color: showIncogPicker ? "#7aa2f7" : t.textFaint, cursor: "pointer", fontSize: "14px", fontWeight: 600, padding: "4px 10px", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "4px", fontFamily: "'Cinzel', serif", letterSpacing: "1px" }}
              onMouseEnter={e => e.currentTarget.style.background = "#2563eb22"}
              onMouseLeave={e => { e.currentTarget.style.background = showIncogPicker ? "#2563eb22" : "#1a121266"; }}
            >🕵️</button>
            {showIncogPicker && (
              <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#16131a', border: '1px solid #2a1a2a', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', padding: '10px', zIndex: 9999, minWidth: '200px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', fontFamily: "'Cinzel', serif", letterSpacing: '1px', textTransform: 'uppercase', padding: '0 4px 8px', borderBottom: '1px solid #2a1a2a', marginBottom: '8px' }}>Choose disguise</div>
                {[
                  { mode: 'office', icon: '💼', label: 'Office Mode', sub: 'Excel payroll spreadsheet' },
                  { mode: 'tasks', icon: '🔧', label: 'Parts & Work Orders', sub: 'Service & task tracker' },
                  { mode: 'education', icon: '🎓', label: 'Student Portal', sub: 'Online course viewer' },
                  { mode: 'staff', icon: '🏫', label: 'School Staff', sub: 'Faculty reading portal' },
                ].map(opt => (
                  <button key={opt.mode} onClick={() => { setShowIncogPicker(false); openIncognitoPopup(opt.mode); }} style={{ width: '100%', background: 'transparent', border: '1px solid transparent', borderRadius: '7px', padding: '8px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#2563eb18'; e.currentTarget.style.borderColor = '#2563eb44'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}>
                    <span style={{ fontSize: '20px' }}>{opt.icon}</span>
                    <div><div style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0' }}>{opt.label}</div><div style={{ fontSize: '11px', color: '#6b7280' }}>{opt.sub}</div></div>
                  </button>
                ))}
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #2a1a2a', fontSize: '10px', color: '#4b5563', textAlign: 'center' }}>Press Option (⌥) to toggle views inside the popup</div>
              </div>
            )}
          </div>
          {/* Daily Fate Card (Desktop) */}
          <button
            onClick={onShowFateCard}
            aria-label="Daily Fate Card"
            title="Daily Fate Card"
            style={{
              background: "#1a121266",
              border: "1px solid var(--ds-accent-a33, #c6282833)", borderRadius: "6px",
              color: t.textFaint,
              cursor: "pointer", fontSize: "14px", fontWeight: 600,
              padding: "4px 10px", transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: "4px",
              fontFamily: "'Cinzel', serif", letterSpacing: "1px",
              position: "relative",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-gold-a22, #c4a88222)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#1a121266"; }}
          >
            🌌
            {/* Daily-fate-card nag dot removed per Haley's request. */}
          </button>
          {/* User List Toggle */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowUserList(prev => !prev)}
              aria-label={showUserList ? "Close user list" : "Show users"}
              title={showUserList ? "Close user list" : "Show users"}
              style={{
                background: showUserList ? "var(--ds-accent-a33, #c6282833)" : "#1a121266", border: "1px solid var(--ds-accent-a33, #c6282833)", borderRadius: "6px",
                color: t.accentGold, cursor: "pointer", fontSize: "12px", fontWeight: 600, padding: "4px 10px",
                fontFamily: "'Cinzel', serif", letterSpacing: "1px", transition: "all 0.2s",
                display: "flex", alignItems: "center", gap: "5px",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-accent-a33, #c6282833)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = showUserList ? "var(--ds-accent-a33, #c6282833)" : "#1a121266"; }}
            >
              👥
              {onlineCount > 0 && <span style={{ fontSize: "11px", fontWeight: 700, color: t.accentSuccess }}>{onlineCount}</span>}
              {friendIncoming.length > 0 && (
                <span style={{
                  position: "absolute", top: "-4px", right: "-4px", minWidth: "16px", height: "16px",
                  borderRadius: "8px", background: t.accentDanger || "#ed4245", color: "#fff",
                  fontSize: "10px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "0 4px", lineHeight: 1,
                }}>{friendIncoming.length}</span>
              )}
            </button>
            {/* User List Dropdown */}
            {showUserList && (
              <div style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0, width: "240px", maxHeight: "400px",
                background: t.bgBase, borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                zIndex: 200, display: "flex", flexDirection: "column", overflow: "hidden",
                border: "1px solid var(--ds-accent-a22, #c6282822)", animation: "fadeSlideIn 0.2s ease-out",
              }}>
                <div style={{ padding: "10px 14px 0", borderBottom: "1px solid #1a1212" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--ds-ink-hi, #fff)" }}>People</span>
                    <button onClick={() => setShowUserList(false)} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: "16px", padding: 0 }}>×</button>
                  </div>
                  {/* All / Friends tabs */}
                  <div style={{ display: "flex", gap: "4px", marginBottom: "8px" }}>
                    {[["all", "All"], ["friends", `Friends${friendsList.length > 0 ? ` (${friendsList.length})` : ''}`]].map(([id, label]) => (
                      <button key={id} onClick={() => setUserListTab(id)} style={{
                        background: userListTab === id ? "var(--ds-accent-a33, rgba(198,40,40,0.2))" : "none",
                        border: userListTab === id ? "1px solid var(--ds-accent-a55, #c6282855)" : "1px solid transparent",
                        borderRadius: "6px", padding: "3px 10px", fontSize: "11px", fontWeight: 600,
                        color: userListTab === id ? "#e57373" : t.textMuted, cursor: "pointer",
                      }}>{label}</button>
                    ))}
                  </div>
                </div>
                {/* Friend Requests Banner */}
                {friendIncoming.length > 0 && (
                  <div style={{ padding: "8px 12px", background: `${t.accentDanger || "#ed4245"}11`, borderBottom: "1px solid #1a1212" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: t.accentDanger || "#ed4245", textTransform: "uppercase", marginBottom: "6px" }}>
                      Friend Requests ({friendIncoming.length})
                    </div>
                    {friendIncoming.map(req => (
                      <div key={req.id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 0" }}>
                        <div style={{ width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0, background: req.profile?.avatar_url ? `url(${req.profile.avatar_url}) center/cover` : `${req.profile?.accent_color || t.accentGold}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" }}>{!req.profile?.avatar_url && "👤"}</div>
                        <span style={{ flex: 1, fontSize: "11px", color: t.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{req.profile?.display_name}</span>
                        <button onClick={async (e) => { e.stopPropagation(); await acceptFriendRequest(req.id); refreshFriends(); }} style={{ background: `${t.accentSuccess}22`, border: `1px solid ${t.accentSuccess}44`, color: t.accentSuccess, borderRadius: "4px", padding: "2px 8px", fontSize: "10px", cursor: "pointer" }}>Accept</button>
                        <button onClick={async (e) => { e.stopPropagation(); await rejectFriendRequest(req.id); refreshFriends(); }} style={{ background: "none", border: `1px solid ${t.textMuted}33`, color: t.textMuted, borderRadius: "4px", padding: "2px 8px", fontSize: "10px", cursor: "pointer" }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
                  {userListTab === "friends" ? (() => {
                    // Friends tab — show accepted friends, sorted by online first
                    const friendProfiles = friendsList.map(f => f.profile).filter(Boolean);
                    const onlineFriends = friendProfiles.filter(p => isUserOnline(p));
                    const offlineFriends = friendProfiles.filter(p => !isUserOnline(p));
                    if (friendProfiles.length === 0) {
                      return (
                        <div style={{ padding: "20px 10px", textAlign: "center", color: t.textFaint, fontSize: "12px" }}>
                          <div style={{ fontSize: "28px", marginBottom: "8px" }}>💛</div>
                          <div>No friends yet.</div>
                          <div style={{ marginTop: "4px", color: t.textFaint, fontSize: "11px" }}>Switch to "All" to find people.</div>
                        </div>
                      );
                    }
                    const renderFriendRow = (p) => {
                      const online = isUserOnline(p);
                      return (
                        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px", borderRadius: "6px", cursor: "pointer", transition: "background 0.15s", opacity: online ? 1 : 0.65 }}
                          onClick={() => setViewingProfile(p)}
                          onMouseEnter={e => { e.currentTarget.style.background = t.bgHover; e.currentTarget.style.opacity = "1"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.opacity = online ? "1" : "0.65"; }}
                        >
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : `${p.accent_color || t.accentGold}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>
                              {!p.avatar_url && "👤"}
                            </div>
                            <div style={{ position: "absolute", bottom: "-1px", right: "-1px", width: "8px", height: "8px", borderRadius: "50%", background: online ? "#43b581" : "#747f8d", border: "2px solid #2f3136" }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: p.accent_color || t.textPrimary, fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.display_name}</div>
                            <div style={{ fontSize: "10px", color: online ? "#43b581" : "#666", marginTop: "1px" }}>{online ? "Online" : p.hide_online_status ? "Offline" : `Last seen ${formatLastActive(p.last_seen)}`}</div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDmTargetUserId(p.id); setDmStartNew(true); setMainTab("dm"); setShowUserList(false); setTimeout(() => setDmStartNew(false), 500); }}
                            style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: "14px", padding: "2px 4px", flexShrink: 0 }}
                            title={`DM ${p.display_name}`}
                          >💌</button>
                        </div>
                      );
                    };
                    return (
                      <>
                        {onlineFriends.length > 0 && <>
                          <div style={{ fontSize: "10px", fontWeight: 700, color: "#43b581", textTransform: "uppercase", padding: "4px 6px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#43b581" }} /> Online — {onlineFriends.length}
                          </div>
                          {onlineFriends.map(renderFriendRow)}
                        </>}
                        {offlineFriends.length > 0 && <>
                          <div style={{ fontSize: "10px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", padding: "8px 6px 4px", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#747f8d" }} /> Offline — {offlineFriends.length}
                          </div>
                          {offlineFriends.map(renderFriendRow)}
                        </>}
                      </>
                    );
                  })() : (() => {
                    // All users tab
                    const online = (onlineProfiles || []).filter(p => p?.id && isUserOnline(p));
                    const offline = (onlineProfiles || []).filter(p => p?.id && !isUserOnline(p));
                    const renderUserRow = (p, isOffline) => {
                      const isMe = p.id === user?.id;
                      return (
                        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px", borderRadius: "6px", cursor: "pointer", transition: "background 0.15s", opacity: isOffline ? 0.6 : 1 }}
                          onClick={() => !isMe && setViewingProfile(p)}
                          onMouseEnter={e => { e.currentTarget.style.background = t.bgHover; if (isOffline) e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; if (isOffline) e.currentTarget.style.opacity = "0.6"; }}
                        >
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : `${p.accent_color || t.accentGold}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", filter: isOffline ? "grayscale(0.3)" : "none" }}>
                              {!p.avatar_url && "👤"}
                            </div>
                            <div style={{ position: "absolute", bottom: "-1px", right: "-1px", width: "8px", height: "8px", borderRadius: "50%", background: isOffline ? "#747f8d" : t.accentSuccess, border: "2px solid #2f3136" }} aria-hidden="true" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: p.accent_color || (isOffline ? t.textMuted : t.textPrimary), fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {p.display_name}{isMe && <span style={{ color: t.textMuted, fontWeight: 400 }}> (You)</span>}
                            </div>
                            {p.status_message
                              ? <div style={{ fontSize: "10px", color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.status_message}</div>
                              : <div style={{ fontSize: "10px", color: isOffline ? "#666" : "#4a9e6e88" }}>{isOffline ? (p.hide_online_status ? "Offline" : `Last active ${formatLastActive(p.last_seen)}`) : "Active now"}</div>
                            }
                          </div>
                          {!isMe && <>
                            <FriendButton userId={user?.id} targetId={p.id} friends={friendsList} incoming={friendIncoming} outgoing={friendOutgoing} onAction={refreshFriends} compact />
                            <button
                              onClick={(e) => { e.stopPropagation(); setDmTargetUserId(p.id); setDmStartNew(true); setMainTab("dm"); setShowUserList(false); setTimeout(() => setDmStartNew(false), 500); }}
                              style={{ background: "none", border: "none", color: isOffline ? "#555" : t.textMuted, cursor: "pointer", fontSize: "14px", padding: "2px 4px", flexShrink: 0 }}
                              title={`DM ${p.display_name}`}
                            >💌</button>
                          </>}
                        </div>
                      );
                    };
                    return (
                      <>
                        <div style={{ fontSize: "10px", fontWeight: 700, color: t.accentSuccess, textTransform: "uppercase", padding: "4px 6px", display: "flex", alignItems: "center", gap: "4px" }}>
                          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: t.accentSuccess }} /> Online — {online.length}
                        </div>
                        {online.map(p => renderUserRow(p, false))}
                        {online.length === 0 && <div style={{ fontSize: "11px", color: "#555", padding: "8px 6px" }}>No users online</div>}
                        <div style={{ fontSize: "10px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", padding: "8px 6px 4px", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: t.textFaint }} /> Offline — {offline.filter(p => p.id !== user?.id).length}
                        </div>
                        {offline.filter(p => p.id !== user?.id).map(p => renderUserRow(p, true))}
                        {offline.length === 0 && <div style={{ fontSize: "11px", color: "#555", padding: "4px 6px" }}>No offline users</div>}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          {isAdmin && (
              <button onClick={onAdmin} aria-label="Admin panel" style={{ background: "var(--ds-accent-a15, #c6282815)", border: "1px solid var(--ds-accent-a33, #c6282833)", borderRadius: "6px", color: t.accentDanger, cursor: "pointer", fontSize: "11px", fontWeight: 600, padding: "5px 10px", fontFamily: "'Cinzel', serif", letterSpacing: "1px", transition: "all 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-accent-a33, #c6282833)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "var(--ds-accent-a15, #c6282815)"; }}
              >ADMIN</button>
          )}
          {/* WORLD — straight into the ONE unified interface (Haley
              2026-08-30: "everything should be available to toggle search and
              edit when a user clicks world I do not want to have to click
              another button to pop the menu open"). Characters, lore, places,
              groups — all sections, one search, pencils everywhere. */}
          <button onClick={onEncyclopedia} aria-label="World" style={{ background: "var(--ds-gold-a15, #c4a88215)", border: "1px solid var(--ds-gold-a33, #c4a88233)", borderRadius: "6px", color: t.accentGold, cursor: "pointer", fontSize: "11px", fontWeight: 600, padding: "5px 10px", fontFamily: "'Cinzel', serif", letterSpacing: "1px", transition: "all 0.2s" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ds-gold-a33, #c4a88233)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "var(--ds-gold-a15, #c4a88215)"; }}
          >WORLD</button>
          {/* Notification bell with snooze shortcut */}
          {(() => {
            const snooze = getSnoozeStatus(notifPrefs);
            const isSnoozed = snooze.active;
            return (
              <div style={{ position: "relative" }}>
                <button onClick={onNotifToggle} onContextMenu={(e) => { e.preventDefault(); setShowSnoozeMenu(prev => !prev); }} aria-label="Notifications" title={isSnoozed ? `Snoozed — ${snooze.remaining} left` : "Notifications"} style={{
                  background: "none", border: "1px solid transparent", color: isSnoozed ? t.textFaint : t.textMuted, cursor: "pointer",
                  fontSize: "14px", padding: "5px 8px", borderRadius: "6px", transition: "all 0.2s", position: "relative",
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "var(--ds-gold, #c4a882)"; e.currentTarget.style.borderColor = "var(--ds-gold-a33, #c4a88233)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = isSnoozed ? "#9a8585" : "#b0a0a0"; e.currentTarget.style.borderColor = "transparent"; }}
                >
                  <span className={unreadNotifCount > 0 && !isSnoozed ? "bell-shake" : ""}>{isSnoozed ? "🔕" : "🔔"}</span>
                  {unreadNotifCount > 0 && !isSnoozed && (
                    <span style={{
                      position: "absolute", top: "0px", right: "0px",
                      width: "14px", height: "14px", borderRadius: "50%",
                      background: t.accentDanger, color: "#fff", fontSize: "8px", fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "1.5px solid #0a0a0a",
                    }}>{unreadNotifCount > 9 ? "9+" : unreadNotifCount}</span>
                  )}
                  {isSnoozed && (
                    <span style={{
                      position: "absolute", top: "-2px", right: "-4px",
                      fontSize: "8px", color: t.accentGold, fontWeight: 600,
                      background: "var(--ds-surface-2, #1a0f0f)", borderRadius: "3px", padding: "0 2px",
                      border: "1px solid var(--ds-gold-a33, #c4a88233)",
                    }}>ZZ</span>
                  )}
                </button>
                {/* Snooze dropdown — long-press / right-click on bell */}
                {showSnoozeMenu && (
                  <>
                    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setShowSnoozeMenu(false)} />
                    <div style={{
                      position: "absolute", top: "100%", right: 0, marginTop: "4px",
                      background: "var(--ds-surface-2, #1a0f0f)", border: "1px solid var(--ds-accent-a33, #c6282833)", borderRadius: "8px",
                      padding: "6px 0", minWidth: "140px", zIndex: 1000,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.5)", animation: "fadeSlideIn 0.15s ease-out",
                    }}>
                      <div style={{ padding: "4px 12px 6px", fontSize: "10px", color: "#9a8585", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>
                        {isSnoozed ? "Snoozed" : "Snooze for..."}
                      </div>
                      {isSnoozed ? (
                        <button onClick={() => { handleSnooze(0); }} style={{
                          display: "block", width: "100%", textAlign: "left", background: "none", border: "none",
                          color: t.accentSuccess, fontSize: "12px", padding: "6px 12px", cursor: "pointer",
                        }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(74,158,110,0.1)"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "none"}
                        >
                          Unsnooze ({snooze.remaining} left)
                        </button>
                      ) : (
                        [1, 2, 4, 8, 24].map(h => (
                          <button key={h} onClick={() => { handleSnooze(h); }} style={{
                            display: "block", width: "100%", textAlign: "left", background: "none", border: "none",
                            color: t.accentGold, fontSize: "12px", padding: "6px 12px", cursor: "pointer",
                          }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "var(--ds-accent-a1a, rgba(198,40,40,0.1))"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "none"}
                          >
                            {h === 1 ? "1 hour" : h === 24 ? "24 hours" : `${h} hours`}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
                {/* ── Notification dropdown panel ── */}
                {showNotifDropdown && (
                  <>
                    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setShowNotifDropdown(false)} />
                    <div style={{
                      position: "absolute", top: "100%", right: 0, marginTop: "6px",
                      width: "340px", maxWidth: "calc(100vw - 24px)", maxHeight: "420px",
                      background: "linear-gradient(135deg, var(--ds-surface-2, #1a0f0f) 0%, var(--ds-surface-0, #0d0808) 100%)",
                      border: "1px solid var(--ds-accent-a40, rgba(198,40,40,0.25))", borderRadius: "12px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 20px var(--ds-accent-a14, rgba(198,40,40,0.08))",
                      zIndex: 999, display: "flex", flexDirection: "column",
                      animation: "fadeSlideIn 0.15s ease-out", overflow: "hidden",
                    }}>
                      {/* Header */}
                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "10px 14px", borderBottom: "1px solid var(--ds-accent-a26, rgba(198,40,40,0.15))",
                      }}>
                        <span style={{ fontSize: "11px", color: t.accentGold, fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600 }}>
                          Notifications
                        </span>
                        {liveNotifications.length > 0 && (
                          <button onClick={() => dismissAllNotifications()} style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: "10px", padding: "2px 8px", fontFamily: "'Cormorant Garamond', serif", transition: "color 0.2s" }}
                            onMouseEnter={e => e.target.style.color = "var(--ds-gold, #c4a882)"}
                            onMouseLeave={e => e.target.style.color = "#9a8585"}
                          >Clear all</button>
                        )}
                      </div>
                      {/* Notification list */}
                      <div style={{ overflowY: "auto", flex: 1, padding: "6px 0" }}>
                        {liveNotifications.length === 0 ? (
                          <div style={{ padding: "24px 14px", textAlign: "center", color: t.textFaint, fontSize: "13px", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                            No new notifications
                          </div>
                        ) : (
                          liveNotifications.map(n => (
                            <button key={n.id} onClick={(e) => { e.stopPropagation(); handleNotifClick(n); setShowNotifDropdown(false); }} style={{
                              padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: "10px",
                              borderBottom: "1px solid var(--ds-accent-a14, rgba(198,40,40,0.08))", transition: "background 0.15s",
                              background: "none", border: "none", width: "100%", textAlign: "left",
                              touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none", minHeight: "44px",
                            }}
                              onMouseEnter={e => e.currentTarget.style.background = "var(--ds-accent-a0f, rgba(198,40,40,0.06))"}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                            >
                              <span style={{ fontSize: "18px", flexShrink: 0, lineHeight: 1.2 }}>{n.icon || "🔔"}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: t.accentGold, fontFamily: "'Cinzel', serif", lineHeight: 1.3, marginBottom: "2px" }}>
                                  {n.title}
                                </div>
                                <div style={{ fontSize: "12px", color: t.textMuted, lineHeight: 1.4, fontFamily: "'Cormorant Garamond', serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {n.body}
                                </div>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); dismissNotification(n.id); }} style={{
                                background: "none", border: "none", color: "#3a2525", cursor: "pointer", fontSize: "12px", padding: "2px 4px", flexShrink: 0, lineHeight: 1, transition: "color 0.15s",
                              }}
                                onMouseEnter={e => e.target.style.color = "var(--ds-gold, #c4a882)"}
                                onMouseLeave={e => e.target.style.color = "#3a2525"}
                              >✕</button>
                            </button>
                          ))
                        )}
                      </div>
                      {liveNotifications.length > 0 && (
                        <button
                          onClick={() => { dismissAllNotifications(); setShowNotifDropdown(false); }}
                          style={{
                            flexShrink: 0,
                            padding: "12px 14px",
                            background: "linear-gradient(135deg, var(--ds-accent-a2e, rgba(198,40,40,0.18)), var(--ds-accent-a14, rgba(198,40,40,0.08)))",
                            border: "none", borderTop: "1px solid var(--ds-accent-a40, rgba(198,40,40,0.25))",
                            color: t.accentGold, cursor: "pointer",
                            fontSize: "12px", fontWeight: 600,
                            fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "linear-gradient(135deg, var(--ds-accent-a47, rgba(198,40,40,0.28)), var(--ds-accent-a24, rgba(198,40,40,0.14)))"}
                          onMouseLeave={e => e.currentTarget.style.background = "linear-gradient(135deg, var(--ds-accent-a2e, rgba(198,40,40,0.18)), var(--ds-accent-a14, rgba(198,40,40,0.08)))"}
                        >Clear notifications</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          {/* ── Story Channel Notification Bell ── */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                if (storyChannelUnread > 0) {
                  setShowStoryNotifPanel(prev => !prev);
                } else {
                  onStoryNotifClick();
                }
              }}
              aria-label="Story channel updates"
              title={storyChannelUnread > 0 ? `${storyChannelUnread} new story update${storyChannelUnread > 1 ? 's' : ''}` : "Story channels"}
              style={{
                background: "none", border: "1px solid transparent", cursor: "pointer",
                fontSize: "14px", padding: "5px 8px", borderRadius: "6px", transition: "all 0.2s",
                position: "relative", color: storyChannelUnread > 0 ? t.accentGold : t.textFaint,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--ds-gold, #c4a882)"; e.currentTarget.style.borderColor = "var(--ds-gold-a33, #c4a88233)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = storyChannelUnread > 0 ? "var(--ds-gold, #c4a882)" : "#9a8585"; e.currentTarget.style.borderColor = "transparent"; }}
            >
              <span className={storyChannelUnread > 0 ? "story-bell-glow" : ""} style={{ fontSize: "15px" }}>🍔</span>
              {storyChannelUnread > 0 && (
                <span style={{
                  position: "absolute", top: "-1px", right: "0px",
                  minWidth: "16px", height: "16px", borderRadius: "8px",
                  background: "linear-gradient(135deg, var(--ds-gold, #c4a882), #a88652)", color: t.bgDeepest,
                  fontSize: "9px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1.5px solid #0a0a0a", padding: "0 3px",
                  boxShadow: "0 0 8px var(--ds-gold-a80, rgba(196,168,130,0.5)), 0 0 16px var(--ds-gold-a33, rgba(196,168,130,0.2))",
                  animation: "storyBadgePulse 2s ease-in-out infinite",
                }}>{storyChannelUnread > 9 ? "9+" : storyChannelUnread}</span>
              )}
            </button>
            {/* Story notification dropdown panel */}
            {showStoryNotifPanel && storyNotifMessages.length > 0 && (
              <>
                <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setShowStoryNotifPanel(false)} />
                <div style={{
                  position: "absolute", top: "100%", right: 0, marginTop: "6px",
                  width: "320px", maxWidth: "calc(100vw - 24px)", maxHeight: "380px",
                  background: "linear-gradient(135deg, var(--ds-surface-2, #1a1408) 0%, var(--ds-surface-0, #0d0a05) 100%)",
                  border: "1px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))", borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 24px var(--ds-gold-a1a, rgba(196,168,130,0.1))",
                  zIndex: 999, display: "flex", flexDirection: "column",
                  animation: "fadeSlideIn 0.15s ease-out", overflow: "hidden",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderBottom: "1px solid var(--ds-gold-a26, rgba(196,168,130,0.15))",
                  }}>
                    <span style={{ fontSize: "11px", color: t.accentGold, fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                      🍔 Story Updates
                    </span>
                    <button onClick={onClearStoryNotifs} style={{ background: "none", border: "none", color: "#5a4535", cursor: "pointer", fontSize: "10px", padding: "2px 8px", fontFamily: "'Cormorant Garamond', serif", transition: "color 0.2s" }}
                      onMouseEnter={e => e.target.style.color = "var(--ds-gold, #c4a882)"}
                      onMouseLeave={e => e.target.style.color = "#5a4535"}
                    >Clear all</button>
                  </div>
                  <div style={{ overflowY: "auto", flex: 1, padding: "4px 0" }}>
                    {storyNotifMessages.map(n => (
                      <button key={n.id} onClick={(e) => { e.stopPropagation(); onStoryNotifClick(n); }} style={{
                        padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: "10px",
                        borderBottom: "1px solid var(--ds-gold-a0f, rgba(196,168,130,0.06))", transition: "background 0.15s",
                        background: "none", border: "none", width: "100%", textAlign: "left",
                        touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none", minHeight: "44px",
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--ds-gold-a0f, rgba(196,168,130,0.06))"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        {n.avatar ? (
                          <img src={n.avatar} alt="" style={{ width: "24px", height: "24px", borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${n.color}44`, flexShrink: 0 }} />
                        ) : (
                          <span style={{ fontSize: "16px", flexShrink: 0, lineHeight: 1.2 }}>✨</span>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: n.color || t.accentGold, fontFamily: "'Cinzel', serif", lineHeight: 1.3, marginBottom: "1px" }}>
                            {n.sender}
                          </div>
                          <div style={{ fontSize: "12px", color: "#8a7565", lineHeight: 1.4, fontFamily: "'Cormorant Garamond', serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {n.content}
                          </div>
                        </div>
                        <span style={{ fontSize: "10px", color: "#5a4535", flexShrink: 0, fontFamily: "'Cormorant Garamond', serif" }}>{n.time}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={onClearStoryNotifs}
                    style={{
                      flexShrink: 0,
                      padding: "12px 14px",
                      background: "linear-gradient(135deg, var(--ds-gold-a38, rgba(196,168,130,0.22)), var(--ds-gold-a14, rgba(196,168,130,0.08)))",
                      border: "none", borderTop: "1px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))",
                      color: t.accentGold, cursor: "pointer",
                      fontSize: "12px", fontWeight: 600,
                      fontFamily: "'Cinzel', serif", letterSpacing: "1.5px", textTransform: "uppercase",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "linear-gradient(135deg, var(--ds-gold-a52, rgba(196,168,130,0.32)), var(--ds-gold-a24, rgba(196,168,130,0.14)))"}
                    onMouseLeave={e => e.currentTarget.style.background = "linear-gradient(135deg, var(--ds-gold-a38, rgba(196,168,130,0.22)), var(--ds-gold-a14, rgba(196,168,130,0.08)))"}
                  >Clear notifications</button>
                </div>
              </>
            )}
          </div>
          {/* The gear is gone. Haley, 2026-08-10: "combine these two menus
              there should only be the profile button not the gears but all
              features should be accessible from settings and profile by
              pressing the profile button." Both controls always opened the
              SAME component — the gear just preselected initialTab="app" —
              and ProfileSettings always receives appTab, so "App & AI" is
              permanently in its tab bar. Nothing became unreachable. */}
          <button onClick={onProfile} aria-label="Profile and settings" title="Profile and settings" style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "4px 8px", borderRadius: "8px", transition: "all 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-accent-a1a, rgba(198,40,40,0.1))")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div style={{ position: "relative" }}>
              <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: userProfile?.avatar_url ? `url(${userProfile.avatar_url}) center/cover` : `linear-gradient(135deg, ${userProfile?.accent_color || t.accentGold}55, ${userProfile?.accent_color || t.accentGold}22)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", border: `2px solid ${userProfile?.accent_color || t.accentGold}66`, boxShadow: `0 0 8px ${userProfile?.accent_color || t.accentGold}22` }}>
                {!userProfile?.avatar_url && <span style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", color: userProfile?.accent_color || t.accentGold }}>{currentPlayer?.charAt(0)?.toUpperCase()}</span>}
              </div>
              <div style={{ position: "absolute", bottom: "-1px", right: "-1px", width: "8px", height: "8px", borderRadius: "50%", background: t.accentSuccess, border: "2px solid #0a0a0a", boxShadow: "0 0 4px #43b58166" }}>
                <span className="sr-only">Online</span>
              </div>
            </div>
            <span style={{ color: userProfile?.accent_color || PLAYERS[currentPlayer]?.color || t.accentGold, fontSize: "12px", fontWeight: 600, fontFamily: "'Cormorant Garamond', serif" }}>{currentPlayer}</span>
          </button>
          <button onClick={onLogout} style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: "11px", fontFamily: "'Cormorant Garamond', serif", transition: "color 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-accent, #c62828)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9a8585")}
          >Log out</button>
        </div>
      )}

      {/* Mobile dropdown — secondary items only (nav tabs moved to bottom bar) */}
      {isMobile && mobileMenuOpen && (
        <div style={{ position: "absolute", top: "52px", left: 0, right: 0, background: "linear-gradient(180deg, var(--ds-surface-2, #120e0e), var(--ds-surface-0, #0a0a0a))", borderBottom: "1px solid var(--ds-accent-a33, #c6282833)", zIndex: 100, padding: "6px 8px", backdropFilter: "blur(12px)", animation: "fadeSlideIn 0.15s ease-out", touchAction: "manipulation", maxHeight: "calc(100dvh - 52px)", overflowY: "auto" }} role="menu">
          {/* All tabs — bottom mobile tab bar was removed, so the hamburger
              is now the single source of navigation on mobile. */}
          {tabs.map((tab) => {
            const isActiveTab = mainTab === tab.id;
            const tabHasUnread = unreadTabs?.[tab.id];
            const tabHasChannelTurn = tab.id === "channels" && eatSpeakNeedsReply && isEatSpeakUser;
            return (
              <button key={tab.id} role="menuitem" onClick={(e) => { e.stopPropagation(); setMainTab(tab.id); setMobileMenuOpen(false); }} style={{
                background: isActiveTab ? "var(--ds-accent-a22, #c6282822)" : "transparent",
                border: "none", color: isActiveTab ? "var(--ds-ink-hi, #e8d5d5)" : "var(--ds-ink-low, #a09080)",
                borderLeft: isActiveTab ? "2px solid var(--ds-accent, #c62828)" : "2px solid transparent",
                padding: "13px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer", width: "100%", textAlign: "left",
                fontFamily: "'Cinzel', serif", letterSpacing: "2px", textTransform: "uppercase",
                minHeight: "48px", boxSizing: "border-box", display: "flex", alignItems: "center", gap: "10px",
                touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none",
                position: "relative",
              }}>
                <span style={{ fontSize: "18px" }}>{tab.icon}</span>
                <span style={{ flex: 1 }}>{tab.label}</span>
                {(tabHasUnread || tabHasChannelTurn) && !isActiveTab && (
                  <span style={{
                    width: "10px", height: "10px", borderRadius: "50%",
                    background: tabHasChannelTurn ? "var(--ds-gold, #c4a882)" : "var(--ds-accent, #c62828)",
                    boxShadow: `0 0 6px ${tabHasChannelTurn ? "var(--ds-gold-a99, #c4a88299)" : "var(--ds-accent-a99, #c6282899)"}`,
                    flexShrink: 0,
                  }} />
                )}
              </button>
            );
          })}
          <div style={{ height: "1px", background: "linear-gradient(90deg, transparent, var(--ds-accent-a33, #c6282833), transparent)", margin: "4px 16px" }} />
          {/* Quick actions moved out of the cramped top bar */}
          <button role="menuitem" onClick={(e) => { e.stopPropagation(); onToggleMusic(); setMobileMenuOpen(false); }} style={{ background: musicPlayerVisible ? "var(--ds-gold-a1a, rgba(196,168,130,0.10))" : "none", border: "none", color: musicPlayerVisible ? t.accentGold : t.textMuted, cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
            <span style={{ fontSize: "18px" }}>♫</span> MUSIC PLAYER {musicPlayerVisible && <span style={{ fontSize: "10px", color: t.accentSuccess, marginLeft: "auto" }}>ON</span>}
          </button>
          <button role="menuitem" onClick={(e) => { e.stopPropagation(); onShowFateCard?.(); setMobileMenuOpen(false); }} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
            {/* Daily-fate-card nag dot + gold highlight removed per Haley's request. */}
            <span style={{ fontSize: "18px" }}>🌌</span> DAILY FATE CARD
          </button>
          <button role="menuitem" onClick={(e) => { e.stopPropagation(); setShowIncogPicker(p => !p); }} style={{ background: showIncogPicker ? "rgba(37,99,235,0.10)" : "none", border: "none", color: showIncogPicker ? "#7aa2f7" : t.textMuted, cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
            <span style={{ fontSize: "18px" }}>🕵️</span> INCOGNITO CHAT {showIncogPicker && <span style={{ fontSize: "10px", color: "#7aa2f7", marginLeft: "auto" }}>▼</span>}
          </button>
          {showIncogPicker && (
            <div style={{ background: '#0f0c0e', border: '1px solid #2a1a2a', borderRadius: '0', padding: '8px 16px', marginBottom: '4px' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 0 6px' }}>Choose disguise</div>
              {[
                { mode: 'office', icon: '💼', label: 'Office Mode', sub: 'Excel payroll spreadsheet' },
                { mode: 'tasks', icon: '🔧', label: 'Parts & Work Orders', sub: 'Service & task tracker' },
                { mode: 'education', icon: '🎓', label: 'Student Portal', sub: 'Online course viewer' },
                { mode: 'staff', icon: '🏫', label: 'School Staff', sub: 'Faculty reading portal' },
              ].map(opt => (
                <button key={opt.mode} onClick={() => { setShowIncogPicker(false); setMobileMenuOpen(false); openIncognitoPopup(opt.mode); }} style={{ width: '100%', background: 'transparent', border: '1px solid transparent', borderRadius: '7px', padding: '10px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left', minHeight: '48px' }}>
                  <span style={{ fontSize: '20px' }}>{opt.icon}</span>
                  <div><div style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0' }}>{opt.label}</div><div style={{ fontSize: '11px', color: '#6b7280' }}>{opt.sub}</div></div>
                </button>
              ))}
            </div>
          )}
          <div style={{ height: "1px", background: "linear-gradient(90deg, transparent, var(--ds-accent-a33, #c6282833), transparent)", margin: "4px 16px" }} />
          <button role="menuitem" onClick={(e) => { e.stopPropagation(); setShowUserList(prev => !prev); setMobileMenuOpen(false); }} style={{ background: "none", border: "none", color: t.accentGold, cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
            <span style={{ fontSize: "18px" }}>👥</span> USERS {onlineCount > 0 && <span style={{ fontSize: "11px", color: t.accentSuccess }}>({onlineCount} online)</span>}
          </button>
          {isAdmin && (
            <button role="menuitem" onClick={(e) => { e.stopPropagation(); onAdmin(); setMobileMenuOpen(false); }} style={{ background: "none", border: "none", color: t.accentDanger, cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
              <span style={{ fontSize: "18px" }}>🛡️</span> ADMIN
            </button>
          )}
          {/* WORLD replaces the old CHARACTERS entry (Haley 2026-08-30) —
              the unified interface holds characters, lore, places, groups,
              one search, and every pencil, so the separate editor entry is
              redundant here. Visible to everyone; editing gated inside. */}
          <button role="menuitem" onClick={(e) => { e.stopPropagation(); onEncyclopedia(); setMobileMenuOpen(false); }} style={{ background: "none", border: "none", color: t.accentGold, cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
            <span style={{ fontSize: "18px" }}>📖</span> WORLD
          </button>
          {/* One entry, matching the desktop nav — SETTINGS and PROFILE were
              two rows opening the same panel on different tabs. */}
          <button role="menuitem" onClick={(e) => { e.stopPropagation(); onProfile(); setMobileMenuOpen(false); }} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: "13px", padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
            <span style={{ fontSize: "18px" }}>👤</span> PROFILE &amp; SETTINGS
          </button>
          <button role="menuitem" onClick={(e) => { e.stopPropagation(); onLogout(); setMobileMenuOpen(false); }} style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: "13px", padding: "13px 16px", width: "100%", textAlign: "left", fontFamily: "'Cinzel', serif", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px", minHeight: "48px", touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" }}>
            <span style={{ fontSize: "18px" }}>🚪</span> LOG OUT
          </button>
        </div>
      )}

      {/* Mobile bottom tab bar removed at user request — all tabs are now
          accessible via the hamburger (☰) menu in the upper-right. */}

      {/* Mobile User List Panel — renders as a full-width slide-down below nav */}
      {isMobile && showUserList && !mobileMenuOpen && (
        <div style={{
          position: "absolute", top: "52px", left: 0, right: 0,
          background: "linear-gradient(180deg, var(--ds-surface-2, #120e0e), var(--ds-surface-0, #0a0a0a))",
          borderBottom: "1px solid var(--ds-accent-a33, #c6282833)", zIndex: 99,
          maxHeight: "60vh", overflowY: "auto",
          backdropFilter: "blur(12px)", animation: "fadeSlideIn 0.2s ease-out",
          touchAction: "manipulation",
        }}>
          <div style={{ padding: "12px 14px", borderBottom: "1px solid #1a1212", fontSize: "13px", fontWeight: 700, color: "var(--ds-ink-hi, #fff)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Users</span>
            <button onClick={() => setShowUserList(false)} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: "20px", padding: "4px 8px", minWidth: "44px", minHeight: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
          <div style={{ padding: "8px 12px" }}>
            {(() => {
              const online = (onlineProfiles || []).filter(p => p?.id && isUserOnline(p));
              const offline = (onlineProfiles || []).filter(p => p?.id && !isUserOnline(p));
              return (
                <>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: t.accentSuccess, textTransform: "uppercase", padding: "4px 6px", display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: t.accentSuccess }} /> Online — {online.length}
                  </div>
                  {online.map(p => {
                    const isMe = p.id === user?.id;
                    return (
                      <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 6px", borderRadius: "6px" }}>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <ProfileFrame frameId={p.profile_frame || 'none'} size={32}>
                            {p.avatar_url ? (
                              <img src={p.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', background: `${p.accent_color || t.accentGold}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>👤</div>
                            )}
                          </ProfileFrame>
                          <div style={{ position: "absolute", bottom: "-1px", right: "-1px", width: "8px", height: "8px", borderRadius: "50%", background: t.accentSuccess, border: "2px solid #120e0e" }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: p.accent_color || t.textPrimary, fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.display_name}{isMe && <span style={{ color: t.textMuted, fontWeight: 400 }}> (You)</span>}</div>
                          {p.status_message ? <div style={{ fontSize: "11px", color: t.textMuted }}>{p.status_message}</div> : p.last_seen && !p.hide_online_status && <div style={{ fontSize: "10px", color: "#4a9e6e88" }}>Active {formatLastActive(p.last_seen)}</div>}
                        </div>
                        {!isMe && <button
                          onClick={() => { setDmTargetUserId(p.id); setDmStartNew(true); setMainTab("dm"); setShowUserList(false); setTimeout(() => setDmStartNew(false), 500); }}
                          style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: "16px", padding: "8px", minWidth: "44px", minHeight: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}
                          title={`DM ${p.display_name}`}
                        >💌</button>}
                      </div>
                    );
                  })}
                  {online.length === 0 && <div style={{ fontSize: "12px", color: "#555", padding: "10px 6px" }}>No users online</div>}

                  <div style={{ fontSize: "10px", fontWeight: 700, color: t.textMuted, textTransform: "uppercase", padding: "8px 6px 4px", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: t.textFaint }} /> Offline — {offline.filter(p => p.id !== user?.id).length}
                  </div>
                  {offline.filter(p => p.id !== user?.id).map(p => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 6px", borderRadius: "6px", opacity: 0.6 }}>
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <ProfileFrame frameId={p.profile_frame || 'none'} size={32}>
                          {p.avatar_url ? (
                            <img src={p.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: `${p.accent_color || t.accentGold}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>👤</div>
                          )}
                        </ProfileFrame>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: t.textMuted, fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.display_name}</div>
                        {p.last_seen && !p.hide_online_status && <div style={{ fontSize: "10px", color: "#9a858588" }}>Last seen {formatLastActive(p.last_seen)}</div>}
                      </div>
                      <button
                        onClick={() => { setDmTargetUserId(p.id); setDmStartNew(true); setMainTab("dm"); setShowUserList(false); setTimeout(() => setDmStartNew(false), 500); }}
                        style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: "16px", padding: "8px", minWidth: "44px", minHeight: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}
                        title={`DM ${p.display_name}`}
                      >💌</button>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
// NOTE: These are module-level style objects — use hardcoded fallbacks only.
// Dynamic theme values (t.xxx) are NOT available here; components that need
// theme-responsive styles should compute their own styles from t via useContext.
